/* eslint-disable react-hooks/exhaustive-deps, react/no-unescaped-entities, no-empty, no-undef */
import React, { useState, useEffect, createContext, useContext, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Package, DollarSign, AlertTriangle, Zap, User, 
  LogOut, Plus, Minus, TrendingUp, Calendar, Users, ArrowLeft, Trash2,
  Settings, Key, Crown, Copy, Check, Loader2, Gift,
  Clock, Search, BarChart3, Award, MessageCircle, Mail, Instagram,
  Bell, Network, UserPlus, Briefcase, MapPin, Truck, CheckCircle, Bot, Send,
  Mic, MicOff, Ban, ShieldCheck, X, BookOpen, LifeBuoy, Sparkles, Image as ImageIcon, Menu, Megaphone, FileText, Wallet, Pencil, ChevronDown, CheckCircle2
} from "lucide-react";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { LangProvider, useLang, LANGUAGES } from "./i18n";
import ManagerDashboardV2 from "./ManagerDashboardV2";
import HistoryModal from "./HistoryModal";
import { MarketIntelligenceTab, DossierTab, PerformanceOverviewTab, NmSubStockersModal, ActivityReportTab, LowStockAlertsButton, SystemMessageButton, PendingActivationsButton } from "./CeoIntel";
import CreditScoreCard from "./CreditScoreCard";
import LanguageSwitcher from "./LanguageSwitcher";
import NotificationBell from "./NotificationBell";
import SupportCenter from "./SupportCenter";
import PaymentProfileCard from "./PaymentProfileCard";
import CeoAiAssistant from "./CeoAiAssistant";
import { CoverMedia, coverSlides } from "./CoverMedia";
import { matchProductName, imageForName } from "./SharedDashboard";
import { TeamBonusStatCard } from "./TeamBonusCard";
import { usePageVisuals, PageBackground, SectionAd } from "./PageVisuals";
import { CountryHero, StatsRow, MyStockVisual, ProductAutocomplete, ProductThumb, AnnouncementSlider, useSiteConfig, ModelPhotoConfirm } from "./SharedDashboard";
import { FinanceInvestments } from "./FinanceInvestments";
import { SaleCorrections, ExpensesPanel, PlatformIncome, LotAssignButton, LotLinkBanner, BatchLinkBar, LiveSales, ExportCsvPanel, SupportHistoryButton } from "./CeoOps";
import { PhotoVerifyButton, UnsoldStockAlert, CeoStockVerifyPanel } from "./StockPhotoVerify";
import { Camera as CameraIcon } from "lucide-react";
import CeoVisualManager from "./CeoVisualManager";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_asset-tracker-app-8/artifacts/5njv1r2x_Photoroom_20260219_033627.png";

// Haiti Cities
const HAITI_CITIES = ["Cap Haïtien", "Jacmel", "Port-au-Prince", "Port-de-Paix", "Arcahaie"];

// Calculate current cycle sold (not lifetime)
const getCycleSold = (user) => Math.max(0, (user?.initial_stock || 0) - (user?.current_stock || 0));
const getCycleBonus = (user) => getCycleSold(user) * 0.99;

const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

// ==================== SCREENSHOT PROTECTION ====================
const useScreenshotProtection = () => {
  useEffect(() => {
    const warningMessage = "En accédant à cette plateforme, vous acceptez de ne pas prendre de captures d'écran, d'enregistrer ou de partager toute information provenant de la plateforme Stocker de Vape-YARD.";
    
    // Detect PrintScreen and screenshot shortcuts
    const handleKeyDown = (e) => {
      // PrintScreen key
      if (e.key === 'PrintScreen' || e.keyCode === 44) {
        e.preventDefault();
        toast.error(warningMessage, { duration: 5000 });
        return false;
      }
      // Ctrl+Shift+S (Windows screenshot)
      if (e.ctrlKey && e.shiftKey && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        toast.error(warningMessage, { duration: 5000 });
        return false;
      }
      // Cmd+Shift+3, Cmd+Shift+4, Cmd+Shift+5 (Mac screenshots)
      if (e.metaKey && e.shiftKey && ['3', '4', '5'].includes(e.key)) {
        e.preventDefault();
        toast.error(warningMessage, { duration: 5000 });
        return false;
      }
      // Windows+Shift+S
      if (e.metaKey && e.shiftKey && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        toast.error(warningMessage, { duration: 5000 });
        return false;
      }
    };

    // Detect visibility change (may indicate screenshot on mobile)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // User switched away, could be taking screenshot
      }
    };

    // Prevent right-click context menu
    const handleContextMenu = (e) => {
      e.preventDefault();
      toast.warning("Clic droit désactivé sur cette plateforme", { duration: 2000 });
      return false;
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyDown);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyDown);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);
};

// ==================== CONTACT US COMPONENT ====================
const ContactUs = () => {
  return (
    <div className="glass-panel p-4 mt-4">
      <h4 className="text-white font-bold mb-3 flex items-center gap-2">
        <MessageCircle className="w-4 h-4 text-emerald-400" />
        Contact Us
      </h4>
      <div className="space-y-2">
        <a href="https://wa.me/message" target="_blank" rel="noopener noreferrer" 
          className="flex items-center gap-3 text-emerald-400 hover:text-emerald-300 transition-colors py-2 px-3 bg-emerald-500/10 rounded-lg">
          <MessageCircle className="w-5 h-5" />
          <span className="text-sm">WhatsApp for Help</span>
        </a>
        <a href="https://instagram.com/vapeyard1" target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-3 text-pink-400 hover:text-pink-300 transition-colors py-2 px-3 bg-pink-500/10 rounded-lg">
          <Instagram className="w-5 h-5" />
          <span className="text-sm">@vapeyard1</span>
        </a>
        <a href="mailto:vapeyard1@gmail.com"
          className="flex items-center gap-3 text-blue-400 hover:text-blue-300 transition-colors py-2 px-3 bg-blue-500/10 rounded-lg">
          <Mail className="w-5 h-5" />
          <span className="text-sm">vapeyard1@gmail.com</span>
        </a>
      </div>
    </div>
  );
};

// ==================== TIME DISPLAY COMPONENT ====================
const TimeDisplay = () => {
  const [times, setTimes] = useState({ usa: {}, dr: {}, haiti: {} });

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      
      // USA Eastern (UTC-5)
      const usaTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
      // DR Atlantic (UTC-4)
      const drTime = new Date(now.toLocaleString("en-US", { timeZone: "America/Santo_Domingo" }));
      // Haiti (UTC-5)
      const haitiTime = new Date(now.toLocaleString("en-US", { timeZone: "America/Port-au-Prince" }));
      
      setTimes({
        usa: { time: usaTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }), 
               date: usaTime.toLocaleDateString('fr-FR') },
        dr: { time: drTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }), 
              date: drTime.toLocaleDateString('fr-FR') },
        haiti: { time: haitiTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }), 
                 date: haitiTime.toLocaleDateString('fr-FR') }
      });
    };
    
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex justify-center gap-4 text-xs mb-6" data-testid="time-display">
      <div className="flex items-center gap-1 text-zinc-500">
        <span>🇺🇸</span>
        <span>{times.usa.time}</span>
      </div>
      <div className="flex items-center gap-1 text-zinc-500">
        <span>🇩🇴</span>
        <span>{times.dr.time}</span>
      </div>
      <div className="flex items-center gap-1 text-zinc-500">
        <span>🇭🇹</span>
        <span>{times.haiti.time}</span>
      </div>
    </div>
  );
};

// ==================== WELCOME PAGE ====================
const WelcomePage = () => {
  const pageVisuals = usePageVisuals();
  const navigate = useNavigate();
  const { lang, setLang, t } = useLang();
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [onlineCount, setOnlineCount] = useState(42);
  const [showSplash, setShowSplash] = useState(true);
  const [splashFade, setSplashFade] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);

  useEffect(() => {
    const baseCount = Math.floor(Math.random() * 23) + 20;
    setOnlineCount(baseCount);
    const interval = setInterval(() => {
      setOnlineCount(prev => {
        const change = Math.random() > 0.5 ? 1 : -1;
        return Math.max(20, Math.min(58, prev + change));
      });
    }, 30000);
    
    // Splash screen: show for 2.5s then fade out
    const fadeTimer = setTimeout(() => setSplashFade(true), 2500);
    const hideTimer = setTimeout(() => setShowSplash(false), 3200);
    
    return () => { clearInterval(interval); clearTimeout(fadeTimer); clearTimeout(hideTimer); };
  }, []);

  const countries = [
    { code: "usa", name: "États-Unis", flag: "🇺🇸" },
    { code: "dr", name: "République Dominicaine", flag: "🇩🇴" },
    { code: "haiti", name: "Haïti", flag: "🇭🇹" }
  ];

  const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/72c30d04-9e44-49da-8199-b0c342a39630/images/6d599e27140587dd2a9ed17dad54b4d78cea25c3415433086ec4d44ca5527bcc.png";
  const SLIDE_1 = "https://static.prod-images.emergentagent.com/jobs/72c30d04-9e44-49da-8199-b0c342a39630/images/af6db464170c50ea30eba890c1b3251b23472985063c1b7b41ccff3a2c468f31.png";
  const SLIDE_2 = "https://static.prod-images.emergentagent.com/jobs/72c30d04-9e44-49da-8199-b0c342a39630/images/04c3c79cdf8a147b7a8c189a511c72aca3a117079b338951c959e88493cdf845.png";
  const FLAGS_IMG = "https://static.prod-images.emergentagent.com/jobs/72c30d04-9e44-49da-8199-b0c342a39630/images/01ae370a8aa7d4c2745d507e27a125fbc869cc73c7c5556e6292ba4f2f8b2659.png";

  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState([SLIDE_1, SLIDE_2]);

  useEffect(() => {
    axios.get(`${API}/slider-images`).then(r => {
      if (r.data && r.data.length > 0) setSlides(r.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => setCurrentSlide(p => (p + 1) % slides.length), 6000);
    return () => clearInterval(timer);
  }, [slides]);

  // ===== SPLASH SCREEN =====
  if (showSplash) {
    return (
      <div className={`fixed inset-0 bg-black z-[100] flex flex-col items-center justify-center transition-opacity duration-700 ${splashFade ? 'opacity-0' : 'opacity-100'}`}>
        <img src={LOGO_URL} alt="Vape Yard" className="w-32 h-32 object-contain mb-6" style={{ animation: 'pulseGlow 2s ease-in-out infinite' }} />
        <h1 className="font-heading text-3xl font-light text-white tracking-tight mb-2" style={{ animation: 'fadeSlide 1s ease-out 0.5s forwards', opacity: 0 }}>Welcome to</h1>
        <h2 className="font-heading text-5xl font-bold text-white tracking-tighter" style={{ animation: 'fadeSlide 1s ease-out 0.8s forwards', opacity: 0 }}>VAPE YARD</h2>
        <div className="mt-8" style={{ animation: 'fadeSlide 1s ease-out 1.2s forwards', opacity: 0 }}>
          <div className="flex items-center gap-3">
            <span className="text-2xl">🇺🇸</span>
            <span className="text-2xl">🇩🇴</span>
            <span className="text-2xl">🇭🇹</span>
          </div>
        </div>
        <p className="text-zinc-600 text-xs mt-6 tracking-widest uppercase" style={{ animation: 'fadeSlide 1s ease-out 1.5s forwards', opacity: 0, fontFamily: 'var(--font-heading)' }}>Enterprise Distribution Platform</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Hero Background with slow zoom */}
      {pageVisuals?.welcome?.url ? <PageBackground cfg={pageVisuals.welcome} testid="welcome-custom-bg" /> : <div className="hero-bg-image" style={{ backgroundImage: `url(${HERO_BG})` }} />}
      
      {/* Gradient overlay */}
      {!pageVisuals?.welcome?.url && <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black z-[1]" />}
      
      <div className="relative z-10 max-w-6xl mx-auto px-6 py-6" style={pageVisuals?.welcome?.url ? { textShadow: "0 1px 6px rgba(0,0,0,0.9)" } : undefined}>
        {/* Top bar */}
        <div className="flex items-center justify-between mb-6 animate-fade-slide">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Vape Yard" className="w-12 h-12 object-contain" data-testid="welcome-logo" />
            <span className="font-heading text-white text-xl font-bold tracking-tight">VAPE YARD</span>
          </div>
          <div className="online-badge" data-testid="online-count">
            <span className="online-dot" />
            <span className="text-xs font-medium text-zinc-300">+{onlineCount} {t("online")}</span>
          </div>
        </div>

        {/* LANGUAGE SELECTOR */}
        <div className="flex justify-end mb-4 relative">
          <button
            onClick={() => setShowLangMenu(!showLangMenu)}
            data-testid="lang-selector-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/15 hover:bg-white/10 transition-all"
          >
            <span className="text-lg">{LANGUAGES.find(l => l.code === lang)?.flag}</span>
            <span className="text-xs text-white font-medium uppercase tracking-wider">{LANGUAGES.find(l => l.code === lang)?.label}</span>
            <span className="text-zinc-400 text-xs">▼</span>
          </button>
          {showLangMenu && (
            <div className="absolute top-12 right-0 bg-zinc-900/95 backdrop-blur-xl border border-white/20 rounded-2xl p-2 shadow-2xl z-50 min-w-[180px]" data-testid="lang-menu">
              {LANGUAGES.map(l => (
                <button
                  key={l.code}
                  onClick={() => { setLang(l.code); setShowLangMenu(false); toast.success(`Lang chanje → ${l.label}`); }}
                  data-testid={`lang-opt-${l.code}`}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-all ${lang === l.code ? 'bg-emerald-600/20 text-emerald-300' : 'text-zinc-300 hover:bg-white/5'}`}
                >
                  <span className="text-xl">{l.flag}</span>
                  <span className="text-sm font-medium">{l.label}</span>
                  {lang === l.code && <Check className="w-4 h-4 ml-auto" />}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* MAIN SLIDER - Full width on top */}
        <div className="animate-fade-slide mb-8">
          <div className="slide-container w-full aspect-[21/9] rounded-3xl overflow-hidden border border-white/10 shadow-2xl relative">
            {slides.map((slide, i) => (
              <img key={i} src={slide} alt={`Slide ${i+1}`} 
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${currentSlide === i ? 'opacity-100' : 'opacity-0'}`}
                style={{ animation: currentSlide === i ? 'slowPan 25s ease-in-out infinite alternate' : 'none' }} />
            ))}
            {/* Overlay gradient on slider */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent z-10" />
            {/* Slide indicators */}
            <div className="absolute bottom-3 right-6 z-20 flex gap-1.5">
              {slides.map((_, i) => (
                <div key={i} className={`w-2 h-2 rounded-full transition-all ${currentSlide === i ? 'bg-white w-6' : 'bg-white/30'}`} />
              ))}
            </div>
          </div>
        </div>

        {/* Hero content - below slider */}
        <div className="grid md:grid-cols-2 gap-8 items-start mb-10">
          <div className="animate-fade-slide-delay">
            <p className="text-zinc-400 text-base leading-relaxed mb-6 max-w-lg">
              {t("platform_tagline")}
            </p>
            
            {/* Country Selection */}
            <p className="text-xs tracking-[0.2em] uppercase text-zinc-600 font-semibold mb-3" style={{fontFamily: 'var(--font-heading)'}}>{t("select_zone")}</p>
            <div className="flex gap-3">
              {countries.map((country) => (
                <button
                  key={country.code}
                  onClick={() => { setSelectedCountry(country.code); setTimeout(() => navigate(`/login/${country.code}`), 300); }}
                  className={`flex items-center gap-3 px-5 py-3.5 rounded-2xl border transition-all duration-500 ${
                    selectedCountry === country.code 
                      ? 'bg-white/10 border-white/30 shadow-lg shadow-white/5' 
                      : 'bg-white/5 border-white/10 hover:border-white/20 hover:-translate-y-1'
                  }`}
                  data-testid={`country-${country.code}`}
                >
                  <span className="text-2xl">{country.flag}</span>
                  <span className="text-sm text-zinc-300 font-medium hidden md:block">{country.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Right side */}
          <div className="animate-fade-slide-delay2 flex flex-col items-center gap-4">
          </div>
        </div>

        {/* Access buttons */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl mx-auto animate-fade-slide-delay2">
          <button 
            onClick={() => navigate('/ceo/login')}
            className="glass-panel p-4 flex flex-col items-center gap-2 hover:-translate-y-1 transition-all duration-300"
            data-testid="ceo-access-link">
            <Crown className="w-5 h-5 text-amber-400" />
            <span className="text-white text-xs font-heading font-semibold">CEO</span>
          </button>
          <button 
            onClick={() => navigate('/manager/login')}
            className="glass-panel p-4 flex flex-col items-center gap-2 hover:-translate-y-1 transition-all duration-300"
            data-testid="manager-access-link">
            <BarChart3 className="w-5 h-5 text-blue-400" />
            <span className="text-white text-xs font-heading font-semibold">Manager</span>
          </button>
          <button 
            onClick={() => navigate('/network-marketer/login')}
            className="glass-panel p-4 flex flex-col items-center gap-2 hover:-translate-y-1 transition-all duration-300"
            data-testid="network-marketer-access-link">
            <Network className="w-5 h-5 text-violet-400" />
            <span className="text-white text-xs font-heading font-semibold">Network</span>
          </button>
          <button 
            onClick={() => navigate('/inv/login')}
            className="glass-panel p-4 flex flex-col items-center gap-2 hover:-translate-y-1 transition-all duration-300"
            data-testid="inv-access-link">
            <DollarSign className="w-5 h-5 text-emerald-400" />
            <span className="text-white text-xs font-heading font-semibold">INV</span>
          </button>
        </div>

        {/* Footer */}
        <div className="text-center mt-16 mb-8">
          <p className="text-zinc-600 text-xs tracking-widest uppercase" style={{fontFamily: 'var(--font-heading)'}}>Vape Yard Enterprise &copy; 2026</p>
        </div>
      </div>
    </div>
  );
};

// ==================== LOGIN PAGE (3 STEPS) ====================
const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [section, setSection] = useState(null); // null=choose, "vape", "hookah"
  const pageVisuals = usePageVisuals();
  const [step, setStep] = useState(1);
  const [code, setCode] = useState("");
  const [secretCode, setSecretCode] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [needSecretCreation, setNeedSecretCreation] = useState(false);
  const [readyToEnter, setReadyToEnter] = useState(false);
  const [stockerData, setStockerData] = useState(null);
  const country = window.location.pathname.split('/')[2] || 'haiti';

  const countryFlags = { usa: "🇺🇸", dr: "🇩🇴", haiti: "🇭🇹" };
  const countryNames = { usa: "États-Unis", dr: "République Dominicaine", haiti: "Haïti" };

  const handleHookahLogin = async () => {
    if (!code || !name.trim()) {
      toast.error("Entrez votre code et nom");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/hookah-login`, { code, name: name.trim(), country });
      login({ ...res.data, type: "hookah" }, {});
      toast.success("Bienvenue dans Hookah Experience!");
      navigate('/hookah/dashboard');
    } catch(e) {
      toast.error(e.response?.data?.detail || "Code incorrect");
    } finally {
      setLoading(false);
    }
  };

  const handleNumpad = (num) => {
    if (step === 1 && code.length < 6) setCode(prev => prev + num);
    else if (step === 2 && secretCode.length < 4) setSecretCode(prev => prev + num);
  };

  const handleDelete = () => {
    if (step === 1) setCode(prev => prev.slice(0, -1));
    else setSecretCode(prev => prev.slice(0, -1));
  };

  const handleStep1 = async () => {
    if (!code || code.length !== 6 || !name.trim()) {
      toast.error("Veuillez entrer votre code (6 chiffres) et nom");
      return;
    }
    
    setLoading(true);
    try {
      // Check if stocker needs to create or enter secret code
      const res = await axios.post(`${API}/auth/check-secret`, {
        code, name: name.trim(), country
      });
      
      if (res.data.found) {
        setNeedSecretCreation(res.data.need_secret_creation);
        setStep(2);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code ou nom incorrect");
    } finally {
      setLoading(false);
    }
  };

  const handleStep2 = async () => {
    if (!secretCode || secretCode.length !== 4) {
      toast.error("Veuillez entrer 4 chiffres");
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        code, 
        name: name.trim(), 
        country
      };
      
      if (needSecretCreation) {
        payload.new_secret_code = secretCode;
      } else {
        payload.secret_code = secretCode;
      }
      
      const res = await axios.post(`${API}/auth/login`, payload);
      
      // NEW: if backend returned a network_marketer role (shadow-guard fallback),
      // route to NM dashboard instead of stocker.
      if (res.data.success && res.data.role === "network_marketer" && res.data.user) {
        localStorage.setItem("nm_data", JSON.stringify(res.data.user));
        toast.success("Bienvenue Network Marketer!");
        navigate('/network-marketer/dashboard');
        return;
      }

      if (res.data.success) {
        // If creating new secret code, show START screen
        if (needSecretCreation) {
          setStockerData({ stocker: res.data.stocker, settings: res.data.settings });
          setReadyToEnter(true);
          setStep(3);
        } else {
          // Existing secret code - direct login
          login(res.data.stocker, res.data.settings);
          toast.success("Bienvenue chez Vape Yard!");
          navigate('/dashboard');
        }
      } else if (res.data.need_secret_creation) {
        setNeedSecretCreation(true);
      } else if (res.data.need_secret_entry) {
        setNeedSecretCreation(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code incorrect");
    } finally {
      setLoading(false);
    }
  };

  const handleStart = () => {
    if (stockerData) {
      login(stockerData.stocker, stockerData.settings);
      toast.success("Bienvenue chez Vape Yard!");
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 relative overflow-hidden">
      {pageVisuals?.section?.url ? <PageBackground cfg={pageVisuals.section} testid="section-custom-bg" /> : <div className="vape-bg" />}
      <div className="content-wrapper max-w-md mx-auto px-6 py-8 relative z-10" style={pageVisuals?.section?.url ? { textShadow: "0 1px 6px rgba(0,0,0,0.9)" } : undefined}>

        {/* Section Selection */}
        {!section && (
          <div className="animate-fade-slide">
            <button onClick={() => navigate('/')} className="text-zinc-500 hover:text-white transition-colors mb-6">← Retour</button>
            <div className="text-center mb-8">
              <span className="text-5xl mb-3 block">{countryFlags[country]}</span>
              <h2 className="font-heading text-2xl font-bold text-white">{countryNames[country]}</h2>
              <p className="text-zinc-500 text-sm mt-1">Choisissez votre section</p>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <button onClick={() => setSection("vape")}
                className="p-6 rounded-2xl border-2 border-yellow-500/40 bg-yellow-500/10 hover:bg-yellow-500/20 hover:border-yellow-500/60 transition-all duration-300 text-left group"
                data-testid="section-vape">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                    <Package className="w-7 h-7 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-yellow-400">Vape Section</h3>
                    <p className="text-zinc-400 text-sm">Gestion de stock vape</p>
                  </div>
                  <span className="ml-auto text-yellow-400 text-2xl opacity-0 group-hover:opacity-100 transition-opacity">→</span>
                </div>
              </button>
              <button onClick={() => setSection("hookah")}
                className="p-6 rounded-2xl border-2 border-violet-500/40 bg-violet-500/10 hover:bg-violet-500/20 hover:border-violet-500/60 transition-all duration-300 text-left group"
                data-testid="section-hookah">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-violet-500/20 flex items-center justify-center">
                    <Zap className="w-7 h-7 text-violet-400" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-violet-400">Hookah Experience</h3>
                    <p className="text-zinc-400 text-sm">Location & gestion hookah</p>
                  </div>
                  <span className="ml-auto text-violet-400 text-2xl opacity-0 group-hover:opacity-100 transition-opacity">→</span>
                </div>
              </button>
            </div>
            <SectionAd ad={pageVisuals?.ad} />
          </div>
        )}

        {/* Hookah Login */}
        {section === "hookah" && (
          <div className="animate-fade-slide">
            <button onClick={() => { setSection(null); setCode(""); setName(""); }} className="text-zinc-500 hover:text-white transition-colors mb-6">← Retour</button>
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-2xl bg-violet-500/20 flex items-center justify-center mx-auto mb-3">
                <Zap className="w-8 h-8 text-violet-400" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-white">Hookah Experience</h2>
              <p className="text-zinc-500 text-sm">{countryFlags[country]} {countryNames[country]}</p>
            </div>
            <div className="glass-panel p-6 space-y-4">
              <div>
                <label className="text-zinc-400 text-xs uppercase tracking-wider">Nom</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Votre nom..." className="w-full bg-zinc-800 border border-zinc-700 rounded-xl py-3 px-4 text-white mt-1" />
              </div>
              <div>
                <label className="text-zinc-400 text-xs uppercase tracking-wider">Code Hookah (lettres + chiffres)</label>
                <input type="text" value={code} onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))}
                  placeholder="Ex: AB12C3" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl py-3 px-4 text-white mt-1 font-mono text-lg tracking-widest"
                  data-testid="hookah-code-input" />
              </div>
              <button onClick={handleHookahLogin} disabled={loading}
                className="w-full bg-violet-600 hover:bg-violet-700 text-white py-4 rounded-xl font-heading font-bold text-lg transition-all disabled:opacity-50"
                data-testid="hookah-login-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "ENTRER"}
              </button>
            </div>
          </div>
        )}

        {/* Vape Login */}
        {section === "vape" && step !== 3 && (
          <button onClick={() => step === 1 ? setSection(null) : setStep(step - 1)} className="text-zinc-500 hover:text-white transition-colors mb-6" data-testid="back-button">
            ← {step === 1 ? 'Retour' : 'Étape précédente'}
          </button>
        )}

        {section === "vape" && (<>
        <div className="flex flex-col items-center mb-6">
          <span className="text-5xl mb-2">{countryFlags[country]}</span>
          <span className="text-zinc-400 text-sm">{countryNames[country]}</span>
        </div>

        <div className="flex justify-center gap-2 mb-6">
          <div className={`w-3 h-3 rounded-full ${step >= 1 ? 'bg-violet-500' : 'bg-zinc-700'}`} />
          <div className={`w-3 h-3 rounded-full ${step >= 2 ? 'bg-violet-500' : 'bg-zinc-700'}`} />
          <div className={`w-3 h-3 rounded-full ${step >= 3 ? 'bg-emerald-500' : 'bg-zinc-700'}`} />
        </div>

        {step === 1 ? (
          <>
            <h2 className="font-heading text-2xl font-bold text-center text-white mb-2">Code Principal</h2>
            <p className="text-zinc-500 text-center text-sm mb-6">Entrez le code donné par le CEO</p>
            <div className="flex justify-center gap-2 mb-6" data-testid="code-display">
              {[...Array(6)].map((_, i) => (
                <div key={i} className={`w-12 h-14 rounded-xl flex items-center justify-center text-2xl font-mono ${code[i] ? 'bg-violet-600 text-white' : 'bg-zinc-800 text-zinc-600 border border-zinc-700'}`}>
                  {code[i] ? '•' : '-'}
                </div>
              ))}
            </div>
            
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 'C', 0, '⌫'].map((num) => (
                <button key={num} onClick={() => {
                  if (num === 'C') setCode('');
                  else if (num === '⌫') handleDelete();
                  else handleNumpad(num.toString());
                }} className={`numpad-btn ${num === 'C' ? 'text-red-400' : num === '⌫' ? 'text-amber-400' : ''}`}
                data-testid={`numpad-${num}`}>{num}</button>
              ))}
            </div>
            
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Votre nom"
              className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-violet-500 text-white placeholder:text-zinc-600 rounded-xl py-4 px-5 text-lg mb-6 focus:outline-none"
              data-testid="name-input" />
            
            <button onClick={handleStep1} disabled={loading || !code || code.length !== 6 || !name}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white font-bold py-4 rounded-xl transition-all active:scale-95 uppercase tracking-wider disabled:opacity-50"
              data-testid="next-step-button">
              {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : "Suivant"}
            </button>
          </>
        ) : step === 2 ? (
          <>
            <h2 className="font-heading text-2xl font-bold text-center text-white mb-2">
              {needSecretCreation ? "Créez Votre Code Secret" : "Code Secret"}
            </h2>
            <p className="text-zinc-500 text-center text-sm mb-4">
              {needSecretCreation 
                ? "Choisissez un code secret de 4 chiffres (valable 3 jours)" 
                : "Entrez votre code secret personnel"}
            </p>
            
            <div className="flex justify-center gap-3 mb-4" data-testid="secret-code-display">
              {[...Array(4)].map((_, i) => (
                <div key={i} className={`w-14 h-16 rounded-xl flex items-center justify-center text-3xl font-mono ${secretCode[i] ? (needSecretCreation ? 'bg-yellow-500 text-zinc-900' : 'bg-emerald-600 text-white') : 'bg-zinc-800 text-zinc-600 border border-zinc-700'}`}>
                  {secretCode[i] ? '•' : '-'}
                </div>
              ))}
            </div>
            
            <div className={`border rounded-xl p-3 mb-6 ${needSecretCreation ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-zinc-900/50 border-zinc-800'}`}>
              <p className={`text-sm text-center ${needSecretCreation ? 'text-yellow-400' : 'text-zinc-400'}`}>
                {needSecretCreation 
                  ? "🔐 Mémorisez ce code! Vous seul le connaissez." 
                  : "🔒 Ce code est valable 3 jours"}
              </p>
            </div>
            
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 'C', 0, '⌫'].map((num) => (
                <button key={num} onClick={() => {
                  if (num === 'C') setSecretCode('');
                  else if (num === '⌫') setSecretCode(prev => prev.slice(0, -1));
                  else if (secretCode.length < 4) setSecretCode(prev => prev + num);
                }} className={`numpad-btn ${num === 'C' ? 'text-red-400' : num === '⌫' ? 'text-amber-400' : ''}`}
                data-testid={`secret-numpad-${num}`}>{num}</button>
              ))}
            </div>
            
            <button onClick={handleStep2} disabled={loading || secretCode.length !== 4}
              className={`w-full font-bold py-4 rounded-xl transition-all active:scale-95 uppercase tracking-wider disabled:opacity-50 ${needSecretCreation ? 'bg-yellow-500 hover:bg-yellow-600 text-zinc-900' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}
              data-testid="login-button">
              {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : (needSecretCreation ? "Créer Code" : "Entrer")}
            </button>
          </>
        ) : (
          /* Step 3 - Ready to Start */
          <div className="text-center">
            <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-full p-6 w-32 h-32 mx-auto mb-6 flex items-center justify-center">
              <Check className="w-16 h-16 text-emerald-400" />
            </div>
            
            <h2 className="font-heading text-3xl font-bold text-white mb-3">Code Secret Créé!</h2>
            <p className="text-zinc-400 mb-8">Votre code secret est maintenant actif pour 3 jours</p>
            
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 mb-8">
              <p className="text-zinc-500 text-sm mb-2">Bienvenue</p>
              <p className="text-white text-xl font-bold">{name}</p>
            </div>
            
            <button onClick={handleStart}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-5 rounded-xl transition-all active:scale-95 uppercase tracking-widest text-xl"
              data-testid="start-button">
              🚀 START
            </button>
            
            <p className="text-zinc-600 text-xs mt-4">Cliquez pour accéder à votre dashboard</p>
          </div>
        )}
        </>)}
      </div>
    </div>
  );
};

// ==================== CEO LOGIN ====================
const CEOLogin = () => {
  const pageVisuals = usePageVisuals();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");

  const handleLogin = () => {
    if (name.toLowerCase() === "kymbell" && code === "482316") {
      localStorage.setItem('ceoAuth', 'true');
      toast.success("Bienvenue CEO Kymbell!");
      navigate('/ceo/dashboard');
    } else {
      toast.error("Identifiants CEO incorrects");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 relative overflow-hidden">
      {pageVisuals?.section?.url ? <PageBackground cfg={pageVisuals.section} testid="section-custom-bg" /> : <div className="vape-bg" />}
      <div className="relative z-10 content-wrapper max-w-md mx-auto px-6 py-8">
        <button onClick={() => navigate('/')} className="text-zinc-500 hover:text-white transition-colors mb-6" data-testid="ceo-back-button">← Retour</button>

        <div className="flex justify-center mb-6">
          <div className="bg-yellow-400/20 border border-yellow-400/30 rounded-full p-4">
            <Crown className="w-12 h-12 text-yellow-400" />
          </div>
        </div>

        <h2 className="font-heading text-2xl font-bold text-center text-white mb-2">Accès CEO</h2>
        <p className="text-zinc-500 text-center mb-8">Générer des codes pour les stockers</p>

        <div className="flex justify-center gap-2 mb-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className={`w-12 h-14 rounded-xl flex items-center justify-center text-2xl font-mono ${code[i] ? 'bg-yellow-500 text-zinc-900' : 'bg-zinc-800 text-zinc-600 border border-zinc-700'}`}>
              {code[i] ? '•' : '-'}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 'C', 0, '⌫'].map((num) => (
            <button key={num} onClick={() => {
              if (num === 'C') setCode('');
              else if (num === '⌫') setCode(prev => prev.slice(0, -1));
              else if (code.length < 6) setCode(prev => prev + num);
            }} className={`numpad-btn ${num === 'C' ? 'text-red-400' : num === '⌫' ? 'text-amber-400' : ''}`}
            data-testid={`ceo-numpad-${num}`}>{num}</button>
          ))}
        </div>

        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom du CEO"
          className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-yellow-500 text-white placeholder:text-zinc-600 rounded-xl py-4 px-5 text-lg mb-6 focus:outline-none"
          data-testid="ceo-name-input" />

        <button onClick={handleLogin} disabled={!code || !name}
          className="w-full bg-yellow-500 hover:bg-yellow-600 text-zinc-900 font-bold py-4 rounded-xl transition-all active:scale-95 uppercase tracking-wider disabled:opacity-50"
          data-testid="ceo-login-button">Accéder</button>
      </div>
    </div>
  );
};

// ==================== MANAGER LOGIN ====================
const ManagerLogin = () => {
  const pageVisuals = usePageVisuals();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const handleNumpad = (num) => {
    if (code.length < 6) setCode(prev => prev + num);
  };

  const handleLogin = async () => {
    if (!code || code.length < 5 || !name.trim()) {
      toast.error("Entrez votre code (5-6 chiffres) et nom");
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/manager/login`, {
        code: code,
        name: name.trim()
      });
      
      if (res.data.success) {
        localStorage.setItem('managerAuth', JSON.stringify({ 
          country: res.data.country, 
          name: res.data.name 
        }));
        toast.success(`Bienvenue Manager ${res.data.name}!`);
        navigate('/manager/dashboard');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code ou nom incorrect");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 relative overflow-hidden">
      {pageVisuals?.section?.url ? <PageBackground cfg={pageVisuals.section} testid="section-custom-bg" /> : <div className="vape-bg" />}
      <div className="relative z-10 content-wrapper max-w-md mx-auto px-6 py-8">
        <button onClick={() => navigate('/')} className="text-zinc-500 hover:text-white transition-colors mb-6" data-testid="manager-back-button">← Retour</button>

        <div className="flex justify-center mb-6">
          <div className="bg-blue-400/20 border border-blue-400/30 rounded-full p-4">
            <BarChart3 className="w-12 h-12 text-blue-400" />
          </div>
        </div>

        <h2 className="font-heading text-2xl font-bold text-center text-white mb-2">Accès Manager</h2>
        <p className="text-zinc-500 text-center mb-6">Entrez votre code donné par le CEO</p>

        <div className="flex justify-center gap-2 mb-2" data-testid="manager-code-display">
          {[...Array(6)].map((_, i) => (
            <div key={i} className={`w-12 h-14 rounded-xl flex items-center justify-center text-2xl font-mono ${code[i] ? 'bg-blue-500 text-white' : 'bg-zinc-800 text-zinc-600 border border-zinc-700'}`}>
              {code[i] ? '•' : '-'}
            </div>
          ))}
        </div>
        <p className="text-zinc-600 text-xs text-center mb-4">5-6 chiffres</p>

        <div className="grid grid-cols-3 gap-3 mb-6">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 'C', 0, '⌫'].map((num) => (
            <button key={num} onClick={() => {
              if (num === 'C') setCode('');
              else if (num === '⌫') setCode(prev => prev.slice(0, -1));
              else handleNumpad(num.toString());
            }} className={`numpad-btn ${num === 'C' ? 'text-red-400' : num === '⌫' ? 'text-amber-400' : ''}`}
            data-testid={`manager-numpad-${num}`}>{num}</button>
          ))}
        </div>

        <input type="text" value={name} onChange={(e) => setName(e.target.value)} 
          placeholder="Votre nom"
          className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-blue-500 text-white placeholder:text-zinc-600 rounded-xl py-4 px-5 text-lg mb-6 focus:outline-none"
          data-testid="manager-name" />

        <button onClick={handleLogin} disabled={loading || !code || code.length < 5 || !name}
          className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 rounded-xl transition-all active:scale-95 uppercase tracking-wider disabled:opacity-50"
          data-testid="manager-login-button">
          {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : "Accéder"}
        </button>
      </div>
    </div>
  );
};

// ==================== MANAGER DASHBOARD ====================
const ManagerDashboard = () => {
  const navigate = useNavigate();
  const [stockers, setStockers] = useState([]);
  const [networkMarketers, setNetworkMarketers] = useState([]);
  const [totals, setTotals] = useState({});
  const [upcomingControls, setUpcomingControls] = useState([]);
  const [recentSales, setRecentSales] = useState([]);
  const [country, setCountry] = useState(null);
  const [managerName, setManagerName] = useState("");
  const [activeTab, setActiveTab] = useState("stockers");
  const [managerHookahSessions, setManagerHookahSessions] = useState([]);
  const [showCityControl, setShowCityControl] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  const [availableCities, setAvailableCities] = useState([]);

  // Screenshot protection
  useScreenshotProtection();

  const countryNames = { usa: "États-Unis", dr: "République Dominicaine", haiti: "Haïti" };
  const countryFlags = { usa: "🇺🇸", dr: "🇩🇴", haiti: "🇭🇹" };

  useEffect(() => {
    const auth = localStorage.getItem('managerAuth');
    if (!auth) {
      navigate('/manager/login');
      return;
    }
    const { country: c, name } = JSON.parse(auth);
    setCountry(c);
    setManagerName(name || "Manager");
    fetchStats(c);
  }, [navigate]);

  const fetchStats = async (c, city = null) => {
    try {
      const url = city ? `${API}/manager/stats/${c}?city=${encodeURIComponent(city)}` : `${API}/manager/stats/${c}`;
      const res = await axios.get(url);
      setStockers(res.data.stockers);
      setNetworkMarketers(res.data.network_marketers || []);
      setTotals(res.data.totals);
      setUpcomingControls(res.data.upcoming_controls);
      setRecentSales(res.data.recent_sales || []);
      setAvailableCities(res.data.available_cities || []);
    } catch (e) {
      console.log("Error fetching stats");
    }
    // Manager hookah live sessions (country-filtered)
    try {
      const hres = await axios.get(`${API}/hookah/daily-session/country/${c}/active`);
      setManagerHookahSessions(hres.data || []);
    } catch (e) {}
  };

  // Auto-refresh hookah sessions every 15s
  useEffect(() => {
    if (!country) return;
    const tick = setInterval(async () => {
      try {
        const hres = await axios.get(`${API}/hookah/daily-session/country/${country}/active`);
        setManagerHookahSessions(hres.data || []);
      } catch (e) {}
    }, 15000);
    return () => clearInterval(tick);
  }, [country]);

  const handleLogout = () => {
    localStorage.removeItem('managerAuth');
    navigate('/');
  };

  if (!country) return null;

  return (
    <div className="min-h-screen bg-zinc-950 relative overflow-hidden" data-testid="manager-dashboard">
      <div className="vape-bg" />
      <div className="content-wrapper max-w-4xl mx-auto px-6 py-6">
        {/* Header with Welcome */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="bg-blue-400/20 border border-blue-400/30 rounded-full p-2">
              <BarChart3 className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold text-white">Bienvenue sur la plateforme</h1>
              <p className="text-blue-400 font-bold text-lg">Manager {managerName}</p>
              <p className="text-zinc-500 flex items-center gap-2">
                <span className="text-xl">{countryFlags[country]}</span> {countryNames[country]}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <TimeDisplay />
            <button onClick={handleLogout} className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors" data-testid="manager-logout">
              <LogOut className="w-5 h-5" /> Sortir
            </button>
          </div>
        </div>

        {/* Stats Cards - NO MONEY */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="glass-panel p-4 text-center">
            <Users className="w-6 h-6 text-violet-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{totals.total_stockers || 0}</p>
            <p className="text-zinc-500 text-xs">Stockers</p>
          </div>
          <div className="glass-panel p-4 text-center">
            <Package className="w-6 h-6 text-blue-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{totals.total_sold || 0}</p>
            <p className="text-zinc-500 text-xs">Vapes Vendus</p>
          </div>
          <div className="glass-panel p-4 text-center">
            <Package className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-emerald-400">{totals.total_current_stock || 0}/{totals.total_initial_stock || 0}</p>
            <p className="text-zinc-500 text-xs">Stock Total</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button onClick={() => setActiveTab("stockers")}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${activeTab === "stockers" ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
            <Users className="w-4 h-4" /> Stockers ({stockers.length})
          </button>
          <button onClick={() => setActiveTab("network")}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${activeTab === "network" ? 'bg-purple-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
            <Network className="w-4 h-4" /> Network Marketers ({networkMarketers.length})
          </button>
          <button onClick={() => setActiveTab("rapports")}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${activeTab === "rapports" ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
            <TrendingUp className="w-4 h-4" /> Rapports
          </button>
          <button onClick={() => setActiveTab("alertes")}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${activeTab === "alertes" ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
            <AlertTriangle className="w-4 h-4" /> Alertes ({upcomingControls.length})
          </button>
          <button onClick={() => setActiveTab("hookah")} data-testid="manager-hookah-tab"
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${activeTab === "hookah" ? 'bg-violet-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
            <Zap className="w-4 h-4" /> Hookah ({managerHookahSessions.length})
          </button>
        </div>

        {/* Stockers Tab */}
        {activeTab === "stockers" && (
          <div className="glass-panel p-6">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
              <h3 className="text-white font-bold flex items-center gap-2">
                <Users className="w-5 h-5 text-violet-400" /> 
                Stockers {countryFlags[country]} {selectedCity ? `— ${selectedCity}` : ''} ({stockers.length})
              </h3>
              <div className="flex gap-2">
                <button onClick={() => setShowCityControl(!showCityControl)} data-testid="manager-control-btn"
                  className="bg-violet-600 hover:bg-violet-500 text-white text-xs font-bold px-3 py-2 rounded-lg">
                  🎯 Kontwol Manager
                </button>
                {selectedCity && (
                  <button onClick={() => { setSelectedCity(null); fetchStats(country, null); }} data-testid="manager-clear-city"
                    className="bg-zinc-700 hover:bg-zinc-600 text-white text-xs font-bold px-3 py-2 rounded-lg">
                    ✕ {selectedCity}
                  </button>
                )}
              </div>
            </div>

            {/* City picker panel */}
            {showCityControl && (
              <div className="bg-zinc-900/80 rounded-xl p-3 mb-4 border border-violet-500/30" data-testid="manager-city-panel">
                <p className="text-zinc-400 text-xs font-bold uppercase mb-2">Chwazi yon vil pou filtre:</p>
                <div className="flex flex-wrap gap-2">
                  {(availableCities || []).map(c => (
                    <button key={c} onClick={() => { setSelectedCity(c); setShowCityControl(false); fetchStats(country, c); }}
                      data-testid={`manager-city-${c.toLowerCase().replace(/\s+/g,'-')}`}
                      className={`text-xs font-bold px-3 py-1.5 rounded-full ${selectedCity === c ? 'bg-violet-600 text-white' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}>
                      📍 {c}
                    </button>
                  ))}
                  {availableCities.length === 0 && <span className="text-zinc-600 text-xs">Pa gen vil ki deklare nan stockers yo</span>}
                </div>
              </div>
            )}
            
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {stockers.map((stocker) => (
                <div key={stocker.id} className="bg-zinc-900/50 p-4 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-white font-bold text-lg">{stocker.name}</p>
                      <p className="text-zinc-500 text-sm">
                        {stocker.city && <span className="text-violet-400">📍 {stocker.city} • </span>}
                        Contrôle: {new Date(stocker.control_date).toLocaleDateString('fr-FR')}
                        {stocker.days_remaining <= 3 && (
                          <span className="text-amber-400 ml-2">({stocker.days_remaining}j)</span>
                        )}
                      </p>
                      {stocker.last_sale_date && (
                        <p className="text-emerald-400 text-[11px] mt-0.5">
                          💰 Dènye vant: {new Date(stocker.last_sale_date).toLocaleDateString('fr-FR')} • <span className="text-zinc-300"><ProductThumb name={stocker.last_vape_model} size={18} /> {stocker.last_quantity}x {stocker.last_vape_model}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* CARRYOVER BANNER - RED when unsold, GREEN when fully sold this cycle */}
                  {(stocker.carryover_from_previous_cycle || 0) > 0 && (() => {
                    const carryTotal = stocker.carryover_from_previous_cycle || 0;
                    const cycleSold = Math.max(0, (stocker.initial_stock || 0) - (stocker.current_stock || 0));
                    const carryFullySold = cycleSold >= carryTotal;
                    if (carryFullySold) {
                      return (
                        <div className="bg-emerald-950/40 border-2 border-emerald-500 rounded-lg p-2 mb-3 flex items-center justify-between" data-testid={`carryover-banner-${stocker.id}`}>
                          <div className="flex items-center gap-2">
                            <span className="w-4 h-4 flex items-center justify-center text-emerald-400 text-lg">✅</span>
                            <div>
                              <p className="text-emerald-300 text-xs font-bold">{t("carryover_cleared")}</p>
                              <p className="text-emerald-400 text-[10px]">{t("carryover_cleared_hint")}</p>
                            </div>
                          </div>
                          <div className="text-emerald-400 font-bold text-2xl">✓{carryTotal}</div>
                        </div>
                      );
                    }
                    return (
                      <div className="bg-red-950/40 border-2 border-red-600 rounded-lg p-2 mb-3 flex items-center justify-between" data-testid={`carryover-banner-${stocker.id}`}>
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-400" />
                          <div>
                            <p className="text-red-300 text-xs font-bold">{t("carryover_pending_title")}</p>
                            <p className="text-red-400 text-[10px]">{t("carryover_pending_hint")}</p>
                          </div>
                        </div>
                        <div className="text-red-400 font-bold text-2xl">{carryTotal - cycleSold}</div>
                      </div>
                    );
                  })()}
                  
                  {/* Stock & Sales Info - CURRENT CYCLE ONLY */}
                  <div className="grid grid-cols-5 gap-2 text-center text-sm mb-3">
                    <div className="bg-blue-500/10 border border-blue-500/30 p-2 rounded-lg">
                      <p className="text-blue-400 text-[10px] uppercase">Stock</p>
                      <p className="text-white font-bold text-lg">{stocker.initial_stock || 0}</p>
                    </div>
                    <div className="bg-emerald-500/10 border border-emerald-500/30 p-2 rounded-lg">
                      <p className="text-emerald-400 text-[10px] uppercase">Vendus</p>
                      <p className="text-emerald-400 font-bold text-lg">{Math.max(0, (stocker.initial_stock || 0) - (stocker.current_stock || 0))}</p>
                    </div>
                    <div className="bg-amber-500/10 border border-amber-500/30 p-2 rounded-lg">
                      <p className="text-amber-400 text-[10px] uppercase">Restant</p>
                      <p className="text-amber-400 font-bold text-lg">{stocker.current_stock || 0}</p>
                    </div>
                    <div className="bg-pink-500/10 border border-pink-500/30 p-2 rounded-lg">
                      <p className="text-pink-400 text-[10px] uppercase">Prix</p>
                      <p className="text-pink-400 font-bold text-lg">${stocker.vape_models?.[0]?.price || 0}</p>
                    </div>
                    <div className="bg-violet-500/10 border border-violet-500/30 p-2 rounded-lg">
                      <p className="text-violet-400 text-[10px] uppercase">Bonus</p>
                      <p className="text-violet-400 font-bold text-lg">${((Math.max(0, (stocker.initial_stock || 0) - (stocker.current_stock || 0))) * 0.99).toFixed(0)}</p>
                    </div>
                  </div>
                  
                  {/* Commission for CEO */}
                  <div className="bg-zinc-800/50 p-2 rounded-lg flex items-center justify-between mb-2">
                    <span className="text-zinc-400 text-xs">Commission ({stocker.commission_rate || 0}%)</span>
                    <span className="text-emerald-400 font-bold text-sm">${(((stocker.initial_stock || 0) - (stocker.current_stock || 0)) * (stocker.vape_models?.[0]?.price || 0) * (stocker.commission_rate || 0) / 100).toFixed(2)}</span>
                  </div>
                  
                  {/* Progress Bar - Sales Progress */}
                  <div className="mt-3">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-zinc-500">Progression des ventes</span>
                      <span className={`font-bold ${stocker.alert_status === 'good' ? 'text-emerald-400' : stocker.alert_status === 'warning' ? 'text-amber-400' : 'text-red-400'}`}>
                        {(stocker.stock_percentage || 0).toFixed(0)}%
                      </span>
                    </div>
                    <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${stocker.alert_status === 'good' ? 'bg-emerald-400' : stocker.alert_status === 'warning' ? 'bg-amber-400' : 'bg-red-400'}`}
                        style={{ width: `${stocker.stock_percentage || 0}%` }} />
                    </div>
                  </div>
                </div>
              ))}
              
              {stockers.length === 0 && (
                <p className="text-zinc-500 text-center py-8">Aucun stocker dans ce pays</p>
              )}
            </div>
          </div>
        )}

        {/* Network Marketers Tab */}
        {activeTab === "network" && (
          <div className="glass-panel p-6">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <Network className="w-5 h-5 text-purple-400" /> 
              Network Marketers {countryFlags[country]} ({networkMarketers.length})
            </h3>
            
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {networkMarketers.map((nm) => (
                <div key={nm.id} className="bg-zinc-900/50 p-4 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-purple-500/20 p-2 rounded-lg">
                        <Network className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-white font-bold text-lg">{nm.name}</p>
                        <p className="text-purple-400 text-sm">{nm.stocker_count || 0}/3 Stockers</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3 text-center text-sm">
                    <div className="bg-purple-500/10 border border-purple-500/30 p-3 rounded-lg">
                      <p className="text-purple-400 text-xs uppercase">Stockers</p>
                      <p className="text-white font-bold text-xl">{nm.stocker_count || 0}</p>
                    </div>
                    <div className="bg-emerald-500/10 border border-emerald-500/30 p-3 rounded-lg">
                      <p className="text-emerald-400 text-xs uppercase">Vendus</p>
                      <p className="text-emerald-400 font-bold text-xl">{nm.total_sold || 0}</p>
                    </div>
                    <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded-lg">
                      <p className="text-amber-400 text-xs uppercase">Stock</p>
                      <p className="text-amber-400 font-bold text-xl">{nm.current_stock || 0}</p>
                    </div>
                  </div>
                  
                  {/* NM's sub-stockers */}
                  {nm.stockers && nm.stockers.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <p className="text-zinc-500 text-xs uppercase">Sous-Stockers:</p>
                      {nm.stockers.map((st, idx) => (
                        <div key={st.id || idx} className="bg-zinc-800/50 p-2 rounded-lg flex items-center justify-between text-sm">
                          <span className="text-zinc-300">{st.stocker_number || `ST${idx+1}`} - {st.name}</span>
                          <div className="flex gap-3 text-xs">
                            <span className="text-blue-400">Stock: {st.quantity || 0}</span>
                            <span className="text-emerald-400">Vendus: {getCycleSold(st)}</span>
                            <span className="text-amber-400">Restant: {st.current_stock || 0}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              
              {networkMarketers.length === 0 && (
                <p className="text-zinc-500 text-center py-8">Aucun Network Marketer dans ce pays</p>
              )}
            </div>
          </div>
        )}

        {/* Rapports Tab - Recent Sales */}
        {activeTab === "rapports" && (
          <div className="glass-panel p-6">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-400" /> 
              Rapports de Ventes Récentes
            </h3>
            
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {recentSales.map((sale, idx) => (
                <div key={idx} className="bg-zinc-900/50 p-3 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-emerald-500/20 p-2 rounded-lg">
                      <Package className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{sale.stocker_name}</p>
                      <p className="text-zinc-500 text-xs flex items-center gap-1"><ProductThumb name={sale.vape_model} size={18} />{sale.vape_model}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-emerald-400 font-bold">+{sale.quantity_sold}</p>
                    <p className="text-zinc-500 text-xs">
                      {new Date(sale.recorded_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              ))}
              
              {recentSales.length === 0 && (
                <p className="text-zinc-500 text-center py-8">Aucune vente récente</p>
              )}
            </div>
          </div>
        )}

        {/* Alertes Tab */}
        {activeTab === "alertes" && (
          <div className="glass-panel p-6">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Contrôles à Venir
            </h3>
            
            {upcomingControls.length > 0 ? (
              <div className="space-y-2">
                {upcomingControls.map((s) => (
                  <div key={s.id} className="flex items-center justify-between bg-amber-500/10 p-4 rounded-lg border border-amber-500/30">
                    <div>
                      <span className="text-white font-bold">{s.name}</span>
                      <p className="text-zinc-500 text-sm">Stock: {s.current_stock}/{s.initial_stock}</p>
                    </div>
                    <div className="text-right">
                      <span className={`font-bold ${s.days_remaining === 0 ? 'text-red-400' : 'text-amber-400'}`}>
                        {s.days_remaining === 0 ? "Aujourd'hui!" : `${s.days_remaining} jour(s)`}
                      </span>
                      <p className="text-zinc-500 text-xs">
                        {new Date(s.control_date).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-500 text-center py-8">Aucun contrôle prévu prochainement</p>
            )}
          </div>
        )}

        {/* HOOKAH TAB — Live sessions for this manager's country */}
        {activeTab === "hookah" && (
          <div className="space-y-4" data-testid="manager-hookah-content">
            {/* Hero */}
            <div className="relative rounded-2xl overflow-hidden border border-violet-700/30" style={{minHeight: 140}}>
              <img src={HOOKAH_HERO} alt="" onError={(e) => { e.target.src = HOOKAH_HERO_FALLBACK; }} className="absolute inset-0 w-full h-full object-cover opacity-40" />
              <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/70 to-transparent" />
              <div className="relative p-5">
                <p className="text-violet-300 text-[10px] font-bold tracking-[0.3em] uppercase">Live Hookah</p>
                <h3 className="text-white font-bold text-2xl mt-1" style={{fontFamily:'var(--font-heading)'}}>Hookah Experience — {countryFlags[country]} {countryNames[country]}</h3>
                <p className="text-zinc-300 text-xs mt-1">{managerHookahSessions.length} sesyon aktif • Mouvman tan reyèl</p>
              </div>
            </div>

            {/* Daily revenue total */}
            {(() => {
              const totalRev = managerHookahSessions.reduce((s, ds) => s + (ds.total_revenue || 0), 0);
              const totalBase = managerHookahSessions.reduce((s, ds) => s + (ds.base_revenue || 0), 0);
              const totalRefill = managerHookahSessions.reduce((s, ds) => s + (ds.refill_revenue || 0), 0);
              const totalTables = managerHookahSessions.reduce((s, ds) => s + (ds.table_count_total || 0), 0);
              return (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="manager-hookah-revenue">
                  <div className="glass-panel p-4 border-l-4 border-emerald-500">
                    <p className="text-zinc-500 text-[10px] uppercase">Total Hookah Vann</p>
                    <p className="text-emerald-400 text-xl font-black">${totalBase.toFixed(2)}</p>
                  </div>
                  <div className="glass-panel p-4 border-l-4 border-blue-500">
                    <p className="text-zinc-500 text-[10px] uppercase">Total Refil</p>
                    <p className="text-blue-400 text-xl font-black">${totalRefill.toFixed(2)}</p>
                  </div>
                  <div className="glass-panel p-4 border-l-4 border-yellow-500">
                    <p className="text-zinc-500 text-[10px] uppercase">Total Global Jou A</p>
                    <p className="text-yellow-400 text-xl font-black" data-testid="manager-hookah-total">${totalRev.toFixed(2)}</p>
                  </div>
                  <div className="glass-panel p-4 border-l-4 border-violet-500">
                    <p className="text-zinc-500 text-[10px] uppercase">Total Tab</p>
                    <p className="text-violet-400 text-xl font-black">{totalTables}</p>
                  </div>
                </div>
              );
            })()}

            {/* Active sessions */}
            <div className="glass-panel p-4">
              <p className="text-white font-bold text-sm mb-3" style={{fontFamily:'var(--font-heading)'}}>🔥 Sesyon Aktif — Live</p>
              {managerHookahSessions.length === 0 && (
                <div className="text-center py-8 text-zinc-500 text-sm">Pa gen sesyon hookah ouvri pou {countryFlags[country]} kounye a</div>
              )}
              <div className="space-y-3">
                {managerHookahSessions.map(ds => (
                  <div key={ds.id} className="bg-zinc-900/80 rounded-xl border border-emerald-700/40 p-3">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <div>
                        <p className="text-white font-bold">{ds.stocker_name}</p>
                        <p className="text-zinc-500 text-xs">
                          Responsab: <span className="text-emerald-400 font-bold">{ds.responsable_name}</span>
                          {ds.servers?.length > 0 && <> • Sèvè: <span className="text-blue-400">{ds.servers.join(", ")}</span></>}
                          {ds.address && <> • {ds.address}</>}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-yellow-400 font-black text-lg">${ds.total_revenue.toFixed(2)}</p>
                        <p className="text-zinc-500 text-[10px]">{ds.active_count} aktif / {ds.completed_count} fini</p>
                      </div>
                    </div>
                    {ds.tables.length > 0 && (
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-1.5">
                        {ds.tables.map((t, i) => (
                          <div key={i} className={`p-1.5 rounded-md text-[10px] border ${t.status === "active" ? "border-yellow-500/40 bg-yellow-500/5" : "border-zinc-700 bg-zinc-800/40"}`}>
                            <div className="flex items-center justify-between">
                              <span className="text-white font-bold">#{t.hookah_number}</span>
                              <span className={`w-1.5 h-1.5 rounded-full ${t.status === "active" ? "bg-yellow-400 animate-pulse" : "bg-zinc-600"}`} />
                            </div>
                            <p className="text-white truncate text-[10px]">{t.client_name}</p>
                            <p className="text-emerald-400 text-[9px]">${((t.price||0)+(t.total_refill_amount||0)).toFixed(2)}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Session Report — final report when sessions are closed */}
            <div className="glass-panel p-4">
              <p className="text-white font-bold text-sm mb-2" style={{fontFamily:'var(--font-heading)'}}>📋 Rapò Sesyon Jou A</p>
              <p className="text-zinc-500 text-xs">Sesyon hookah ki fèmen jodi a — verifye si tout bagay te kòrèk</p>
              <div className="mt-3 text-center py-6 text-zinc-600 text-xs">
                {managerHookahSessions.length > 0 ? `${managerHookahSessions.length} sesyon toujou ap mache` : "Pa gen sesyon fèmen pou jodi a"}
              </div>
            </div>
          </div>
        )}

        {/* Contact Us */}
        <ContactUs />
      </div>
    </div>
  );
};

// ==================== NETWORK MARKETER LOGIN ====================
const NetworkMarketerLogin = () => {
  const pageVisuals = usePageVisuals();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=country, 2=code+name, 3=secret, 4=ready
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [code, setCode] = useState("");
  const [secretCode, setSecretCode] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [needSecretCreation, setNeedSecretCreation] = useState(false);
  const [readyToEnter, setReadyToEnter] = useState(false);
  const [marketerData, setMarketerData] = useState(null);

  const countries = [
    { code: "usa", name: "États-Unis", flag: "🇺🇸" },
    { code: "dr", name: "République Dominicaine", flag: "🇩🇴" },
    { code: "haiti", name: "Haïti", flag: "🇭🇹" }
  ];

  const handleStep2 = async () => {
    if (!code || code.length !== 6 || !name.trim()) {
      toast.error("Entrez votre code (6 chiffres) et nom");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/network-marketer/check-secret`, {
        code, name: name.trim(), country: selectedCountry
      });
      if (res.data.found) {
        setNeedSecretCreation(res.data.need_secret_creation);
        setStep(3);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code ou nom incorrect");
    } finally {
      setLoading(false);
    }
  };

  const handleStep3 = async () => {
    if (!secretCode || secretCode.length !== 4) {
      toast.error("Entrez 4 chiffres");
      return;
    }
    setLoading(true);
    try {
      const payload = { code, name: name.trim(), country: selectedCountry };
      if (needSecretCreation) payload.new_secret_code = secretCode;
      else payload.secret_code = secretCode;
      
      const res = await axios.post(`${API}/auth/network-marketer/login`, payload);
      if (res.data.success) {
        if (needSecretCreation) {
          setMarketerData(res.data.marketer);
          setReadyToEnter(true);
          setStep(4);
        } else {
          localStorage.setItem('networkMarketerAuth', JSON.stringify(res.data.marketer));
          toast.success("Bienvenue!");
          navigate('/network-marketer/dashboard');
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code incorrect");
    } finally {
      setLoading(false);
    }
  };

  const handleStart = () => {
    if (marketerData) {
      localStorage.setItem('networkMarketerAuth', JSON.stringify(marketerData));
      toast.success("Bienvenue à votre bureau!");
      navigate('/network-marketer/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 relative overflow-hidden">
      {pageVisuals?.section?.url ? <PageBackground cfg={pageVisuals.section} testid="section-custom-bg" /> : <div className="vape-bg" />}
      <div className="relative z-10 content-wrapper max-w-md mx-auto px-6 py-8">
        {step !== 4 && (
          <button onClick={() => step === 1 ? navigate('/') : setStep(step - 1)} 
            className="text-zinc-500 hover:text-white transition-colors mb-6">
            ← {step === 1 ? 'Retour' : 'Étape précédente'}
          </button>
        )}

        <div className="flex justify-center mb-6">
          <div className="bg-purple-400/20 border border-purple-400/30 rounded-full p-4">
            <Network className="w-12 h-12 text-purple-400" />
          </div>
        </div>

        <h2 className="font-heading text-2xl font-bold text-center text-white mb-2">Network Marketer</h2>

        {step === 1 && (
          <>
            <p className="text-zinc-500 text-center mb-6">Sélectionnez votre pays</p>
            <div className="grid grid-cols-3 gap-3 mb-6">
              {countries.map((c) => (
                <button key={c.code} onClick={() => { setSelectedCountry(c.code); setStep(2); }}
                  className="p-4 rounded-xl border bg-zinc-800/50 border-zinc-700 hover:border-purple-500 transition-all flex flex-col items-center gap-2">
                  <span className="text-3xl">{c.flag}</span>
                  <span className="text-xs text-zinc-400">{c.name}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <p className="text-zinc-500 text-center mb-6">Entrez votre code CEO</p>
            <div className="flex justify-center gap-2 mb-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className={`w-12 h-14 rounded-xl flex items-center justify-center text-2xl font-mono ${code[i] ? 'bg-purple-600 text-white' : 'bg-zinc-800 text-zinc-600 border border-zinc-700'}`}>
                  {code[i] ? '•' : '-'}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 'C', 0, '⌫'].map((num) => (
                <button key={num} onClick={() => {
                  if (num === 'C') setCode('');
                  else if (num === '⌫') setCode(prev => prev.slice(0, -1));
                  else if (code.length < 6) setCode(prev => prev + num);
                }} className={`numpad-btn ${num === 'C' ? 'text-red-400' : num === '⌫' ? 'text-amber-400' : ''}`}>{num}</button>
              ))}
            </div>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Votre nom"
              className="w-full bg-zinc-900/50 border border-zinc-800 text-white rounded-xl py-4 px-5 text-lg mb-6" />
            <button onClick={handleStep2} disabled={loading || code.length !== 6 || !name}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 rounded-xl disabled:opacity-50">
              {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : "Suivant"}
            </button>
          </>
        )}

        {step === 3 && (
          <>
            <p className="text-zinc-500 text-center mb-4">
              {needSecretCreation ? "Créez votre code secret (4 chiffres)" : "Entrez votre code secret"}
            </p>
            <div className="flex justify-center gap-3 mb-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className={`w-14 h-16 rounded-xl flex items-center justify-center text-3xl font-mono ${secretCode[i] ? 'bg-purple-500 text-white' : 'bg-zinc-800 border border-zinc-700'}`}>
                  {secretCode[i] ? '•' : '-'}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 'C', 0, '⌫'].map((num) => (
                <button key={num} onClick={() => {
                  if (num === 'C') setSecretCode('');
                  else if (num === '⌫') setSecretCode(prev => prev.slice(0, -1));
                  else if (secretCode.length < 4) setSecretCode(prev => prev + num);
                }} className={`numpad-btn ${num === 'C' ? 'text-red-400' : num === '⌫' ? 'text-amber-400' : ''}`}>{num}</button>
              ))}
            </div>
            <button onClick={handleStep3} disabled={loading || secretCode.length !== 4}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 rounded-xl disabled:opacity-50">
              {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : (needSecretCreation ? "Créer" : "Entrer")}
            </button>
          </>
        )}

        {step === 4 && (
          <div className="text-center">
            <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-full p-6 w-32 h-32 mx-auto mb-6 flex items-center justify-center">
              <Check className="w-16 h-16 text-emerald-400" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-white mb-3">Code Secret Créé!</h2>
            <p className="text-zinc-400 mb-8">Valable pour 3 jours</p>
            <button onClick={handleStart}
              className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-5 rounded-xl text-xl">
              🚀 START
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== NETWORK MARKETER DASHBOARD ====================
const NetworkMarketerDashboard = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const [marketer, setMarketer] = useState(null);
  const [stockers, setStockers] = useState([]);
  const [showAddStocker, setShowAddStocker] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [bonusCard, setBonusCard] = useState({ balance: 13, withdrawn: false, can_withdraw: true });
  const [newStocker, setNewStocker] = useState({ name: "", quantity: 10, price: 0, payment_info: "" });
  const [loading, setLoading] = useState(false);
  const [withdrawSuccess, setWithdrawSuccess] = useState(false);
  const [nmWithdrawStep, setNmWithdrawStep] = useState("idle");
  const [nmWithdrawTimer, setNmWithdrawTimer] = useState(10);
  const [nmRenewStep, setNmRenewStep] = useState("idle");
  const [personalSaleLoading, setPersonalSaleLoading] = useState(false);
  const [nmMessages, setNmMessages] = useState([]);
  const [activeMenu, setActiveMenu] = useState("dashboard");
  const [editingStocker, setEditingStocker] = useState(null);
  const [editStockQty, setEditStockQty] = useState(0);
  const [editStockPrice, setEditStockPrice] = useState(0);
  const [saleModelName, setSaleModelName] = useState("");
  const [saleQuantity, setSaleQuantity] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [flashMessage, setFlashMessage] = useState(null);
  const [profileImage, setProfileImage] = useState(null);

  // 3-second red pulsing control-date reminder popup
  const [showControlReminder, setShowControlReminder] = useState(false);
  useEffect(() => {
    if (marketer && marketer.end_date) {
      const end = new Date(marketer.end_date);
      const now = new Date();
      const daysOverdue = Math.floor((now - end) / (1000 * 60 * 60 * 24));
      if (daysOverdue >= 0) {
        setShowControlReminder(true);
        const t = setTimeout(() => setShowControlReminder(false), 3000);
        return () => clearTimeout(t);
      }
    }
  }, [marketer?.id, marketer?.end_date]);

  // 3-second profile-completion reminder popup
  const [showProfileReminder, setShowProfileReminder] = useState(false);
  const [profileReminderText, setProfileReminderText] = useState("");
  useEffect(() => {
    if (!marketer?.id) return;
    let cancelled = false;
    (async () => {
      const missing = [];
      const hasPhoto = !!(marketer.profile_image || profileImage || localStorage.getItem(`nm_photo_${marketer.id}`));
      if (!hasPhoto) missing.push("foto pwofil");
      try {
        const pp = await axios.get(`${API}/user/${marketer.id}/payment-profile`);
        const method = pp.data?.payment_profile?.method;
        const accountId = pp.data?.payment_profile?.account_id;
        const phoneHint = pp.data?.payment_profile?.phone;
        if (!method || !accountId) missing.push("mòd peman");
        if (!phoneHint && !marketer.phone) missing.push("nimewo telefòn");
      } catch (_) {
        missing.push("mòd peman");
      }
      if (cancelled) return;
      if (missing.length) {
        setProfileReminderText(`Konplete ${missing.join(", ")} nan pwofil ou.`);
        setShowProfileReminder(true);
        setTimeout(() => setShowProfileReminder(false), 3000);
      }
    })();
    return () => { cancelled = true; };
  }, [marketer?.id]);

  // Monthly report state (NM tab)
  const [reportContent, setReportContent] = useState("");
  const [reportTitle, setReportTitle] = useState("Rapò Mansyèl");
  const [nmMonthlyReports, setNmMonthlyReports] = useState([]);
  const [reportSaving, setReportSaving] = useState(false);
  const loadNmReports = async () => {
    if (!marketer?.id) return;
    try { const r = await axios.get(`${API}/network-marketers/${marketer.id}/monthly-reports`); setNmMonthlyReports(r.data || []); } catch (_) {}
  };
  useEffect(() => { if (marketer?.id && activeMenu === "report") loadNmReports(); /* eslint-disable-next-line */ }, [marketer?.id, activeMenu]);
  const submitMonthlyReport = async () => {
    if (!reportContent.trim() || reportContent.trim().length < 5) { toast.error("Rapò a twò kout"); return; }
    setReportSaving(true);
    try {
      await axios.post(`${API}/network-marketers/${marketer.id}/monthly-report`, { title: reportTitle, content: reportContent });
      toast.success("Rapò voye bay CEO!");
      setReportContent("");
      loadNmReports();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erè");
    } finally {
      setReportSaving(false);
    }
  };

  useScreenshotProtection();

  const countryFlags = { usa: "🇺🇸", dr: "🇩🇴", haiti: "🇭🇹" };

  useEffect(() => {
    const auth = localStorage.getItem('networkMarketerAuth');
    if (!auth) { navigate('/network-marketer/login'); return; }
    const m = JSON.parse(auth);
    setMarketer(m);
    setProfileImage(localStorage.getItem(`nmProfileImage_${m.id}`) || null);
    fetchStockers(m.id);
    fetchBonusCard(m.id);
    refreshMarketer(m.id);
    fetchMessages(m.id);
    // Show role-conversion welcome message (promotion to NM)
    if (m.pending_welcome_message) {
      toast.success(m.pending_welcome_message, { duration: 10000 });
      axios.post(`${API}/user/${m.id}/clear-welcome-message`).catch(() => {});
    }
    // Check for flash messages
    const checkFlash = async () => {
      try {
        const res = await axios.get(`${API}/nm/${m.id}/flash-messages`);
        if (res.data.length > 0) {
          setFlashMessage(res.data[0].message);
          setTimeout(() => setFlashMessage(null), 10000);
        }
      } catch(e) {}
    };
    checkFlash();
    const flashInterval = setInterval(checkFlash, 30000);
    return () => clearInterval(flashInterval);
  }, [navigate]);

  const refreshMarketer = async (marketerId) => {
    try {
      const res = await axios.get(`${API}/network-marketer/${marketerId}`);
      setMarketer(res.data);
      localStorage.setItem('networkMarketerAuth', JSON.stringify(res.data));
    } catch (e) { console.log("Error refreshing marketer"); }
  };

  const fetchStockers = async (marketerId) => {
    try {
      const res = await axios.get(`${API}/network-marketer/${marketerId}/stockers`);
      setStockers(res.data);
    } catch (e) { console.log("Error"); }
  };

  const fetchBonusCard = async (marketerId) => {
    try {
      const res = await axios.get(`${API}/network-marketer/${marketerId}/bonus-card`);
      setBonusCard(res.data);
    } catch (e) { console.log("Error fetching bonus"); }
  };

  const fetchMessages = async (marketerId) => {
    try {
      const res = await axios.get(`${API}/network-marketer/${marketerId}/messages`);
      setNmMessages(res.data);
    } catch (e) { console.log("Error fetching messages"); }
  };

  const handlePersonalSale = async () => {
    const qty = parseInt(saleQuantity);
    const price = parseFloat(salePrice);
    if (!saleModelName.trim()) { toast.error("Entrez le nom du modèle"); return; }
    if (!qty || qty <= 0) { toast.error("Entrez une quantité valide"); return; }
    if (!price || price <= 0) { toast.error("Entrez un prix valide"); return; }
    setPersonalSaleLoading(true);
    try {
      await axios.post(`${API}/network-marketer/${marketer.id}/record-personal-sale`, {
        quantity_sold: qty,
        model_name: saleModelName.trim(),
        price: price
      });
      toast.success(`${qty} ${saleModelName} vendu(s) à $${price}!`);
      setSaleModelName("");
      setSaleQuantity("");
      setSalePrice("");
      refreshMarketer(marketer.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setPersonalSaleLoading(false);
    }
  };

  const handleWithdrawBonus = async () => {
    if (!phoneNumber || phoneNumber.length < 8) {
      toast.error("Entrez un numéro valide");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/network-marketer/${marketer.id}/withdraw-bonus`, {
        phone_number: phoneNumber
      });
      setWithdrawSuccess(true);
      toast.success(res.data.message);
      fetchBonusCard(marketer.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const handleAddStocker = async () => {
    const models = newStocker.models || [];
    const totalQty = models.reduce((a, m) => a + (m.quantity || 0), 0);
    if (!newStocker.name || totalQty <= 0) {
      toast.error("Remplissez le nom et au moins un modèle");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...newStocker, quantity: totalQty, price: models[0]?.price || 0 };
      await axios.post(`${API}/network-marketer/${marketer.id}/add-stocker`, payload);
      toast.success("Stocker ajouté!");
      setShowAddStocker(false);
      setNewStocker({ name: "", quantity: 0, price: 0, payment_info: "", stock_batch: "", models: [{name:"",quantity:0,price:0}] });
      fetchStockers(marketer.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const handleRecordSale = async (stockerId, qty) => {
    try {
      await axios.put(`${API}/network-marketer/stocker/${stockerId}/sale`, { quantity_sold: qty });
      toast.success("Vente enregistrée!");
      fetchStockers(marketer.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleDeleteStocker = async (stockerId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce stocker?")) return;
    try {
      await axios.delete(`${API}/network-marketer/${marketer.id}/stocker/${stockerId}`);
      toast.success("Stocker supprimé!");
      fetchStockers(marketer.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleUpdateSubStock = async (stockerId) => {
    try {
      await axios.put(`${API}/network-marketer/${marketer.id}/stocker/${stockerId}/stock`, {
        quantity: editStockQty, price: editStockPrice
      });
      toast.success("Stock modifié!");
      setEditingStocker(null);
      fetchStockers(marketer.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleMarkMessageRead = async (msgId) => {
    try {
      await axios.put(`${API}/network-marketer/${marketer.id}/messages/${msgId}/read`);
      fetchMessages(marketer.id);
    } catch (e) { console.log("Error"); }
  };

  const handleLogout = () => {
    localStorage.removeItem('networkMarketerAuth');
    navigate('/');
  };

  const handleProfileImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Foto twò gwo (max 2MB)");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result;
      setProfileImage(dataUrl);
      try {
        localStorage.setItem(`nmProfileImage_${marketer.id}`, dataUrl);
        toast.success("Foto anrejistre");
      } catch (_) {
        toast.error("Foto twò gwo pou anrejistre lokal");
      }
    };
    reader.readAsDataURL(file);
  };

  if (!marketer) return null;

  const sellPercentage = marketer.initial_stock > 0 ? ((marketer.total_sold || 0) / marketer.initial_stock) * 100 : 0;
  const startDate = marketer.start_date ? new Date(marketer.start_date) : null;
  const endDate = marketer.end_date ? new Date(marketer.end_date) : null;
  const daysLeft = endDate ? Math.max(0, Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24))) : 0;
  const unreadMessages = nmMessages.filter(m => !m.read).length;

  return (
    <div className="min-h-screen bg-white relative" data-testid="nm-dashboard" style={{fontFamily: 'Inter, system-ui, sans-serif'}}>
      {/* VAPE-YARD TOPBAR */}
      <div className="bg-black border-b-4 border-amber-400 px-4 py-3 sticky top-0 z-30 flex items-center justify-between shadow-xl">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 bg-amber-400 rounded-lg flex items-center justify-center">
            <Network className="w-5 h-5 text-black" />
          </div>
          <div>
            <p className="text-white font-black text-base tracking-wider leading-none">VAPE<span className="text-amber-400">-</span>YARD</p>
            <p className="text-zinc-500 text-[9px] uppercase tracking-[0.25em] mt-0.5">Network Marketer</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell userId={marketer?.id} />
          <LanguageSwitcher />
          <div className="hidden sm:flex items-center gap-2 pl-2 pr-3 py-1 rounded-full bg-zinc-900">
            {profileImage ? (
              <img src={profileImage} alt="" className="w-7 h-7 rounded-full object-cover ring-1 ring-amber-400" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-amber-400 flex items-center justify-center text-black font-black text-xs">{(marketer?.name||"N").charAt(0).toUpperCase()}</div>
            )}
            <div className="text-left leading-tight">
              <p className="text-white font-bold text-xs truncate max-w-[140px]">{marketer?.name}</p>
              <p className="text-amber-400 text-[9px]">NM • {marketer?.code}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="p-2 text-zinc-400 hover:text-red-400 hover:bg-zinc-900 rounded-lg" data-testid="nm-logout" title="Déconnexion">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Flash Message from CEO */}
      {flashMessage && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-bounce">
          <div className="bg-white text-zinc-900 border-2 border-amber-400 px-6 py-4 rounded-2xl shadow-2xl max-w-md">
            <div className="flex items-center gap-2 mb-1">
              <Crown className="w-4 h-4 text-amber-500" />
              <span className="font-bold text-sm text-amber-600">{t("nm_ceo_message")}</span>
            </div>
            <p className="text-sm font-medium">{flashMessage}</p>
          </div>
        </div>
      )}
      {/* 3-second red pulsing control-date reminder */}
      {showControlReminder && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] animate-pulse" data-testid="control-date-reminder">
          <div className="bg-red-600 border-2 border-red-300 rounded-2xl shadow-2xl px-6 py-5 text-white font-black text-center relative min-w-[280px]">
            <button onClick={() => setShowControlReminder(false)} className="absolute top-1 right-2 text-white/70 hover:text-white text-lg" data-testid="close-control-reminder">×</button>
            <div className="text-3xl mb-1">🔴</div>
            <p className="text-base">MOMAN KONTWÒL RIVE</p>
            <p className="text-xs font-normal opacity-90 mt-1">Fè retrait ou / kontakte CEO w</p>
          </div>
        </div>
      )}
      {/* 3-second profile-completion reminder */}
      {showProfileReminder && (
        <div className="fixed top-20 right-6 z-[60]" data-testid="profile-reminder">
          <div className="bg-amber-600 border-2 border-amber-300 rounded-2xl shadow-2xl px-5 py-4 text-white font-bold relative max-w-xs">
            <button onClick={() => setShowProfileReminder(false)} className="absolute top-1 right-2 text-white/70 hover:text-white text-lg" data-testid="close-profile-reminder">×</button>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl">👤</span>
              <p className="text-sm">Konplete pwofil w</p>
            </div>
            <p className="text-xs font-normal opacity-95">{profileReminderText}</p>
          </div>
        </div>
      )}
      <div className="max-w-6xl mx-auto px-4 lg:px-6 py-5">
        {/* Welcome banner */}
        <div className="bg-gradient-to-r from-zinc-900 to-black border-l-4 border-amber-400 rounded-xl px-4 py-3 mb-5 shadow-md flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {profileImage ? (
              <img src={profileImage} alt="Profile" className="w-12 h-12 rounded-full object-cover ring-2 ring-amber-400" data-testid="nm-header-photo" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-amber-400 flex items-center justify-center">
                <Network className="w-6 h-6 text-black" />
              </div>
            )}
            <div>
              <p className="text-white font-black text-lg">Bienvenue, <span className="text-amber-400">{marketer?.name}</span></p>
              <p className="text-zinc-400 text-xs">Network Marketer · Code {marketer?.code}</p>
            </div>
          </div>
        </div>
        {/* Legacy Header REMOVED — replaced by VAPE-YARD topbar + welcome banner above */}
        <button onClick={() => navigate("/network-marketer/dashboard")} className="mb-3 mr-2 px-3 py-2 rounded-xl bg-amber-400 hover:bg-amber-300 text-black text-xs font-black inline-flex items-center gap-2" data-testid="nm-team-back-btn"><ArrowLeft className="w-4 h-4" /> Retour au tableau de bord</button>
        {showHistory && <HistoryModal userId={marketer.id} onClose={() => setShowHistory(false)} />}
        <SupportCenter userId={marketer.id} userName={marketer.name} />
        {showHistory !== null && (
          <button
            onClick={() => setShowHistory(true)}
            data-testid="nm-history-btn"
            className="mb-3 px-3 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-black inline-flex items-center gap-2"
          >
            📜 Istori · Istori personèl ou
          </button>
        )}
        <span className="ml-2 inline-block align-top"><PhotoVerifyButton user={marketer} /></span>

        {/* Dates Section */}
        {startDate && (
          <div className="glass-panel p-4 mb-4 border border-zinc-700">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-zinc-500 text-xs">{t("date_start")}</p>
                <p className="text-white font-bold text-sm">{startDate.toLocaleDateString('fr-FR')}</p>
              </div>
              <div>
                <p className="text-zinc-500 text-xs">{t("date_control")}</p>
                <p className={`font-bold text-sm ${daysLeft <= 5 ? 'text-red-400' : 'text-blue-400'}`}>{endDate?.toLocaleDateString('fr-FR')}</p>
              </div>
              <div>
                <p className="text-zinc-500 text-xs">{t("days_left")}</p>
                <p className={`font-bold text-lg ${daysLeft <= 5 ? 'text-red-400 animate-pulse' : 'text-emerald-400'}`}>{daysLeft}</p>
              </div>
            </div>
          </div>
        )}

        {/* Menu Navigation */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            {id: "dashboard", label: t("tab_dashboard"), icon: BarChart3},
            {id: "stock", label: t("my_stock"), icon: Package},
            {id: "stockers", label: `${t("my_stockers")} (${stockers.length}/3)`, icon: Users},
            {id: "bonus", label: t("tab_bonus"), icon: Gift},
            {id: "retrait", label: t("withdraw_short"), icon: DollarSign},
            {id: "notifications", label: `${t("notifications_label")}${unreadMessages > 0 ? ` (${unreadMessages})` : ''}`, icon: Bell},
            {id: "report", label: "Rapò", icon: TrendingUp},
            {id: "profile", label: t("tab_profile"), icon: Settings},
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveMenu(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all whitespace-nowrap text-sm ${
                activeMenu === tab.id ? 'bg-purple-600 text-white' : 'bg-zinc-800/50 text-zinc-400 hover:text-white'
              }`} data-testid={`nm-tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        {/* ========== DASHBOARD TAB ========== */}
        {activeMenu === "dashboard" && (
          <>
            <CountryHero user={marketer} roleLabel="Network Marketer" onViewStock={() => document.querySelector('[data-testid="nm-my-stock-visual"]')?.scrollIntoView({ behavior: "smooth" })} />
            <UnsoldStockAlert user={marketer} />
            <StatsRow sold={getCycleSold(marketer)} commission={marketer.cycle_commission || 0} rate={marketer.commission_rate}
              bonus={marketer.total_bonus || 0} balance={(marketer.cycle_commission || 0) + (marketer.total_bonus || 0) + (marketer.team_bonus_balance || 0)} userId={marketer.id} />
            <MyStockVisual user={marketer} />
            {/* Personal Stock Summary (parity with stocker) */}
            <div className="glass-panel p-4 mb-4" data-testid="nm-personal-stock">
              <h3 className="text-white font-bold mb-3 flex items-center gap-2"><Package className="w-5 h-5 text-purple-400" /> {t("my_personal_stock")}</h3>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="bg-zinc-800/50 rounded-xl p-3 text-center">
                  <p className="text-zinc-500 text-xs mb-1">{t("had_available")}</p>
                  <p className="text-white font-black text-2xl">{marketer.initial_stock || 0}</p>
                </div>
                <div className="bg-emerald-950/40 border border-emerald-500/30 rounded-xl p-3 text-center">
                  <p className="text-emerald-500 text-xs mb-1">{t("sold_short")}</p>
                  <p className="text-emerald-400 font-black text-2xl">{marketer.total_sold || 0}</p>
                </div>
                <div className="bg-blue-950/40 border border-blue-500/30 rounded-xl p-3 text-center">
                  <p className="text-blue-500 text-xs mb-1">{t("remaining_short")}</p>
                  <p className="text-blue-400 font-black text-2xl">{marketer.current_stock || 0}</p>
                </div>
              </div>
              <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-500 to-purple-500 rounded-full transition-all" style={{ width: `${marketer.initial_stock ? Math.min(100, ((marketer.total_sold || 0) / marketer.initial_stock) * 100) : 0}%` }} />
              </div>
              <p className="text-zinc-500 text-xs mt-2">{marketer.initial_stock ? Math.round(((marketer.total_sold || 0) / marketer.initial_stock) * 100) : 0}% {t("sold_short").toLowerCase()} • {t("control_date_short")}: {marketer.end_date ? new Date(marketer.end_date).toLocaleDateString("fr-FR") : "—"}</p>
              {(marketer.carryover_from_previous_cycle || 0) > 0 && (() => {
                const carryTotal = marketer.carryover_from_previous_cycle || 0;
                const cycleSold = marketer.total_sold || 0;
                const carryFullySold = cycleSold >= carryTotal;
                if (carryFullySold) {
                  return (
                    <div className="mt-3 p-3 rounded-xl border-2" style={{ backgroundColor: 'rgba(16, 185, 129, 0.12)', borderColor: 'rgba(16, 185, 129, 0.6)' }} data-testid="nm-carryover-alert">
                      <p className="text-sm font-bold text-emerald-400">✅ {carryTotal} {t("carryover_cleared")}</p>
                      <p className="text-xs mt-1 text-emerald-300">{t("carryover_cleared_hint")}</p>
                    </div>
                  );
                }
                return (
                  <div className="mt-3 p-3 rounded-xl border-2 border-dashed" style={{ backgroundColor: 'rgba(251, 191, 36, 0.12)', borderColor: 'rgba(251, 146, 60, 0.6)' }} data-testid="nm-carryover-alert">
                    <p className="text-sm font-bold" style={{ color: '#fb923c' }}>⚠️ {carryTotal - cycleSold} {t("carryover_pending_title").replace('⚠️ ','').toLowerCase()}</p>
                    <p className="text-xs mt-1" style={{ color: '#fdba74' }}>{t("carryover_pending_hint")}</p>
                  </div>
                );
              })()}
            </div>

            {/* Credit Score (parity with stocker) */}
            <CreditScoreCard userId={marketer.id} />

            {/* Stats */}
            <div className="grid grid-cols-4 gap-3 mb-6">
              <div className="glass-panel p-4 text-center">
                <Package className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{marketer.current_stock || 0}</p>
                <p className="text-zinc-500 text-xs">{t("my_stock")}</p>
              </div>
              <div className="glass-panel p-4 text-center">
                <TrendingUp className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-emerald-400">{marketer.total_sold || 0}</p>
                <p className="text-zinc-500 text-xs">{t("my_sales")}</p>
              </div>
              <div className="glass-panel p-4 text-center">
                <Users className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-blue-400">{stockers.length}/3</p>
                <p className="text-zinc-500 text-xs">{t("my_stockers")}</p>
              </div>
              <div className="glass-panel p-4 text-center">
                <DollarSign className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-amber-400">${(marketer.total_earnings || 0).toFixed(2)}</p>
                <p className="text-zinc-500 text-xs">{t("total_gains")}</p>
              </div>
            </div>

            {/* Performance Summary */}
            <div className="glass-panel p-6 mb-6">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-400" /> {t("performance")}
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-zinc-800">
                  <span className="text-zinc-400 text-sm">{t("personal_sales")}</span>
                  <span className="text-white font-bold">{marketer.total_sold || 0} vapes</span>
                </div>
                <div className="flex justify-between py-2 border-b border-zinc-800">
                  <span className="text-zinc-400 text-sm">{t("substocker_sales")}</span>
                  <span className="text-white font-bold">{stockers.reduce((a, s) => a + (s.total_sold || 0), 0)} vapes</span>
                </div>
                <div className="flex justify-between py-2 border-b border-zinc-800">
                  <span className="text-zinc-400 text-sm">{t("personal_revenue")}</span>
                  <span className="text-white font-bold">${(marketer.total_revenue || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-zinc-800">
                  <span className="text-zinc-400 text-sm">{t("commission_15_label")}</span>
                  <span className="text-yellow-400 font-bold">${((marketer.total_revenue || 0) * 0.15).toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-zinc-800">
                  <span className="text-zinc-400 text-sm">{t("substocker_bonus")} ({stockers.length} x 2.5%)</span>
                  <span className="text-blue-400 font-bold">{(stockers.length * 2.5).toFixed(1)}%</span>
                </div>
                <div className="flex justify-between py-2 border-b border-zinc-800">
                  <span className="text-zinc-400 text-sm">{t("bonus_per_vape_2")}</span>
                  <span className="text-emerald-400 font-bold">${(marketer.total_bonus || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-3 mt-1">
                  <span className="text-white font-bold text-lg">{t("total_gains")}</span>
                  <span className="text-emerald-400 font-black text-2xl">${(marketer.total_earnings || 0).toFixed(2)}</span>
                </div>
              </div>
              {/* Progress bar */}
              <div className="mt-4">
                <p className="text-zinc-500 text-xs mb-1">{t("percent_sold_progress")}: {sellPercentage.toFixed(0)}%</p>
                <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${sellPercentage >= 50 ? 'bg-emerald-400' : 'bg-amber-400'}`}
                    style={{ width: `${Math.min(100, sellPercentage)}%` }} />
                </div>
              </div>
            </div>
          </>
        )}

        {/* ========== MON STOCK TAB ========== */}
        {activeMenu === "stock" && (
          <div className="space-y-6">
            {/* Stock Overview */}
            <div className="glass-panel p-6 border border-purple-500/30 bg-gradient-to-br from-purple-500/5 to-purple-600/10" data-testid="nm-personal-stock">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-purple-500/20 p-3 rounded-xl">
                  <Package className="w-7 h-7 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">Mon Stock Personnel</h3>
                  <p className="text-purple-400 text-sm">
                    {marketer.initial_stock > 0
                      ? `${marketer.total_sold || 0}/${marketer.initial_stock} vendus (${sellPercentage.toFixed(0)}%)`
                      : 'Aucun stock assigné'}
                  </p>
                </div>
              </div>

              {/* Progress bar */}
              <div className="h-3 bg-zinc-800 rounded-full overflow-hidden mb-4">
                <div className="h-full rounded-full bg-purple-500 transition-all"
                  style={{ width: `${Math.min(100, sellPercentage)}%` }} />
              </div>

              {/* Current vape models in stock */}
              {marketer.vape_models && marketer.vape_models.length > 0 && (
                <div className="space-y-2 mb-4">
                  {marketer.vape_models.map((model, idx) => (
                    <div key={idx} className="bg-zinc-900/60 p-3 rounded-xl flex items-center justify-between">
                      <div>
                        <p className="text-white font-bold text-sm">{model.name}</p>
                        <p className="text-zinc-500 text-xs">${model.price} / unité</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${model.quantity > 0 ? 'text-emerald-400' : 'text-red-400'}`}>{model.quantity} restant</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ====== FORMULAIRE VENTE ====== */}
            <div className="glass-panel p-6 border border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-emerald-600/10" data-testid="nm-sale-form">
              <div className="flex items-center gap-3 mb-5">
                <div className="bg-emerald-500/20 p-3 rounded-xl">
                  <TrendingUp className="w-7 h-7 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">Enregistrer une Vente</h3>
                  <p className="text-emerald-400 text-sm">Entrez les détails de votre vente</p>
                </div>
              </div>

              <div className="space-y-4">
                {/* Model Name */}
                <div>
                  <label className="text-zinc-400 text-sm block mb-2">Modèle de Vape</label>
                  <ProductAutocomplete value={saleModelName} models={marketer.vape_models || []}
                    onChange={setSaleModelName}
                    onPick={(p) => { setSaleModelName(p.name); if (p.price) setSalePrice(String(p.price)); }}
                    placeholder="Ex: Vape Pro, Mega Cloud, Mini..."
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-emerald-500"
                    testid="nm-sale-model-input" />
                  {/* Quick select from existing models */}
                  {marketer.vape_models && marketer.vape_models.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {marketer.vape_models.filter(m => m.quantity > 0).map((m, i) => (
                        <button key={i} onClick={() => { setSaleModelName(m.name); setSalePrice(String(m.price)); }}
                          className={`text-xs px-3 py-1 rounded-lg transition-all ${saleModelName === m.name ? 'bg-emerald-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}>
                          {m.name} ({m.quantity})
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Quantity + Price */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-zinc-400 text-sm block mb-2">Quantité Vendue</label>
                    <input
                      type="number"
                      value={saleQuantity}
                      onChange={(e) => setSaleQuantity(e.target.value)}
                      placeholder="Ex: 2"
                      min="1"
                      className="w-full bg-zinc-900 border border-zinc-700 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-emerald-500"
                      data-testid="nm-sale-quantity-input"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-sm block mb-2">Prix par unité ($)</label>
                    <input
                      type="number"
                      value={salePrice}
                      onChange={(e) => setSalePrice(e.target.value)}
                      placeholder="Ex: 25"
                      min="0"
                      step="0.01"
                      className="w-full bg-zinc-900 border border-zinc-700 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-emerald-500"
                      data-testid="nm-sale-price-input"
                    />
                  </div>
                </div>

                {/* Sale Summary */}
                {saleQuantity && salePrice && (
                  <div className="bg-zinc-900/80 rounded-xl p-4 border border-zinc-800">
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-400">Revenu</span>
                      <span className="text-white font-bold">${(parseInt(saleQuantity || 0) * parseFloat(salePrice || 0)).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-zinc-400">Bonus ($0.99 x {saleQuantity})</span>
                      <span className="text-emerald-400 font-bold">${(parseInt(saleQuantity || 0) * 0.99).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-zinc-400">Commission (15%)</span>
                      <span className="text-yellow-400 font-bold">${(parseInt(saleQuantity || 0) * parseFloat(salePrice || 0) * 0.15).toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {/* Submit */}
                <button
                  onClick={handlePersonalSale}
                  disabled={personalSaleLoading || !saleModelName || !saleQuantity || !salePrice}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-4 rounded-xl flex items-center justify-center gap-3 transition-all active:scale-95"
                  data-testid="nm-submit-sale-btn"
                >
                  {personalSaleLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <TrendingUp className="w-5 h-5" />}
                  Enregistrer la Vente
                </button>
              </div>
            </div>

            {/* Sales History */}
            {marketer.sales_history && marketer.sales_history.length > 0 && (
              <div className="glass-panel p-6">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-400" /> Historique des Ventes
                </h3>
                <div className="space-y-2">
                  {marketer.sales_history.slice(-10).reverse().map((sale, i) => (
                    <div key={i} className="bg-zinc-900/60 p-3 rounded-xl flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm font-bold">{sale.model || 'Vape'}</p>
                        <p className="text-zinc-500 text-xs">{new Date(sale.date).toLocaleDateString('fr-FR')} — {sale.quantity} unité(s) à ${sale.price || (sale.revenue / sale.quantity).toFixed(2)}</p>
                      </div>
                      <p className="text-emerald-400 font-bold">${sale.revenue?.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Earnings Summary */}
            <div className="glass-panel p-6">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-zinc-500 text-xs">Revenus</p>
                  <p className="text-white font-bold text-lg">${(marketer.total_revenue || 0).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-zinc-500 text-xs">Commission (15%)</p>
                  <p className="text-yellow-400 font-bold text-lg">${((marketer.total_revenue || 0) * 0.15).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-zinc-500 text-xs">Bonus ($0.99/vape)</p>
                  <p className="text-emerald-400 font-bold text-lg">${(marketer.total_bonus || 0).toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* Renewal section */}
            <div className="glass-panel p-6 border border-blue-500/30">
              <h4 className="text-white font-bold mb-3 flex items-center gap-2">
                <Package className="w-4 h-4 text-blue-400" /> Renouveler Stock
                <span className="text-xs text-zinc-500 ml-auto">{sellPercentage >= 50 ? 'Éligible' : `${sellPercentage.toFixed(0)}% (min 50%)`}</span>
              </h4>
              {nmRenewStep === "idle" && (
                <button onClick={() => {
                  if (sellPercentage < 50) { setNmRenewStep("not_eligible"); }
                  else { setNmRenewStep("accepted"); }
                }}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2"
                  data-testid="nm-renew-btn">
                  <Package className="w-5 h-5" /> Demander Renouvellement
                </button>
              )}
              {nmRenewStep === "not_eligible" && (
                <div className="text-center py-4">
                  <p className="text-red-400 font-bold mb-2">Non Éligible</p>
                  <p className="text-zinc-400 text-sm mb-3">Ventes inférieures à 50%. Contactez votre Manager.</p>
                  <button onClick={() => setNmRenewStep("idle")} className="text-zinc-500 hover:text-zinc-300 text-sm underline">Retour</button>
                </div>
              )}
              {nmRenewStep === "accepted" && (
                <div className="text-center py-4">
                  <CheckCircle className="w-10 h-10 text-blue-400 mx-auto mb-2" />
                  <p className="text-blue-400 font-bold">{t("request_accepted")}</p>
                  <p className="text-zinc-400 text-sm">{t("ceo_will_add_stock")}</p>
                  <p className="text-emerald-400 text-xs mt-2 italic animate-pulse">⏳ {t("stock_in_progress")}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========== STOCKERS TAB ========== */}
        {activeMenu === "stockers" && (
          <div className="space-y-4">
            {/* Add Stocker Button */}
            {stockers.length < 3 && (
              <button onClick={() => setShowAddStocker(true)}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 rounded-xl mb-4 flex items-center justify-center gap-2"
                data-testid="nm-add-stocker-btn">
                <UserPlus className="w-5 h-5" /> Ajouter Stocker (ST{stockers.length + 1})
              </button>
            )}

            {/* Add Stocker Modal */}
            {showAddStocker && (
              <div className="glass-panel p-6 mb-4 border border-purple-500/30">
                <h3 className="text-white font-bold mb-4">Nouveau Stocker (ST{stockers.length + 1})</h3>
                <div className="grid gap-4">
                  <input type="text" placeholder="Nom du stocker" value={newStocker.name}
                    onChange={(e) => setNewStocker({...newStocker, name: e.target.value})}
                    className="bg-zinc-800 border border-zinc-700 rounded-xl py-3 px-4 text-white" />
                  
                  {/* Nom du Lot */}
                  <div>
                    <label className="text-blue-400 text-xs font-bold">Nom du Lot (Stock Batch)</label>
                    <input type="text" placeholder="Ex: Stock A, Stock B..." value={newStocker.stock_batch || ""}
                      onChange={(e) => setNewStocker({...newStocker, stock_batch: e.target.value})}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl py-3 px-4 text-white" />
                  </div>

                  {/* Models */}
                  <div>
                    <label className="text-purple-400 text-xs font-bold uppercase">Modèles de Vape</label>
                    {(newStocker.models || [{name:"",quantity:0,price:0}]).map((m, idx) => (
                      <div key={idx} className="grid grid-cols-3 gap-2 mt-2">
                        <input type="text" placeholder={`Modèle ${idx+1}`} value={m.name}
                          onChange={(e) => { const models = [...(newStocker.models || [{name:"",quantity:0,price:0}])]; models[idx] = {...models[idx], name: e.target.value}; setNewStocker({...newStocker, models}); }}
                          className="bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-white text-sm" />
                        <input type="number" placeholder="Qté" value={m.quantity || ""}
                          onChange={(e) => { const models = [...(newStocker.models || [{name:"",quantity:0,price:0}])]; models[idx] = {...models[idx], quantity: parseInt(e.target.value) || 0}; setNewStocker({...newStocker, models}); }}
                          className="bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-white text-sm" />
                        <input type="number" step="0.01" placeholder="Prix $" value={m.price || ""}
                          onChange={(e) => { const models = [...(newStocker.models || [{name:"",quantity:0,price:0}])]; models[idx] = {...models[idx], price: parseFloat(e.target.value) || 0}; setNewStocker({...newStocker, models}); }}
                          className="bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-white text-sm" />
                      </div>
                    ))}
                    {(newStocker.models || [{name:"",quantity:0,price:0}]).length < 5 && (
                      <button onClick={() => setNewStocker({...newStocker, models: [...(newStocker.models || [{name:"",quantity:0,price:0}]), {name:"",quantity:0,price:0}]})}
                        className="w-full border border-dashed border-zinc-600 text-zinc-400 py-1 rounded-lg text-xs mt-2">+ Modèle</button>
                    )}
                  </div>

                  <input type="text" placeholder="Info paiement (optionnel)" value={newStocker.payment_info}
                    onChange={(e) => setNewStocker({...newStocker, payment_info: e.target.value})}
                    className="bg-zinc-800 border border-zinc-700 rounded-xl py-3 px-4 text-white" />
                  <div className="flex gap-3">
                    <button onClick={() => setShowAddStocker(false)}
                      className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-3 rounded-xl">Annuler</button>
                    <button onClick={handleAddStocker} disabled={loading}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl">
                      {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Ajouter"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Stockers List */}
            <div className="glass-panel p-6">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-400" /> Mes Stockers ({stockers.length}/3)
              </h3>

              <div className="space-y-4">
                {stockers.map((s) => (
                  <div key={s.id} className="bg-zinc-900/50 p-4 rounded-xl border border-zinc-800">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className="bg-purple-500 text-white text-xs px-2 py-1 rounded mr-2">{s.stocker_number}</span>
                        <span className="text-white font-bold">{s.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => {
                          if (editingStocker === s.id) { setEditingStocker(null); }
                          else { setEditingStocker(s.id); setEditStockQty(s.quantity); setEditStockPrice(s.price); }
                        }}
                          className="text-blue-400 hover:text-blue-300 p-1" data-testid={`nm-edit-stocker-${s.id}`}>
                          <Settings className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeleteStocker(s.id)}
                          className="text-red-400 hover:text-red-300 p-1" data-testid={`nm-delete-stocker-${s.id}`}>
                          <AlertTriangle className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Dates */}
                    {s.start_date && (
                      <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                        <div className="bg-zinc-800/50 p-2 rounded-lg text-center">
                          <p className="text-zinc-500">Début</p>
                          <p className="text-white font-bold">{new Date(s.start_date).toLocaleDateString('fr-FR')}</p>
                        </div>
                        <div className="bg-zinc-800/50 p-2 rounded-lg text-center">
                          <p className="text-zinc-500">Contrôle</p>
                          <p className="text-blue-400 font-bold">{new Date(s.end_date).toLocaleDateString('fr-FR')}</p>
                        </div>
                      </div>
                    )}

                    {/* Stock Stats */}
                    <div className="grid grid-cols-4 gap-2 text-center text-sm mb-3">
                      <div className="bg-zinc-800/50 p-2 rounded-lg">
                        <p className="text-zinc-400 text-xs">Stock</p>
                        <p className="text-white font-bold">{s.quantity}</p>
                      </div>
                      <div className="bg-zinc-800/50 p-2 rounded-lg">
                        <p className="text-zinc-400 text-xs">Vendus</p>
                        <p className="text-emerald-400 font-bold">{s.total_sold || 0}</p>
                      </div>
                      <div className="bg-zinc-800/50 p-2 rounded-lg">
                        <p className="text-zinc-400 text-xs">Restant</p>
                        <p className="text-amber-400 font-bold">{s.current_stock}</p>
                      </div>
                      <div className="bg-zinc-800/50 p-2 rounded-lg">
                        <p className="text-zinc-400 text-xs">Prix</p>
                        <p className="text-white font-bold">${s.price}</p>
                      </div>
                    </div>

                    {/* Progress */}
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden mb-3">
                      <div className={`h-full rounded-full ${s.quantity > 0 && s.total_sold / s.quantity >= 0.5 ? 'bg-emerald-400' : 'bg-amber-400'}`}
                        style={{ width: `${s.quantity > 0 ? Math.min(100, (s.total_sold || 0) / s.quantity * 100) : 0}%` }} />
                    </div>

                    {/* Edit Panel */}
                    {editingStocker === s.id && (
                      <div className="bg-zinc-800/80 p-4 rounded-xl mb-3 border border-blue-500/30">
                        <p className="text-blue-400 text-sm font-bold mb-3">Modifier Stock</p>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="text-zinc-500 text-xs">Quantité (max 10)</label>
                            <input type="number" max={10} value={editStockQty}
                              onChange={(e) => setEditStockQty(Math.min(10, parseInt(e.target.value) || 0))}
                              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg py-2 px-3 text-white text-sm" />
                          </div>
                          <div>
                            <label className="text-zinc-500 text-xs">Prix ($)</label>
                            <input type="number" value={editStockPrice}
                              onChange={(e) => setEditStockPrice(parseFloat(e.target.value) || 0)}
                              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg py-2 px-3 text-white text-sm" />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => setEditingStocker(null)} className="flex-1 bg-zinc-700 text-white py-2 rounded-lg text-sm">Annuler</button>
                          <button onClick={() => handleUpdateSubStock(s.id)} className="flex-1 bg-blue-600 text-white py-2 rounded-lg text-sm font-bold">Sauvegarder</button>
                        </div>
                      </div>
                    )}

                    {/* Record Sale - detailed */}
                    {s.current_stock > 0 && (
                      <div className="bg-zinc-800/50 p-3 rounded-xl mb-2">
                        <p className="text-emerald-400 text-xs font-bold uppercase mb-2">Enregistrer une vente</p>
                        <div className="grid grid-cols-3 gap-2 mb-2">
                          <input type="text" placeholder="Modèle" 
                            id={`sale-model-${s.id}`}
                            className="bg-zinc-900 border border-zinc-700 rounded-lg py-2 px-2 text-white text-sm"
                            data-testid={`sale-model-${s.id}`} />
                          <input type="number" placeholder="Qté" min="1" max={s.current_stock}
                            id={`sale-qty-${s.id}`} defaultValue="1"
                            className="bg-zinc-900 border border-zinc-700 rounded-lg py-2 px-2 text-white text-sm"
                            data-testid={`sale-qty-${s.id}`} />
                          <input type="number" placeholder="Prix $" step="0.01"
                            id={`sale-price-${s.id}`} defaultValue={s.price || ""}
                            className="bg-zinc-900 border border-zinc-700 rounded-lg py-2 px-2 text-white text-sm"
                            data-testid={`sale-price-${s.id}`} />
                        </div>
                        <button onClick={async () => {
                          const modelEl = document.getElementById(`sale-model-${s.id}`);
                          const qtyEl = document.getElementById(`sale-qty-${s.id}`);
                          const priceEl = document.getElementById(`sale-price-${s.id}`);
                          const qty = parseInt(qtyEl?.value) || 1;
                          const model = modelEl?.value || "";
                          const price = parseFloat(priceEl?.value) || 0;
                          try {
                            await axios.put(`${API}/network-marketer/stocker/${s.id}/sale`, { quantity_sold: qty, model_name: model, price: price });
                            toast.success(`${qty} vente(s) enregistrée(s)!`);
                            if(modelEl) modelEl.value = "";
                            if(qtyEl) qtyEl.value = "1";
                            loadStockers();
                          } catch(e) { toast.error(e.response?.data?.detail || "Erreur"); }
                        }}
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-2 rounded-lg text-sm font-bold"
                          data-testid={`record-sale-btn-${s.id}`}>
                          {t("record_sale")}
                        </button>
                      </div>
                    )}

                    {/* Pay Stocker Button */}
                    {s.total_sold > 0 && !s.paid && (
                      <button onClick={async () => {
                        try {
                          await axios.post(`${API}/network-marketer/${user.id}/stocker/${s.id}/pay`);
                          toast.success(`Paiement de ${s.name} confirmé! Le CEO sera notifié.`);
                          loadStockers();
                        } catch(e) { toast.error("Erreur"); }
                      }}
                        className="w-full bg-yellow-500 hover:bg-yellow-600 text-zinc-900 font-bold py-3 rounded-xl flex items-center justify-center gap-2 mt-2"
                        data-testid={`pay-stocker-${s.id}`}>
                        <DollarSign className="w-5 h-5" /> Payer {s.name}
                      </button>
                    )}
                    {s.paid && (
                      <div className="bg-emerald-500/20 text-emerald-400 text-center py-2 rounded-xl mt-2 text-sm font-bold">
                        Payé
                      </div>
                    )}
                  </div>
                ))}

                {stockers.length === 0 && (
                  <p className="text-zinc-500 text-center py-8">Ajoutez votre premier stocker</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ========== BONUS TAB ========== */}
        {activeMenu === "bonus" && (
          <>
            {/* Bonus Card Section */}
            <div className="glass-panel p-6 mb-6 border border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-orange-500/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-amber-500/20 p-3 rounded-xl">
                    <Gift className="w-8 h-8 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">{t("bonus_card_recharge")}</p>
                    <p className="text-3xl font-bold text-amber-400">${bonusCard.balance.toFixed(2)}</p>
                    {bonusCard.withdrawn && (
                      <p className="text-zinc-500 text-xs">{t("bonus_card_daily")}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowWithdrawModal(true)}
                  disabled={!bonusCard.can_withdraw}
                  className={`px-6 py-3 rounded-xl font-bold transition-all ${
                    bonusCard.can_withdraw
                      ? 'bg-amber-500 hover:bg-amber-600 text-zinc-900'
                      : 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
                  }`}>
                  {bonusCard.can_withdraw ? t("withdraw_short") : t("not_available")}
                </button>
              </div>
            </div>

            {/* Bonus per vape */}
            <div className="glass-panel p-6">
              <h3 className="text-white font-bold mb-4">{t("bonus_per_sold")}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-zinc-900/60 p-4 rounded-xl text-center">
                  <p className="text-zinc-500 text-xs">{t("bonus_per_vape_stat")}</p>
                  <p className="text-emerald-400 font-bold text-2xl">$0.99</p>
                </div>
                <div className="bg-zinc-900/60 p-4 rounded-xl text-center">
                  <p className="text-zinc-500 text-xs">{t("total_bonus_stat")}</p>
                  <p className="text-emerald-400 font-bold text-2xl">${(marketer.total_bonus || 0).toFixed(2)}</p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ========== RETRAIT TAB ========== */}
        {activeMenu === "retrait" && (
          <div className="glass-panel p-5 border border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-emerald-600/10" data-testid="nm-withdraw-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-emerald-500/20 p-3 rounded-xl">
                <DollarSign className="w-7 h-7 text-emerald-400" />
              </div>
              <div>
                <h4 className="text-white font-bold text-lg">{t("withdraw_money")}</h4>
                <p className="text-zinc-500 text-sm">{t("contact_ceo_gains")}</p>
              </div>
            </div>

            {!nmWithdrawStep || nmWithdrawStep === "idle" ? (
              <>
                <div className="bg-zinc-900/60 rounded-xl p-4 mb-4">
                  <div className="flex justify-between py-2 border-b border-zinc-800">
                    <span className="text-zinc-400 text-sm">{t("total_earnings_label")}</span>
                    <span className="text-white font-bold">${(marketer.total_earnings || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-zinc-800">
                    <span className="text-zinc-400 text-sm">{t("commission_break_label")} (15% + {stockers.length}x2.5%)</span>
                    <span className="text-yellow-400 font-bold">${((stockers.reduce((a,s) => a + (s.total_sold || 0), 0)) * 2.5).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-zinc-800">
                    <span className="text-zinc-400 text-sm">{t("bonus_card_label")}</span>
                    <span className="text-amber-400 font-bold">${bonusCard.balance.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-3 mt-1">
                    <span className="text-white font-bold">{t("total_amount_label")}</span>
                    <span className="text-emerald-400 font-black text-2xl">${((marketer.total_earnings || 0) + bonusCard.balance).toFixed(2)}</span>
                  </div>
                </div>

                <button onClick={async () => {
                  // Step 1: If NM has no payment method saved, prompt them to pick one via Profile tab
                  try {
                    const pp = await axios.get(`${API}/user/${marketer.id}/payment-profile`);
                    const method = pp.data?.payment_profile?.method;
                    if (!method) {
                      toast.error(t("payment_method_required"));
                      setActiveMenu("profile");
                      return;
                    }
                  } catch (_) {
                    toast.error(t("payment_method_required"));
                    setActiveMenu("profile");
                    return;
                  }
                  // Step 2: Fire the CEO notification with payment profile attached
                  try {
                    await axios.post(`${API}/network-marketer/${marketer.id}/request-withdrawal`);
                    toast.success("Demand voye bay CEO");
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Erè pandan demand");
                  }
                  setNmWithdrawStep("waiting");
                  setNmWithdrawTimer(10);
                  let t2 = 10;
                  const interval = setInterval(() => {
                    t2 -= 1;
                    setNmWithdrawTimer(t2);
                    if (t2 <= 0) {
                      clearInterval(interval);
                      setNmWithdrawStep("approved");
                    }
                  }, 1000);
                }}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-3 transition-all active:scale-95"
                  data-testid="nm-withdraw-btn">
                  <DollarSign className="w-6 h-6" />
                  {t("withdraw_money")}
                </button>
              </>
            ) : nmWithdrawStep === "waiting" ? (
              <div className="text-center py-8">
                <div className="w-24 h-24 mx-auto mb-4 rounded-full border-4 border-emerald-500 flex items-center justify-center animate-pulse">
                  <span className="text-4xl font-black text-emerald-400">{nmWithdrawTimer}</span>
                </div>
                <p className="text-white font-bold text-lg mb-2">{t("please_wait")}</p>
                <p className="text-zinc-400 text-sm">{t("processing_short")}</p>
                <div className="mt-4 h-2 bg-zinc-800 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-all duration-1000"
                    style={{ width: `${((10 - nmWithdrawTimer) / 10) * 100}%` }} />
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-emerald-400" />
                </div>
                <p className="text-emerald-400 font-bold text-xl mb-2">{t("request_approved")}</p>
                <p className="text-zinc-400 text-sm mb-4">{t("contact_ceo_wa")}</p>

                <a href={`https://wa.me/17866309220?text=${encodeURIComponent(`Salut l'équipe Vape Yard! Je suis NM ${marketer.name} (Code: ${marketer.code}, Pays: ${marketer.country?.toUpperCase()}). Ma demande de retrait a été approuvée.\n📅 Date de la demande: ${new Date().toLocaleString('fr-FR')}`)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-3">
                  <MessageCircle className="w-6 h-6" />
                  {t("contact_ceo_whatsapp_btn")}
                </a>
                <p className="text-zinc-500 text-xs mt-2">+1 786 630 9220</p>
                <button onClick={() => setNmWithdrawStep("idle")} className="mt-3 text-zinc-500 hover:text-zinc-300 text-sm underline">{t("close_btn")}</button>
              </div>
            )}
          </div>
        )}

        {/* ========== NOTIFICATIONS TAB ========== */}
        {activeMenu === "notifications" && (
          <div className="glass-panel p-6" data-testid="nm-notifications">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5 text-purple-400" /> {t("notifications_label")} {unreadMessages > 0 && <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">{unreadMessages}</span>}
            </h3>
            <div className="space-y-3">
              {nmMessages.length === 0 ? (
                <p className="text-zinc-500 text-center py-8">{t("no_notifications")}</p>
              ) : (
                nmMessages.map((msg) => (
                  <div key={msg.id} className={`p-4 rounded-xl border ${msg.read ? 'bg-zinc-900/30 border-zinc-800' : 'bg-purple-500/10 border-purple-500/30'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${msg.read ? 'bg-zinc-800' : 'bg-purple-500/20'}`}>
                          <MessageCircle className={`w-4 h-4 ${msg.read ? 'text-zinc-500' : 'text-purple-400'}`} />
                        </div>
                        <div>
                          <p className="text-zinc-400 text-xs">{msg.from || 'CEO'}</p>
                          <p className="text-white text-sm mt-1">{msg.message}</p>
                          <p className="text-zinc-600 text-xs mt-2">{new Date(msg.created_at).toLocaleString('fr-FR')}</p>
                        </div>
                      </div>
                      {!msg.read && (
                        <button onClick={() => handleMarkMessageRead(msg.id)}
                          className="text-purple-400 hover:text-purple-300 text-xs underline">{t("mark_read")}</button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ========== MONTHLY REPORT TAB (NM) ========== */}
        {activeMenu === "report" && <UnsoldStockAlert user={marketer} />}
        {activeMenu === "report" && (
          <div className="space-y-4" data-testid="nm-report-tab">
            <div className="glass-panel p-6">
              <h3 className="text-white font-bold text-lg mb-1 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-400" /> Rapò Mansyèl bay CEO</h3>
              <p className="text-zinc-500 text-xs mb-4">Bay obsèvasyon w oswa pwopozisyon chanjman nan antrepriz la. CEO ap gen dosye chak rapò.</p>
              <input
                type="text"
                value={reportTitle}
                onChange={(e) => setReportTitle(e.target.value)}
                placeholder="Tit rapò (ex: Rapò Mansyèl Mas 2026)"
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm mb-2"
                data-testid="nm-report-title"
              />
              <textarea
                value={reportContent}
                onChange={(e) => setReportContent(e.target.value)}
                placeholder="Ekri obsèvasyon w, sijesyon chanjman, pwoblèm ke w rankontre, oswa siksè mwa a…"
                rows={6}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm mb-3"
                data-testid="nm-report-content"
              />
              <button
                onClick={submitMonthlyReport}
                disabled={reportSaving || !reportContent.trim()}
                className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold py-2 rounded-lg text-sm"
                data-testid="nm-report-submit"
              >
                {reportSaving ? "Ap voye..." : "✓ Voye Rapò bay CEO"}
              </button>
            </div>

            <div className="glass-panel p-4">
              <p className="text-zinc-400 text-xs font-bold uppercase mb-2">Rapò ki soumèt deja</p>
              {nmMonthlyReports.length === 0 && <p className="text-zinc-500 text-xs">Poko gen rapò soumèt.</p>}
              <div className="space-y-2">
                {nmMonthlyReports.map(r => (
                  <div key={r.id} className="bg-zinc-900/60 border border-zinc-700 rounded-lg p-3" data-testid={`nm-report-item-${r.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-white font-bold text-sm">{r.title}</p>
                      <span className="text-emerald-400 text-[10px] font-bold">{r.month}</span>
                    </div>
                    <p className="text-zinc-300 text-xs whitespace-pre-wrap">{r.content}</p>
                    <p className="text-zinc-500 text-[10px] mt-1">{new Date(r.created_at).toLocaleString('fr-FR')}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ========== PROFILE TAB (photo + payment profile + info) ========== */}
        {activeMenu === "profile" && (
          <div className="space-y-4" data-testid="nm-profile-tab">
            <div className="glass-panel p-6 text-center">
              <div className="relative inline-block mb-4">
                {profileImage ? (
                  <img src={profileImage} alt="Profile" className="w-24 h-24 rounded-full object-cover border-4 border-purple-500" data-testid="nm-profile-photo" />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold text-3xl">
                    {marketer.name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <label className="absolute bottom-0 right-0 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-purple-700" data-testid="nm-photo-upload-label">
                  <input type="file" accept="image/*" onChange={handleProfileImage} className="hidden" data-testid="nm-photo-upload-input" />
                  <Plus className="w-4 h-4 text-white" />
                </label>
              </div>
              <h3 className="text-white font-bold text-xl">{marketer.name}</h3>
              <p className="text-zinc-400">{t("nm_title")} • {marketer.country?.toUpperCase()}</p>
              <p className="text-zinc-500 text-sm mt-1">Code: {marketer.code}</p>
            </div>

            {/* Payment Profile — Zelle / CashApp / Cash */}
            <PaymentProfileCard userId={marketer.id} />

            <div className="glass-panel p-4">
              <h4 className="text-white font-bold mb-3">{t("informations")}</h4>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-zinc-800">
                  <span className="text-zinc-400">{t("pays")}</span>
                  <span className="text-white">{marketer.country === 'usa' ? '🇺🇸' : marketer.country === 'dr' ? '🇩🇴' : '🇭🇹'} {marketer.country?.toUpperCase()}</span>
                </div>
                {marketer.start_date && (
                  <div className="flex justify-between py-2 border-b border-zinc-800">
                    <span className="text-zinc-400">{t("member_since")}</span>
                    <span className="text-white">{new Date(marketer.start_date).toLocaleDateString('fr-FR')}</span>
                  </div>
                )}
                {marketer.end_date && (
                  <div className="flex justify-between py-2 border-b border-zinc-800">
                    <span className="text-zinc-400">{t("end_period")}</span>
                    <span className="text-white">{new Date(marketer.end_date).toLocaleDateString('fr-FR')}</span>
                  </div>
                )}
                <div className="flex justify-between py-2">
                  <span className="text-zinc-400">{t("nm_commission")}</span>
                  <span className="text-white font-bold">{marketer.commission_rate || 15}%</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Withdraw Bonus Modal */}
        {showWithdrawModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 max-w-md w-full">
              {!withdrawSuccess ? (
                <>
                  <div className="text-center mb-6">
                    <div className="bg-amber-500/20 p-4 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                      <Gift className="w-10 h-10 text-amber-400" />
                    </div>
                    <h3 className="text-white text-xl font-bold">Retirer Bonus Carte</h3>
                    <p className="text-amber-400 text-2xl font-bold mt-2">$10.00</p>
                  </div>

                  <div className="mb-6">
                    <label className="text-zinc-400 text-sm block mb-2">Numéro pour recevoir la carte</label>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="Ex: +509 1234 5678"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl py-4 px-4 text-white text-lg"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => { setShowWithdrawModal(false); setPhoneNumber(""); }}
                      className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-3 rounded-xl">
                      Annuler
                    </button>
                    <button
                      onClick={handleWithdrawBonus}
                      disabled={loading || !phoneNumber}
                      className="flex-1 bg-amber-500 hover:bg-amber-600 text-zinc-900 font-bold py-3 rounded-xl disabled:opacity-50">
                      {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Retirer"}
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center">
                  <div className="bg-emerald-500/20 p-4 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                    <Check className="w-10 h-10 text-emerald-400" />
                  </div>
                  <h3 className="text-white text-xl font-bold mb-2">Accès Envoyé!</h3>
                  <p className="text-zinc-400 mb-6">Vous recevrez la carte dans 24-48hrs</p>
                  <button
                    onClick={() => { setShowWithdrawModal(false); setWithdrawSuccess(false); setPhoneNumber(""); }}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 rounded-xl">
                    OK
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contact Us */}
        <ContactUs />
      </div>

      {/* VAPE-YARD FOOTER */}
      <footer className="bg-black border-t border-zinc-900 px-4 lg:px-8 py-5 mt-6" data-testid="nm-footer">
        <div className="flex flex-wrap items-center justify-between gap-4 max-w-6xl mx-auto">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-amber-400 rounded-lg flex items-center justify-center"><Network className="w-4 h-4 text-black" /></div>
            <div>
              <p className="text-white font-black text-sm tracking-wider leading-none">VAPE<span className="text-amber-400">-</span>YARD</p>
              <p className="text-zinc-600 text-[9px] uppercase tracking-[0.25em] mt-0.5">Flavors meet the future</p>
            </div>
          </div>
          <p className="text-zinc-500 text-[10px] font-semibold">© {new Date().getFullYear()} VAPE-YARD Enterprise · Network Marketer Zone</p>
        </div>
      </footer>
    </div>
  );
};

// ==================== CEO DASHBOARD ====================
// CEO panel: users without a profile photo. Lets CEO send a "Please add a photo"
// notification to each user IN THEIR LANGUAGE.
const PhotoProfilePanel = () => {
  const [data, setData] = React.useState({ stockers: [], network_marketers: [], managers: [] });
  const [loading, setLoading] = React.useState(true);
  const [pending, setPending] = React.useState({});

  const load = () => {
    setLoading(true);
    axios.get(`${API}/ceo/users-without-photo`).then(r => setData(r.data)).finally(() => setLoading(false));
  };
  React.useEffect(() => { load(); }, []);

  const requestPhoto = async (uid, name) => {
    setPending(p => ({ ...p, [uid]: true }));
    try {
      const r = await axios.post(`${API}/ceo/user/${uid}/request-photo`);
      toast.success(`Demand voye bay ${name} (${r.data.lang})`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erè");
    } finally {
      setPending(p => ({ ...p, [uid]: false }));
    }
  };

  const groups = [
    { key: "stockers", label: "Stockers", tone: "text-blue-400" },
    { key: "network_marketers", label: "Network Marketers", tone: "text-purple-400" },
    { key: "managers", label: "Managers", tone: "text-emerald-400" },
  ];
  const total = data.stockers.length + data.network_marketers.length + data.managers.length;

  return (
    <div className="space-y-4" data-testid="photo-profil-tab">
      <div className="glass-panel p-6">
        <h3 className="text-white font-bold text-xl mb-1 flex items-center gap-2">
          <User className="w-5 h-5 text-amber-400" /> Foto Pwofil Actif
        </h3>
        <p className="text-zinc-500 text-sm mb-4">Wè kilès poko ajoute yon foto pwofil. Klike "Mande Foto" pou voye yon notifikasyon amikal nan lang chak moun.</p>
        <div className="flex gap-3 mb-4">
          <div className="glass-panel p-3 flex-1 text-center">
            <p className="text-zinc-500 text-xs">San Foto</p>
            <p className="text-amber-400 font-black text-2xl">{loading ? "..." : total}</p>
          </div>
          <button onClick={load} className="bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2 rounded-xl text-sm" data-testid="photo-refresh-btn">
            🔄 Rafrechi
          </button>
        </div>
      </div>

      {groups.map(g => (
        <div key={g.key} className="glass-panel p-5">
          <h4 className={`font-bold mb-3 ${g.tone}`}>{g.label} ({data[g.key].length})</h4>
          {data[g.key].length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-4">✅ Tout {g.label} yo gen foto pwofil</p>
          ) : (
            <div className="space-y-2">
              {data[g.key].map(u => (
                <div key={u.id} className="bg-zinc-900/50 p-3 rounded-lg flex items-center justify-between" data-testid={`photo-row-${u.id}`}>
                  <div>
                    <p className="text-white font-bold text-sm">{u.name}</p>
                    <p className="text-zinc-500 text-xs">Code: {u.code} • {(u.country || "").toUpperCase()} • Lang: {u.preferred_lang || "ht"}</p>
                    {u.photo_requested_at && (
                      <p className="text-amber-400 text-[10px]">📤 Deja mande {new Date(u.photo_requested_at).toLocaleDateString('fr-FR')}</p>
                    )}
                  </div>
                  <button
                    disabled={pending[u.id]}
                    onClick={() => requestPhoto(u.id, u.name)}
                    className="bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white font-bold px-3 py-1.5 rounded-lg text-xs"
                    data-testid={`request-photo-${u.id}`}>
                    {pending[u.id] ? "..." : "📸 Mande Foto"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const CEODashboard = () => {
  const navigate = useNavigate();
  const [showAiAssistant, setShowAiAssistant] = useState(false);
  const [stockers, setStockers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [networkMarketers, setNetworkMarketers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [commissions, setCommissions] = useState({});
  const [settings, setSettings] = useState({ bonus_per_vape: 1.25 });
  const [globalStats, setGlobalStats] = useState({});
  const [searchCode, setSearchCode] = useState("");
  const [foundStocker, setFoundStocker] = useState(null);
  const [stockerSearchQuery, setStockerSearchQuery] = useState("");
  const [filterCity, setFilterCity] = useState("all");
  const [editStock, setEditStock] = useState({ stockerId: null, models: [] });
  const [editDate, setEditDate] = useState({ stockerId: null, newDate: "" });
  const [editPrice, setEditPrice] = useState({ stockerId: null, newPrice: 0 });
  const [newStocker, setNewStocker] = useState({ name: "", country: "haiti", city: "" });
  const [newManager, setNewManager] = useState({ name: "", country: "haiti" });
  const [newNetworkMarketer, setNewNetworkMarketer] = useState({ name: "", country: "haiti" });
  const [generatedManagerCode, setGeneratedManagerCode] = useState(null);
  const [generatedNMCode, setGeneratedNMCode] = useState(null);
  const [vapeModels, setVapeModels] = useState([
    { name: "Model 1", quantity: 0, price: 0 },
    { name: "Model 2", quantity: 0, price: 0 },
    { name: "Model 3", quantity: 0, price: 0 }
  ]);
  const [nmVapeModels, setNmVapeModels] = useState([
    { name: "Model 1", quantity: 0, price: 0 },
    { name: "Model 2", quantity: 0, price: 0 },
    { name: "Model 3", quantity: 0, price: 0 }
  ]);
  const [editNmStock, setEditNmStock] = useState({ nmId: null, newStock: 0 });
  const [editNmDate, setEditNmDate] = useState({ nmId: null, newDate: "" });
  const [showNmStockEdit, setShowNmStockEdit] = useState(null);
  const [nmEditModels, setNmEditModels] = useState([]);

  const [generatedCode, setGeneratedCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("generate");

  // Jenny AI state
  const [jennyMessages, setJennyMessages] = useState([]);
  const [jennyInput, setJennyInput] = useState("");
  const [jennySending, setJennySending] = useState(false);
  const jennyEndRef = useRef(null);

  // Add Stock state
  const [addStockTarget, setAddStockTarget] = useState(null); // {type, id, name}
  const [addStockModels, setAddStockModels] = useState([{ name: "", quantity: 0, price: 0 }]);
  const [addStockLoading, setAddStockLoading] = useState(false);
  const [addStockBatch, setAddStockBatch] = useState("");
  
  // Performance state
  const [performanceData, setPerformanceData] = useState([]);
  const [perfFilter, setPerfFilter] = useState("all");
  const [perfSearch, setPerfSearch] = useState("");
  
  // Add Stock search
  const [addStockSearch, setAddStockSearch] = useState("");
  
  // Notification filter
  const [notifFilter, setNotifFilter] = useState("all");
  const [showOverdueModal, setShowOverdueModal] = useState(false);
  const [overdueSummary, setOverdueSummary] = useState({ count: 0, items: [] });
  const [kontwolReport, setKontwolReport] = useState(null); // { user, role }
  const [showCloseAllPreview, setShowCloseAllPreview] = useState(null); // list of scanned
  
  // NM Sub-Stockers view state
  const [viewNmSubs, setViewNmSubs] = useState(null);
  
  // Control Date notifications (today + overdue)
  const [controlNotifs, setControlNotifs] = useState({ today: [], overdue: [], today_count: 0, overdue_count: 0 });
  
  // Custom buttons from Jenny
  const [customButtons, setCustomButtons] = useState([]);
  
  // ==================== Edit Stocker / NM Panel state ====================
  const [editTargetType, setEditTargetType] = useState("stocker"); // 'stocker' | 'nm'
  const [editTargetId, setEditTargetId] = useState("");
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editSearch, setEditSearch] = useState("");
  const [showEditUserHistory, setShowEditUserHistory] = useState(false);
  
  // ==================== INV Management state ====================
  const [invs, setInvs] = useState([]);
  const [newInv, setNewInv] = useState({ name: "", code: "", country: "haiti", investment_amount: 0, reserve_stock: 0, phone: "" });
  const [selectedInvId, setSelectedInvId] = useState("");
  
  const fetchInvs = async () => {
    try {
      const r = await axios.get(`${API}/ceo/inv/list`);
      setInvs(r.data);
    } catch (e) { /* ignore */ }
  };
  
  useEffect(() => { fetchInvs(); }, []);

  // Financial Dashboard state
  const [financeData, setFinanceData] = useState(null);
  const [financeArea, setFinanceArea] = useState("haiti");
  const [lotLinkTarget, setLotLinkTarget] = useState(null);
  const goLinkLot = (u) => {
    const c = (u.country || "").toLowerCase();
    setFinanceArea(c.startsWith("dom") || c === "do" ? "dr" : ["haiti", "dr", "usa"].includes(c) ? c : "haiti");
    setLotLinkTarget({ id: u.id, name: u.name });
    setActiveTab("finance");
    setTimeout(() => document.querySelector('[data-testid="batch-section"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
  };
  const [showAddInvestment, setShowAddInvestment] = useState(false);
  const [showAddBatch, setShowAddBatch] = useState(false);
  const [investForm, setInvestForm] = useState({ country: "haiti", amount: 0, units: 0, description: "", date_sent: new Date().toISOString().split('T')[0] });
  const [batchForm, setBatchForm] = useState({ name: "Stock A", country: "haiti", total_units: 0, cost_per_unit: 0, sell_price: 0, investment_amount: 0, investment_description: "", date_sent: new Date().toISOString().split('T')[0], models: [{name: "", quantity: 0, price: 0}] });

  // Cities state
  const [citiesList, setCitiesList] = useState({});
  const [newCityName, setNewCityName] = useState("");
  const [newCityCountry, setNewCityCountry] = useState("haiti");

  // Slider management state
  const [sliderImages, setSliderImages] = useState([]);
  const [newSlideUrl, setNewSlideUrl] = useState("");

  // Hookah CEO state
  const [ceoHookahs, setCeoHookahs] = useState([]);
  const [hookahCountry, setHookahCountry] = useState("haiti");
  const [hookahSetupCount, setHookahSetupCount] = useState(15);
  const [hookahHistory, setHookahHistory] = useState([]);
  const [activeDailySessions, setActiveDailySessions] = useState([]);

  // Manager AI (Tanie) state
  const [managerMessages, setManagerMessages] = useState([]);
  const [managerInput, setManagerInput] = useState("");
  const [managerSending, setManagerSending] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false); // true = response will be spoken
  const managerEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);

  // Play TTS audio from base64
  const playManagerAudio = async (text) => {
    try {
      const res = await axios.post(`${API}/ceo/manager/speak`, { text: text.substring(0, 2000) });
      if (res.data.audio) {
        const audio = new Audio(`data:audio/mp3;base64,${res.data.audio}`);
        audio.play();
      }
    } catch(e) {
      if (window.speechSynthesis) {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'fr-FR';
        utterance.rate = 0.9;
        window.speechSynthesis.speak(utterance);
      }
    }
  };

  // Send message to manager
  const sendManagerMessage = async (msg, speakResponse = false) => {
    setManagerMessages(prev => [...prev, { role: 'user', content: msg }]);
    setManagerSending(true);
    // Auto-retry with exponential backoff for professional resilience
    const postWithRetry = async () => {
      let lastErr = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          return await axios.post(`${API}/ceo/manager`, { message: msg }, { timeout: 60000 });
        } catch(err) {
          lastErr = err;
          if (attempt < 2) await new Promise(res => setTimeout(res, 800 * (attempt + 1)));
        }
      }
      throw lastErr;
    };
    try {
      const r = await postWithRetry();
      const response = r.data.response;
      const actions = r.data.actions || [];
      const mode = r.data.mode || 'ai';
      setManagerMessages(prev => [...prev, { role: 'assistant', content: response, image: r.data.image || null, actions, mode }]);
      
      // Refresh data after any action
      if (actions.length > 0) {
        fetchData();
      }
      
      // If Jenny was called, actually invoke Jenny in the chat
      if (actions.some(a => a.action === "call_jenny")) {
        toast.success("Jenny rejoint la réunion!");
        setManagerMessages(prev => [...prev, { role: 'jenny_join', content: "Jenny Assistante a rejoint la réunion" }]);
        // Actually call Jenny with the context
        try {
          const jennyMsg = actions.find(a => a.action === "call_jenny")?.params?.message || msg;
          const jennyRes = await axios.post(`${API}/ceo/jenny`, { message: jennyMsg }, { timeout: 60000 });
          setManagerMessages(prev => [...prev, { role: 'jenny', content: jennyRes.data.response }]);
        } catch(e) {
          setManagerMessages(prev => [...prev, { role: 'jenny', content: "Jenny: M la Boss! Kisa ou bezwen?" }]);
        }
      }
      // If someone was suspended
      if (actions.some(a => a.action === "suspend_user")) {
        toast.success("Compte suspendu avec succès!");
        fetchData();
      }
      
      // Only play audio if voice mode is on
      if (speakResponse || voiceMode) {
        playManagerAudio(response);
      }
    } catch(e) {
      setManagerMessages(prev => [...prev, { role: 'assistant', content: "Boss, koneksyon an fèb yon segond. M ap reyalinnye… tanpri voye mesaj la yon lòt fwa.", retry: true, retryMsg: msg }]);
    } finally {
      setManagerSending(false);
      setVoiceMode(false);
    }
  };

  // Screenshot protection
  useScreenshotProtection();

  useEffect(() => {
    if (!localStorage.getItem('ceoAuth')) { navigate('/ceo/login'); return; }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [stockersRes, settingsRes, statsRes, managersRes, nmRes, notifRes, commRes] = await Promise.all([
        axios.get(`${API}/stockers`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/stats/global`),
        axios.get(`${API}/managers`),
        axios.get(`${API}/network-marketers`),
        axios.get(`${API}/ceo/notifications`),
        axios.get(`${API}/settings/commissions`)
      ]);
      setStockers(stockersRes.data);
      setSettings(settingsRes.data);
      setGlobalStats(statsRes.data);
      setManagers(managersRes.data);
      setNetworkMarketers(nmRes.data);
      setNotifications(notifRes.data);
      setCommissions(commRes.data);

      // Control date notifications (today + overdue)
      try {
        const cn = await axios.get(`${API}/ceo/control-date-notifications`);
        setControlNotifs(cn.data);
      } catch (e) { /* ignore */ }
      
      // Load performance & custom buttons
      try {
        const [perfRes, btnRes, jennyHistRes, finRes] = await Promise.all([
          axios.get(`${API}/ceo/performance`),
          axios.get(`${API}/ceo/custom-buttons`),
          axios.get(`${API}/ceo/jenny/history`),
          axios.get(`${API}/ceo/financial-dashboard`)
        ]);
        setPerformanceData(perfRes.data);
        setCustomButtons(btnRes.data);
        setFinanceData(finRes.data);
        // Load cities
        try {
          const [hCities, drCities, usCities] = await Promise.all([
            axios.get(`${API}/ceo/cities/haiti`),
            axios.get(`${API}/ceo/cities/dr`),
            axios.get(`${API}/ceo/cities/usa`)
          ]);
          setCitiesList({ haiti: hCities.data, dr: drCities.data, usa: usCities.data });
        } catch(e) {}
        // Load slider images
        try {
          const slRes = await axios.get(`${API}/slider-images`);
          if (slRes.data) setSliderImages(slRes.data);
        } catch(e) {}
        // Load hookahs
        try {
          const hRes = await axios.get(`${API}/hookah/${hookahCountry}`);
          setCeoHookahs(hRes.data);
        } catch(e) {}
        // Load active hookah daily sessions for live monitoring
        try {
          const dsRes = await axios.get(`${API}/hookah/daily-session/country/${hookahCountry}/active`);
          setActiveDailySessions(dsRes.data || []);
        } catch(e) {}
        if (jennyHistRes.data.length > 0 && jennyMessages.length === 0) {
          setJennyMessages(jennyHistRes.data.map(h => ({ role: h.role, content: h.content, actions: h.actions || [] })));
        }
      } catch (e) { console.log("Extra data load error"); }
    } catch (e) { console.log("Error fetching data"); }
  };

  const handleAddStock = async () => {
    if (!addStockTarget) return;
    const validModels = addStockModels.filter(m => m.name && m.quantity > 0 && m.price > 0);
    if (validModels.length === 0) { toast.error("Entrez au moins un modèle valide"); return; }
    setAddStockLoading(true);
    try {
      const res = await axios.post(`${API}/ceo/add-stock/${addStockTarget.type}/${addStockTarget.id}`, { models: validModels, stock_batch: addStockBatch });
      toast.success(`Stock ajouté pour ${res.data.user_name}! Total: ${res.data.total_stock}`);
      setAddStockTarget(null);
      setAddStockModels([{ name: "", quantity: 0, price: 0 }]);
      setAddStockBatch("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setAddStockLoading(false); }
  };

  const handleSearchStocker = async () => {
    if (!searchCode) return;
    try {
      const res = await axios.get(`${API}/stockers/by-code/${searchCode}`);
      setFoundStocker(res.data);
      toast.success("Stocker trouvé!");
    } catch (e) {
      toast.error("Stocker non trouvé");
      setFoundStocker(null);
    }
  };

  const handleUpdateStockerStock = async () => {
    if (!foundStocker) return;
    setLoading(true);
    try {
      await axios.put(`${API}/stockers/${foundStocker.id}/stock`, {
        vape_models: vapeModels.filter(m => m.quantity > 0)
      });
      toast.success("Stock mis à jour!");
      setFoundStocker(null);
      setSearchCode("");
      fetchData();
    } catch (e) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setLoading(false);
    }
  };

  const handleQuickStockUpdate = async (stockerId, isNm = false) => {
    const models = editStock.models.filter(m => m.name.trim());
    const totalQty = models.reduce((a, m) => a + (m.quantity || 0), 0);
    try {
      const url = isNm ? `${API}/ceo/network-marketers/${stockerId}` : `${API}/stockers/${stockerId}`;
      await axios.put(url, {
        vape_models: models,
        initial_stock: totalQty,
        current_stock: totalQty
      });
      toast.success("Modèles, stock & prix modifiés!");
      setEditStock({ stockerId: null, models: [] });
      fetchData();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const handleDeleteStocker = async (stockerId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce stocker?")) return;
    try {
      await axios.delete(`${API}/stockers/${stockerId}`);
      toast.success("Stocker supprimé");
      fetchData();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const handleGenerateStocker = async () => {
    if (!newStocker.name.trim()) { toast.error("Entrez le nom du stocker"); return; }
    
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setLoading(true);
    
    try {
      const res = await axios.post(`${API}/stockers`, {
        name: newStocker.name.trim(),
        code: code,
        country: newStocker.country,
        city: newStocker.country === "haiti" ? newStocker.city : "",
        start_date: new Date().toISOString(),
        vape_models: vapeModels.filter(m => m.quantity > 0),
        manager_id: newStocker.assignment_type === "manager" ? (newStocker.manager_id || null) : null,
        inv_id: newStocker.assignment_type === "inv" ? (newStocker.inv_id || null) : null
      });
      
      setGeneratedCode({
        code: code,
        name: newStocker.name,
        country: newStocker.country
      });
      toast.success("Code généré! Le stocker créera son propre code secret.");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateManager = async () => {
    if (!newManager.name.trim()) { toast.error("Entrez le nom du manager"); return; }
    
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setLoading(true);
    
    try {
      await axios.post(`${API}/managers`, {
        name: newManager.name.trim(),
        code: code,
        country: newManager.country
      });
      
      setGeneratedManagerCode({
        code: code,
        name: newManager.name,
        country: newManager.country
      });
      toast.success("Code Manager généré!");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur - un manager existe peut-être déjà pour ce pays");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteManager = async (managerId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce manager?")) return;
    try {
      await axios.delete(`${API}/managers/${managerId}`);
      toast.success("Manager supprimé");
      fetchData();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const handleGenerateNetworkMarketer = async () => {
    if (!newNetworkMarketer.name.trim()) { toast.error("Entrez le nom"); return; }
    
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setLoading(true);
    
    try {
      await axios.post(`${API}/network-marketers`, {
        name: newNetworkMarketer.name.trim(),
        code: code,
        country: newNetworkMarketer.country,
        start_date: new Date().toISOString(),
        vape_models: nmVapeModels.filter(m => m.quantity > 0)
      });
      
      setGeneratedNMCode({
        code: code,
        name: newNetworkMarketer.name,
        country: newNetworkMarketer.country
      });
      toast.success("Code Network Marketer généré!");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateNmStock = async (nmId) => {
    try {
      await axios.put(`${API}/ceo/network-marketer/${nmId}/stock`, {
        vape_models: nmEditModels.filter(m => m.quantity > 0)
      });
      toast.success("Stock NM mis à jour!");
      setShowNmStockEdit(null);
      setNmEditModels([]);
      fetchData();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const handleUpdateNmStartDate = async (nmId, newDate) => {
    if (!newDate) return;
    try {
      await axios.put(`${API}/ceo/network-marketer/${nmId}/start-date`, {
        start_date: new Date(newDate).toISOString()
      });
      toast.success("Date NM mise à jour!");
      fetchData();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const handleQuickNmStockUpdate = async (nmId, newStock) => {
    try {
      await axios.put(`${API}/ceo/network-marketer/${nmId}/quick-stock`, { stock: newStock });
      toast.success("Stock modifié!");
      fetchData();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const handleDeleteNetworkMarketer = async (nmId) => {
    if (!window.confirm("Supprimer ce Network Marketer?")) return;
    try {
      await axios.delete(`${API}/network-marketers/${nmId}`);
      toast.success("Supprimé");
      fetchData();
    } catch (e) { toast.error("Erreur"); }
  };

  const handleUpdateCommissions = async () => {
    try {
      await axios.put(`${API}/settings/commissions`, commissions);
      toast.success("Commissions mises à jour!");
    } catch (e) { toast.error("Erreur"); }
  };

  const handleUpdateStartDate = async (stockerId, newDate, isNm = false) => {
    if (!newDate) return;
    try {
      if (isNm) {
        await axios.put(`${API}/ceo/network-marketers/${stockerId}`, {
          start_date: new Date(newDate).toISOString()
        });
      } else {
        await axios.put(`${API}/ceo/stocker/${stockerId}/start-date`, {
          start_date: new Date(newDate).toISOString()
        });
      }
      toast.success("Date de début mise à jour!");
      fetchData();
    } catch (e) {
      toast.error("Erreur lors de la mise à jour de la date");
    }
  };

  const handleUpdatePrice = async (stockerId, newPrice) => {
    try {
      await axios.put(`${API}/stockers/${stockerId}`, {
        vape_models: [{ name: "Vape", quantity: editPrice.newStock || 0, price: newPrice }],
        initial_stock: editPrice.newStock || 0,
        current_stock: editPrice.newStock || 0
      });
      toast.success("Prix mis à jour!");
      fetchData();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const handleResetSecretCode = async (stockerId, stockerName) => {
    if (!window.confirm(`Réinitialiser le code secret de ${stockerName}? Il devra créer un nouveau code.`)) return;
    try {
      await axios.post(`${API}/ceo/reset-secret-code/${stockerId}`);
      toast.success(`Code secret de ${stockerName} réinitialisé`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const countryFlags = { usa: "🇺🇸", dr: "🇩🇴", haiti: "🇭🇹" };
  const countryNames = { usa: "États-Unis", dr: "République Dominicaine", haiti: "Haïti" };

  return (
    <div className="min-h-screen bg-white relative" data-testid="ceo-dashboard" style={{fontFamily: 'Inter, system-ui, sans-serif'}}>
      {/* VAPE-YARD CEO TOPBAR */}
      <div className="bg-black border-b-4 border-amber-400 px-4 py-3 sticky top-0 z-30 flex items-center justify-between shadow-xl">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 bg-amber-400 rounded-lg flex items-center justify-center">
            <Crown className="w-5 h-5 text-black" />
          </div>
          <div>
            <p className="text-white font-black text-base tracking-wider leading-none">VAPE<span className="text-amber-400">-</span>YARD</p>
            <p className="text-zinc-500 text-[9px] uppercase tracking-[0.25em] mt-0.5">CEO Command Center</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <TimeDisplay />
          <button
            onClick={() => setShowAiAssistant(true)}
            data-testid="open-ceo-ai"
            className="relative flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black font-black px-3 py-2 rounded-xl text-xs"
            title="Ada — AI Pèsonèl CEO"
          >
            <Sparkles className="w-4 h-4" />
            <span className="hidden md:inline uppercase tracking-widest">Ada AI</span>
          </button>
          <button
            onClick={() => { localStorage.removeItem('ceoAuth'); navigate('/'); }}
            className="p-2 text-zinc-400 hover:text-red-400 hover:bg-zinc-900 rounded-lg"
            data-testid="ceo-logout"
            title="Logout"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-5">
        {/* Welcome banner */}
        <div className="bg-gradient-to-r from-zinc-900 via-black to-amber-900/40 border-l-4 border-amber-400 rounded-xl px-4 py-3 mb-5 shadow-md flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-amber-400 flex items-center justify-center">
              <Crown className="w-6 h-6 text-black" />
            </div>
            <div>
              <p className="text-white font-black text-lg">Bienvenue, <span className="text-amber-400">Kymbell</span></p>
              <p className="text-zinc-400 text-xs">CEO Dashboard — kontwole tout VAPE-YARD la</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <TeamBonusManagerButton />
            <LowStockAlertsButton />
            <PendingActivationsButton />
            <button
              onClick={async () => {
                if (!window.confirm("Rekalkile chif sik la pou TOUT stockers + NM depi sales_history? Sa ap ranje nenpòt drift.")) return;
                const t = toast.loading("Ap rekalkile tout moun...");
                try {
                  const r = await axios.post(`${API}/ceo/recalc-all`);
                  toast.dismiss(t);
                  const { fixed_count, unchanged, fixed } = r.data;
                  if (fixed_count === 0) {
                    toast.success(`✅ Tout ${unchanged} moun yo gen chif korèk`);
                  } else {
                    toast.success(`🔄 ${fixed_count} moun ranje / ${unchanged} deja korèk\n${fixed.slice(0,5).map(f => `• ${f.name}: vann ${f.diff.total_sold>=0?'+':''}${f.diff.total_sold}, revni ${f.diff.total_revenue>=0?'+':''}$${f.diff.total_revenue}`).join('\n')}${fixed.length>5?`\n… + ${fixed.length-5} lòt`:''}`, { duration: 12000 });
                  }
                  fetchData();
                } catch (e) {
                  toast.dismiss(t);
                  toast.error("Erè rekalkile");
                }
              }}
              data-testid="recalc-all-btn"
              title="Rekalkile TOUT chif sik depi sales_history"
              className="relative flex items-center gap-1.5 px-3 py-2 bg-cyan-600/20 hover:bg-cyan-600/40 border border-cyan-500/40 rounded-xl text-cyan-300 font-bold text-xs uppercase tracking-widest transition-colors"
            >
              🔄 <span className="hidden md:inline">Rekalkile Tout</span>
            </button>
            <SystemMessageButton />
            <LanguageSwitcher />
          </div>
        </div>

        {/* Global Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="glass-panel p-4 text-center">
            <Users className="w-6 h-6 text-violet-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{globalStats.total_stockers || 0}</p>
            <p className="text-zinc-500 text-xs">Stockers</p>
          </div>
          <div className="glass-panel p-4 text-center">
            <DollarSign className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-emerald-400">${(globalStats.total_revenue || 0).toFixed(0)}</p>
            <p className="text-zinc-500 text-xs">Revenus</p>
          </div>
          <div className="glass-panel p-4 text-center">
            <Package className="w-6 h-6 text-blue-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{globalStats.total_sold || 0}</p>
            <p className="text-zinc-500 text-xs">Vapes Vendus</p>
          </div>
          <div className="glass-panel p-4 text-center">
            <Award className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-yellow-400">${(globalStats.total_bonus_paid || 0).toFixed(0)}</p>
            <p className="text-zinc-500 text-xs">Bonus Payés</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            {id: "generate", label: "Stocker", icon: Key}, 
            {id: "network", label: "Network Marketer", icon: Network},
            {id: "managers", label: "Managers", icon: BarChart3},
            {id: "cities", label: "Villes", icon: MapPin},
            {id: "add-stock", label: "Ajouter Stock", icon: Plus},
            {id: "performance", label: "Performance", icon: TrendingUp},
            {id: "finance", label: "Finance", icon: DollarSign},
            {id: "commissions", label: "Commissions", icon: DollarSign},
            {id: "notifications", label: `Notifs (${notifications.filter(n => !n.read).length})`, icon: Bell},
            {id: "stockers", label: "Mes Stockers", icon: Users},
            {id: "control-dates", label: "Notif Kontwòl", icon: Bell},
            {id: "market-intel", label: "Market Intel", icon: TrendingUp},
            {id: "performance-kont", label: "Pèfòmans Kont", icon: Award},
            {id: "activity", label: "Aktivite", icon: Clock},
            {id: "dossier", label: "Dosye", icon: BookOpen},
            {id: "manager", label: "Tanie (Manager)", icon: Briefcase},
            {id: "jenny", label: "Jenny Assistante", icon: Bot},
            {id: "slides", label: "Image Slides", icon: Instagram},
            {id: "visuel", label: "Vizyèl Dashboard", icon: ImageIcon},
            {id: "stock-verify", label: "Réceptions Stock", icon: CameraIcon},
            {id: "expenses", label: "Dépenses", icon: DollarSign},
            {id: "sale-corrections", label: "Corrections Ventes", icon: Pencil},
            {id: "hookah", label: "Hookah", icon: Zap},
            {id: "edit-stocker", label: "Paramèt Stocker", icon: Settings},
            {id: "photo-profil", label: "Foto Pwofil", icon: User},
            {id: "inv-mgmt", label: "Investisseurs", icon: DollarSign}
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`relative flex items-center gap-2 px-3 py-2 rounded-xl transition-all text-sm ${activeTab === tab.id ? 'bg-violet-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
              <tab.icon className="w-4 h-4" /> {tab.label}
              {tab.id === "control-dates" && (controlNotifs.today_count + controlNotifs.overdue_count) > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="control-notif-badge">
                  {controlNotifs.today_count + controlNotifs.overdue_count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Generate Code Tab */}
        {activeTab === "generate" && (
          <div className="glass-panel p-6">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2"><Key className="w-5 h-5 text-yellow-400" /> Générer Nouveau Stocker</h3>
            
            {!generatedCode ? (
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <input type="text" placeholder="Nom du stocker" value={newStocker.name}
                    onChange={(e) => setNewStocker({...newStocker, name: e.target.value})}
                    className="bg-zinc-900 border border-zinc-800 rounded-xl py-3 px-4 text-white" data-testid="generator-name" />
                  <select value={newStocker.country} onChange={(e) => setNewStocker({...newStocker, country: e.target.value, city: ""})}
                    className="bg-zinc-900 border border-zinc-800 rounded-xl py-3 px-4 text-white" data-testid="generator-country">
                    <option value="haiti">🇭🇹 Haïti</option>
                    <option value="usa">🇺🇸 États-Unis</option>
                    <option value="dr">🇩🇴 République Dominicaine</option>
                  </select>
                </div>
                
                {/* City selection for Haiti */}
                {newStocker.country === "haiti" && (
                  <select value={newStocker.city} onChange={(e) => setNewStocker({...newStocker, city: e.target.value})}
                    className="bg-zinc-900 border border-zinc-800 rounded-xl py-3 px-4 text-white" data-testid="generator-city">
                    <option value="">-- Sélectionnez une ville --</option>
                    {HAITI_CITIES.map(city => <option key={city} value={city}>{city}</option>)}
                  </select>
                )}

                {/* Assignment Type Toggle: INV ↔ Manager ↔ None */}
                <div className="bg-zinc-900/50 p-4 rounded-xl border border-zinc-800">
                  <p className="text-zinc-300 text-sm mb-3 font-bold">📌 Asiyen Stocker la a (chwazi yon sèl)</p>
                  <div className="grid grid-cols-3 gap-2 mb-3" data-testid="assignment-toggle">
                    <button
                      type="button"
                      onClick={() => setNewStocker({ ...newStocker, assignment_type: "none", manager_id: null, inv_id: null })}
                      data-testid="assign-none-btn"
                      className={`py-2 px-3 rounded-lg text-xs font-bold transition-all ${(!newStocker.assignment_type || newStocker.assignment_type === "none") ? "bg-zinc-700 text-white border-2 border-zinc-500" : "bg-zinc-900 text-zinc-500 border border-zinc-800"}`}
                    >
                      ⚪ Okenn
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewStocker({ ...newStocker, assignment_type: "manager", inv_id: null })}
                      data-testid="assign-manager-btn"
                      className={`py-2 px-3 rounded-lg text-xs font-bold transition-all ${newStocker.assignment_type === "manager" ? "bg-blue-600 text-white border-2 border-blue-400" : "bg-zinc-900 text-zinc-500 border border-zinc-800"}`}
                    >
                      👔 Manager
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewStocker({ ...newStocker, assignment_type: "inv", manager_id: null })}
                      data-testid="assign-inv-btn"
                      className={`py-2 px-3 rounded-lg text-xs font-bold transition-all ${newStocker.assignment_type === "inv" ? "bg-emerald-600 text-white border-2 border-emerald-400" : "bg-zinc-900 text-zinc-500 border border-zinc-800"}`}
                    >
                      💰 Investisseur
                    </button>
                  </div>

                  {newStocker.assignment_type === "manager" && (
                    <select value={newStocker.manager_id || ""} onChange={(e) => setNewStocker({ ...newStocker, manager_id: e.target.value || null })}
                      className="w-full bg-zinc-800 border border-blue-500/40 rounded-xl py-2 px-3 text-white text-sm" data-testid="generator-manager">
                      <option value="">— Chwazi Manager —</option>
                      {managers.filter(mg => mg.country === newStocker.country).map(mg => (
                        <option key={mg.id} value={mg.id}>👔 {mg.name} ({countryFlags[mg.country] || mg.country})</option>
                      ))}
                    </select>
                  )}

                  {newStocker.assignment_type === "inv" && (
                    <select value={newStocker.inv_id || ""} onChange={(e) => setNewStocker({ ...newStocker, inv_id: e.target.value || null })}
                      className="w-full bg-zinc-800 border border-emerald-500/40 rounded-xl py-2 px-3 text-white text-sm" data-testid="generator-inv">
                      <option value="">— Chwazi Investisseur —</option>
                      {(invs || []).filter(v => v.country === newStocker.country).map(v => (
                        <option key={v.id} value={v.id}>💰 {v.name} ({countryFlags[v.country] || v.country})</option>
                      ))}
                    </select>
                  )}

                  {(!newStocker.assignment_type || newStocker.assignment_type === "none") && (
                    <p className="text-zinc-500 text-xs italic">Stocker la pap asiyen ak okenn Manager oswa INV.</p>
                  )}
                </div>
                
                <div className="bg-zinc-900/50 p-4 rounded-xl">
                  <p className="text-zinc-400 text-sm mb-3">Modèles de Vape (optionnel)</p>
                  {vapeModels.map((model, idx) => (
                    <div key={idx} className="grid grid-cols-3 gap-3 mb-3">
                      <input type="text" value={model.name} onChange={(e) => {
                        const updated = [...vapeModels]; updated[idx].name = e.target.value; setVapeModels(updated);
                      }} className="bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-white text-sm" placeholder="Nom modèle" />
                      <input type="number" value={model.quantity || ''} onChange={(e) => {
                        const updated = [...vapeModels]; updated[idx].quantity = parseInt(e.target.value) || 0; setVapeModels(updated);
                      }} className="bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-white text-sm" placeholder="Quantité" />
                      <input type="number" value={model.price || ''} onChange={(e) => {
                        const updated = [...vapeModels]; updated[idx].price = parseFloat(e.target.value) || 0; setVapeModels(updated);
                      }} className="bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-white text-sm" placeholder="Prix $" />
                    </div>
                  ))}
                </div>
                
                <button onClick={handleGenerateStocker} disabled={loading || !newStocker.name}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-zinc-900 font-bold py-4 rounded-xl transition-all flex items-center justify-center gap-2"
                  data-testid="generate-code-button">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Key className="w-5 h-5" /> Générer le Code</>}
                </button>
              </div>
            ) : (
              <div className="text-center">
                <div className="bg-zinc-900/50 rounded-2xl p-6 mb-4">
                  <p className="text-zinc-500 text-sm mb-2">Code pour {generatedCode.name}</p>
                  <p className="text-zinc-400 text-xs uppercase mb-2">Code Principal</p>
                  <div className="flex justify-center gap-2 mb-4">
                    {generatedCode.code.split('').map((d, i) => (
                      <div key={i} className="w-12 h-14 bg-yellow-500 rounded-xl flex items-center justify-center text-2xl font-mono font-bold text-zinc-900">{d}</div>
                    ))}
                  </div>
                  <div className="bg-zinc-800/50 rounded-xl p-3 mb-4">
                    <p className="text-zinc-400 text-sm">🔐 Le stocker créera son propre code secret lors de sa première connexion</p>
                  </div>
                  <p className="text-zinc-400 text-sm">{countryFlags[generatedCode.country]} {generatedCode.country}</p>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => { navigator.clipboard.writeText(`Code Principal: ${generatedCode.code}`); toast.success("Code copié!"); }}
                    className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2">
                    <Copy className="w-5 h-5" /> Copier Code
                  </button>
                  <button onClick={() => { setGeneratedCode(null); setNewStocker({ name: "", country: "haiti" }); setVapeModels([{ name: "Model 1", quantity: 0, price: 0 }, { name: "Model 2", quantity: 0, price: 0 }, { name: "Model 3", quantity: 0, price: 0 }]); }}
                    className="flex-1 bg-violet-600 hover:bg-violet-700 text-white font-bold py-3 rounded-xl">Nouveau</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Managers Tab */}
        {activeTab === "managers" && (
          <div className="glass-panel p-6">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-400" /> Gestion des Managers
            </h3>
            
            {!generatedManagerCode ? (
              <div className="space-y-6">
                {/* Create Manager Form */}
                <div className="bg-zinc-900/50 p-4 rounded-xl">
                  <p className="text-zinc-400 text-sm mb-3">Créer un nouveau Manager (1 par pays)</p>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <input type="text" placeholder="Nom du manager" value={newManager.name}
                      onChange={(e) => setNewManager({...newManager, name: e.target.value})}
                      className="bg-zinc-800 border border-zinc-700 rounded-xl py-3 px-4 text-white" 
                      data-testid="manager-name-input" />
                    <select value={newManager.country} onChange={(e) => setNewManager({...newManager, country: e.target.value})}
                      className="bg-zinc-800 border border-zinc-700 rounded-xl py-3 px-4 text-white"
                      data-testid="manager-country-select">
                      <option value="haiti">🇭🇹 Haïti</option>
                      <option value="usa">🇺🇸 États-Unis</option>
                      <option value="dr">🇩🇴 République Dominicaine</option>
                    </select>
                  </div>
                  <button onClick={handleGenerateManager} disabled={loading || !newManager.name}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2"
                    data-testid="create-manager-button">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Key className="w-5 h-5" /> Générer Code Manager</>}
                  </button>
                </div>

                {/* Existing Managers List */}
                <div>
                  <p className="text-zinc-400 text-sm mb-3">Managers Existants ({managers.length})</p>
                  <div className="space-y-2">
                    {managers.map((manager) => (
                      <div key={manager.id} className="bg-zinc-900/50 p-4 rounded-xl flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <span className="text-2xl">{countryFlags[manager.country]}</span>
                          <div>
                            <p className="text-white font-bold">{manager.name}</p>
                            <p className="text-zinc-500 text-sm">Code: {manager.code}</p>
                            <p className="text-zinc-600 text-xs">{countryNames[manager.country]}</p>
                          </div>
                        </div>
                        <button onClick={() => handleDeleteManager(manager.id)}
                          className="text-red-400 hover:text-red-300 text-sm px-3 py-1 rounded-lg bg-red-500/10 hover:bg-red-500/20">
                          Supprimer
                        </button>
                      </div>
                    ))}
                    {managers.length === 0 && (
                      <p className="text-zinc-500 text-center py-4">Aucun manager créé</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <div className="bg-zinc-900/50 rounded-2xl p-6 mb-4">
                  <p className="text-zinc-500 text-sm mb-2">Code Manager pour {generatedManagerCode.name}</p>
                  <p className="text-blue-400 text-xs uppercase mb-2">{countryNames[generatedManagerCode.country]}</p>
                  <div className="flex justify-center gap-2 mb-4">
                    {generatedManagerCode.code.split('').map((d, i) => (
                      <div key={i} className="w-12 h-14 bg-blue-500 rounded-xl flex items-center justify-center text-2xl font-mono font-bold text-white">{d}</div>
                    ))}
                  </div>
                  <div className="bg-zinc-800/50 rounded-xl p-3">
                    <p className="text-zinc-400 text-sm">Le manager peut se connecter avec ce code et son nom</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => { navigator.clipboard.writeText(`Code Manager: ${generatedManagerCode.code}`); toast.success("Code copié!"); }}
                    className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2">
                    <Copy className="w-5 h-5" /> Copier
                  </button>
                  <button onClick={() => { setGeneratedManagerCode(null); setNewManager({ name: "", country: "haiti" }); }}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl">Nouveau</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Network Marketer Tab */}
        {activeTab === "network" && (
          <div className="glass-panel p-6">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <Network className="w-5 h-5 text-purple-400" /> Network Marketers
            </h3>
            
            {!generatedNMCode ? (
              <div className="space-y-6">
                <div className="bg-zinc-900/50 p-4 rounded-xl">
                  <p className="text-zinc-400 text-sm mb-3">Créer un nouveau Network Marketer</p>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <input type="text" placeholder="Nom" value={newNetworkMarketer.name}
                      onChange={(e) => setNewNetworkMarketer({...newNetworkMarketer, name: e.target.value})}
                      className="bg-zinc-800 border border-zinc-700 rounded-xl py-3 px-4 text-white"
                      data-testid="nm-name-input" />
                    <select value={newNetworkMarketer.country} onChange={(e) => setNewNetworkMarketer({...newNetworkMarketer, country: e.target.value})}
                      className="bg-zinc-800 border border-zinc-700 rounded-xl py-3 px-4 text-white"
                      data-testid="nm-country-select">
                      <option value="haiti">🇭🇹 Haïti</option>
                      <option value="usa">🇺🇸 États-Unis</option>
                      <option value="dr">🇩🇴 République Dominicaine</option>
                    </select>
                  </div>
                  
                  {/* Vape Models for NM */}
                  <div className="bg-zinc-800/50 p-3 rounded-xl mb-4">
                    <p className="text-purple-400 text-sm mb-3">Stock du Network Marketer (Modèles de Vape)</p>
                    {nmVapeModels.map((model, idx) => (
                      <div key={idx} className="grid grid-cols-3 gap-3 mb-2">
                        <input type="text" value={model.name} onChange={(e) => {
                          const updated = [...nmVapeModels]; updated[idx].name = e.target.value; setNmVapeModels(updated);
                        }} className="bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-white text-sm" placeholder="Nom modèle" />
                        <input type="number" value={model.quantity || ''} onChange={(e) => {
                          const updated = [...nmVapeModels]; updated[idx].quantity = parseInt(e.target.value) || 0; setNmVapeModels(updated);
                        }} className="bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-white text-sm" placeholder="Quantité" />
                        <input type="number" value={model.price || ''} onChange={(e) => {
                          const updated = [...nmVapeModels]; updated[idx].price = parseFloat(e.target.value) || 0; setNmVapeModels(updated);
                        }} className="bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-white text-sm" placeholder="Prix $" />
                      </div>
                    ))}
                  </div>
                  
                  <button onClick={handleGenerateNetworkMarketer} disabled={loading || !newNetworkMarketer.name}
                    className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2"
                    data-testid="create-nm-button">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Key className="w-5 h-5" /> Générer Code NM</>}
                  </button>
                </div>

                {/* Existing Network Marketers with full stock info */}
                <div>
                  <p className="text-zinc-400 text-sm mb-3">Network Marketers ({networkMarketers.length})</p>
                  <div className="space-y-4 max-h-[500px] overflow-y-auto">
                    {networkMarketers.map((nm) => (
                      <div key={nm.id} className="bg-zinc-900/50 p-4 rounded-xl" data-testid={`nm-card-${nm.id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-4">
                            <span className="text-2xl">{countryFlags[nm.country]}</span>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-white font-bold text-lg">{nm.name}</p>
                                <span className="bg-purple-500 text-white text-xs px-2 py-0.5 rounded">NM</span>
                              </div>
                              <p className="text-zinc-500 text-sm">Code: {nm.code}</p>
                              <p className="text-purple-400 text-xs">Commission: {nm.commission_rate || 15}% + {nm.stocker_count || 0} x 2.5%</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${nm.alert_status === 'good' ? 'bg-emerald-400' : nm.alert_status === 'warning' ? 'bg-amber-400' : 'bg-red-400'}`} />
                            <button onClick={() => handleDeleteNetworkMarketer(nm.id)}
                              className="text-red-400 hover:text-red-300 text-sm px-3 py-1 rounded-lg bg-red-500/10">
                              Supprimer
                            </button>
                          </div>
                        </div>
                        
                        {/* Stock Info */}
                        <div className="grid grid-cols-4 gap-2 text-center text-sm mb-3">
                          <div className="bg-blue-500/10 border border-blue-500/30 p-2 rounded-lg">
                            <p className="text-blue-400 text-xs">Stock</p>
                            <p className="text-white font-bold text-lg">{nm.initial_stock || 0}</p>
                          </div>
                          <div className="bg-emerald-500/10 border border-emerald-500/30 p-2 rounded-lg">
                            <p className="text-emerald-400 text-xs">Vendus</p>
                            <p className="text-emerald-400 font-bold text-lg">{nm.total_sold || 0}</p>
                          </div>
                          <div className="bg-amber-500/10 border border-amber-500/30 p-2 rounded-lg">
                            <p className="text-amber-400 text-xs">Restant</p>
                            <p className="text-amber-400 font-bold text-lg">{nm.current_stock || 0}</p>
                          </div>
                          <div className="bg-purple-500/10 border border-purple-500/30 p-2 rounded-lg">
                            <p className="text-purple-400 text-xs">Sub-ST</p>
                            <p className="text-purple-400 font-bold text-lg">{nm.stocker_count || 0}/3</p>
                          </div>
                        </div>
                        
                        {/* Bonus Card + Per-Vape Bonus */}
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded-lg flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Gift className="w-5 h-5 text-amber-400" />
                              <div>
                                <p className="text-amber-400 text-xs uppercase">Bonus Carte</p>
                                <p className="text-amber-400 font-bold">${(nm.bonus_card_balance || 0).toFixed(2)}</p>
                              </div>
                            </div>
                            <span className={`text-[10px] px-2 py-0.5 rounded ${nm.bonus_card_withdrawn ? 'bg-zinc-700 text-zinc-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                              {nm.bonus_card_withdrawn ? 'Retiré' : 'Dispo'}
                            </span>
                          </div>
                          <div className="bg-emerald-500/10 border border-emerald-500/30 p-3 rounded-lg flex items-center justify-between" data-testid={`nm-per-vape-bonus-${nm.id}`}>
                            <div className="flex items-center gap-2">
                              <DollarSign className="w-5 h-5 text-emerald-400" />
                              <div>
                                <p className="text-emerald-400 text-xs uppercase">Bonus par Vape</p>
                                <p className="text-emerald-400 font-bold">${(settings?.bonus_per_vape || 0.99).toFixed(2)}</p>
                              </div>
                            </div>
                            <span className="text-emerald-300 text-[10px] font-bold">
                              = ${((nm.total_sold || 0) * (settings?.bonus_per_vape || 0.99)).toFixed(2)}
                            </span>
                          </div>
                        </div>
                        
                        {/* Dates */}
                        <div className="bg-zinc-800/40 rounded-lg p-3 mb-3">
                          <div className="grid grid-cols-2 gap-3 mb-2">
                            <div>
                              <p className="text-zinc-500 text-xs uppercase mb-1">Date Début</p>
                              <p className="text-zinc-300 text-sm">{nm.start_date ? new Date(nm.start_date).toLocaleDateString('fr-FR') : 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-zinc-500 text-xs uppercase mb-1">Date Contrôle</p>
                              <p className={`text-sm font-bold ${(nm.days_remaining || 0) <= 3 ? 'text-red-400' : (nm.days_remaining || 0) <= 7 ? 'text-amber-400' : 'text-emerald-400'}`}>
                                {nm.end_date ? new Date(nm.end_date).toLocaleDateString('fr-FR') : 'N/A'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className={`text-sm ${(nm.days_remaining || 0) <= 3 ? 'text-red-400' : 'text-emerald-400'}`}>
                              <Clock className="w-3 h-3 inline mr-1" />{nm.days_remaining || 0} jour(s)
                            </span>
                            <div className="flex items-center gap-2">
                              {editNmDate.nmId === nm.id ? (
                                <>
                                  <input type="date" value={editNmDate.newDate}
                                    onChange={(e) => setEditNmDate({ ...editNmDate, newDate: e.target.value })}
                                    className="bg-zinc-800 border border-zinc-700 rounded py-1 px-2 text-white text-sm" />
                                  <button onClick={() => { handleUpdateNmStartDate(nm.id, editNmDate.newDate); setEditNmDate({ nmId: null, newDate: "" }); }}
                                    className="bg-emerald-600 text-white px-2 py-1 rounded text-sm">OK</button>
                                  <button onClick={() => setEditNmDate({ nmId: null, newDate: "" })}
                                    className="bg-zinc-700 text-white px-2 py-1 rounded text-sm">X</button>
                                </>
                              ) : (
                                <button onClick={() => setEditNmDate({ nmId: nm.id, newDate: nm.start_date ? nm.start_date.split('T')[0] : '' })}
                                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs">
                                  <Calendar className="w-3 h-3 inline mr-1" />Modifier Date
                                </button>
                              )}
                              <button onClick={() => setViewNmSubs({ id: nm.id, name: nm.name })} data-testid={`view-nm-subs-${nm.id}`}
                                className="bg-violet-700 hover:bg-violet-600 text-white px-3 py-1 rounded text-xs flex items-center gap-1">
                                <Users className="w-3 h-3" /> Sub-Stockers
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Stock Edit */}
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-zinc-400 text-sm">Modifier Stock:</span>
                          <div className="flex items-center gap-2">
                            {editNmStock.nmId === nm.id ? (
                              <>
                                <input type="number" value={editNmStock.newStock}
                                  onChange={(e) => setEditNmStock({ ...editNmStock, newStock: parseInt(e.target.value) || 0 })}
                                  className="w-20 bg-zinc-800 border border-zinc-700 rounded py-1 px-2 text-white text-center text-sm" />
                                <button onClick={() => { handleQuickNmStockUpdate(nm.id, editNmStock.newStock); setEditNmStock({ nmId: null, newStock: 0 }); }}
                                  className="bg-emerald-600 text-white px-2 py-1 rounded text-sm">OK</button>
                                <button onClick={() => setEditNmStock({ nmId: null, newStock: 0 })}
                                  className="bg-zinc-700 text-white px-2 py-1 rounded text-sm">X</button>
                              </>
                            ) : (
                              <button onClick={() => setEditNmStock({ nmId: nm.id, newStock: nm.initial_stock || 0 })}
                                className="bg-zinc-800 hover:bg-zinc-700 text-zinc-300 px-3 py-1 rounded text-xs">Modifier</button>
                            )}
                          </div>
                        </div>
                        
                        {/* Vape Models Edit */}
                        {showNmStockEdit === nm.id ? (
                          <div className="bg-zinc-800/50 p-3 rounded-lg mt-2">
                            <p className="text-purple-400 text-sm mb-2">Modèles de Vape</p>
                            {nmEditModels.map((model, idx) => (
                              <div key={idx} className="grid grid-cols-3 gap-2 mb-2">
                                <input type="text" value={model.name} onChange={(e) => {
                                  const updated = [...nmEditModels]; updated[idx].name = e.target.value; setNmEditModels(updated);
                                }} className="bg-zinc-800 border border-zinc-700 rounded py-1 px-2 text-white text-sm" placeholder="Modèle" />
                                <input type="number" value={model.quantity || ''} onChange={(e) => {
                                  const updated = [...nmEditModels]; updated[idx].quantity = parseInt(e.target.value) || 0; setNmEditModels(updated);
                                }} className="bg-zinc-800 border border-zinc-700 rounded py-1 px-2 text-white text-sm" placeholder="Qté" />
                                <input type="number" value={model.price || ''} onChange={(e) => {
                                  const updated = [...nmEditModels]; updated[idx].price = parseFloat(e.target.value) || 0; setNmEditModels(updated);
                                }} className="bg-zinc-800 border border-zinc-700 rounded py-1 px-2 text-white text-sm" placeholder="Prix $" />
                              </div>
                            ))}
                            <div className="flex gap-2 mt-2">
                              <button onClick={() => handleUpdateNmStock(nm.id)}
                                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-2 rounded text-sm">Sauvegarder</button>
                              <button onClick={() => { setShowNmStockEdit(null); setNmEditModels([]); }}
                                className="flex-1 bg-zinc-700 text-white py-2 rounded text-sm">Annuler</button>
                            </div>
                          </div>
                        ) : (
                          <button onClick={() => {
                            setShowNmStockEdit(nm.id);
                            setNmEditModels(nm.vape_models && nm.vape_models.length > 0 
                              ? [...nm.vape_models] 
                              : [{ name: "Model 1", quantity: 0, price: 0 }, { name: "Model 2", quantity: 0, price: 0 }, { name: "Model 3", quantity: 0, price: 0 }]);
                          }}
                            className="w-full bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 py-2 rounded-lg text-sm mt-2 flex items-center justify-center gap-2">
                            <Settings className="w-4 h-4" /> Gérer Modèles & Prix
                          </button>
                        )}
                        
                        {/* Progress Bar */}
                        <div className="mt-3">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-zinc-500">Progression</span>
                            <span className={`font-bold ${nm.alert_status === 'good' ? 'text-emerald-400' : nm.alert_status === 'warning' ? 'text-amber-400' : 'text-red-400'}`}>
                              {(nm.stock_percentage || 0).toFixed(0)}%
                            </span>
                          </div>
                          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${nm.alert_status === 'good' ? 'bg-emerald-400' : nm.alert_status === 'warning' ? 'bg-amber-400' : 'bg-red-400'}`}
                              style={{ width: `${nm.stock_percentage || 0}%` }} />
                          </div>
                        </div>
                      </div>
                    ))}
                    {networkMarketers.length === 0 && (
                      <p className="text-zinc-500 text-center py-4">Aucun Network Marketer</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <div className="bg-zinc-900/50 rounded-2xl p-6 mb-4">
                  <p className="text-zinc-500 text-sm mb-2">Code pour {generatedNMCode.name}</p>
                  <div className="flex justify-center gap-2 mb-4">
                    {generatedNMCode.code.split('').map((d, i) => (
                      <div key={i} className="w-12 h-14 bg-purple-500 rounded-xl flex items-center justify-center text-2xl font-mono font-bold text-white">{d}</div>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => { navigator.clipboard.writeText(`Code NM: ${generatedNMCode.code}`); toast.success("Copié!"); }}
                    className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2">
                    <Copy className="w-5 h-5" /> Copier
                  </button>
                  <button onClick={() => { setGeneratedNMCode(null); setNewNetworkMarketer({ name: "", country: "haiti" }); setNmVapeModels([{ name: "Model 1", quantity: 0, price: 0 }, { name: "Model 2", quantity: 0, price: 0 }, { name: "Model 3", quantity: 0, price: 0 }]); }}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 rounded-xl">Nouveau</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Commissions Tab */}
        {activeTab === "commissions" && (
          <div className="glass-panel p-6">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-400" /> Commissions par Pays
            </h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-zinc-900/50 p-4 rounded-xl">
                  <p className="text-zinc-400 text-sm mb-2">🇭🇹 Haïti Stocker</p>
                  <div className="flex items-center gap-2">
                    <input type="number" step="0.01" value={commissions.haiti || 11.14}
                      onChange={(e) => setCommissions({...commissions, haiti: parseFloat(e.target.value)})}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded py-2 px-3 text-white text-center" />
                    <span className="text-zinc-400">%</span>
                  </div>
                </div>
                <div className="bg-zinc-900/50 p-4 rounded-xl">
                  <p className="text-zinc-400 text-sm mb-2">🇩🇴 DR Stocker</p>
                  <div className="flex items-center gap-2">
                    <input type="number" step="0.01" value={commissions.dr || 12.5}
                      onChange={(e) => setCommissions({...commissions, dr: parseFloat(e.target.value)})}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded py-2 px-3 text-white text-center" />
                    <span className="text-zinc-400">%</span>
                  </div>
                </div>
                <div className="bg-zinc-900/50 p-4 rounded-xl">
                  <p className="text-zinc-400 text-sm mb-2">🇺🇸 USA Stocker</p>
                  <div className="flex items-center gap-2">
                    <input type="number" step="0.01" value={commissions.usa || 12.5}
                      onChange={(e) => setCommissions({...commissions, usa: parseFloat(e.target.value)})}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded py-2 px-3 text-white text-center" />
                    <span className="text-zinc-400">%</span>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-purple-500/10 border border-purple-500/30 p-4 rounded-xl">
                  <p className="text-purple-400 text-sm mb-2">Network Marketer</p>
                  <div className="flex items-center gap-2">
                    <input type="number" step="0.01" value={commissions.network_marketer || 15}
                      onChange={(e) => setCommissions({...commissions, network_marketer: parseFloat(e.target.value)})}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded py-2 px-3 text-white text-center" />
                    <span className="text-zinc-400">%</span>
                  </div>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-xl">
                  <p className="text-amber-400 text-sm mb-2">Bonus NM par Stocker</p>
                  <div className="flex items-center gap-2">
                    <input type="number" step="0.01" value={commissions.network_marketer_bonus || 2.5}
                      onChange={(e) => setCommissions({...commissions, network_marketer_bonus: parseFloat(e.target.value)})}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded py-2 px-3 text-white text-center" />
                    <span className="text-zinc-400">%</span>
                  </div>
                </div>
              </div>
              
              <button onClick={handleUpdateCommissions}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl">
                Sauvegarder les Commissions
              </button>
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === "notifications" && (
          <div className="glass-panel p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="text-white font-bold flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-400" /> Notifications
              </h3>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={async () => {
                    try {
                      const r = await axios.get(`${API}/ceo/overdue-summary`);
                      setOverdueSummary(r.data);
                      setShowOverdueModal(true);
                    } catch { toast.error("Erè"); }
                  }}
                  data-testid="overdue-reminder-btn"
                  className="bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold px-3 py-2 rounded-lg flex items-center gap-1">
                  ⚠️ Rapèl Overdue
                </button>
                <button
                  onClick={async () => {
                    try {
                      const pre = await axios.post(`${API}/ceo/close-all-cycles`, { dry_run: true });
                      const list = pre.data?.scanned || [];
                      if (list.length === 0) { toast.info("Pa gen sik ki bezwen fèmen."); return; }
                      setShowCloseAllPreview(list);
                    } catch { toast.error("Erè"); }
                  }}
                  data-testid="close-all-cycles-btn"
                  className="bg-red-600 hover:bg-red-500 text-white text-xs font-bold px-3 py-2 rounded-lg flex items-center gap-1">
                  🔒 Ferme Tout Sik
                </button>
              </div>
            </div>
            
            {/* Filter by type */}
            <div className="flex gap-2 mb-4 flex-wrap">
              {[
                {id: "all", label: "Tous", color: "bg-zinc-600"},
                {id: "withdrawal", label: "Retrait", color: "bg-emerald-600"},
                {id: "stock_renewal", label: "Renouvellement", color: "bg-blue-600"},
                {id: "gift_milestone", label: "Cadeaux", color: "bg-yellow-600"},
                {id: "control_date", label: "Contrôle", color: "bg-red-600"},
                {id: "nm_payment", label: "Paiement NM", color: "bg-purple-600"},
              ].map(f => (
                <button key={f.id} onClick={() => setNotifFilter(prev => prev === f.id ? "all" : f.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${(notifFilter || "all") === f.id ? f.color + ' text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}
                  data-testid={`notif-filter-${f.id}`}>
                  {f.label} ({f.id === "all" ? notifications.length : notifications.filter(n => n.type === f.id).length})
                </button>
              ))}
            </div>
            
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {notifications.filter(n => !notifFilter || notifFilter === "all" || n.type === notifFilter).map((notif) => {
                const isWithdrawal = notif.type === 'withdrawal' || notif.type === 'nm_withdrawal';
                const sameW = isWithdrawal && !notif.paid ? notifications.filter(n => (n.type === 'withdrawal' || n.type === 'nm_withdrawal') && !n.paid && (n.stocker_id || n.marketer_id || n.user_id) === (notif.stocker_id || notif.marketer_id || notif.user_id) && Math.abs((n.amount || 0) - (notif.amount || 0)) < 0.01) : [];
                const dupCount = sameW.length;
                const isDupLater = dupCount > 1 && [...sameW].sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)))[0]?.id !== notif.id;
                const isBonusCard = notif.type === 'bonus_card_withdrawal';
                return (
                <div key={notif.id} className={`p-4 rounded-xl ${
                  isWithdrawal && !notif.paid 
                    ? 'bg-emerald-500/10 border border-emerald-500/30' 
                    : notif.type === 'control_date'
                    ? 'bg-red-500/10 border border-red-500/30'
                    : notif.type === 'stock_renewal'
                    ? 'bg-blue-500/10 border border-blue-500/30'
                    : notif.type === 'gift_milestone'
                    ? 'bg-yellow-500/10 border border-yellow-500/30'
                    : notif.type === 'nm_payment'
                    ? 'bg-purple-500/10 border border-purple-500/30'
                    : isBonusCard
                    ? 'bg-amber-500/10 border border-amber-500/30'
                    : notif.read 
                    ? 'bg-zinc-900/30' 
                    : 'bg-amber-500/10 border border-amber-500/30'
                }`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {isWithdrawal && <DollarSign className={`w-4 h-4 ${notif.type === 'nm_withdrawal' ? 'text-purple-400' : 'text-emerald-400'}`} />}
                        {notif.type === 'control_date' && <AlertTriangle className="w-4 h-4 text-red-400" />}
                        {notif.type === 'stock_renewal' && <Package className="w-4 h-4 text-blue-400" />}
                        {notif.type === 'gift_milestone' && <Gift className="w-4 h-4 text-yellow-400" />}
                        {notif.type === 'nm_payment' && <DollarSign className="w-4 h-4 text-purple-400" />}
                        {isBonusCard && <Gift className="w-4 h-4 text-amber-400" />}
                        <span className={`text-xs px-2 py-0.5 rounded font-bold ${
                          notif.type === 'withdrawal' ? 'bg-emerald-500/20 text-emerald-400' :
                          notif.type === 'nm_withdrawal' ? 'bg-purple-500/20 text-purple-400' :
                          notif.type === 'control_date' ? 'bg-red-500/20 text-red-400' :
                          notif.type === 'stock_renewal' ? 'bg-blue-500/20 text-blue-400' :
                          notif.type === 'gift_milestone' ? 'bg-yellow-500/20 text-yellow-400' :
                          notif.type === 'nm_payment' ? 'bg-purple-500/20 text-purple-400' :
                          isBonusCard ? 'bg-amber-500/20 text-amber-400' :
                          'bg-amber-500/20 text-amber-400'
                        }`}>
                          {notif.type === 'withdrawal' ? 'RETRAIT' : 
                           notif.type === 'nm_withdrawal' ? 'RETRAIT NM' :
                           notif.type === 'control_date' ? 'CONTRÔLE' :
                           notif.type === 'stock_renewal' ? 'RENOUVELLEMENT' : 
                           notif.type === 'gift_milestone' ? 'CADEAU' :
                           notif.type === 'nm_payment' ? 'PAIEMENT NM' : 
                           isBonusCard ? 'BONUS CARTE' : 'INFO'}
                        </span>
                      </div>
                      <p className="text-white text-sm">{notif.message}</p>
                      {isWithdrawal && notif.amount && (
                        <div className="mt-2 space-y-1">
                          <p className={`font-bold text-xl ${notif.type === 'nm_withdrawal' ? 'text-purple-400' : 'text-emerald-400'}`}>${notif.amount?.toFixed(2)}</p>
                          <div className="grid grid-cols-3 gap-2 mt-2 bg-zinc-900/60 rounded-lg p-3">
                            <div className="text-center">
                              <p className="text-zinc-500 text-[10px] uppercase">Revenue</p>
                              <p className="text-white font-bold text-sm">${(notif.revenue || notif.cycle_revenue || 0).toFixed(2)}</p>
                            </div>
                            <div className="text-center border-x border-zinc-800">
                              <p className="text-zinc-500 text-[10px] uppercase">Bonus</p>
                              <p className="text-amber-400 font-bold text-sm">${(notif.bonus || notif.cycle_bonus || 0).toFixed(2)}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-zinc-500 text-[10px] uppercase">Comm ({notif.commission_rate || 0}%)</p>
                              <p className="text-blue-400 font-bold text-sm">${(notif.commission_earned || notif.commission || 0).toFixed(2)}</p>
                            </div>
                          </div>
                          {(notif.vapes_sold > 0 || notif.cycle_sold > 0) && (
                            <p className="text-zinc-400 text-xs mt-1">Vapes vendus: <span className="text-white font-bold">{notif.vapes_sold || notif.cycle_sold}</span> {notif.remaining_stock !== undefined && <>| Restant: <span className="text-amber-400 font-bold">{notif.remaining_stock || 0}</span></>}</p>
                          )}
                          {notif.payment_profile?.method && (
                            <p className="text-cyan-300 text-xs mt-2 bg-zinc-900/60 rounded p-2">
                              💳 <span className="font-bold uppercase">{notif.payment_profile.method}</span>: {notif.payment_profile.account_id} {notif.payment_profile.account_name ? `(${notif.payment_profile.account_name})` : ''}
                            </p>
                          )}
                        </div>
                      )}
                      {isBonusCard && notif.amount && (
                        <div className="mt-2 bg-zinc-900/60 rounded-lg p-3">
                          <p className="text-amber-400 font-bold text-lg">💳 ${notif.amount?.toFixed(2)}</p>
                          {notif.phone_number && <p className="text-cyan-300 text-xs">📱 {notif.phone_number}</p>}
                        </div>
                      )}
                      {(() => {
                        // RED DATE: highlight when a control/withdrawal notif is still un-verified.
                        // "Kontwòl POKO Fèt" for previous cycle → date wouj bright.
                        const unverified = (notif.type === 'withdrawal' || notif.type === 'nm_withdrawal' || notif.type === 'control_date')
                          && !notif.kontwol_effectue && !notif.paid;
                        return (
                          <p className={`text-xs mt-1 font-bold ${unverified ? 'text-red-400 animate-pulse' : 'text-zinc-500'}`} data-testid={`notif-date-${notif.id}`}>
                            {unverified && '🔴 '}{new Date(notif.created_at).toLocaleString('fr-FR')}
                            {unverified && <span className="ml-2 text-[10px] text-red-500">(Kontwòl poko fèt)</span>}
                          </p>
                        );
                      })()}
                    </div>
                    <div className="flex flex-col gap-2 items-end">
                      {notif.type === 'nm_withdrawal' && !notif.paid && (
                        <button onClick={async () => {
                          try {
                            const r = await axios.post(`${API}/ceo/notifications/${notif.id}/refresh`);
                            toast.success(`Rafrechi ✔ Revenue: $${r.data.cycle_revenue}`);
                            fetchData();
                          } catch(e) { toast.error(e.response?.data?.detail || "Erè"); }
                        }}
                          className="bg-zinc-700 hover:bg-zinc-600 text-white font-bold px-2 py-1 rounded text-[10px] flex items-center gap-1"
                          data-testid={`refresh-notif-${notif.id}`}>
                          🔄 Rafrechi
                        </button>
                      )}
                      {isWithdrawal && dupCount > 1 && !notif.paid && (
                        <span className="bg-red-600 text-white font-black px-3 py-2 rounded-lg text-xs animate-pulse" data-testid={`dup-withdrawal-${notif.id}`}>
                          ⚠ DOUBLON ×{dupCount} — {isDupLater ? "NE PAS PAYER" : "payer une seule fois"}
                        </span>
                      )}
                      {isWithdrawal && notif.paid && (
                        <span className="bg-zinc-700 text-zinc-200 font-bold px-3 py-2 rounded-lg text-xs" data-testid={`paid-lock-${notif.id}`}>🔒 Payé {notif.paid_at ? new Date(notif.paid_at).toLocaleDateString("fr-FR") : ""}</span>
                      )}
                      {isWithdrawal && !notif.paid && (
                        <>
                          <button disabled={isDupLater} onClick={async (ev) => {
                            window.__payLock = window.__payLock || {};
                            if (window.__payLock[notif.id]) return;
                            if (!window.confirm(`Confirmer le paiement de $${(notif.amount || 0).toFixed(2)} à ${notif.stocker_name || notif.user_name || ""} ? Le bouton sera verrouillé ensuite.`)) return;
                            window.__payLock[notif.id] = true;
                            const btn = ev.currentTarget; btn.disabled = true;
                            try {
                              await axios.post(`${API}/ceo/pay-withdrawal/${notif.id}`);
                              toast.success("Paiement confirmé — bouton verrouillé.");
                            } catch(e) { toast.error(e.response?.data?.detail || "Erreur"); }
                            fetchData();
                          }}
                            className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2"
                            data-testid={`pay-btn-${notif.id}`}>
                            <DollarSign className="w-4 h-4" /> Payer
                          </button>
                          {!notif.kontwol_effectue && (
                            <button onClick={async () => {
                              try {
                                await axios.post(`${API}/ceo/notifications/${notif.id}/kontwol-effectue`);
                                toast.success("Kontwòl efektye — nouvo sik aktive otomatik");
                                fetchData();
                              } catch(e) { toast.error(e.response?.data?.detail || "Erreur"); }
                            }}
                              className="bg-blue-600/80 hover:bg-blue-500 text-white font-bold px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5"
                              data-testid={`kontwol-effectue-btn-${notif.id}`}>
                              <CheckCircle className="w-3.5 h-3.5" /> Kontwòl Efektye
                            </button>
                          )}
                          {notif.kontwol_effectue && (
                            <span className="text-blue-400 text-[10px] font-bold flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Kontwòl OK</span>
                          )}
                        </>
                      )}
                      {isWithdrawal && notif.paid && (
                        <span className="bg-emerald-500/20 text-emerald-400 px-3 py-1.5 rounded-lg text-sm font-bold">Payé</span>
                      )}
                      {isBonusCard && !notif.approved && (
                        <button onClick={async () => {
                          try {
                            await axios.post(`${API}/ceo/bonus-card/${notif.id}/approve`);
                            toast.success("Demand bonus kat apwouve — NM ap wè li en cours");
                            fetchData();
                          } catch(e) { toast.error(e.response?.data?.detail || "Erreur"); }
                        }}
                          className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-3 py-1.5 rounded-lg text-xs flex items-center gap-2"
                          data-testid={`approve-bonus-${notif.id}`}>
                          <CheckCircle className="w-3.5 h-3.5" /> Approuver
                        </button>
                      )}
                      {isBonusCard && notif.approved && !notif.sent && (
                        <button onClick={async () => {
                          try {
                            await axios.post(`${API}/ceo/bonus-card/${notif.id}/send`);
                            toast.success("Kat voye — NM notifye ak dat la");
                            fetchData();
                          } catch(e) { toast.error(e.response?.data?.detail || "Erreur"); }
                        }}
                          className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-3 py-1.5 rounded-lg text-xs flex items-center gap-2"
                          data-testid={`send-bonus-${notif.id}`}>
                          <Package className="w-3.5 h-3.5" /> Envoyer
                        </button>
                      )}
                      {isBonusCard && notif.sent && (
                        <span className="bg-emerald-500/20 text-emerald-400 px-3 py-1.5 rounded-lg text-xs font-bold">Envoyé</span>
                      )}
                      {isBonusCard && notif.approved && !notif.sent && (
                        <span className="text-amber-400 text-[10px] font-bold flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Approuvé</span>
                      )}
                      {notif.type === 'stock_renewal' && !notif.accepted && (
                        <button onClick={async () => {
                          try {
                            await axios.post(`${API}/ceo/accept-renewal/${notif.id}`);
                            toast.success("Renouvellement accepté! Ajoutez le stock maintenant.");
                            // Redirect to Add Stock tab with target pre-filled
                            const targetName = notif.stocker_name || notif.nm_name || "";
                            const targetType = notif.nm_name ? "nm" : "stocker";
                            const targetId = notif.stocker_id || notif.nm_id || "";
                            setAddStockTarget({ type: targetType, id: targetId, name: targetName });
                            setAddStockModels([{ name: "", quantity: 0, price: 0 }]);
                            setActiveTab("add-stock");
                            fetchData();
                          } catch(e) { toast.error("Erreur"); }
                        }}
                          className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2"
                          data-testid={`accept-renewal-btn-${notif.id}`}>
                          <CheckCircle className="w-4 h-4" /> Accepter & Ajouter Stock
                        </button>
                      )}
                      {notif.type === 'stock_renewal' && notif.accepted && (
                        <span className="bg-blue-500/20 text-blue-400 px-3 py-1.5 rounded-lg text-sm font-bold">
                          Accepté
                        </span>
                      )}
                      {notif.type !== 'withdrawal' && notif.type !== 'stock_renewal' && notif.type !== 'gift_milestone' && !notif.read && (
                        <span className="bg-amber-500 text-zinc-900 text-xs px-2 py-1 rounded">Nouveau</span>
                      )}
                      {/* Gift Milestone - CEO chooses gift */}
                      {notif.type === 'gift_milestone' && !notif.gift_assigned && (
                        <div className="space-y-2">
                          <p className="text-yellow-400 text-xs font-bold">Choisir un cadeau:</p>
                          <div className="grid grid-cols-1 gap-1">
                            {(notif.milestone === 100 
                              ? ["iPhone 15", "Cash $500", "Tennis Nike", "Dîner Restaurant", "AirPods Pro"]
                              : ["MacBook Air", "$1000 USD", "Voyage 3 Jours", "PlayStation 5", "Apple Watch"]
                            ).map(gift => (
                              <button key={gift} onClick={async () => {
                                try {
                                  await axios.post(`${API}/ceo/assign-gift/${notif.id}`, { gift_name: gift });
                                  toast.success(`Cadeau "${gift}" assigné à ${notif.stocker_name}!`);
                                  fetchData();
                                } catch(e) { toast.error("Erreur"); }
                              }}
                                className="bg-yellow-500/20 hover:bg-yellow-500/40 text-yellow-400 text-xs px-3 py-1.5 rounded-lg text-left transition-colors"
                                data-testid={`assign-gift-${notif.id}-${gift.replace(/\s/g, '-')}`}>
                                🎁 {gift}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {notif.type === 'gift_milestone' && notif.gift_assigned && (
                        <span className="bg-emerald-500/20 text-emerald-400 px-3 py-1.5 rounded-lg text-sm font-bold">
                          🎁 {notif.gift_name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
              {notifications.length === 0 && (
                <p className="text-zinc-500 text-center py-8">Aucune notification</p>
              )}
            </div>
          </div>
        )}

        {/* Stockers List Tab */}
        {activeTab === "stockers" && (
          <div className="glass-panel p-6">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2"><Users className="w-5 h-5 text-violet-400" /> Mes Stockers ({stockers.length}) + NM ({networkMarketers.length})</h3>
            
            {/* Search Bar & City Filter */}
            <div className="flex gap-3 mb-4">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-3.5" />
                <input type="text" placeholder="Rechercher par code ou nom..." value={stockerSearchQuery}
                  onChange={(e) => setStockerSearchQuery(e.target.value)}
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-xl py-3 pl-10 pr-4 text-white text-sm"
                  data-testid="stocker-search-input" />
              </div>
              <select value={filterCity} onChange={(e) => setFilterCity(e.target.value)}
                className="bg-zinc-900 border border-zinc-800 rounded-xl py-3 px-4 text-white text-sm"
                data-testid="stocker-city-filter">
                <option value="all">Toutes les villes</option>
                <option value="haiti">🇭🇹 Haiti (tous)</option>
                <option value="usa">🇺🇸 USA</option>
                <option value="dr">🇩🇴 DR</option>
                {HAITI_CITIES.map(city => <option key={city} value={`haiti-${city}`}>🇭🇹 {city}</option>)}
              </select>
            </div>
            
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {[
                ...stockers.map(s => ({ ...s, _kind: 'stocker' })),
                ...networkMarketers.map(n => ({ ...n, _kind: 'nm' })),
              ]
                .filter(s => {
                  const q = stockerSearchQuery.toLowerCase();
                  const matchSearch = !q || (s.name || "").toLowerCase().includes(q) || (s.code || "").toLowerCase().includes(q);
                  let matchCity = true;
                  if (filterCity === "haiti") matchCity = s.country === "haiti";
                  else if (filterCity === "usa") matchCity = s.country === "usa";
                  else if (filterCity === "dr") matchCity = s.country === "dr";
                  else if (filterCity.startsWith("haiti-")) matchCity = s.country === "haiti" && s.city === filterCity.replace("haiti-", "");
                  return matchSearch && matchCity;
                })
                .map((stocker) => {
                const isNm = stocker._kind === 'nm';
                return (
                <div key={`${stocker._kind}-${stocker.id}`} className="bg-zinc-900/50 p-4 rounded-xl" data-testid={`ceo-${stocker._kind}-${stocker.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-4">
                      <span className="text-2xl">{countryFlags[stocker.country]}</span>
                      <div>
                        <p className="text-white font-bold text-lg flex items-center gap-2 flex-wrap">
                          {stocker.name}
                          {isNm && <span className="text-[10px] bg-purple-600 text-white px-2 py-0.5 rounded-full font-bold" data-testid="nm-badge">NM</span>}
                          {stocker.activation_status === 'pending' && (
                            <span className="text-[10px] bg-amber-500/25 text-amber-300 border border-amber-500/50 px-2 py-0.5 rounded-full font-black uppercase tracking-widest animate-pulse" data-testid="activation-pending-badge">
                              ⏳ activation pending
                            </span>
                          )}
                          {stocker.parent_marketer_name && (
                            <span className="text-[10px] bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 px-2 py-0.5 rounded-full font-bold">
                              anba {stocker.parent_marketer_name}
                            </span>
                          )}
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const r = await axios.post(`${API}/ceo/recalc-user/${stocker.id}`);
                                const { before, after, diff, commission, commission_rate } = r.data;
                                const changed = Object.values(diff).some(v => v !== 0);
                                if (changed) {
                                  toast.success(
                                    `🔄 ${stocker.name} rekalkile:\n` +
                                    `Vann: ${before.total_sold} → ${after.total_sold}\n` +
                                    `Revni: $${before.total_revenue} → $${after.total_revenue}\n` +
                                    `Stock: ${before.current_stock} → ${after.current_stock}\n` +
                                    `Komisyon (${commission_rate}%): $${commission}`,
                                    { duration: 8000 }
                                  );
                                } else {
                                  toast.success(`✅ ${stocker.name}: chif yo deja korèk`);
                                }
                                fetchData();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Erè rekalkile');
                              }
                            }}
                            data-testid={`recalc-btn-${stocker.id}`}
                            title="Rekalkile chif sik la depi sales_history"
                            className="text-[10px] bg-gradient-to-r from-cyan-600/30 to-fuchsia-600/30 hover:from-cyan-500/50 hover:to-fuchsia-500/50 border border-cyan-500/40 hover:border-cyan-400 text-cyan-300 hover:text-white px-2 py-0.5 rounded-full font-black uppercase tracking-widest transition-all"
                          >
                            🔄 Rekalkile
                          </button>
                          <LotAssignButton user={stocker} onGo={goLinkLot} />
                          <SupportHistoryButton user={stocker} />
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              const sold = (stocker.initial_stock||0) - (stocker.current_stock||0);
                              const leftList = (stocker.vape_models||[]).map(m => ({n: m.name, l: Math.max(0,(parseInt(m.quantity)||0)-(parseInt(m.sold)||0))})).filter(x => x.l > 0).map(x => `   - ${x.n} ×${x.l}`).join("\n");
                              if (!window.confirm(`EFEKTYE KONTWÒL SIK AKTYÈL pou ${stocker.name}?\n\n• Paiement du stocker enregistré (entrée d'argent) pour ${sold} vape(s)\n• Vapes non écoulées restantes de ${stocker.name}${(stocker.stock_batches || []).length ? ` (lot : ${stocker.stock_batches.map(l => l.name).join(", ")})` : ""} :\n${leftList || "   - aucune"}\n\n• Sik aktyèl ap fèmen (${sold} vann ap achiv)\n• ${stocker.current_stock||0} inite ki pa vann ap kontinye nan nouvo sik la\n• Nouvo sik 30 jou kòmanse kounye a\n• Lè w ajoute stock apre, l ap ajoute sou sa ki rete a sèlman (pa sou ansyen total la)\n\nKonfime?`)) return;
                              try {
                                const r = await axios.post(`${API}/ceo/efektye-kontwol-cycle/${stocker.id}`);
                                toast.success(
                                  `✅ Kontwòl efektye pou ${stocker.name}:\n` +
                                  `Vann: ${r.data.cycle_sold} • Revni: $${r.data.cycle_revenue}\n` +
                                  `Nouvo stock baz: ${r.data.new_initial} (unsold rete aktif)\n` +
                                  `Nouvo sik: 30 jou`,
                                  { duration: 10000 }
                                );
                                fetchData();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Erè kontwòl');
                              }
                            }}
                            data-testid={`efektye-kontwol-btn-${stocker.id}`}
                            title="Efektye kontwòl sik aktyèl: fèmen vant yo, kenbe unsold sèlman aktif"
                            className="text-[10px] bg-gradient-to-r from-amber-600/40 to-orange-600/40 hover:from-amber-500/60 hover:to-orange-500/60 border border-amber-500/50 hover:border-amber-400 text-amber-200 hover:text-white px-2 py-0.5 rounded-full font-black uppercase tracking-widest transition-all"
                          >
                            ✅ Efektye Kontwòl
                          </button>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const r = await axios.post(`${API}/ceo/stocker/${stocker.id}/toggle-verified`);
                                toast.success(r.data.verified ? `✓ ${stocker.name} verifye (Blue Check)` : `${stocker.name} pa verifye ankò`);
                                fetchData();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || "Erè");
                              }
                            }}
                            data-testid={`toggle-verified-${stocker.id}`}
                            title={stocker.verified ? "Retire Blue Check" : "Aktive Blue Check"}
                            className={`text-[10px] px-2 py-0.5 rounded-full font-black uppercase tracking-widest border transition-all ${stocker.verified ? "bg-blue-500 text-white border-blue-600 hover:bg-blue-400" : "bg-zinc-200 text-zinc-700 border-zinc-300 hover:bg-blue-100 hover:text-blue-700"}`}
                          >
                            {stocker.verified ? "✓ Verifye" : "☐ Verifye"}
                          </button>
                        </p>
                        <p className="text-zinc-500 text-sm">
                          {stocker.activation_status === 'pending' ? (
                            <span className="text-amber-400">Kòd: <em>poko kreye — peze "Aktive Kòd" nan header a</em></span>
                          ) : (
                            <>Code: {stocker.code}</>
                          )}
                          {stocker.city ? ` | ${stocker.city}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${stocker.alert_status === 'good' ? 'bg-emerald-400' : stocker.alert_status === 'warning' ? 'bg-amber-400' : 'bg-red-400'}`} />
                    </div>
                  </div>
                  
                  {/* Stock & Sales Info - Clear Display */}
                  <div className="grid grid-cols-5 gap-2 text-center text-sm mb-3">
                    <div className="bg-blue-500/10 border border-blue-500/30 p-2 rounded-lg">
                      <p className="text-blue-400 text-xs">Stock</p>
                      <p className="text-white font-bold text-lg">{stocker.initial_stock || 0}</p>
                    </div>
                    <div className="bg-emerald-500/10 border border-emerald-500/30 p-2 rounded-lg">
                      <p className="text-emerald-400 text-xs">Vendus</p>
                      <p className="text-emerald-400 font-bold text-lg">{getCycleSold(stocker)}</p>
                    </div>
                    <div className="bg-amber-500/10 border border-amber-500/30 p-2 rounded-lg">
                      <p className="text-amber-400 text-xs">Restant</p>
                      <p className="text-amber-400 font-bold text-lg">{stocker.current_stock || 0}</p>
                    </div>
                    <div className="bg-pink-500/10 border border-pink-500/30 p-2 rounded-lg">
                      <p className="text-pink-400 text-xs">Prix</p>
                      <p className="text-pink-400 font-bold text-lg">${stocker.vape_models?.[0]?.price || 0}</p>
                    </div>
                    <div className="bg-violet-500/10 border border-violet-500/30 p-2 rounded-lg">
                      <p className="text-violet-400 text-xs">Bonus Sik ({getCycleSold(stocker)} × $0.99)</p>
                      <p className="text-violet-400 font-bold text-lg">${(getCycleSold(stocker) * 0.99).toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Previous-cycle carryover: GREEN when fully sold, apricot when pending */}
                  {((stocker.carryover_from_previous_cycle || 0) > 0 || stocker.previous_cycle_pending) && (
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      {(stocker.carryover_from_previous_cycle || 0) > 0 && (() => {
                        const carryTotal = stocker.carryover_from_previous_cycle || 0;
                        const cycleSold = Math.max(0, (stocker.initial_stock || 0) - (stocker.current_stock || 0));
                        const carryFullySold = cycleSold >= carryTotal;
                        if (carryFullySold) {
                          return (
                            <div className="p-2 rounded-lg border" style={{ backgroundColor: 'rgba(16, 185, 129, 0.15)', borderColor: 'rgba(16, 185, 129, 0.6)' }} data-testid={`ceo-carryover-${stocker.id}`}>
                              <p className="text-xs text-emerald-400">✅ Sik pase fin vann</p>
                              <p className="font-bold text-lg text-emerald-300">✓ {carryTotal} vape</p>
                            </div>
                          );
                        }
                        return (
                          <div className="p-2 rounded-lg border" style={{ backgroundColor: 'rgba(251, 191, 36, 0.15)', borderColor: 'rgba(251, 146, 60, 0.6)' }} data-testid={`ceo-carryover-${stocker.id}`}>
                            <p className="text-xs" style={{ color: '#fb923c' }}>⚠️ Sik pase (poko vann)</p>
                            <p className="font-bold text-lg" style={{ color: '#fdba74' }}>{carryTotal - cycleSold} vape</p>
                          </div>
                        );
                      })()}
                      {stocker.previous_cycle_pending && (
                        <button
                          onClick={() => setKontwolReport({ user: stocker, role: 'stocker' })}
                          className="bg-red-500/15 hover:bg-red-500/25 border border-red-500/50 p-2 rounded-lg text-left transition-colors" data-testid={`ceo-kontwol-pending-${stocker.id}`}>
                          <p className="text-red-400 text-xs font-bold flex items-center justify-between">
                            <span>🔴 Kontwòl POKO Fèt</span>
                            <span className="text-[10px] text-red-300 underline">Klike pou detay →</span>
                          </p>
                          <p className="text-red-300 text-[10px]">Dat sik pase: {stocker.previous_cycle?.cycle_ended_at ? new Date(stocker.previous_cycle.cycle_ended_at).toLocaleDateString('fr-FR') : '—'}</p>
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* Commission row - based on CYCLE revenue × rate (per-model weighted math) */}
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    {(() => {
                      // Prefer server-computed cycle_revenue when available (uses sales_history).
                      // Fallback to weighted per-model math.
                      let revenue = null;
                      if (typeof stocker.cycle_revenue === "number") {
                        revenue = stocker.cycle_revenue;
                      } else {
                        const models = stocker.vape_models || [];
                        revenue = 0;
                        let hasPerModelSold = false;
                        for (const m of models) {
                          const soldQty = parseInt(m.sold || 0);
                          if (soldQty > 0) hasPerModelSold = true;
                          revenue += soldQty * (parseFloat(m.price) || 0);
                        }
                        if (!hasPerModelSold) {
                          const totalQty = models.reduce((a, m) => a + (parseFloat(m.quantity) || 0), 0);
                          const totalValue = models.reduce((a, m) => a + (parseFloat(m.quantity) || 0) * (parseFloat(m.price) || 0), 0);
                          const avgPrice = totalQty > 0 ? totalValue / totalQty : (parseFloat(models[0]?.price) || 0);
                          revenue = getCycleSold(stocker) * avgPrice;
                        }
                      }
                      const rate = stocker.commission_rate || 12.5;
                      const commission = typeof stocker.cycle_commission === "number" ? stocker.cycle_commission : (revenue * rate / 100);
                      return (
                        <>
                          <div className="bg-emerald-500/10 border border-emerald-500/30 p-2 rounded-lg">
                            <p className="text-emerald-400 text-xs">Revenus Sik</p>
                            <p className="text-emerald-400 font-bold text-sm" data-testid={`ceo-cycle-revenue-${stocker.id}`}>${revenue.toFixed(2)}</p>
                          </div>
                          <div className="bg-yellow-500/10 border border-yellow-500/30 p-2 rounded-lg">
                            <p className="text-yellow-400 text-xs">Commission Sik ({rate}%)</p>
                            <p className="text-yellow-400 font-bold text-sm">${commission.toFixed(2)}</p>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  
                  <div className="flex items-center justify-between py-2 border-t border-zinc-800">
                    <div className="flex items-center gap-2">
                      <span className="text-zinc-500 text-sm">Code Secret:</span>
                      <span className={`text-sm ${stocker.has_secret_code ? 'text-emerald-400' : 'text-zinc-600'}`}>
                        {stocker.has_secret_code ? '●●●● (actif)' : 'Non créé'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {stocker.has_secret_code && !isNm && (
                        <button onClick={() => handleResetSecretCode(stocker.id, stocker.name)}
                          className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 px-2 py-1 rounded text-xs"
                          data-testid={`reset-secret-${stocker.id}`}>
                          Réinitialiser
                        </button>
                      )}
                      <span className="text-xs text-zinc-600">
                        {stocker.has_secret_code ? '' : 'En attente'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Stock & Price Edit Section */}
                  <div className="pt-3 border-t border-zinc-800">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-zinc-400 text-sm">Modifier Modèles & Prix:</span>
                      {editStock.stockerId !== stocker.id && (
                        <button onClick={() => {
                          const existing = stocker.vape_models && stocker.vape_models.length > 0 
                            ? stocker.vape_models.map(m => ({...m}))
                            : [{ name: "Model 1", quantity: 0, price: 0 }];
                          while (existing.length < 3) existing.push({ name: "", quantity: 0, price: 0 });
                          setEditStock({ stockerId: stocker.id, models: existing.slice(0, 3) });
                        }}
                          className="bg-zinc-800 hover:bg-zinc-700 text-zinc-300 px-3 py-1 rounded text-xs"
                          data-testid={`edit-stock-btn-${stocker.id}`}>Modifier</button>
                      )}
                    </div>
                    
                    {editStock.stockerId === stocker.id && (
                      <div className="bg-zinc-800/50 p-3 rounded-xl mb-3">
                        <div className="grid grid-cols-3 gap-2 mb-2">
                          <span className="text-zinc-500 text-xs">Nom Modèle</span>
                          <span className="text-zinc-500 text-xs">Quantité</span>
                          <span className="text-zinc-500 text-xs">Prix ($)</span>
                        </div>
                        {editStock.models.map((model, idx) => (
                          <div key={idx} className="grid grid-cols-3 gap-2 mb-2">
                            <input type="text" value={model.name} onChange={(e) => {
                              const updated = [...editStock.models]; updated[idx] = {...updated[idx], name: e.target.value}; setEditStock({...editStock, models: updated});
                            }} className="bg-zinc-900 border border-zinc-700 rounded py-1.5 px-2 text-white text-sm" placeholder={`Model ${idx+1}`}
                              data-testid={`edit-model-name-${stocker.id}-${idx}`} />
                            <input type="number" value={model.quantity || ''} onChange={(e) => {
                              const updated = [...editStock.models]; updated[idx] = {...updated[idx], quantity: parseInt(e.target.value) || 0}; setEditStock({...editStock, models: updated});
                            }} className="bg-zinc-900 border border-zinc-700 rounded py-1.5 px-2 text-white text-sm" placeholder="Qté"
                              data-testid={`edit-model-qty-${stocker.id}-${idx}`} />
                            <input type="number" step="0.01" value={model.price || ''} onChange={(e) => {
                              const updated = [...editStock.models]; updated[idx] = {...updated[idx], price: parseFloat(e.target.value) || 0}; setEditStock({...editStock, models: updated});
                            }} className="bg-zinc-900 border border-zinc-700 rounded py-1.5 px-2 text-white text-sm" placeholder="$"
                              data-testid={`edit-model-price-${stocker.id}-${idx}`} />
                          </div>
                        ))}
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => handleQuickStockUpdate(stocker.id, isNm)}
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2 rounded-lg text-sm font-bold">Sauvegarder</button>
                          <button onClick={() => setEditStock({ stockerId: null, models: [] })}
                            className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-2 rounded-lg text-sm">Annuler</button>
                        </div>
                      </div>
                    )}

                    {/* Show current models */}
                    {editStock.stockerId !== stocker.id && stocker.vape_models && stocker.vape_models.length > 0 && (
                      <div className="space-y-1 mb-2">
                        {stocker.vape_models.filter(m => (parseInt(m.quantity) || 0) > 0).map((m, idx) => {
                          const modelSold = Math.max(0, parseInt(m.sold || 0));
                          const carryQty = Math.max(0, parseInt(m.carryover_qty || 0));
                          const carryUnsold = Math.max(0, carryQty - modelSold);
                          const wasCarryover = m.is_carryover_model || carryQty > 0;
                          const carryoverFullySold = wasCarryover && carryUnsold === 0 && modelSold > 0;
                          const hasSales = modelSold > 0 || carryoverFullySold;
                          const stillCarryPending = wasCarryover && carryUnsold > 0;
                          // Priority: pending-carry (amber) → sold-carry (bright green ✓) → normal sold (green) → new stock (gray)
                          const bg = stillCarryPending
                            ? "bg-amber-500/15 border border-amber-500/40"
                            : carryoverFullySold
                              ? "bg-emerald-500/20 border border-emerald-500/50"
                              : "bg-zinc-800/30";
                          const dot = stillCarryPending
                            ? "bg-amber-400 animate-pulse"
                            : hasSales
                              ? "bg-emerald-400"
                              : "bg-zinc-600";
                          const nameCls = stillCarryPending
                            ? "text-amber-300"
                            : carryoverFullySold
                              ? "text-emerald-300"
                              : "text-zinc-300";
                          return (
                          <div key={idx} className={`flex items-center justify-between text-xs px-3 py-1.5 rounded-lg ${bg}`} data-testid={`ceo-model-row-${stocker.id}-${idx}`}>
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${dot}`}></span>
                              <span className={`font-medium ${nameCls}`}>
                                {m.name}
                                {stillCarryPending && <span className="ml-1 text-[10px] text-amber-400">• sik pase poko vann ({carryUnsold})</span>}
                                {carryoverFullySold && <span className="ml-1 text-[10px] text-emerald-400">✓ Carryover vann ({modelSold})</span>}
                              </span>
                            </div>
                            <div className="flex gap-3">
                              <span className={stillCarryPending ? "text-amber-300" : "text-blue-400"}>x{m.quantity}</span>
                              <span className="text-pink-400 font-bold">${m.price}</span>
                            </div>
                          </div>
                        );})}
                      </div>
                    )}
                    
                    {/* Dates Section */}
                    <div className="bg-zinc-800/40 rounded-lg p-3 mt-2 mb-2">
                      <div className="grid grid-cols-2 gap-3 mb-2">
                        <div>
                          <p className="text-zinc-500 text-xs uppercase mb-1">Date Début</p>
                          <p className="text-zinc-300 text-sm font-medium">{stocker.start_date ? new Date(stocker.start_date).toLocaleDateString('fr-FR') : 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-zinc-500 text-xs uppercase mb-1">Date Contrôle</p>
                          <p className={`text-sm font-bold ${stocker.days_remaining <= 3 ? 'text-red-400' : stocker.days_remaining <= 7 ? 'text-amber-400' : 'text-emerald-400'}`}>
                            {stocker.end_date ? new Date(stocker.end_date).toLocaleDateString('fr-FR') : 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-zinc-500" />
                          <span className={`text-sm font-bold ${stocker.days_remaining <= 3 ? 'text-red-400' : stocker.days_remaining <= 7 ? 'text-amber-400' : 'text-emerald-400'}`}>
                            {stocker.days_remaining === 0 ? "Contrôle aujourd'hui!" : `${stocker.days_remaining} jour(s) restant(s)`}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {editDate.stockerId === stocker.id ? (
                            <>
                              <input type="date" value={editDate.newDate}
                                onChange={(e) => setEditDate({ ...editDate, newDate: e.target.value })}
                                className="bg-zinc-800 border border-zinc-700 rounded py-1 px-2 text-white text-sm"
                                data-testid={`edit-date-input-${stocker.id}`} />
                              <button onClick={() => { handleUpdateStartDate(stocker.id, editDate.newDate, isNm); setEditDate({ stockerId: null, newDate: "" }); }}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded text-sm" data-testid={`save-date-${stocker.id}`}>OK</button>
                              <button onClick={() => setEditDate({ stockerId: null, newDate: "" })}
                                className="bg-zinc-700 hover:bg-zinc-600 text-white px-2 py-1 rounded text-sm">X</button>
                            </>
                          ) : (
                            <button onClick={() => setEditDate({ stockerId: stocker.id, newDate: stocker.start_date ? stocker.start_date.split('T')[0] : new Date().toISOString().split('T')[0] })}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs"
                              data-testid={`edit-date-btn-${stocker.id}`}>
                              <Calendar className="w-3 h-3 inline mr-1" />Modifier Date
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Progress Bar - fills up as sales increase */}
                    <div className="mt-2">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-zinc-500">Progression des ventes</span>
                        <span className={`font-bold ${stocker.alert_status === 'good' ? 'text-emerald-400' : stocker.alert_status === 'warning' ? 'text-amber-400' : 'text-red-400'}`}>
                          {(stocker.stock_percentage || 0).toFixed(0)}%
                        </span>
                      </div>
                      <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all duration-500 ${stocker.alert_status === 'good' ? 'bg-emerald-400' : stocker.alert_status === 'warning' ? 'bg-amber-400' : 'bg-red-400'}`}
                          style={{ width: `${stocker.stock_percentage || 0}%` }} />
                      </div>
                    </div>
                    
                    <div className="flex justify-between mt-3 text-xs text-zinc-500">
                      <span>Revenus: ${(() => {
                        if (typeof stocker.cycle_revenue === "number") return stocker.cycle_revenue.toFixed(0);
                        const models = stocker.vape_models || [];
                        let r = 0, has = false;
                        for (const m of models) { const s = parseInt(m.sold || 0); if (s > 0) has = true; r += s * (parseFloat(m.price) || 0); }
                        if (!has) {
                          const tq = models.reduce((a, m) => a + (parseFloat(m.quantity) || 0), 0);
                          const tv = models.reduce((a, m) => a + (parseFloat(m.quantity) || 0) * (parseFloat(m.price) || 0), 0);
                          r = getCycleSold(stocker) * (tq > 0 ? tv / tq : (parseFloat(models[0]?.price) || 0));
                        }
                        return r.toFixed(0);
                      })()}</span>
                      <div className="flex gap-3">
                        <button onClick={async () => {
                          try {
                            if (stocker.suspended) {
                              const url = isNm ? `${API}/ceo/network-marketers/${stocker.id}` : `${API}/ceo/unsuspend/stocker/${stocker.id}`;
                              if (isNm) await axios.put(url, { suspended: false }); else await axios.post(url);
                              toast.success(`${stocker.name} réactivé!`);
                            } else {
                              const url = isNm ? `${API}/ceo/network-marketers/${stocker.id}` : `${API}/ceo/suspend/stocker/${stocker.id}`;
                              if (isNm) await axios.put(url, { suspended: true }); else await axios.post(url);
                              toast.success(`${stocker.name} suspendu!`);
                            }
                            fetchData();
                          } catch(e) { toast.error("Erreur"); }
                        }}
                          className={`flex items-center gap-1 ${stocker.suspended ? 'text-emerald-400 hover:text-emerald-300' : 'text-amber-400 hover:text-amber-300'}`}
                          data-testid={`suspend-${stocker._kind}-${stocker.id}`}>
                          {stocker.suspended ? <><ShieldCheck className="w-3 h-3" /> Réactiver</> : <><Ban className="w-3 h-3" /> Suspendre</>}
                        </button>
                        <button onClick={async () => {
                          if (!window.confirm(`Supprimer ${stocker.name}?`)) return;
                          try {
                            const url = isNm ? `${API}/network-marketers/${stocker.id}` : `${API}/stockers/${stocker.id}`;
                            await axios.delete(url);
                            toast.success(`${stocker.name} supprimé`);
                            fetchData();
                          } catch (e) { toast.error("Erreur"); }
                        }}
                          className="text-red-400 hover:text-red-300">Supprimer</button>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ========== ADD STOCK TAB ========== */}
        {activeTab === "add-stock" && (
          <div className="glass-panel p-6" data-testid="add-stock-tab">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <Plus className="w-5 h-5 text-emerald-400" /> Ajouter Nouveau Stock
            </h3>

            {/* Target Selection */}
            {!addStockTarget ? (
              <div className="space-y-4">
                {/* Search Bar */}
                <div className="relative">
                  <Search className="w-5 h-5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input type="text" value={addStockSearch} onChange={(e) => setAddStockSearch(e.target.value)}
                    placeholder="Rechercher par nom ou pays..."
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-xl py-3 pl-10 pr-4 text-white focus:outline-none focus:border-emerald-500"
                    data-testid="add-stock-search" />
                </div>
                
                <div className="mb-4">
                  <p className="text-zinc-500 text-xs mb-2 uppercase tracking-wider">Stockers</p>
                  <div className="grid grid-cols-2 gap-2">
                    {stockers.filter(s => !addStockSearch || s.name.toLowerCase().includes(addStockSearch.toLowerCase()) || (s.country||'').toLowerCase().includes(addStockSearch.toLowerCase())).map(s => (
                      <button key={s.id} onClick={() => { setAddStockTarget({type: "stocker", id: s.id, name: s.name}); setAddStockModels([{name: "", quantity: 0, price: 0}]); }}
                        className={`p-3 rounded-xl text-left transition-all border ${s.awaiting_new_stock ? 'border-blue-500/50 bg-blue-500/10' : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-600'}`}
                        data-testid={`add-stock-select-${s.id}`}>
                        <p className="text-white font-bold text-sm">{s.name}</p>
                        <p className="text-zinc-500 text-xs">Stock: {s.current_stock || 0} | {s.country}</p>
                        {s.awaiting_new_stock && <span className="text-blue-400 text-xs">En attente de stock</span>}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-zinc-500 text-xs mb-2 uppercase tracking-wider">Network Marketers</p>
                  <div className="grid grid-cols-2 gap-2">
                    {networkMarketers.filter(nm => !addStockSearch || nm.name.toLowerCase().includes(addStockSearch.toLowerCase()) || (nm.country||'').toLowerCase().includes(addStockSearch.toLowerCase())).map(nm => (
                      <button key={nm.id} onClick={() => { setAddStockTarget({type: "nm", id: nm.id, name: nm.name}); setAddStockModels([{name: "", quantity: 0, price: 0}]); }}
                        className="p-3 rounded-xl text-left border border-zinc-800 bg-zinc-900/50 hover:border-zinc-600 transition-all"
                        data-testid={`add-stock-select-nm-${nm.id}`}>
                        <p className="text-white font-bold text-sm">{nm.name}</p>
                        <p className="text-zinc-500 text-xs">Stock: {nm.current_stock || 0} | {nm.country}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              /* Add Stock Form */
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${addStockTarget.type === 'stocker' ? 'bg-emerald-500/20' : 'bg-purple-500/20'}`}>
                      <User className={`w-5 h-5 ${addStockTarget.type === 'stocker' ? 'text-emerald-400' : 'text-purple-400'}`} />
                    </div>
                    <div>
                      <p className="text-white font-bold">{addStockTarget.name}</p>
                      <p className="text-zinc-500 text-xs">{addStockTarget.type === 'stocker' ? 'Stocker' : 'Network Marketer'}</p>
                    </div>
                  </div>
                  <button onClick={() => setAddStockTarget(null)} className="text-zinc-500 hover:text-white text-sm">Changer</button>
                </div>

                <div className="space-y-3 mb-4">
                  {/* Nom du Lot selector */}
                  <div className="bg-zinc-900/60 p-4 rounded-xl border border-blue-500/30">
                    <label className="text-blue-400 text-xs font-bold uppercase">Nom du Lot (Stock Batch)</label>
                    <select value={addStockBatch} onChange={(e) => setAddStockBatch(e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-white text-sm mt-1"
                      data-testid="add-stock-batch-select">
                      <option value="">-- Aucun lot --</option>
                      {financeData && financeData.stock_batches && financeData.stock_batches.map(b => (
                        <option key={b.id} value={b.name}>{b.name} ({b.country?.toUpperCase()}) — {b.total_units} unités</option>
                      ))}
                    </select>
                  </div>

                  {addStockModels.map((model, idx) => (
                    <div key={idx} className="bg-zinc-900/60 p-4 rounded-xl border border-zinc-800">
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="text-zinc-500 text-xs">Modèle</label>
                          <input type="text" value={model.name}
                            onChange={(e) => { const m = [...addStockModels]; m[idx].name = e.target.value; setAddStockModels(m); }}
                            placeholder="Ex: Vape Pro"
                            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-white text-sm"
                            data-testid={`add-stock-model-${idx}`} />
                        </div>
                        <div>
                          <label className="text-zinc-500 text-xs">Quantité</label>
                          <input type="number" value={model.quantity || ""}
                            onChange={(e) => { const m = [...addStockModels]; m[idx].quantity = parseInt(e.target.value) || 0; setAddStockModels(m); }}
                            placeholder="10"
                            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-white text-sm"
                            data-testid={`add-stock-qty-${idx}`} />
                        </div>
                        <div>
                          <label className="text-zinc-500 text-xs">Prix ($)</label>
                          <input type="number" value={model.price || ""}
                            onChange={(e) => { const m = [...addStockModels]; m[idx].price = parseFloat(e.target.value) || 0; setAddStockModels(m); }}
                            placeholder="15"
                            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-white text-sm"
                            data-testid={`add-stock-price-${idx}`} />
                        </div>
                      </div>
                      {addStockModels.length > 1 && (
                        <button onClick={() => setAddStockModels(addStockModels.filter((_, i) => i !== idx))}
                          className="text-red-400 text-xs mt-2 hover:text-red-300">Supprimer</button>
                      )}
                    </div>
                  ))}
                </div>

                <button onClick={() => setAddStockModels([...addStockModels, { name: "", quantity: 0, price: 0 }])}
                  className="w-full border border-dashed border-zinc-700 text-zinc-400 hover:text-white py-2 rounded-xl text-sm mb-4">
                  + Ajouter un autre modèle
                </button>

                {/* Summary */}
                <div className="bg-zinc-900/80 rounded-xl p-4 mb-4 border border-zinc-800">
                  <p className="text-zinc-400 text-sm">Total à ajouter: <span className="text-white font-bold">{addStockModels.reduce((a, m) => a + (m.quantity || 0), 0)} vapes</span></p>
                  <p className="text-zinc-500 text-xs">Ce stock sera ajouté SUR le stock existant</p>
                </div>

                <button onClick={handleAddStock} disabled={addStockLoading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2"
                  data-testid="add-stock-submit-btn">
                  {addStockLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
                  Ajouter le Stock
                </button>
              </div>
            )}

            {/* Custom Buttons from Jenny */}
            {customButtons.length > 0 && (
              <div className="mt-6 border-t border-zinc-800 pt-4">
                <p className="text-zinc-500 text-xs mb-3 uppercase tracking-wider">Boutons Rapides (créés par Jenny)</p>
                <div className="grid grid-cols-2 gap-2">
                  {customButtons.map(btn => {
                    const colorMap = {
                      emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
                      violet: 'bg-violet-500/10 border-violet-500/30 text-violet-400',
                      amber: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
                      red: 'bg-red-500/10 border-red-500/30 text-red-400',
                      blue: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
                    };
                    const btnColorMap = {
                      emerald: 'bg-emerald-600 hover:bg-emerald-700',
                      violet: 'bg-violet-600 hover:bg-violet-700',
                      amber: 'bg-amber-600 hover:bg-amber-700',
                      red: 'bg-red-600 hover:bg-red-700',
                      blue: 'bg-blue-600 hover:bg-blue-700',
                    };
                    const c = btn.color || 'violet';
                    return (
                    <div key={btn.id} className={`border p-3 rounded-xl ${colorMap[c] || colorMap.violet}`} data-testid={`custom-btn-${btn.id}`}>
                      <p className="text-white font-bold text-sm">{btn.label}</p>
                      {btn.target_name && <p className={`text-xs ${(colorMap[c] || '').split(' ').pop()}`}>Cible: {btn.target_name}</p>}
                      {btn.action_type && <p className="text-zinc-500 text-[10px]">Action: {btn.action_type}</p>}
                      <div className="flex gap-2 mt-2">
                        <button onClick={async () => {
                          if (btn.action_type === "add_stock" && btn.target_name) {
                            const target = stockers.find(s => s.name.toLowerCase().includes(btn.target_name.toLowerCase()));
                            const nmTarget = networkMarketers.find(n => n.name.toLowerCase().includes(btn.target_name.toLowerCase()));
                            if (target) { setAddStockTarget({type: "stocker", id: target.id, name: target.name}); }
                            else if (nmTarget) { setAddStockTarget({type: "nm", id: nmTarget.id, name: nmTarget.name}); }
                            setAddStockModels(btn.models?.length > 0 ? btn.models : [{name: "", quantity: 0, price: 0}]);
                            setActiveTab("add-stock");
                          } else if (btn.action_type === "send_message" && btn.target_name) {
                            try {
                              await axios.post(`${API}/ceo/jenny`, { message: `Envoie ce message à ${btn.target_name}: "${btn.custom_action || 'Notification du CEO'}"` });
                              alert(`Message envoyé à ${btn.target_name}!`);
                              fetchData();
                            } catch(e) { alert("Erreur envoi message"); }
                          } else if (btn.action_type === "send_broadcast") {
                            try {
                              await axios.post(`${API}/ceo/jenny`, { message: `Envoie un message broadcast à tous: "${btn.custom_action || 'Notification générale'}"` });
                              alert("Broadcast envoyé!");
                              fetchData();
                            } catch(e) { alert("Erreur broadcast"); }
                          } else if (btn.action_type === "update_stock" && btn.target_name) {
                            try {
                              await axios.post(`${API}/ceo/jenny`, { message: `Met à jour le stock de ${btn.target_name} avec: ${JSON.stringify(btn.models || [])}` });
                              alert(`Stock de ${btn.target_name} mis à jour!`);
                              fetchData();
                            } catch(e) { alert("Erreur mise à jour"); }
                          } else if (btn.custom_action) {
                            try {
                              await axios.post(`${API}/ceo/jenny`, { message: btn.custom_action });
                              alert("Action exécutée!");
                              fetchData();
                            } catch(e) { alert("Erreur exécution"); }
                          }
                        }}
                          className={`flex-1 ${btnColorMap[c] || btnColorMap.violet} text-white text-xs py-1.5 rounded-lg font-bold`}
                          data-testid={`exec-btn-${btn.id}`}>
                          Exécuter
                        </button>
                        <button onClick={async () => {
                          await axios.delete(`${API}/ceo/custom-buttons/${btn.id}`);
                          fetchData();
                        }}
                          className="text-red-400 hover:text-red-300 text-xs px-2" data-testid={`del-btn-${btn.id}`}>X</button>
                      </div>
                    </div>
                  );})}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ========== CITIES TAB ========== */}
        {activeTab === "cities" && (
          <div className="glass-panel p-6" data-testid="cities-tab">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-400" /> Gestion des Villes
            </h3>
            <div className="flex gap-3 mb-4">
              <select value={newCityCountry} onChange={(e) => setNewCityCountry(e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded-xl py-3 px-4 text-white">
                <option value="haiti">Haiti</option>
                <option value="dr">Rép. Dominicaine</option>
                <option value="usa">USA</option>
              </select>
              <input type="text" value={newCityName} onChange={(e) => setNewCityName(e.target.value)}
                placeholder="Nouvelle ville..." className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl py-3 px-4 text-white"
                data-testid="new-city-input" />
              <button onClick={async () => {
                if (!newCityName.trim()) return;
                await axios.post(`${API}/ceo/add-city`, { country: newCityCountry, city: newCityName.trim() });
                toast.success(`Ville "${newCityName}" ajoutée!`);
                setNewCityName("");
                fetchData();
              }} className="bg-blue-600 hover:bg-blue-700 text-white px-6 rounded-xl font-bold" data-testid="add-city-btn">
                + Ajouter
              </button>
            </div>
            {["haiti", "dr", "usa"].map(c => (
              <div key={c} className="mb-4">
                <p className="text-zinc-400 text-sm font-bold mb-2">{c === "haiti" ? "🇭🇹 Haiti" : c === "dr" ? "🇩🇴 Rép. Dominicaine" : "🇺🇸 USA"}</p>
                <div className="flex flex-wrap gap-2">
                  {(citiesList[c] || []).map(city => (
                    <span key={city} className="bg-zinc-800 border border-zinc-700 text-white px-3 py-1.5 rounded-lg text-sm">{city}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ========== PERFORMANCE TAB ========== */}
        {activeTab === "performance" && (
          <div className="glass-panel p-6" data-testid="performance-tab">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-400" /> Performance
            </h3>

            {/* Search Bar */}
            <div className="relative mb-4">
              <Search className="w-5 h-5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input type="text" value={perfSearch} onChange={(e) => setPerfSearch(e.target.value)}
                placeholder="Rechercher par nom ou pays..."
                className="w-full bg-zinc-900 border border-zinc-700 rounded-xl py-3 pl-10 pr-4 text-white focus:outline-none focus:border-emerald-500"
                data-testid="perf-search" />
            </div>

            {/* Filter */}
            <div className="flex gap-2 mb-4">
              {["all", "stocker", "nm"].map(f => (
                <button key={f} onClick={() => setPerfFilter(f)}
                  className={`px-4 py-2 rounded-lg text-sm ${perfFilter === f ? 'bg-emerald-600 text-white' : 'bg-zinc-800 text-zinc-400'}`}>
                  {f === "all" ? "Tous" : f === "stocker" ? "Stockers" : "NM"}
                </button>
              ))}
            </div>

            {/* Performance Table */}
            <div className="space-y-3">
              {performanceData
                .filter(p => perfFilter === "all" || p.type === perfFilter)
                .filter(p => !perfSearch || p.name.toLowerCase().includes(perfSearch.toLowerCase()) || (p.country||'').toLowerCase().includes(perfSearch.toLowerCase()))
                .map(p => {
                // Calculate score /10
                let score = 0;
                // Sell percentage: max 4 points
                score += Math.min(4, (p.sell_percentage / 100) * 4);
                // Speed: max 3 points (1 vape/day = 3 points)
                score += Math.min(3, p.speed * 3);
                // No late: 2 points if no late, -1 per late
                score += Math.max(0, 2 - p.late_count);
                // Active: 1 point if active > 7 days
                score += p.days_active >= 7 ? 1 : p.days_active / 7;
                // Cap and penalties
                if (p.banned) score = Math.min(score, 1);
                if (p.overdue) score = Math.max(0, score - 1.5);
                score = Math.min(10, Math.max(0, score));
                const scoreFixed = score.toFixed(1);
                const scoreColor = score >= 7 ? 'text-emerald-400' : score >= 4 ? 'text-amber-400' : 'text-red-400';
                const scoreBg = score >= 7 ? 'bg-emerald-500/20 border-emerald-500/30' : score >= 4 ? 'bg-amber-500/20 border-amber-500/30' : 'bg-red-500/20 border-red-500/30';

                return (
                <div key={p.id} className={`p-4 rounded-xl border ${
                  p.banned ? 'bg-red-500/10 border-red-500/50' : 
                  p.overdue ? 'bg-amber-500/10 border-amber-500/50' : 
                  'bg-zinc-900/50 border-zinc-800'
                }`} data-testid={`perf-card-${p.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      {/* Score Badge */}
                      <div className={`w-14 h-14 rounded-xl border flex flex-col items-center justify-center ${scoreBg}`}>
                        <p className={`text-xl font-black ${scoreColor}`}>{scoreFixed}</p>
                        <p className="text-zinc-500 text-[10px]">/10</p>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-white font-bold">{p.name}</p>
                          <span className={`text-xs px-2 py-0.5 rounded ${p.type === 'stocker' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-purple-500/20 text-purple-400'}`}>
                            {p.type === 'stocker' ? 'Stocker' : 'NM'}
                          </span>
                          {p.banned && <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded font-bold animate-pulse">BANNI - Contacter CEO</span>}
                          {p.suspended && !p.banned && <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded font-bold">SUSPENDU</span>}
                          {p.past_due_risk && !p.suspended && <span className="bg-red-500/80 text-white text-xs px-2 py-0.5 rounded font-bold animate-pulse">PAST DUE - RISK ({p.overdue_days}j)</span>}
                          {p.past_due && !p.past_due_risk && !p.suspended && <span className="bg-amber-500 text-zinc-900 text-xs px-2 py-0.5 rounded font-bold">PAST DUE ({p.overdue_days}j)</span>}
                          {p.overdue && !p.banned && !p.suspended && <span className="bg-amber-500 text-zinc-900 text-xs px-2 py-0.5 rounded font-bold">EN RETARD ({p.overdue_days}j)</span>}
                        </div>
                        <p className="text-zinc-500 text-xs">{p.country} {p.city ? `- ${p.city}` : ''} | {p.days_active} jours actif</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-black ${p.sell_percentage >= 80 ? 'text-emerald-400' : p.sell_percentage >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                        {p.sell_percentage}%
                      </p>
                      <p className="text-zinc-500 text-xs">vendu</p>
                      {/* Reliability Score */}
                      <div className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                        (p.reliability_score || 0) >= 70 ? 'bg-emerald-500/20 text-emerald-400' : 
                        (p.reliability_score || 0) >= 40 ? 'bg-amber-500/20 text-amber-400' : 
                        'bg-red-500/20 text-red-400'
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${
                          (p.reliability_score || 0) >= 70 ? 'bg-emerald-400' : 
                          (p.reliability_score || 0) >= 40 ? 'bg-amber-400' : 'bg-red-400'
                        }`}></span>
                        {p.reliability_score || 0}%
                      </div>
                    </div>
                  </div>

                  {/* Stats Row */}
                  <div className="grid grid-cols-5 gap-2 text-center text-xs mt-3">
                    <div className="bg-zinc-800/50 p-2 rounded-lg">
                      <p className="text-zinc-500">Stock</p>
                      <p className="text-white font-bold">{p.initial_stock}</p>
                    </div>
                    <div className="bg-zinc-800/50 p-2 rounded-lg">
                      <p className="text-zinc-500">Vendus</p>
                      <p className="text-emerald-400 font-bold">{p.total_sold}</p>
                    </div>
                    <div className="bg-zinc-800/50 p-2 rounded-lg">
                      <p className="text-zinc-500">Restant</p>
                      <p className="text-amber-400 font-bold">{p.current_stock}</p>
                    </div>
                    <div className="bg-zinc-800/50 p-2 rounded-lg">
                      <p className="text-zinc-500">Vitesse</p>
                      <p className="text-blue-400 font-bold">{p.speed}/j</p>
                    </div>
                    <div className="bg-zinc-800/50 p-2 rounded-lg">
                      <p className="text-zinc-500">Gains</p>
                      <p className="text-yellow-400 font-bold">${p.total_earnings?.toFixed(0)}</p>
                    </div>
                  </div>

                  {/* Dates */}
                  {p.start_date && (
                    <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500">
                      <span>Début: {new Date(p.start_date).toLocaleDateString('fr-FR')}</span>
                      <span className={p.overdue ? 'text-red-400 font-bold' : ''}>
                        Contrôle: {new Date(p.end_date).toLocaleDateString('fr-FR')}
                      </span>
                      {p.late_count > 0 && <span className="text-red-400">Retards: {p.late_count}</span>}
                    </div>
                  )}

                  {/* Progress Bar */}
                  <div className="h-2 bg-zinc-800 rounded-full overflow-hidden mt-3">
                    <div className={`h-full rounded-full ${
                      p.banned ? 'bg-red-500' : p.sell_percentage >= 80 ? 'bg-emerald-400' : p.sell_percentage >= 50 ? 'bg-amber-400' : 'bg-red-400'
                    }`} style={{ width: `${Math.min(100, p.sell_percentage)}%` }} />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => { setAddStockTarget({type: p.type, id: p.id, name: p.name}); setAddStockModels([{name:"",quantity:0,price:0}]); setActiveTab("add-stock"); }}
                      className="text-xs bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30 px-3 py-1.5 rounded-lg">
                      + Stock
                    </button>
                  </div>
                </div>
              )})}
            </div>
          </div>
        )}

        {/* ========== FINANCIAL DASHBOARD TAB ========== */}
        {activeTab === "finance" && (
          <div className="space-y-4" data-testid="finance-tab">
            <PlatformIncome />
            <ExportCsvPanel />
            <FinanceInvestments />
            <div className="glass-panel p-6">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-400" /> Financial Dashboard
              </h3>

              {/* Area Selector */}
              <div className="flex gap-2 mb-6">
                {[{id:"haiti",flag:"🇭🇹",label:"Haïti"},{id:"dr",flag:"🇩🇴",label:"Rép. Dominicaine"},{id:"usa",flag:"🇺🇸",label:"USA"}].map(a => (
                  <button key={a.id} onClick={() => setFinanceArea(a.id)}
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl transition-all flex-1 ${financeArea === a.id ? 'bg-emerald-600 text-white border border-emerald-500' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 border border-zinc-700'}`}
                    data-testid={`finance-area-${a.id}`}>
                    <span className="text-xl">{a.flag}</span>
                    <span className="font-bold text-sm">{a.label}</span>
                  </button>
                ))}
              </div>

              {financeData && financeData.areas && financeData.areas[financeArea] && (() => {
                const area = financeData.areas[financeArea];
                return (
                <div className="space-y-4">
                  {/* Key Metrics Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-blue-500/10 border border-blue-500/30 p-3 rounded-xl text-center">
                      <p className="text-blue-400 text-xs uppercase">Stock Déployé</p>
                      <p className="text-white font-black text-2xl">{area.total_stock_deployed}</p>
                      <p className="text-zinc-500 text-[10px]">{area.total_users} utilisateurs</p>
                    </div>
                    <div className="bg-emerald-500/10 border border-emerald-500/30 p-3 rounded-xl text-center">
                      <p className="text-emerald-400 text-xs uppercase">Total Vendus</p>
                      <p className="text-emerald-400 font-black text-2xl">{area.total_sold}</p>
                      <p className="text-zinc-500 text-[10px]">{area.total_current_stock} restants</p>
                    </div>
                    <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 rounded-xl text-center">
                      <p className="text-yellow-400 text-xs uppercase">Revenue</p>
                      <p className="text-yellow-400 font-black text-2xl">${area.total_revenue.toFixed(0)}</p>
                      <p className="text-zinc-500 text-[10px]">brut</p>
                    </div>
                    <div className={`${area.net_benefit >= 0 ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'} border p-3 rounded-xl text-center`}>
                      <p className={`text-xs uppercase ${area.net_benefit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>Bénéfice Net</p>
                      <p className={`font-black text-2xl ${area.net_benefit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>${area.net_benefit.toFixed(0)}</p>
                      <p className="text-zinc-500 text-[10px]">investi: ${area.total_invested.toFixed(0)}</p>
                    </div>
                  </div>

                  {/* Expenses Breakdown */}
                  <div className="bg-zinc-900/60 rounded-xl p-4 border border-zinc-800">
                    <p className="text-zinc-400 text-sm font-bold mb-3">Détails Financiers</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex justify-between items-center bg-zinc-800/50 p-2 rounded-lg">
                        <span className="text-zinc-400 text-xs">Bonus Payés</span>
                        <span className="text-amber-400 font-bold text-sm">${area.total_bonus_paid.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center bg-zinc-800/50 p-2 rounded-lg">
                        <span className="text-zinc-400 text-xs">Commissions</span>
                        <span className="text-blue-400 font-bold text-sm">${area.total_commission_paid.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center bg-zinc-800/50 p-2 rounded-lg">
                        <span className="text-zinc-400 text-xs">Investissement Total</span>
                        <span className="text-pink-400 font-bold text-sm">${area.total_invested.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center bg-zinc-800/50 p-2 rounded-lg">
                        <span className="text-zinc-400 text-xs">Revenue Brut</span>
                        <span className="text-yellow-400 font-bold text-sm">${area.gross_benefit.toFixed(2)}</span>
                      </div>
                    </div>
                    {/* Visual Bar */}
                    <div className="mt-3">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-zinc-500">Revenue vs Investissement</span>
                        <span className={`font-bold ${area.total_revenue >= area.total_invested ? 'text-emerald-400' : 'text-red-400'}`}>
                          {area.total_invested > 0 ? ((area.total_revenue / area.total_invested) * 100).toFixed(0) : 0}% ROI
                        </span>
                      </div>
                      <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${area.total_revenue >= area.total_invested ? 'bg-emerald-500' : 'bg-red-500'}`}
                          style={{ width: `${Math.min(100, area.total_invested > 0 ? (area.total_revenue / area.total_invested) * 100 : 0)}%` }} />
                      </div>
                    </div>
                  </div>

                  {/* Best/Worst Performers */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
                      <p className="text-emerald-400 text-xs font-bold uppercase mb-2">Top Performance</p>
                      {area.best_performers.map((p, i) => (
                        <div key={i} className="flex justify-between items-center py-1">
                          <span className="text-white text-sm">{i+1}. {p.name}</span>
                          <span className="text-emerald-400 font-bold text-sm">{p.pct}%</span>
                        </div>
                      ))}
                      {area.best_performers.length === 0 && <p className="text-zinc-500 text-xs">Aucun utilisateur</p>}
                    </div>
                    <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
                      <p className="text-red-400 text-xs font-bold uppercase mb-2">Bas Performance</p>
                      {area.worst_performers.map((p, i) => (
                        <div key={i} className="flex justify-between items-center py-1">
                          <span className="text-white text-sm">{i+1}. {p.name}</span>
                          <span className="text-red-400 font-bold text-sm">{p.pct}%</span>
                        </div>
                      ))}
                      {area.worst_performers.length === 0 && <p className="text-zinc-500 text-xs">Aucun utilisateur</p>}
                    </div>
                  </div>

                  {/* UNIFIED: Investissement + Nouvo Lo (fusionne — one flow, one entry) */}
                  <div className="bg-zinc-900/60 rounded-xl p-4 border border-zinc-800 scroll-mt-24" data-testid="batch-section">
                    <LotLinkBanner target={lotLinkTarget} onCancel={() => setLotLinkTarget(null)} />
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-zinc-400 text-sm font-bold">💰 Investissement + Nouveau Lot</p>
                        <p className="text-zinc-500 text-[10px]">Yon sèl fòm — sistèm nan lye lo a (Stock A, B…) ak envestisman an otomatikman.</p>
                      </div>
                      <button onClick={() => { setBatchForm({ ...batchForm, country: financeArea }); setShowAddBatch(!showAddBatch); }}
                        className="bg-gradient-to-r from-pink-600 to-blue-600 hover:from-pink-500 hover:to-blue-500 text-white text-xs px-3 py-1.5 rounded-lg font-bold"
                        data-testid="add-batch-btn">
                        + Nouveau Lot d'Investissement
                      </button>
                    </div>

                    {showAddBatch && (
                      <div className="bg-zinc-800/50 p-4 rounded-xl mb-3 border border-zinc-700">
                        {/* ── INVESTISSEMENT (Envestisman) ── */}
                        <p className="text-pink-400 text-xs font-black uppercase mb-2 tracking-wide">🅰️ Envestisman</p>
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div>
                            <label className="text-zinc-500 text-xs">Montant Investi ($)</label>
                            <input type="number" step="0.01" value={batchForm.investment_amount || ""} onChange={(e) => setBatchForm({...batchForm, investment_amount: parseFloat(e.target.value) || 0})}
                              className="w-full bg-zinc-900 border border-pink-700/50 rounded-lg py-2 px-3 text-white text-sm" placeholder="Ex: 5000" data-testid="batch-invest-amount" />
                          </div>
                          <div>
                            <label className="text-zinc-500 text-xs">Deskripsyon Envestisman</label>
                            <input type="text" value={batchForm.investment_description || ""} onChange={(e) => setBatchForm({...batchForm, investment_description: e.target.value})}
                              className="w-full bg-zinc-900 border border-pink-700/50 rounded-lg py-2 px-3 text-white text-sm" placeholder="Ex: Envoi stock mars 2026" data-testid="batch-invest-desc" />
                          </div>
                        </div>

                        {/* ── LOT (Nouvo Lo) ── */}
                        <p className="text-blue-400 text-xs font-black uppercase mb-2 tracking-wide">🅱️ Nouvo Lo (Batch)</p>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="text-zinc-500 text-xs">Non Lo</label>
                            <input type="text" value={batchForm.name} onChange={(e) => setBatchForm({...batchForm, name: e.target.value})}
                              className="w-full bg-zinc-900 border border-blue-700/50 rounded-lg py-2 px-3 text-white text-sm" placeholder="Stock A" data-testid="batch-name" />
                          </div>
                          <div>
                            <label className="text-zinc-500 text-xs">Date Envoi</label>
                            <input type="date" value={batchForm.date_sent?.split('T')[0] || ''} onChange={(e) => setBatchForm({...batchForm, date_sent: e.target.value})}
                              className="w-full bg-zinc-900 border border-blue-700/50 rounded-lg py-2 px-3 text-white text-sm" data-testid="batch-date" />
                          </div>
                          <div>
                            <label className="text-zinc-500 text-xs">Date Kontwòl</label>
                            <input type="date" value={batchForm.control_date || ''} onChange={(e) => setBatchForm({...batchForm, control_date: e.target.value})}
                              className="w-full bg-zinc-900 border border-blue-700/50 rounded-lg py-2 px-3 text-white text-sm" data-testid="batch-control" />
                          </div>
                          <div>
                            <label className="text-zinc-500 text-xs">Kou Total Lo ($)</label>
                            <input type="number" step="0.01" value={batchForm.cost_per_unit || ""} onChange={(e) => setBatchForm({...batchForm, cost_per_unit: parseFloat(e.target.value) || 0})}
                              className="w-full bg-zinc-900 border border-blue-700/50 rounded-lg py-2 px-3 text-white text-sm" placeholder="Kou total lot la" data-testid="batch-cost" />
                          </div>
                        </div>

                        {/* Modèl Vape */}
                        <div className="mb-3">
                          <p className="text-zinc-400 text-xs font-bold uppercase mb-2">Modèl Vape nan Lo sa a</p>
                          <div className="space-y-2">
                            {(batchForm.models || []).map((model, idx) => (
                              <div key={idx} className="bg-zinc-900/60 p-3 rounded-xl border border-zinc-700">
                                <div className="grid grid-cols-3 gap-2">
                                  <div>
                                    <label className="text-zinc-500 text-[10px]">Modèl {idx + 1}</label>
                                    <input type="text" value={model.name} onChange={(e) => {
                                      const m = [...batchForm.models]; m[idx] = {...m[idx], name: e.target.value}; setBatchForm({...batchForm, models: m});
                                    }} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-white text-sm" placeholder={`Ex: Vape Model ${idx+1}`}
                                      data-testid={`batch-model-name-${idx}`} />
                                  </div>
                                  <div>
                                    <label className="text-zinc-500 text-[10px]">Kantite</label>
                                    <input type="number" value={model.quantity || ""} onChange={(e) => {
                                      const m = [...batchForm.models]; m[idx] = {...m[idx], quantity: parseInt(e.target.value) || 0}; setBatchForm({...batchForm, models: m});
                                    }} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-white text-sm" placeholder="Qté"
                                      data-testid={`batch-model-qty-${idx}`} />
                                  </div>
                                  <div>
                                    <label className="text-zinc-500 text-[10px]">Pri Vant ($)</label>
                                    <div className="flex gap-1">
                                      <input type="number" step="0.01" value={model.price || ""} onChange={(e) => {
                                        const m = [...batchForm.models]; m[idx] = {...m[idx], price: parseFloat(e.target.value) || 0}; setBatchForm({...batchForm, models: m});
                                      }} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-white text-sm" placeholder="$"
                                        data-testid={`batch-model-price-${idx}`} />
                                      {batchForm.models.length > 1 && (
                                        <button onClick={() => { const m = batchForm.models.filter((_, i) => i !== idx); setBatchForm({...batchForm, models: m}); }}
                                          className="text-red-400 hover:text-red-300 px-2 text-lg">x</button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          {(batchForm.models || []).length < 50 && (
                            <button onClick={() => setBatchForm({...batchForm, models: [...(batchForm.models || []), {name: "", quantity: 0, price: 0}]})}
                              className="w-full border border-dashed border-zinc-600 text-zinc-400 hover:text-white py-2 rounded-xl text-xs mt-2 transition-colors"
                              data-testid="batch-add-model">
                              + Ajoute yon modèl (maks 50)
                            </button>
                          )}
                        </div>

                        {/* Rezime */}
                        <div className="bg-zinc-900/80 rounded-lg p-3 mb-3">
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <p className="text-zinc-400">Total inite: <span className="text-white font-bold">{(batchForm.models || []).reduce((a, m) => a + (m.quantity || 0), 0)}</span></p>
                            <p className="text-zinc-400">Envestisman: <span className="text-pink-400 font-bold">${(batchForm.investment_amount || batchForm.cost_per_unit || 0).toFixed(2)}</span></p>
                            <p className="text-zinc-400">Revni prevwa: <span className="text-emerald-400 font-bold">${(batchForm.models || []).reduce((a, m) => a + ((m.quantity || 0) * (m.price || 0)), 0).toFixed(2)}</span></p>
                            <p className="text-zinc-400">Pwofi estime: <span className="text-yellow-400 font-bold">${((batchForm.models || []).reduce((a, m) => a + ((m.quantity || 0) * (m.price || 0)), 0) - (batchForm.investment_amount || batchForm.cost_per_unit || 0)).toFixed(2)}</span></p>
                          </div>
                          {(batchForm.models || []).filter(m => m.name).length > 0 && (
                            <div className="mt-2 pt-2 border-t border-zinc-700">
                              <p className="text-zinc-500 text-[10px] uppercase mb-1">Detay pa modèl:</p>
                              {(batchForm.models || []).filter(m => m.name).map((m, i) => (
                                <div key={i} className="flex justify-between text-xs">
                                  <span className="text-zinc-300">{m.name}</span>
                                  <span className="text-zinc-400">x{m.quantity} @ ${m.price} = <span className="text-emerald-400 font-bold">${((m.quantity || 0) * (m.price || 0)).toFixed(2)}</span></span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <button onClick={async () => {
                          try {
                            const totalUnits = (batchForm.models || []).reduce((a, m) => a + (m.quantity || 0), 0);
                            const expectedRev = (batchForm.models || []).reduce((a, m) => a + ((m.quantity || 0) * (m.price || 0)), 0);
                            const payload = {
                              ...batchForm,
                              total_units: totalUnits,
                              sell_price: totalUnits > 0 ? expectedRev / totalUnits : 0,
                              expected_revenue: expectedRev,
                              // Backend auto-creates linked investment when investment_amount>0 or description present
                              investment_amount: batchForm.investment_amount || batchForm.cost_per_unit || 0,
                              investment_description: batchForm.investment_description || `Lot ${batchForm.name}`,
                            };
                            const r = await axios.post(`${API}/ceo/stock-batches`, payload);
                            toast.success(`Lo "${batchForm.name}" kreye ak ${totalUnits} inite + envestisman lye!`);
                            setShowAddBatch(false);
                            setBatchForm({ name: "Stock A", country: financeArea, total_units: 0, cost_per_unit: 0, sell_price: 0, investment_amount: 0, investment_description: "", date_sent: new Date().toISOString().split('T')[0], models: [{name: "", quantity: 0, price: 0}] });
                            fetchData();
                          } catch(e) { toast.error("Erè"); }
                        }} className="w-full bg-gradient-to-r from-pink-600 to-blue-600 hover:from-pink-500 hover:to-blue-500 text-white py-2 rounded-lg text-sm font-bold" data-testid="save-batch">
                          ✓ Kreye Lo + Envestisman Ansanm
                        </button>
                      </div>
                    )}

                    <div className="space-y-2">
                      {(area.stock_batches || []).map(batch => {
                        const profitPct = batch.total_cost > 0 ? ((batch.expected_revenue - batch.total_cost) / batch.total_cost * 100) : 0;
                        return (
                        <div key={batch.id} className={`bg-zinc-800/30 p-4 rounded-xl border ${lotLinkTarget ? "border-amber-400" : "border-zinc-700"}`} data-testid={`batch-card-${batch.id}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded font-bold">{batch.name}</span>
                              <span className="text-white font-bold text-sm">{batch.total_units} inite</span>
                              {batch.investment_id && (
                                <span className="bg-pink-600/30 border border-pink-500/50 text-pink-300 text-[10px] px-2 py-0.5 rounded font-bold" data-testid={`batch-inv-link-${batch.id}`}>💰 Envestisman lye</span>
                              )}
                            </div>
                            <button onClick={async () => { await axios.delete(`${API}/ceo/stock-batches/${batch.id}`); fetchData(); }}
                              className="text-red-400 text-xs hover:text-red-300">X</button>
                          </div>
                          {batch.investment_description && (
                            <p className="text-pink-300 text-[11px] mb-2 italic">"{batch.investment_description}"</p>
                          )}
                          <div className="grid grid-cols-3 gap-2 text-center text-xs mb-2">
                            <div className="bg-zinc-900/60 p-2 rounded-lg">
                              <p className="text-zinc-500">Envestisman</p>
                              <p className="text-pink-400 font-bold">${batch.total_cost?.toFixed(0)}</p>
                            </div>
                            <div className="bg-zinc-900/60 p-2 rounded-lg">
                              <p className="text-zinc-500">Revni Estime</p>
                              <p className="text-emerald-400 font-bold">${batch.expected_revenue?.toFixed(0)}</p>
                            </div>
                            <div className="bg-zinc-900/60 p-2 rounded-lg">
                              <p className="text-zinc-500">Pwofi</p>
                              <p className={`font-bold ${profitPct >= 0 ? 'text-yellow-400' : 'text-red-400'}`}>{profitPct.toFixed(0)}%</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-zinc-500">
                            <span>Envoi: {batch.date_sent ? new Date(batch.date_sent).toLocaleDateString('fr-FR') : 'N/A'}</span>
                            {batch.control_date && <span className="text-amber-400">Kontwòl: {new Date(batch.control_date).toLocaleDateString('fr-FR')}</span>}
                          </div>
                          {batch.models && batch.models.filter(m => m.name).length > 0 && (
                            <div className="mt-2 pt-2 border-t border-zinc-700">
                              <p className="text-zinc-500 text-[10px] uppercase mb-1">Modèl:</p>
                              <div className="grid grid-cols-2 gap-1">
                                {batch.models.filter(m => m.name).map((m, i) => (
                                  <div key={i} className="flex items-center justify-between bg-zinc-900/50 px-2 py-1 rounded text-xs">
                                    <span className="text-zinc-300 font-bold">{m.name}</span>
                                    <span className="text-zinc-400">x{m.quantity} <span className="text-emerald-400">${m.price}</span></span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          <BatchLinkBar batch={batch} target={lotLinkTarget} onDone={(action) => { if (action !== "remove") setLotLinkTarget(null); fetchData(); }} />
                        </div>
                      );})}
                      {(area.stock_batches || []).length === 0 && <p className="text-zinc-500 text-xs">Pa gen lo anrejistre. Peze bouton anlè a pou kreye premye Envestisman+Lo ou.</p>}
                    </div>
                  </div>

                  {/* Global Summary */}
                  <div className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border border-emerald-500/20 rounded-xl p-4">
                    <p className="text-emerald-400 text-xs font-bold uppercase mb-2">Résumé Global (Toutes Zones)</p>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <p className="text-white font-black text-xl">${financeData.total_global_revenue?.toFixed(0)}</p>
                        <p className="text-zinc-400 text-xs">Revenue Total</p>
                      </div>
                      <div>
                        <p className="text-pink-400 font-black text-xl">${financeData.total_global_invested?.toFixed(0)}</p>
                        <p className="text-zinc-400 text-xs">Investi Total</p>
                      </div>
                      <div>
                        <p className="text-emerald-400 font-black text-xl">{financeData.total_global_sold}</p>
                        <p className="text-zinc-400 text-xs">Vapes Vendus Global</p>
                      </div>
                    </div>
                  </div>

                  {/* ===== GRAPHIQUES ===== */}
                  <div className="bg-zinc-900/60 rounded-xl p-4 border border-zinc-800">
                    <p className="text-zinc-400 text-sm font-bold uppercase mb-4">Graphiques</p>
                    
                    {/* Revenue by Area - Pie Chart */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-zinc-800/50 p-4 rounded-xl">
                        <p className="text-zinc-400 text-xs font-bold mb-2">Revenue par Zone</p>
                        <ResponsiveContainer width="100%" height={180}>
                          <PieChart>
                            <Pie data={[
                              {name: 'Haiti', value: financeData.areas?.haiti?.total_revenue || 0, fill: '#10b981'},
                              {name: 'DR', value: financeData.areas?.dr?.total_revenue || 0, fill: '#8b5cf6'},
                              {name: 'USA', value: financeData.areas?.usa?.total_revenue || 0, fill: '#3b82f6'}
                            ].filter(d => d.value > 0)} dataKey="value" cx="50%" cy="50%" outerRadius={60} label={({name, value}) => `${name}: $${value}`}>
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="bg-zinc-800/50 p-4 rounded-xl">
                        <p className="text-zinc-400 text-xs font-bold mb-2">Stock Déployé par Zone</p>
                        <ResponsiveContainer width="100%" height={180}>
                          <BarChart data={[
                            {name: 'Haiti', stock: financeData.areas?.haiti?.total_stock_deployed || 0, sold: financeData.areas?.haiti?.total_sold || 0},
                            {name: 'DR', stock: financeData.areas?.dr?.total_stock_deployed || 0, sold: financeData.areas?.dr?.total_sold || 0},
                            {name: 'USA', stock: financeData.areas?.usa?.total_stock_deployed || 0, sold: financeData.areas?.usa?.total_sold || 0}
                          ]}>
                            <XAxis dataKey="name" tick={{fill: '#a1a1aa', fontSize: 11}} />
                            <YAxis tick={{fill: '#a1a1aa', fontSize: 11}} />
                            <Tooltip contentStyle={{background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8}} />
                            <Bar dataKey="stock" fill="#3b82f6" name="Stock" radius={[4,4,0,0]} />
                            <Bar dataKey="sold" fill="#10b981" name="Vendus" radius={[4,4,0,0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Performance by User - Top performers bar */}
                    <div className="bg-zinc-800/50 p-4 rounded-xl mb-4">
                      <p className="text-zinc-400 text-xs font-bold mb-2">Top Performers ({financeArea.toUpperCase()})</p>
                      <ResponsiveContainer width="100%" height={150}>
                        <BarChart data={(area.best_performers || []).slice(0, 5).map(p => ({name: p.name, pct: p.pct}))}>
                          <XAxis dataKey="name" tick={{fill: '#a1a1aa', fontSize: 10}} />
                          <YAxis tick={{fill: '#a1a1aa', fontSize: 10}} domain={[0, 100]} />
                          <Tooltip contentStyle={{background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8}} />
                          <Bar dataKey="pct" fill="#f59e0b" name="% Vendu" radius={[4,4,0,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Stock Batches Performance */}
                    {(area.stock_batches || []).length > 0 && (
                      <div className="bg-zinc-800/50 p-4 rounded-xl">
                        <p className="text-zinc-400 text-xs font-bold mb-2">Lots de Stock — Coût vs Revenue</p>
                        <ResponsiveContainer width="100%" height={150}>
                          <BarChart data={(area.stock_batches || []).map(b => ({name: b.name, cost: b.total_cost || 0, revenue: b.expected_revenue || 0}))}>
                            <XAxis dataKey="name" tick={{fill: '#a1a1aa', fontSize: 11}} />
                            <YAxis tick={{fill: '#a1a1aa', fontSize: 11}} />
                            <Tooltip contentStyle={{background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8}} />
                            <Bar dataKey="cost" fill="#ec4899" name="Coût" radius={[4,4,0,0]} />
                            <Bar dataKey="revenue" fill="#10b981" name="Revenue" radius={[4,4,0,0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </div>
              );})()}
              
              {(!financeData || !financeData.areas) && (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 text-zinc-500 mx-auto animate-spin" />
                  <p className="text-zinc-500 text-sm mt-2">Chargement des données financières...</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========== CONTROL DATES NOTIFICATIONS TAB ========== */}
        {activeTab === "control-dates" && (
          <div className="glass-panel p-6" data-testid="control-dates-tab">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="text-white font-bold flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-400" /> Notifikasyon Kontwòl
              </h3>
              <button onClick={async () => {
                try {
                  const cn = await axios.get(`${API}/ceo/control-date-notifications`);
                  setControlNotifs(cn.data);
                  toast.success("Aktyalize");
                } catch { toast.error("Erè"); }
              }} className="bg-zinc-800 hover:bg-zinc-700 text-white text-xs px-3 py-2 rounded-lg">↻ Refresh</button>
            </div>

            {/* TODAY's controls */}
            <div className="mb-6">
              <p className="text-amber-400 font-bold text-sm mb-2">📅 Dat Kontwòl Jodi a ({controlNotifs.today_count})</p>
              {controlNotifs.today.length === 0 ? (
                <div className="bg-zinc-900/60 rounded-xl p-4 text-center text-zinc-500 text-sm">Pa gen okenn stocker ki gen dat kontwòl jodi a.</div>
              ) : (
                <div className="space-y-2" data-testid="today-controls-list">
                  {controlNotifs.today.map(c => (
                    <div key={c.id} className="bg-amber-950/30 border border-amber-700/40 rounded-xl p-3 flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <p className="text-white font-bold">{c.name} <span className="text-zinc-500 text-xs">({c.code})</span></p>
                        <p className="text-zinc-400 text-xs">📍 {countryFlags[c.country]} {c.city || c.country.toUpperCase()} • Stock: {c.current_stock}/{c.initial_stock} • Vann sik: {c.sold_cycle}</p>
                      </div>
                      <button onClick={() => { setEditTargetType('stocker'); setEditTargetId(c.id); setActiveTab('edit-stocker'); }}
                        className="bg-blue-600 hover:bg-blue-500 text-white text-xs px-3 py-1.5 rounded-lg">📝 Edit</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* OVERDUE list */}
            <div>
              <p className="text-red-400 font-bold text-sm mb-2">⏱️ Anreta — Dat Kontwòl Pase, Poko Peye ({controlNotifs.overdue_count})</p>
              {controlNotifs.overdue.length === 0 ? (
                <div className="bg-zinc-900/60 rounded-xl p-4 text-center text-zinc-500 text-sm">🎉 Pa gen okenn stocker anreta!</div>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto" data-testid="overdue-list">
                  {controlNotifs.overdue.map(c => (
                    <div key={c.id} className={`rounded-xl p-3 border ${c.in_grace ? "bg-orange-950/30 border-orange-700/50" : "bg-red-950/40 border-red-700/60"}`} data-testid={`overdue-${c.id}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <p className="text-white font-bold flex items-center gap-2">
                            <AlertTriangle className={`w-4 h-4 ${c.in_grace ? "text-orange-400" : "text-red-400 animate-pulse"}`} />
                            {c.name} <span className="text-zinc-500 text-xs">({c.code})</span>
                          </p>
                          <p className="text-zinc-400 text-xs">📍 {countryFlags[c.country]} {c.city || c.country.toUpperCase()} • Stock rete: {c.current_stock}/{c.initial_stock}</p>
                          <p className={`text-xs font-bold mt-1 ${c.in_grace ? "text-orange-300" : "text-red-300"}`}>
                            ⚠️ Anreta depi {c.days_overdue}j
                            {c.in_grace ? ` (gen ${c.grace_remaining}j gras rete)` : " (peryòd gras depase)"}
                            {c.pending_withdrawal_count > 0 && ` • ${c.pending_withdrawal_count} retrait an atant`}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={async () => {
                            if (!window.confirm(`Aktive nouvo sik pou ${c.name}?`)) return;
                            try {
                              const r = await axios.post(`${API}/ceo/stockers/${c.id}/activate-new-cycle`, {});
                              toast.success(`Nouvo sik aktive! Carryover: ${r.data.carryover || 0}`);
                              const cn = await axios.get(`${API}/ceo/control-date-notifications`);
                              setControlNotifs(cn.data);
                              fetchData();
                            } catch (e) { toast.error(e.response?.data?.detail || "Erè"); }
                          }} data-testid={`activate-cycle-${c.id}`}
                            className="bg-cyan-600 hover:bg-cyan-500 text-white text-xs px-3 py-1.5 rounded-lg">🔄 Nouvo Sik</button>
                          <button onClick={() => { setEditTargetType('stocker'); setEditTargetId(c.id); setActiveTab('edit-stocker'); }}
                            className="bg-blue-600 hover:bg-blue-500 text-white text-xs px-3 py-1.5 rounded-lg">📝 Edit</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========== MARKET INTELLIGENCE ========== */}
        {activeTab === "market-intel" && <><LiveSales /><MarketIntelligenceTab /></>}
        {activeTab === "expenses" && <ExpensesPanel />}
        {activeTab === "sale-corrections" && <SaleCorrections />}

        {/* ========== PERFORMANCE KONT ========== */}
        {activeTab === "performance-kont" && <PerformanceOverviewTab />}
        {activeTab === "activity" && <ActivityReportTab />}

        {/* ========== DOSYE ========== */}
        {activeTab === "dossier" && <DossierTab />}

        {/* NM Sub-Stockers Modal */}
        {viewNmSubs && (
          <NmSubStockersModal nmId={viewNmSubs.id} nmName={viewNmSubs.name} onClose={() => setViewNmSubs(null)} />
        )}

        {/* CEO Personal AI Assistant (Ada) */}
        <CeoAiAssistant open={showAiAssistant} onClose={() => setShowAiAssistant(false)} />

        {/* ========== MANAGER TANIE TAB ========== */}
        {activeTab === "manager" && (
          <div className="glass-panel p-6" data-testid="manager-tab">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-amber-400" /> Tanie — Manager Personnel
              </h3>
              <button onClick={() => {
                setManagerMessages([]);
                toast.success("Manager & Assistante réinitialisés!");
              }}
                className="bg-amber-500/20 text-amber-400 text-xs px-3 py-1.5 rounded-lg hover:bg-amber-500/30 transition-colors font-bold"
                data-testid="reset-ai-btn">
                Réactiver
              </button>
            </div>
            <p className="text-zinc-500 text-sm mb-4">Votre manager personnel. Rapports, analyses, décisions stratégiques. Parle FR/Créole/EN.</p>
            
            <div className="bg-zinc-900/60 rounded-xl border border-zinc-800 flex flex-col" style={{ height: '550px' }}>
              {/* Chat messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {managerMessages.length === 0 && (
                  <div className="text-center py-8">
                    <Briefcase className="w-12 h-12 text-amber-400 mx-auto mb-3 opacity-50" />
                    <p className="text-zinc-400 text-sm mb-2">Bonjou Boss! Mwen se Tanie, manager personnel ou.</p>
                    <p className="text-zinc-500 text-xs">Mande m nenpòt bagay: rapò, analiz, modifye stock/vant, voye mesaj, konsèy estratejik...</p>
                  </div>
                )}
                {managerMessages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {msg.role === 'jenny_join' ? (
                      <div className="w-full text-center py-2">
                        <span className="bg-violet-500/20 text-violet-400 text-xs px-3 py-1 rounded-full font-bold">{msg.content}</span>
                      </div>
                    ) : (
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user' 
                        ? 'bg-amber-600 text-white' 
                        : msg.role === 'jenny'
                        ? 'bg-violet-900/50 text-zinc-200 border border-violet-500/30'
                        : 'bg-zinc-800 text-zinc-200 border border-zinc-700'
                    }`}>
                      {msg.role === 'assistant' && (
                        <div className="flex items-center gap-1 mb-1 flex-wrap">
                          <Briefcase className="w-3 h-3 text-amber-400" />
                          <span className="text-amber-400 text-xs font-bold">Tanie</span>
                          <span className="w-2 h-2 rounded-full bg-emerald-400 ml-1"></span>
                        </div>
                      )}
                      {msg.role === 'jenny' && (
                        <div className="flex items-center gap-1 mb-1">
                          <Bot className="w-3 h-3 text-violet-400" />
                          <span className="text-violet-400 text-xs font-bold">Jenny</span>
                          <span className="w-2 h-2 rounded-full bg-emerald-400 ml-1"></span>
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      {msg.actions && msg.actions.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-zinc-600">
                          {msg.actions.map((a, j) => (
                            <div key={j} className="flex items-center gap-1 text-xs text-emerald-400">
                              <CheckCircle className="w-3 h-3" />
                              <span>{a.action === 'suspend_user' ? 'Compte suspendu' : a.action === 'call_jenny' ? 'Jenny convoquée' : a.result}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {msg.image && (
                        <div className="mt-3 rounded-xl overflow-hidden border border-zinc-600">
                          <img src={`data:image/png;base64,${msg.image}`} alt="Stratégie" className="w-full rounded-xl" />
                        </div>
                      )}
                    </div>
                    )}
                  </div>
                ))}
                {managerSending && (
                  <div className="flex justify-start">
                    <div className="bg-zinc-800 border border-zinc-700 rounded-2xl px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
                        <span className="text-zinc-400 text-sm">Tanie analyse...</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={managerEndRef} />
              </div>

              {/* Input */}
              <div className="border-t border-zinc-800 p-3 flex gap-2">
                {/* Voice Recording Button */}
                <button
                  onClick={async () => {
                    if (isRecording) {
                      mediaRecorderRef.current?.stop();
                      setIsRecording(false);
                    } else {
                      try {
                        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                        const mediaRecorder = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4' });
                        const chunks = [];
                        mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
                        mediaRecorder.onstop = async () => {
                          stream.getTracks().forEach(t => t.stop());
                          const blob = new Blob(chunks, { type: mediaRecorder.mimeType });
                          const formData = new FormData();
                          formData.append('file', blob, 'voice.webm');
                          try {
                            toast.info("Transcription...");
                            const res = await axios.post(`${API}/ceo/manager/transcribe`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            if (res.data.text) {
                              toast.success("Message envoyé par voix!");
                              setVoiceMode(true);
                              sendManagerMessage(res.data.text, true);
                            } else {
                              toast.error(res.data.error || "Pas de texte détecté");
                            }
                          } catch(e) { toast.error("Erreur transcription"); }
                        };
                        mediaRecorder.start();
                        mediaRecorderRef.current = mediaRecorder;
                        setIsRecording(true);
                        toast.info("Parlez maintenant... Appuyez encore pour arrêter.");
                      } catch(e) { toast.error("Micro non disponible. Vérifiez les permissions."); }
                    }
                  }}
                  className={`p-3 rounded-xl transition-all ${isRecording ? 'bg-red-600 animate-pulse ring-2 ring-red-400' : 'bg-zinc-800 hover:bg-zinc-700'}`}
                  data-testid="manager-voice-btn">
                  {isRecording ? <MicOff className="w-5 h-5 text-white" /> : <Mic className="w-5 h-5 text-amber-400" />}
                </button>
                
                <input
                  type="text"
                  value={managerInput}
                  onChange={(e) => setManagerInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && managerInput.trim() && !managerSending) {
                      e.preventDefault();
                      const msg = managerInput.trim();
                      setManagerInput("");
                      // If Jenny is in the chat and message is addressed to her
                      const jennyInChat = managerMessages.some(m => m.role === 'jenny' || m.role === 'jenny_join');
                      const isForJenny = msg.toLowerCase().includes("jenny");
                      if (jennyInChat && isForJenny) {
                        // Send to Jenny
                        setManagerMessages(prev => [...prev, { role: 'user', content: msg }]);
                        setManagerSending(true);
                        axios.post(`${API}/ceo/jenny`, { message: msg }, { timeout: 60000 })
                          .then(r => setManagerMessages(prev => [...prev, { role: 'jenny', content: r.data.response }]))
                          .catch(async () => {
                            // Retry once
                            try {
                              const rr = await axios.post(`${API}/ceo/jenny`, { message: msg }, { timeout: 60000 });
                              setManagerMessages(prev => [...prev, { role: 'jenny', content: rr.data.response }]);
                            } catch(_) {
                              setManagerMessages(prev => [...prev, { role: 'jenny', content: "Jenny: Boss, m gen yon ti pwoblèm koneksyon — eseye ankò nan yon segond." }]);
                            }
                          })
                          .finally(() => setManagerSending(false));
                      } else {
                        sendManagerMessage(msg);
                      }
                    }
                  }}
                  placeholder="Parlez à Tanie (votre manager)..."
                  className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-amber-500"
                  data-testid="manager-input"
                />
                <button
                  onClick={() => {
                    if (!managerInput.trim() || managerSending) return;
                    const msg = managerInput.trim();
                    setManagerInput("");
                    const jennyInChat = managerMessages.some(m => m.role === 'jenny' || m.role === 'jenny_join');
                    const isForJenny = msg.toLowerCase().includes("jenny");
                    if (jennyInChat && isForJenny) {
                      setManagerMessages(prev => [...prev, { role: 'user', content: msg }]);
                      setManagerSending(true);
                      axios.post(`${API}/ceo/jenny`, { message: msg }, { timeout: 60000 })
                        .then(r => setManagerMessages(prev => [...prev, { role: 'jenny', content: r.data.response }]))
                        .catch(async () => {
                          try {
                            const rr = await axios.post(`${API}/ceo/jenny`, { message: msg }, { timeout: 60000 });
                            setManagerMessages(prev => [...prev, { role: 'jenny', content: rr.data.response }]);
                          } catch(_) {
                            setManagerMessages(prev => [...prev, { role: 'jenny', content: "Jenny: Boss, m gen yon ti pwoblèm koneksyon — eseye ankò nan yon segond." }]);
                          }
                        })
                        .finally(() => setManagerSending(false));
                    } else {
                      sendManagerMessage(msg);
                    }
                  }}
                  disabled={!managerInput.trim() || managerSending}
                  className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white p-3 rounded-xl"
                  data-testid="manager-send-btn">
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ========== BIWO / REUNION TAB ========== */}
        {activeTab === "manager" && (
          <div className="glass-panel p-4 mt-4 border border-amber-500/20" data-testid="bureau-section">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-amber-400 font-bold text-sm flex items-center gap-2">
                <Crown className="w-4 h-4" /> Biwo CEO — Reunion
              </h4>
              <div className="flex gap-2">
                <button onClick={() => {
                  sendManagerMessage("Prépare-moi un résumé de la réunion avec tous les points clés qu'on a discuté.");
                }} className="bg-amber-500/20 text-amber-400 text-xs px-3 py-1.5 rounded-lg hover:bg-amber-500/30 transition-colors"
                  data-testid="reunion-summary-btn">
                  Résumé Réunion
                </button>
              </div>
            </div>
            
            {/* Envoke Section */}
            <div className="bg-zinc-900/60 rounded-xl p-3 mb-3 border border-zinc-700">
              <p className="text-zinc-500 text-[10px] uppercase mb-2">Envoke nan Reunion</p>
              <div className="flex gap-2">
                <button onClick={() => {
                  setVoiceMode(true);
                  sendManagerMessage("Boss mwen la! Mwen pare pou reunion an. Ki sa ou vle diskite jodi a?", true);
                }}
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-colors"
                  data-testid="invoke-manager-btn">
                  <Briefcase className="w-4 h-4" /> Envoke Tanie (Manager)
                </button>
                <button onClick={() => {
                  setActiveTab("jenny");
                }}
                  className="flex-1 bg-violet-600 hover:bg-violet-700 text-white py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-colors"
                  data-testid="invoke-jenny-btn">
                  <Bot className="w-4 h-4" /> Envoke Jenny (Assistante)
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => sendManagerMessage("Ban mwen rapò jou a ak tout chif yo.")}
                className="bg-zinc-800 hover:bg-zinc-700 text-white text-xs p-3 rounded-xl border border-zinc-700 text-left transition-colors">
                <p className="font-bold">Rapò Jou a</p>
                <p className="text-zinc-500 text-[10px]">Analiz konplè</p>
              </button>
              <button onClick={() => sendManagerMessage("Ki stocker ki ba performance? Ki moun mwen dwe pale avèk yo?")}
                className="bg-zinc-800 hover:bg-zinc-700 text-white text-xs p-3 rounded-xl border border-zinc-700 text-left transition-colors">
                <p className="font-bold">Pèfòmans Ba</p>
                <p className="text-zinc-500 text-[10px]">Stocker pou konvoke</p>
              </button>
              <button onClick={() => sendManagerMessage("Ki desizyon stratejik mwen dwe pran pou semèn sa? Ki zone ki bezwen plis stock?")}
                className="bg-zinc-800 hover:bg-zinc-700 text-white text-xs p-3 rounded-xl border border-zinc-700 text-left transition-colors">
                <p className="font-bold">Desizon Stratejik</p>
                <p className="text-zinc-500 text-[10px]">Zone + Stock</p>
              </button>
              <button onClick={() => sendManagerMessage("Fè yon analiz konplè sou investisman mwen yo. Ki stock batch ki fè profit? Ki sa ki pa mache?")}
                className="bg-zinc-800 hover:bg-zinc-700 text-white text-xs p-3 rounded-xl border border-zinc-700 text-left transition-colors">
                <p className="font-bold">Analiz Investisman</p>
                <p className="text-zinc-500 text-[10px]">Profit/Pèt pa batch</p>
              </button>
              <button onClick={() => sendManagerMessage("Voye yon imaj stratejik ki montre plan distribisyon stock nan chak zone.")}
                className="bg-zinc-800 hover:bg-zinc-700 text-white text-xs p-3 rounded-xl border border-zinc-700 text-left transition-colors col-span-2">
                <p className="font-bold">Mande Imaj Stratejik</p>
                <p className="text-zinc-500 text-[10px]">Manager jenere yon vizualizasyon pou ou</p>
              </button>
            </div>
          </div>
        )}

        {/* Jenny AI Assistant Tab */}
        {activeTab === "jenny" && (
          <div className="glass-panel p-6" data-testid="jenny-tab">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <Bot className="w-5 h-5 text-violet-400" /> Jenny — Assistante
            </h3>
            <p className="text-zinc-500 text-sm mb-4">Demandez à Jenny de gérer vos stockers, modifier les stocks, prix, dates, ou toute autre action.</p>
            
            <div className="bg-zinc-900/60 rounded-xl border border-zinc-800 flex flex-col" style={{ height: '500px' }}>
              {/* Chat messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {jennyMessages.length === 0 && (
                  <div className="text-center py-8">
                    <Bot className="w-12 h-12 text-violet-400 mx-auto mb-3 opacity-50" />
                    <p className="text-zinc-400 text-sm mb-2">Bonjour CEO! Je suis Jenny, votre assistante personnelle.</p>
                    <p className="text-zinc-500 text-xs">Demandez-moi: rapports, modifier stocks, envoyer messages, conseils stratégiques, ou n'importe quelle question sur Vape Yard.</p>
                  </div>
                )}
                {jennyMessages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user' 
                        ? 'bg-violet-600 text-white' 
                        : 'bg-zinc-800 text-zinc-200 border border-zinc-700'
                    }`}>
                      {msg.role === 'assistant' && (
                        <div className="flex items-center gap-1 mb-1 flex-wrap">
                          <Bot className="w-3 h-3 text-violet-400" />
                          <span className="text-violet-400 text-xs font-bold">Jenny</span>
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      {msg.actions && msg.actions.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-zinc-600">
                          {msg.actions.map((a, j) => (
                            <div key={j} className="flex items-center gap-1 text-xs text-emerald-400">
                              <CheckCircle className="w-3 h-3" />
                              <span>Action: {a.action}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {jennySending && (
                  <div className="flex justify-start">
                    <div className="bg-zinc-800 border border-zinc-700 rounded-2xl px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 text-violet-400 animate-spin" />
                        <span className="text-zinc-400 text-sm">Jenny réfléchit...</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={jennyEndRef} />
              </div>

              {/* Input */}
              <div className="border-t border-zinc-800 p-3 flex gap-2">
                <input
                  type="text"
                  value={jennyInput}
                  onChange={(e) => setJennyInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && jennyInput.trim() && !jennySending) {
                      e.preventDefault();
                      const msg = jennyInput.trim();
                      setJennyInput("");
                      setJennyMessages(prev => [...prev, { role: 'user', content: msg }]);
                      setJennySending(true);
                      const jennyPost = async (m) => {
                        let lastErr = null;
                        for (let a = 0; a < 3; a++) {
                          try { return await axios.post(`${API}/ceo/jenny`, { message: m }, { timeout: 60000 }); }
                          catch(err) { lastErr = err; if (a < 2) await new Promise(r => setTimeout(r, 800 * (a + 1))); }
                        }
                        throw lastErr;
                      };
                      jennyPost(msg)
                        .then(r => {
                          setJennyMessages(prev => [...prev, { 
                            role: 'assistant', 
                            content: r.data.response, 
                            actions: r.data.actions || [],
                            mode: r.data.mode || 'ai'
                          }]);
                          if (r.data.actions?.length > 0) fetchData();
                        })
                        .catch(() => {
                          setJennyMessages(prev => [...prev, { role: 'assistant', content: "Boss, koneksyon an fèb yon segond. M ap reyalinnye… tanpri voye mesaj la yon lòt fwa.", retry: true, retryMsg: msg }]);
                        })
                        .finally(() => setJennySending(false));
                    }
                  }}
                  placeholder="Demandez quelque chose à Jenny..."
                  className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-violet-500"
                  data-testid="jenny-input"
                />
                <button
                  onClick={() => {
                    if (!jennyInput.trim() || jennySending) return;
                    const msg = jennyInput.trim();
                    setJennyInput("");
                    setJennyMessages(prev => [...prev, { role: 'user', content: msg }]);
                    setJennySending(true);
                    const jennyPostBtn = async (m) => {
                      let lastErr = null;
                      for (let a = 0; a < 3; a++) {
                        try { return await axios.post(`${API}/ceo/jenny`, { message: m }, { timeout: 60000 }); }
                        catch(err) { lastErr = err; if (a < 2) await new Promise(r => setTimeout(r, 800 * (a + 1))); }
                      }
                      throw lastErr;
                    };
                    jennyPostBtn(msg)
                      .then(r => {
                        setJennyMessages(prev => [...prev, { 
                          role: 'assistant', 
                          content: r.data.response, 
                          actions: r.data.actions || [],
                          mode: r.data.mode || 'ai'
                        }]);
                        if (r.data.actions?.length > 0) fetchData();
                      })
                      .catch(() => {
                        setJennyMessages(prev => [...prev, { role: 'assistant', content: "Boss, koneksyon an fèb yon segond. M ap reyalinnye… tanpri voye mesaj la yon lòt fwa.", retry: true, retryMsg: msg }]);
                      })
                      .finally(() => setJennySending(false));
                  }}
                  disabled={!jennyInput.trim() || jennySending}
                  className="bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white p-3 rounded-xl transition-all"
                  data-testid="jenny-send-btn"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}


        {/* ========== VISUAL DASHBOARD MANAGER TAB ========== */}
        {activeTab === "visuel" && <CeoVisualManager />}
        {activeTab === "stock-verify" && <CeoStockVerifyPanel />}

        {/* ========== HOOKAH TAB ========== */}
        {activeTab === "hookah" && (
          <div className="space-y-4" data-testid="hookah-ceo-tab">
            {/* Header with hero hookah image */}
            <div className="rounded-2xl overflow-hidden border border-violet-700/30 relative" style={{minHeight: 180}}>
            <img src={HOOKAH_HERO} alt="" onError={(e) => { e.target.src = HOOKAH_HERO_FALLBACK; }} className="absolute inset-0 w-full h-full object-cover opacity-50" />
              <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/70 to-transparent" />
              <div className="relative p-6">
                <p className="text-violet-300 text-[10px] font-bold tracking-[0.3em] uppercase">Live Control Center</p>
                <h3 className="text-white font-bold text-2xl md:text-3xl mt-1" style={{fontFamily:'var(--font-heading)'}}>Hookah Experience</h3>
                <p className="text-zinc-300 text-sm mt-1">{ceoHookahs.filter(h => h.status === 'in_session').length} aktif / {ceoHookahs.length} total tab — Suivi tan reyèl</p>
              </div>
            </div>

            {/* Country Selector */}
            <div className="flex gap-2">
              {[{id:"haiti",flag:"🇭🇹"},{id:"dr",flag:"🇩🇴"},{id:"usa",flag:"🇺🇸"}].map(c => (
                <button key={c.id} onClick={async () => {
                  setHookahCountry(c.id);
                  const [r, ds] = await Promise.all([
                    axios.get(`${API}/hookah/${c.id}`),
                    axios.get(`${API}/hookah/daily-session/country/${c.id}/active`)
                  ]);
                  setCeoHookahs(r.data);
                  setActiveDailySessions(ds.data || []);
                }}
                  className={`flex-1 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all ${hookahCountry === c.id ? 'bg-violet-600 text-white shadow-lg shadow-violet-600/20' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
                  <span>{c.flag}</span> {c.id.toUpperCase()}
                </button>
              ))}
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-4 gap-2">
              <div className="glass-panel p-3 text-center">
                <p className="text-emerald-400 font-black text-xl">{ceoHookahs.filter(h => h.status === 'available').length}</p>
                <p className="text-zinc-500 text-[10px] uppercase">Disponible</p>
              </div>
              <div className="glass-panel p-3 text-center">
                <p className="text-yellow-400 font-black text-xl">{ceoHookahs.filter(h => h.status === 'in_session').length}</p>
                <p className="text-zinc-500 text-[10px] uppercase">En Session</p>
              </div>
              <div className="glass-panel p-3 text-center">
                <p className="text-blue-400 font-black text-xl">{ceoHookahs.filter(h => h.status === 'cleaning').length}</p>
                <p className="text-zinc-500 text-[10px] uppercase">Nettoyage</p>
              </div>
              <div className="glass-panel p-3 text-center">
                <p className="text-zinc-400 font-black text-xl">{ceoHookahs.filter(h => h.status === 'out_of_service').length}</p>
                <p className="text-zinc-500 text-[10px] uppercase">Hors Service</p>
              </div>
            </div>

            {/* Live Hookah Grid — Website style with images */}
            <div className="glass-panel p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-white font-bold text-sm flex items-center gap-2" style={{fontFamily:'var(--font-heading)'}}>
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE — Mouvman Tab yo
                  </p>
                  <p className="text-zinc-500 text-xs mt-1">Chak tab montre kliyan ki sou li ak tan ki rete</p>
                </div>
                <button onClick={async () => { const r = await axios.get(`${API}/hookah/${hookahCountry}`); setCeoHookahs(r.data); }}
                  className="text-violet-400 text-xs hover:text-violet-300">Rafraîchir</button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {ceoHookahs.map(h => {
                  const inSession = h.status === "in_session" && h.current_session;
                  const isExpired = inSession && new Date() > new Date(h.current_session?.end_time);
                  const timeLeft = inSession ? (() => {
                    const diff = new Date(h.current_session.end_time) - new Date();
                    if (diff <= 0) return "ÉCOULÉ";
                    const hh = Math.floor(diff/3600000);
                    const mm = Math.floor((diff%3600000)/60000);
                    return hh > 0 ? `${hh}h ${mm}m` : `${mm}m`;
                  })() : "";
                  const dotColor = isExpired ? "bg-red-500" : inSession ? "bg-yellow-400" : h.status === "cleaning" ? "bg-blue-400" : h.status === "out_of_service" ? "bg-zinc-600" : "bg-emerald-400";
                  const isAvailable = h.status === "available";
                  return (
                    <div key={h.id} data-testid={`ceo-hookah-${h.number}`}
                      className={`relative rounded-2xl overflow-hidden border-2 transition-all hover:scale-[1.02] ${
                        isExpired ? "border-red-500 shadow-lg shadow-red-900/40" :
                        inSession ? "border-yellow-500 shadow-lg shadow-yellow-900/30" :
                        isAvailable ? "border-emerald-500/40" :
                        "border-zinc-700"
                      }`}>
                      {/* Image */}
                      <div className="relative aspect-square bg-zinc-900">
                        <img src={hookahImageFor(h.number)} alt={`Table ${h.number}`} onError={(e) => { e.target.src = hookahFallbackFor(h.number); }} className={`w-full h-full object-cover transition-all ${isAvailable ? '' : 'brightness-50'}`} />
                        {/* Status light - pulsing */}
                        <div className={`absolute top-2 right-2 w-4 h-4 rounded-full ${dotColor} ${(isAvailable || inSession) ? 'animate-pulse' : ''} shadow-lg`} style={{boxShadow: `0 0 12px currentColor`}} />
                        {/* Table label */}
                        <div className="absolute top-2 left-2 bg-black/70 backdrop-blur px-2 py-0.5 rounded">
                          <p className="text-white font-black text-sm leading-tight">Tab {h.number}</p>
                        </div>
                        {/* Session info overlay */}
                        {inSession && (
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/90 to-transparent p-2">
                            <p className="text-yellow-400 text-[9px] font-bold uppercase tracking-wide">{isExpired ? "Tan fini" : "An sesyon"}</p>
                            <p className="text-white font-bold text-xs truncate">{h.current_session.client_name || "—"}</p>
                            <p className="text-zinc-300 text-[10px] truncate">{h.current_session.stocker_name}</p>
                            <p className={`font-mono font-bold text-xs ${isExpired ? "text-red-400" : "text-emerald-400"}`}>⏱ {timeLeft}</p>
                          </div>
                        )}
                        {isAvailable && (
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-emerald-950/95 to-transparent p-2 text-center">
                            <p className="text-emerald-300 text-[10px] font-bold uppercase">Disponib</p>
                          </div>
                        )}
                        {!isAvailable && !inSession && (
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 to-transparent p-2 text-center">
                            <p className="text-zinc-400 text-[10px] uppercase">{h.status}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Legend */}
              <div className="flex gap-3 flex-wrap text-[10px] text-zinc-500 mt-3 pt-3 border-t border-zinc-800">
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Disponib</span>
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" /> An sesyon</span>
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-500" /> Tan fini</span>
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-blue-400" /> Netwayaj</span>
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-zinc-600" /> H.S.</span>
              </div>
            </div>

            {/* ACTIVE DAILY SESSIONS — per hookah stocker */}
            <div className="glass-panel p-4" data-testid="ceo-active-daily-sessions">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-white font-bold text-sm flex items-center gap-2" style={{fontFamily:'var(--font-heading)'}}>
                    🔥 Sesyon Stocker Aktif — Live
                  </p>
                  <p className="text-zinc-500 text-xs mt-0.5">Chak stocker hookah ki gen yon sesyon ouvri jodi a</p>
                </div>
                <div className="text-right">
                  <p className="text-emerald-400 text-2xl font-black" data-testid="ceo-active-session-count">{activeDailySessions.length}</p>
                  <p className="text-zinc-500 text-[10px]">stocker aktif</p>
                </div>
              </div>
              {activeDailySessions.length === 0 && (
                <div className="text-center py-8 text-zinc-500 text-sm">Pa gen sesyon hookah aktif kounye a pou {hookahCountry.toUpperCase()}</div>
              )}
              <div className="space-y-3">
                {activeDailySessions.map(ds => {
                  const stockerEarnings = ds.tables.reduce((sum, t) => {
                    if (t.status === "active") return sum + (t.price || 0) * 0.25;
                    if (t.status === "completed") return sum + ((t.price || 0) + (t.total_refill_amount || 0)) * 0.50;
                    return sum;
                  }, 0);
                  return (
                    <div key={ds.id} data-testid={`ceo-daily-session-${ds.stocker_id}`}
                      className="bg-zinc-900/80 rounded-2xl border border-emerald-700/40 p-4">
                      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                        <div>
                          <p className="text-white font-bold text-base">{ds.stocker_name}</p>
                          <p className="text-zinc-500 text-xs">
                            Responsab: <span className="text-emerald-400 font-bold">{ds.responsable_name}</span>
                            {ds.servers && ds.servers.length > 0 && <> • Sèvè: <span className="text-blue-400">{ds.servers.join(", ")}</span></>}
                            {ds.address && <> • {ds.address}</>}
                            {ds.event && <> • <span className="text-violet-400">{ds.event}</span></>}
                          </p>
                          <p className="text-zinc-600 text-[10px] mt-1">📅 Ouvri {new Date(ds.opened_at).toLocaleString('fr-FR')}</p>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center text-xs">
                          <div className="bg-emerald-500/10 px-3 py-1 rounded-lg"><p className="text-emerald-400 font-bold text-base">${ds.base_revenue.toFixed(2)}</p><p className="text-zinc-500 text-[9px]">Hookah</p></div>
                          <div className="bg-blue-500/10 px-3 py-1 rounded-lg"><p className="text-blue-400 font-bold text-base">${ds.refill_revenue.toFixed(2)}</p><p className="text-zinc-500 text-[9px]">Refil</p></div>
                          <div className="bg-yellow-500/10 px-3 py-1 rounded-lg"><p className="text-yellow-400 font-bold text-base">${ds.total_revenue.toFixed(2)}</p><p className="text-zinc-500 text-[9px]">Total</p></div>
                          <div className="bg-pink-500/10 px-3 py-1 rounded-lg"><p className="text-pink-400 font-bold text-base">${stockerEarnings.toFixed(2)}</p><p className="text-zinc-500 text-[9px]">Stocker</p></div>
                        </div>
                      </div>
                      {/* Tables this stocker is running */}
                      {ds.tables.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                          {ds.tables.map((t, i) => (
                            <div key={i} className={`p-2 rounded-lg text-xs border ${t.status === "active" ? "border-yellow-500/40 bg-yellow-500/5" : "border-zinc-700 bg-zinc-800/40"}`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-white font-bold">Tab {t.hookah_number}</span>
                                <span className={`w-1.5 h-1.5 rounded-full ${t.status === "active" ? "bg-yellow-400 animate-pulse" : "bg-zinc-600"}`} />
                              </div>
                              <p className="text-white truncate font-bold">{t.client_name}</p>
                              <p className="text-zinc-500 text-[10px] truncate">Sèvè: {t.server_name || t.stocker_name}</p>
                              <p className="text-emerald-400 text-[10px]">${(t.price||0).toFixed(2)} + ${(t.total_refill_amount||0).toFixed(2)}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Setup + Create Stocker */}
            <div className="grid grid-cols-2 gap-3">
              <div className="glass-panel p-4">
                <p className="text-zinc-400 text-xs font-bold uppercase mb-2">Configurer</p>
                <div className="flex gap-2">
                  <input type="number" min="1" max="30" value={hookahSetupCount} onChange={(e) => setHookahSetupCount(parseInt(e.target.value) || 15)}
                    className="w-16 bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-2 text-white text-sm" />
                  <button onClick={async () => {
                    await axios.post(`${API}/ceo/hookah/setup`, { country: hookahCountry, count: hookahSetupCount });
                    toast.success(`${hookahSetupCount} hookahs!`);
                    const r = await axios.get(`${API}/hookah/${hookahCountry}`);
                    setCeoHookahs(r.data);
                  }} className="flex-1 bg-violet-600 hover:bg-violet-700 text-white py-2 rounded-lg text-xs font-bold">
                    Setup
                  </button>
                </div>
              </div>
              <div className="glass-panel p-4">
                <p className="text-zinc-400 text-xs font-bold uppercase mb-2">Ajoute Stocker Hookah</p>
                <div className="space-y-2">
                  <input type="text" id="hookah-stocker-name" placeholder="Non stocker"
                    data-testid="ceo-hookah-stocker-name"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-white text-sm" />
                  <select id="hookah-stocker-country" data-testid="ceo-hookah-stocker-country"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-white text-sm">
                    <option value="haiti">🇭🇹 Haiti</option>
                    <option value="dr">🇩🇴 DR</option>
                    <option value="usa">🇺🇸 USA</option>
                  </select>
                  <button onClick={async () => {
                    const nameEl = document.getElementById('hookah-stocker-name');
                    const countryEl = document.getElementById('hookah-stocker-country');
                    const n = nameEl?.value?.trim();
                    const c = countryEl?.value || 'haiti';
                    if (!n) { toast.error("Antre yon non"); return; }
                    const r = await axios.post(`${API}/ceo/create-hookah-stocker`, { name: n, country: c });
                    toast.success(`Stocker kreye! Kòd: ${r.data.code}`);
                    nameEl.value = '';
                  }} data-testid="ceo-hookah-create-stocker-btn"
                    className="w-full bg-violet-600 hover:bg-violet-700 text-white py-2 rounded-lg text-xs font-bold">
                    ✓ Créer Code
                  </button>
                </div>
              </div>
            </div>

            {/* History */}
            <div className="glass-panel p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-zinc-400 text-xs font-bold uppercase">Historique</p>
                <button onClick={async () => { const r = await axios.get(`${API}/hookah/${hookahCountry}/history`); setHookahHistory(r.data); }}
                  className="text-violet-400 text-xs hover:text-violet-300">Charger</button>
              </div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {hookahHistory.slice(0, 20).map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-zinc-800/50 p-2 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-violet-400 font-bold">#{s.hookah_number}</span>
                      <span className="text-white">{s.client_name}</span>
                      {s.event && <span className="text-zinc-500">({s.event})</span>}
                    </div>
                    <span className="text-zinc-500">{new Date(s.start_time).toLocaleDateString('fr-FR')}</span>
                  </div>
                ))}
                {hookahHistory.length === 0 && <p className="text-zinc-600 text-xs text-center py-4">Cliquez "Charger" pour voir l'historique</p>}
              </div>
            </div>
          </div>
        )}

        {/* ========== EDIT STOCKER / NM SETTINGS TAB ========== */}
        {activeTab === "edit-stocker" && (
          <div className="space-y-4" data-testid="edit-stocker-tab">
            <div className="glass-panel p-6">
              <h3 className="text-white font-bold mb-2 flex items-center gap-2" style={{fontFamily: 'var(--font-heading)'}}>
                <Settings className="w-5 h-5 text-emerald-400" /> Paramèt Konplè — Modifye Tout Detay
              </h3>
              <p className="text-zinc-500 text-sm mb-4">Chwazi yon Stocker oswa Network Marketer epi modifye TOUT detay li: non, vape, kantite, vant, ets.</p>

              {/* Emergency: Unsuspend All */}
              <div className="bg-amber-950/30 border border-amber-700/50 rounded-xl p-3 mb-4 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="text-amber-300 text-xs font-semibold">⚠️ Si gen kontre ki sispann/bani san ou pa mande, klike isit pou reaktive yo tout an menm tan:</p>
                </div>
                <button
                  onClick={async () => {
                    if (!window.confirm('Reaktive TOUT kontre ki sispann oswa bani? Sa ap aplike pou tout stockers ak NMs.')) return;
                    try {
                      const r = await axios.post(`${API}/ceo/unsuspend-all`);
                      toast.success(`Reaktive ${r.data.stockers_unblocked} stockers + ${r.data.nms_unblocked} NMs`);
                      fetchData();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Erè');
                    }
                  }}
                  data-testid="unsuspend-all-btn"
                  className="bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs px-3 py-2 rounded-lg flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" />
                  Reaktive TOUT Kontre
                </button>
              </div>

              {/* Type selector */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => { setEditTargetType('stocker'); setEditTargetId(''); setEditForm(null); }}
                  data-testid="edit-type-stocker"
                  className={`px-4 py-2 rounded-xl text-sm font-semibold ${editTargetType === 'stocker' ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-zinc-400'}`}>
                  📦 Stockers
                </button>
                <button
                  onClick={() => { setEditTargetType('nm'); setEditTargetId(''); setEditForm(null); }}
                  data-testid="edit-type-nm"
                  className={`px-4 py-2 rounded-xl text-sm font-semibold ${editTargetType === 'nm' ? 'bg-purple-600 text-white' : 'bg-zinc-800 text-zinc-400'}`}>
                  👔 Network Marketers
                </button>
              </div>

              {/* Search & select */}
              <input
                type="text"
                placeholder="Chèche pa non..."
                value={editSearch}
                onChange={(e) => setEditSearch(e.target.value)}
                data-testid="edit-search-input"
                className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-2 text-white text-sm mb-3"
              />

              {/* HISTORY button — appears when a user is selected */}
              {editTargetId && editForm && (
                <div className="flex items-center gap-2 mb-3 bg-emerald-950/30 border border-emerald-700/40 rounded-xl p-3">
                  <div className="flex-1">
                    <p className="text-emerald-300 text-xs font-bold uppercase">📜 Istori Konplè</p>
                    <p className="text-zinc-400 text-[11px]">Wè tout mwa yo, kantite vann, revni, ak dat kontwòl peye pou {editForm.name}.</p>
                  </div>
                  <button
                    onClick={() => setShowEditUserHistory(true)}
                    data-testid="edit-user-history-btn"
                    className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold px-4 py-2 rounded-lg">
                    Louvri Istori
                  </button>
                </div>
              )}

              {/* FORCE KONTWOL button — always available; even if user didn't do anything CEO can force-resolve */}
              {editTargetId && editForm && (
                <div className="flex items-center gap-2 mb-3 bg-red-950/30 border border-red-700/40 rounded-xl p-3">
                  <div className="flex-1">
                    <p className="text-red-300 text-xs font-bold uppercase">🔴 Rezoud Kontwòl {editForm.previous_cycle_pending ? "(sik pase POKO fèt)" : ""}</p>
                    <p className="text-zinc-400 text-[11px]">Fose kontwòl fèt pou {editForm.name} — sa efase `previous_cycle_pending` san CEO bezwen peye.</p>
                  </div>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Fose kontwòl fèt pou ${editForm.name}? (san bay peman)`)) return;
                      try {
                        const ep = editTargetType === 'nm'
                          ? `${API}/ceo/network-marketers/${editTargetId}/mark-controlled`
                          : `${API}/ceo/stockers/${editTargetId}/mark-controlled`;
                        await axios.post(ep);
                        toast.success("✓ Kontwòl fose fèt.");
                        setEditForm({...editForm, previous_cycle_pending: false});
                        fetchData();
                      } catch (e) { toast.error(e.response?.data?.detail || "Erè"); }
                    }}
                    data-testid="edit-force-kontwol-btn"
                    className="bg-red-600 hover:bg-red-500 text-white text-xs font-bold px-4 py-2 rounded-lg">
                    ✓ Effectuer Kontwòl
                  </button>
                </div>
              )}

              <div className="max-h-48 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                {(editTargetType === 'stocker' ? stockers : networkMarketers)
                  .filter(u => u.name.toLowerCase().includes(editSearch.toLowerCase()))
                  .map(u => (
                    <button
                      key={u.id}
                      onClick={() => {
                        setEditTargetId(u.id);
                        setEditForm({
                          name: u.name || '',
                          country: u.country || 'haiti',
                          city: u.city || '',
                          current_stock: u.current_stock || 0,
                          initial_stock: u.initial_stock || 0,
                          total_sold: u.total_sold || 0,
                          total_revenue: u.total_revenue || 0,
                          total_bonus: u.total_bonus || 0,
                          cycle_total_sold: u.cycle_total_sold || 0,
                          // Carryover stock from previous cycle
                          carryover_from_previous_cycle: u.carryover_from_previous_cycle || 0,
                          // Lifetime total - read-only reference
                          _lifetime_total_sold: u.total_sold || 0,
                          // Cycle sold = initial - current (auto-computed)
                          _cycle_sold_computed: Math.max(0, (u.initial_stock || 0) - (u.current_stock || 0)),
                          commission_rate: u.commission_rate || 0,
                          start_date: (u.start_date || '').substring(0, 10),
                          end_date: (u.end_date || '').substring(0, 10),
                          suspended: !!u.suspended,
                          is_banned: !!u.is_banned,
                          vape_models: (u.vape_models || []).map(m => ({ name: m.name || '', quantity: m.quantity || 0, price: m.price || 0, sold: m.sold || 0 })),
                          // Snapshot original to detect changes — used to AVOID resetting fields when user only edits the date
                          _orig_vape_models_json: JSON.stringify((u.vape_models || []).map(m => ({ name: m.name || '', quantity: m.quantity || 0, price: m.price || 0, sold: m.sold || 0 }))),
                          _orig_initial_stock: u.initial_stock || 0,
                          _orig_current_stock: u.current_stock || 0,
                        });
                      }}
                      data-testid={`edit-select-${u.id}`}
                      className={`text-left px-3 py-2 rounded-xl text-xs border ${editTargetId === u.id ? 'bg-emerald-900/40 border-emerald-500 text-white' : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700'}`}>
                      <div className="font-semibold">{u.name}</div>
                      <div className="text-[10px] text-zinc-500">{(u.country || '').toUpperCase()} • Stock: {u.current_stock || 0} • Vant Sik: {Math.max(0, (u.initial_stock || 0) - (u.current_stock || 0))}</div>
                    </button>
                  ))}
              </div>

              {/* Edit form - MODAL OVERLAY */}
              {editForm && editTargetId && (
                <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="edit-modal">
                  <div className="bg-zinc-900 rounded-2xl p-6 border-2 border-emerald-500 shadow-2xl w-full max-w-3xl my-8 space-y-4">
                    <div className="flex items-center justify-between border-b border-zinc-700 pb-3">
                      <h4 className="text-white font-bold text-lg flex items-center gap-2">
                        <Settings className="w-5 h-5 text-emerald-400" />
                        Modifye: <span className="text-emerald-400">{editForm.name}</span>
                      </h4>
                      <button
                        onClick={() => { setEditForm(null); setEditTargetId(''); }}
                        data-testid="edit-modal-close"
                        className="bg-zinc-800 hover:bg-red-600 text-white rounded-full p-2">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-zinc-500 block mb-1">Non</label>
                      <input type="text" value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} data-testid="edit-name" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-500 block mb-1">Peyi</label>
                      <select value={editForm.country} onChange={(e) => setEditForm({...editForm, country: e.target.value})} data-testid="edit-country" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm">
                        <option value="haiti">Haiti</option>
                        <option value="dr">DR</option>
                        <option value="usa">USA</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-zinc-500 block mb-1">Vil</label>
                      <input type="text" value={editForm.city} onChange={(e) => setEditForm({...editForm, city: e.target.value})} data-testid="edit-city" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-500 block mb-1">Commission Rate (%)</label>
                      <input type="number" step="0.01" value={editForm.commission_rate} onChange={(e) => setEditForm({...editForm, commission_rate: parseFloat(e.target.value) || 0})} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-500 block mb-1">Stock Kounye a</label>
                      <input type="number" value={editForm.current_stock} onChange={(e) => setEditForm({...editForm, current_stock: parseInt(e.target.value) || 0})} data-testid="edit-current-stock" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-500 block mb-1">Stock Inisyal</label>
                      <input type="number" value={editForm.initial_stock} onChange={(e) => setEditForm({...editForm, initial_stock: parseInt(e.target.value) || 0})} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-500 block mb-1">Vant Sik Aktyèl <span className="text-emerald-500 text-[10px]">(auto = inisyal - restan)</span></label>
                      <input type="number" value={editForm._cycle_sold_computed || 0} disabled data-testid="edit-cycle-sold" className="w-full bg-emerald-950/30 border border-emerald-700 rounded-lg px-3 py-2 text-emerald-400 text-sm font-bold" title="Sa a se nimewo vant pou sik aktyèl la (pa lifetime)." />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-500 block mb-1">Vant Lifetime <span className="text-zinc-600 text-[10px]">(istorik total)</span></label>
                      <input type="number" value={editForm._lifetime_total_sold || 0} disabled className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-zinc-500 text-sm" title="Total vant depi toujou — pa kòrèk pou sik aktyèl." />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-500 block mb-1">Revni Total ($)</label>
                      <input type="number" step="0.01" value={editForm.total_revenue} onChange={(e) => setEditForm({...editForm, total_revenue: parseFloat(e.target.value) || 0})} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-500 block mb-1">Bonis Total ($)</label>
                      <input type="number" step="0.01" value={editForm.total_bonus} onChange={(e) => setEditForm({...editForm, total_bonus: parseFloat(e.target.value) || 0})} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-500 block mb-1">Dat Kòmansman</label>
                      <input type="date" value={editForm.start_date} onChange={(e) => setEditForm({...editForm, start_date: e.target.value})} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-500 block mb-1">Dat Fen Sik</label>
                      <input type="date" value={editForm.end_date} onChange={(e) => setEditForm({...editForm, end_date: e.target.value})} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                    </div>
                    {editTargetType === 'stocker' && (
                      <div className="md:col-span-2">
                        <label className="text-xs text-zinc-500 block mb-1">👔 Manager Asiyen</label>
                        <select value={editForm.manager_id || ""} onChange={(e) => setEditForm({...editForm, manager_id: e.target.value || null})}
                          data-testid="edit-manager-id"
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm">
                          <option value="">— Pa gen Manager asiyen —</option>
                          {managers.filter(mg => mg.country === editForm.country).map(mg => (
                            <option key={mg.id} value={mg.id}>{mg.name} ({countryFlags[mg.country]} {mg.country})</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* SUSPEND / BAN BIG BUTTONS */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Suspend button */}
                    {editForm.suspended ? (
                      <button
                        onClick={() => setEditForm({...editForm, suspended: false})}
                        data-testid="edit-unsuspend-btn"
                        className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 border-2 border-emerald-400">
                        <ShieldCheck className="w-5 h-5" />
                        ✅ AKTIVE — Klike pou Aktive Konpte a
                      </button>
                    ) : (
                      <button
                        onClick={() => setEditForm({...editForm, suspended: true})}
                        data-testid="edit-suspend-btn"
                        className="bg-zinc-800 hover:bg-orange-600 text-zinc-300 hover:text-white font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 border-2 border-zinc-700 hover:border-orange-500">
                        <Ban className="w-5 h-5" />
                        🚫 Sispann Konpte a
                      </button>
                    )}
                    {/* Ban button */}
                    {editForm.is_banned ? (
                      <button
                        onClick={() => setEditForm({...editForm, is_banned: false})}
                        data-testid="edit-unban-btn"
                        className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 border-2 border-blue-400">
                        <Check className="w-5 h-5" />
                        Retire Bani — Konpte Bani Kounye a
                      </button>
                    ) : (
                      <button
                        onClick={() => setEditForm({...editForm, is_banned: true})}
                        data-testid="edit-ban-btn"
                        className="bg-zinc-800 hover:bg-red-700 text-zinc-300 hover:text-white font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 border-2 border-zinc-700 hover:border-red-500">
                        <Ban className="w-5 h-5" />
                        ⛔ Bani Konpte a
                      </button>
                    )}
                  </div>

                  {/* DEMOTE NM TO STOCKER (only when editing NM) */}
                  {editTargetType === 'nm' && (
                    <div className="bg-orange-950/40 border border-orange-700/50 rounded-xl p-4">
                      <p className="text-orange-300 text-xs mb-2">⚠️ Si Network Marketer sa a pa rive nan pèfòmans, ou ka konvèti l an Stocker Nòmal:</p>
                      <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={async () => {
                          const reason = window.prompt('Rezon pou demote a (egz: Pa rive nan objektif ventes):', 'Pèfòmans insuffisante');
                          if (!reason) return;
                          if (!window.confirm(`Konfime: konvèti ${editForm.name} an Stocker Nòmal?`)) return;
                          try {
                            await axios.post(`${API}/ceo/network-marketers/${editTargetId}/demote-to-stocker`, { reason });
                            toast.success(`${editForm.name} konvèti an Stocker Nòmal ak siksè!`);
                            fetchData();
                            setEditForm(null);
                            setEditTargetId('');
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Erè pandan demote');
                          }
                        }}
                        data-testid="demote-nm-btn"
                        className="bg-orange-700 hover:bg-orange-600 text-white font-bold py-2 px-4 rounded-lg flex items-center gap-2 text-sm">
                        <UserPlus className="w-4 h-4" />
                        Konvèti NM an Stocker Nòmal (Ansyen)
                      </button>
                      <button
                        onClick={async () => {
                          const pctStr = window.prompt('Konbyen % komisyon pou nouvo Stocker la?', '12.5');
                          const pct = parseFloat(pctStr);
                          if (!pct || pct <= 0) return;
                          if (!window.confirm(`Konvèti ${editForm.name} an STOCKER ak ${pct}% komisyon?\nMenm kòd login, sub-stockers ap retire.`)) return;
                          try {
                            await axios.post(`${API}/ceo/convert-role/${editTargetId}`, { target_role: 'stocker', percentage: pct });
                            toast.success(`${editForm.name} konvèti an Stocker!`);
                            fetchData();
                            setEditForm(null);
                            setEditTargetId('');
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Erè konvèsyon');
                          }
                        }}
                        data-testid="convert-nm-to-stocker-btn"
                        className="bg-amber-600 hover:bg-amber-500 text-white font-bold py-2 px-4 rounded-lg flex items-center gap-2 text-sm">
                        ⬇️ Konvèti an Stocker (Nouvo API)
                      </button>
                      </div>
                    </div>
                  )}

                  {/* CONVERT STOCKER → NM (only when editing Stocker) */}
                  {editTargetType === 'stocker' && (
                    <div className="bg-violet-950/40 border border-violet-700/50 rounded-xl p-4">
                      <p className="text-violet-300 text-xs mb-2">🚀 Pwomosyon: konvèti Stocker sa a an Network Marketer (menm kòd login, istwa rete an plas):</p>
                      <button
                        onClick={async () => {
                          const pctStr = window.prompt('Konbyen % komisyon pou nouvo NM la? (Default: 15%)', '15');
                          const pct = parseFloat(pctStr);
                          if (!pct || pct <= 0) return;
                          if (!window.confirm(`Konvèti ${editForm.name} an NETWORK MARKETER ak ${pct}% komisyon?\nLi ap konsève menm kòd login.`)) return;
                          try {
                            await axios.post(`${API}/ceo/convert-role/${editTargetId}`, { target_role: 'network_marketer', percentage: pct });
                            toast.success(`🎉 ${editForm.name} PROMU an Network Marketer!`);
                            fetchData();
                            setEditForm(null);
                            setEditTargetId('');
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Erè konvèsyon');
                          }
                        }}
                        data-testid="convert-stocker-to-nm-btn"
                        className="bg-violet-700 hover:bg-violet-600 text-white font-bold py-2 px-4 rounded-lg flex items-center gap-2 text-sm">
                        <Network className="w-4 h-4" />
                        ⬆️ Konvèti Stocker → Network Marketer
                      </button>
                    </div>
                  )}

                  {/* Vape Models Editor with PER-MODEL SALES */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm text-zinc-300 font-semibold">🛍️ Modèl Vape & Vant pa Modèl</label>
                      <button
                        onClick={() => setEditForm({...editForm, vape_models: [...(editForm.vape_models || []), { name: '', quantity: 0, price: 0, sold: 0 }]})}
                        data-testid="edit-add-model"
                        className="bg-emerald-700 hover:bg-emerald-600 text-white text-xs px-3 py-1 rounded-lg flex items-center gap-1">
                        <Plus className="w-3 h-3" /> Ajoute Modèl
                      </button>
                    </div>
                    {/* Header row */}
                    <div className="grid grid-cols-12 gap-2 text-[10px] text-zinc-500 font-bold uppercase mb-1 px-2">
                      <div className="col-span-4">Non Modèl</div>
                      <div className="col-span-2 text-center">Inisyal</div>
                      <div className="col-span-2 text-center">Vendu</div>
                      <div className="col-span-2 text-center">Pri $</div>
                      <div className="col-span-2 text-center">Aksyon</div>
                    </div>
                    <div className="space-y-2">
                      {(editForm.vape_models || []).map((m, idx) => {
                        const restan = (m.quantity || 0) - (m.sold || 0);
                        return (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-zinc-800/40 p-2 rounded-lg">
                          <div className="col-span-4">
                            <ProductAutocomplete value={m.name} placeholder="Non vape" testid={`edit-model-name-${idx}`}
                              className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-white text-xs"
                              onChange={(v) => { const nv = [...editForm.vape_models]; nv[idx] = { ...nv[idx], name: v }; setEditForm({...editForm, vape_models: nv}); }}
                              onPick={(pr) => { const nv = [...editForm.vape_models]; nv[idx] = { ...nv[idx], name: pr.name, price: nv[idx].price || pr.price || 0 }; setEditForm({...editForm, vape_models: nv}); }} />
                            <ModelPhotoConfirm name={m.name} />
                          </div>
                          <input
                            type="number"
                            placeholder="Qty"
                            value={m.quantity}
                            onChange={(e) => {
                              const nv = [...editForm.vape_models];
                              nv[idx] = { ...nv[idx], quantity: parseInt(e.target.value) || 0 };
                              setEditForm({...editForm, vape_models: nv});
                            }}
                            data-testid={`edit-model-qty-${idx}`}
                            className="col-span-2 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-white text-xs text-center"
                          />
                          <input
                            type="number"
                            placeholder="Vendu"
                            value={m.sold || 0}
                            min="0"
                            max={m.quantity || 0}
                            onChange={(e) => {
                              const nv = [...editForm.vape_models];
                              nv[idx] = { ...nv[idx], sold: parseInt(e.target.value) || 0 };
                              setEditForm({...editForm, vape_models: nv});
                            }}
                            data-testid={`edit-model-sold-${idx}`}
                            className={`col-span-2 bg-zinc-900 border ${(m.sold || 0) > (m.quantity || 0) ? 'border-red-500' : 'border-emerald-700'} rounded px-2 py-1 text-emerald-300 text-xs text-center font-bold`}
                          />
                          <input
                            type="number"
                            step="0.01"
                            placeholder="Pri $"
                            value={m.price}
                            onChange={(e) => {
                              const nv = [...editForm.vape_models];
                              nv[idx] = { ...nv[idx], price: parseFloat(e.target.value) || 0 };
                              setEditForm({...editForm, vape_models: nv});
                            }}
                            data-testid={`edit-model-price-${idx}`}
                            className="col-span-2 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-white text-xs text-center"
                          />
                          <div className="col-span-1 text-[10px] text-zinc-400 text-center">
                            <div>Restan</div>
                            <div className={restan < 0 ? 'text-red-400 font-bold' : 'text-emerald-400 font-bold'}>{restan}</div>
                          </div>
                          <button
                            onClick={() => {
                              const nv = editForm.vape_models.filter((_, i) => i !== idx);
                              setEditForm({...editForm, vape_models: nv});
                            }}
                            data-testid={`edit-model-remove-${idx}`}
                            className="col-span-1 bg-red-700 hover:bg-red-600 text-white p-1 rounded flex items-center justify-center">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      );})}
                      {(editForm.vape_models || []).length === 0 && (
                        <p className="text-zinc-600 text-xs text-center py-3">Pa gen modèl. Klike "Ajoute Modèl" pou kòmanse.</p>
                      )}
                      {/* Totals summary */}
                      {(editForm.vape_models || []).length > 0 && (
                        <div className="mt-3 bg-emerald-900/20 border border-emerald-700/40 rounded-lg p-3 grid grid-cols-3 gap-3 text-center text-xs">
                          <div>
                            <div className="text-zinc-500">Stock Total</div>
                            <div className="text-white font-bold text-lg">{(editForm.vape_models || []).reduce((s,m) => s + (m.quantity||0), 0)}</div>
                          </div>
                          <div>
                            <div className="text-zinc-500">Total Vendu</div>
                            <div className="text-emerald-400 font-bold text-lg">{(editForm.vape_models || []).reduce((s,m) => s + (m.sold||0), 0)}</div>
                          </div>
                          <div>
                            <div className="text-zinc-500">Revni Kalkile</div>
                            <div className="text-yellow-400 font-bold text-lg">${(editForm.vape_models || []).reduce((s,m) => s + ((m.sold||0)*(m.price||0)), 0).toFixed(2)}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* CARRYOVER ADD - past cycle leftover (stocker + NM) */}
                  {(editTargetType === 'stocker' || editTargetType === 'nm') && (
                    <div className="bg-red-950/30 border border-red-700/50 rounded-xl p-4 space-y-2">
                      <div>
                        <p className="text-red-300 text-xs font-semibold mb-1">📌 Ajoute Stock Sik Pase</p>
                        <p className="text-zinc-500 text-[11px]">Si gen vape ki te rete nan sik ki te pase a ki poko anrejistre, ajoute yo isi a. Yo ap parèt <span className="text-red-400 font-bold">an wouj</span> sou dashboard stocker la, separe de nouvo stock.</p>
                        {editForm.carryover_from_previous_cycle > 0 && (
                          <p className="text-red-400 text-xs mt-1 font-bold">Aktyèlman: {editForm.carryover_from_previous_cycle} vape carryover</p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <input
                          type="text"
                          placeholder="Non vape (ex: Geek Bar)"
                          id={`carryover-name-${editTargetId}`}
                          data-testid="carryover-name-input"
                          className="bg-zinc-900 border border-red-700/50 rounded-lg px-3 py-2 text-white text-sm"
                        />
                        <div className="grid grid-cols-3 gap-2">
                          <input
                            type="number"
                            min="1"
                            placeholder="Kantite"
                            id={`carryover-qty-${editTargetId}`}
                            data-testid="carryover-qty-input"
                            className="bg-zinc-900 border border-red-700/50 rounded-lg px-3 py-2 text-white text-sm"
                          />
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="Pri $"
                            id={`carryover-price-${editTargetId}`}
                            data-testid="carryover-price-input"
                            className="bg-zinc-900 border border-red-700/50 rounded-lg px-3 py-2 text-white text-sm"
                          />
                          <button
                            onClick={async () => {
                              const qi = document.getElementById(`carryover-qty-${editTargetId}`);
                              const ni = document.getElementById(`carryover-name-${editTargetId}`);
                              const pi = document.getElementById(`carryover-price-${editTargetId}`);
                              const qty = parseInt(qi?.value || '0');
                              const vape_name = (ni?.value || '').trim();
                              const price = parseFloat(pi?.value || '0');
                              if (qty < 1) { toast.error('Kantite dwe omwen 1'); return; }
                              if (!window.confirm(`Konfime ajoute ${qty}${vape_name ? ' × '+vape_name : ' vape'}${price ? ' @ $'+price.toFixed(2) : ''} kòm carryover pou ${editForm.name}?`)) return;
                              try {
                                const r = await axios.post(`${API}/stockers/${editTargetId}/add-carryover`, { quantity: qty, vape_name, price });
                                toast.success(`Ajoute ${qty} carryover. Total: ${r.data.carryover}`);
                                setEditForm({...editForm, carryover_from_previous_cycle: r.data.carryover});
                                if (qi) qi.value = '';
                                if (ni) ni.value = '';
                                if (pi) pi.value = '';
                                fetchData();
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'Erè');
                              }
                            }}
                            data-testid="carryover-add-btn"
                            className="bg-red-700 hover:bg-red-600 text-white font-bold px-3 py-2 rounded-lg flex items-center justify-center gap-1 text-xs">
                            <Plus className="w-4 h-4" />
                            Ajoute
                          </button>
                        </div>
                        <p className="text-zinc-500 text-[10px]">Bay non vape la ak pri li — konsa stocker/NM ka vann li dirèkteman nan dashboard li.</p>
                      </div>

                      {/* ── SET / MODIFY carryover directly (override) ── */}
                      {editForm.carryover_from_previous_cycle >= 0 && (
                        <div className="mt-3 pt-3 border-t border-red-800/50">
                          <p className="text-amber-300 text-xs font-bold uppercase mb-1">✎ Modifye Kantite Carryover Egzak</p>
                          <p className="text-zinc-500 text-[10px] mb-2">Egzanp: Casty ap montre 20 men se sèlman 1 ki rete — mete 1 la epi peze SET.</p>
                          <div className="flex gap-2">
                            <input
                              type="number"
                              min="0"
                              defaultValue={editForm.carryover_from_previous_cycle || 0}
                              id={`carryover-set-${editTargetId}`}
                              data-testid="carryover-set-input"
                              className="flex-1 bg-zinc-900 border border-amber-700/50 rounded-lg px-3 py-2 text-white text-sm"
                              placeholder="Kantite egzak (ranplase)"
                            />
                            <button
                              onClick={async () => {
                                const el = document.getElementById(`carryover-set-${editTargetId}`);
                                const qty = parseInt(el?.value || '0');
                                if (qty < 0) { toast.error('Kantite pa ka negatif'); return; }
                                if (!window.confirm(`Ranplase carryover pou ${editForm.name} ak ${qty} vape? (Sa ap ajiste current_stock tou.)`)) return;
                                try {
                                  const r = await axios.post(`${API}/stockers/${editTargetId}/set-carryover`, { quantity: qty });
                                  toast.success(`Carryover mete a ${r.data.carryover}. Stock aktyèl: ${r.data.current_stock}`);
                                  setEditForm({...editForm, carryover_from_previous_cycle: r.data.carryover, current_stock: r.data.current_stock});
                                  fetchData();
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || 'Erè');
                                }
                              }}
                              data-testid="carryover-set-btn"
                              className="bg-amber-700 hover:bg-amber-600 text-white font-bold px-4 py-2 rounded-lg text-xs">
                              ✎ SET
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Modify Total Vapes Vendus (lifetime) */}
                      <div className="mt-3 pt-3 border-t border-zinc-800">
                        <p className="text-amber-300 text-xs font-bold uppercase mb-1">✏️ Modifye Total Vape Vann (lifetime depi debi)</p>
                        <p className="text-zinc-500 text-[10px] mb-2">Itil si done a deregle (egz: Christoo gen yon lifetime ki pa kòrèk). Sa pap touche stock sik aktyèl la.</p>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            min={0}
                            defaultValue={editForm._lifetime_total_sold || 0}
                            id={`lifetime-sold-${editTargetId}`}
                            data-testid="lifetime-sold-input"
                            className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                            placeholder="Nouvo total"
                          />
                          <button
                            onClick={async () => {
                              const input = document.getElementById(`lifetime-sold-${editTargetId}`);
                              const newTotal = parseInt(input?.value || "0");
                              if (newTotal < 0) { toast.error("Valè pa valab"); return; }
                              if (!window.confirm(`Konfime mete total lifetime vapes vann pou ${editForm.name} a: ${newTotal}?`)) return;
                              try {
                                await axios.post(`${API}/ceo/stockers/${editTargetId}/set-lifetime-sold`, { new_total_sold: newTotal });
                                toast.success(`Lifetime mete a ${newTotal}`);
                                setEditForm({...editForm, _lifetime_total_sold: newTotal, total_sold: newTotal});
                                fetchData();
                              } catch (e) { toast.error(e.response?.data?.detail || "Erè"); }
                            }}
                            data-testid="lifetime-sold-save-btn"
                            className="bg-amber-600 hover:bg-amber-500 text-white font-bold px-4 py-2 rounded-lg flex items-center gap-1 text-sm">
                            <Check className="w-4 h-4" /> Konfime
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Restore User Data Panel - repair stock/sold after wipe (e.g. Christoo, Anaika, Castymeily) */}
                  {editForm && editTargetId && (
                    <div className="mt-3 p-3 bg-red-950/40 border border-red-500/40 rounded-xl" data-testid="restore-stock-panel">
                      <p className="text-red-300 text-xs font-bold uppercase mb-1">🛠️ Restore Done Pèdi (repare wipe aksidantèl)</p>
                      <p className="text-zinc-500 text-[10px] mb-2">
                        Itilize sa a pou ajiste manyèlman current_stock, initial_stock, total_sold, oswa revni pou nenpòt itilizatè (egz: Christoo, Anaika, Castymeily) apre pèt done. Sa ap kreye yon antre nan restore_stock_log.
                      </p>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <label className="text-zinc-400 text-[11px]">
                          Current Stock <span className="text-zinc-500">(aktyèl: {editForm.current_stock ?? 0})</span>
                          <input type="number" min={0} id={`restore-current-${editTargetId}`} defaultValue={editForm.current_stock ?? 0}
                            className="w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-white text-sm" data-testid="restore-current-input" />
                        </label>
                        <label className="text-zinc-400 text-[11px]">
                          Initial Stock <span className="text-zinc-500">(aktyèl: {editForm.initial_stock ?? 0})</span>
                          <input type="number" min={0} id={`restore-initial-${editTargetId}`} defaultValue={editForm.initial_stock ?? 0}
                            className="w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-white text-sm" data-testid="restore-initial-input" />
                        </label>
                        <label className="text-zinc-400 text-[11px]">
                          Total Sold (cycle) <span className="text-zinc-500">(aktyèl: {editForm.total_sold ?? 0})</span>
                          <input type="number" min={0} id={`restore-sold-${editTargetId}`} defaultValue={editForm.total_sold ?? 0}
                            placeholder="Vann sik aktyèl"
                            className="w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-white text-sm" data-testid="restore-sold-input" />
                        </label>
                        <label className="text-zinc-400 text-[11px]">
                          Total Revenue <span className="text-zinc-500">(aktyèl: ${(editForm.total_revenue ?? 0).toFixed ? editForm.total_revenue.toFixed(2) : editForm.total_revenue})</span>
                          <input type="number" min={0} step="0.01" id={`restore-revenue-${editTargetId}`} defaultValue={editForm.total_revenue ?? 0}
                            className="w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-white text-sm" data-testid="restore-revenue-input" />
                        </label>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <input type="checkbox" id={`restore-clear-pending-${editTargetId}`} className="accent-emerald-500" />
                        <label htmlFor={`restore-clear-pending-${editTargetId}`} className="text-zinc-400 text-[11px]">Klarifye "Sik anvan an poko fèmen" (mete kontwol_effectue=true)</label>
                      </div>
                      <button
                        onClick={async () => {
                          const curr = document.getElementById(`restore-current-${editTargetId}`)?.value;
                          const init = document.getElementById(`restore-initial-${editTargetId}`)?.value;
                          const sold = document.getElementById(`restore-sold-${editTargetId}`)?.value;
                          const rev = document.getElementById(`restore-revenue-${editTargetId}`)?.value;
                          const clearPending = document.getElementById(`restore-clear-pending-${editTargetId}`)?.checked;
                          const payload = {};
                          if (curr !== "" && curr != null) payload.current_stock = parseInt(curr);
                          if (init !== "" && init != null) payload.initial_stock = parseInt(init);
                          if (sold !== "" && sold != null) payload.total_sold = parseInt(sold);
                          if (rev !== "" && rev != null) payload.total_revenue = parseFloat(rev);
                          if (clearPending) payload.clear_previous_pending = true;
                          if (Object.keys(payload).length === 0) { toast.error("Anyen pou aplike"); return; }
                          if (!window.confirm(`Restore ${editForm.name} ak: ${JSON.stringify(payload)} ?`)) return;
                          try {
                            const r = await axios.post(`${API}/ceo/restore-user-stock/${editTargetId}`, payload);
                            // Update local editForm INSTANTLY so CEO sees new values reflected
                            setEditForm(prev => prev ? { ...prev, ...payload, is_active: true, suspended: false, is_banned: false, late_reason: null } : prev);
                            // Refresh dashboard data
                            await fetchData();
                            toast.success(`✅ Done restore! ${editForm.name} monte ak: ${Object.entries(payload).map(([k,v]) => `${k}=${v}`).join(', ')}. Frape "Fèmen" pou wè kart la sou dashboard.`, { duration: 6000 });
                            // Auto-close edit panel after 1.5s so CEO sees the refreshed dashboard card
                            setTimeout(() => {
                              setEditTargetId('');
                              setEditForm(null);
                            }, 1500);
                          } catch (e) { toast.error(e.response?.data?.detail || "Erè restore"); }
                        }}
                        data-testid="restore-stock-btn"
                        className="w-full bg-red-600 hover:bg-red-500 text-white font-bold py-2 rounded-lg text-sm flex items-center justify-center gap-2">
                        🛠️ Aplike Restore
                      </button>
                    </div>
                  )}

                  {/* Save / Cancel */}
                  <div className="flex gap-2 pt-2 border-t border-zinc-700">
                    <button
                      disabled={editSaving}
                      onClick={async () => {
                        setEditSaving(true);
                        try {
                          const payload = { ...editForm };
                          // Remove read-only computed + snapshot fields
                          delete payload._lifetime_total_sold;
                          delete payload._cycle_sold_computed;
                          delete payload._orig_vape_models_json;
                          delete payload._orig_initial_stock;
                          delete payload._orig_current_stock;
                          // Don't overwrite lifetime total_sold from this form
                          delete payload.total_sold;
                          // ONLY recalculate stock fields if vape_models was actually modified
                          const currentModelsJson = JSON.stringify(payload.vape_models || []);
                          const modelsChanged = currentModelsJson !== (editForm._orig_vape_models_json || "[]");
                          if (modelsChanged && payload.vape_models && payload.vape_models.length > 0) {
                            const cycleSoldSum = (payload.vape_models || []).reduce((s, m) => s + (m.sold || 0), 0);
                            const totalQty = (payload.vape_models || []).reduce((s, m) => s + (m.quantity || 0), 0);
                            payload.cycle_total_sold = cycleSoldSum;
                            payload.initial_stock = totalQty;
                            payload.current_stock = Math.max(0, totalQty - cycleSoldSum);
                          } else {
                            // Date-only / metadata-only update: DON'T send vape_models so the backend
                            // recompute path doesn't reset initial_stock/current_stock.
                            delete payload.vape_models;
                            // Preserve original stock values regardless of any stale form values
                            payload.initial_stock = editForm._orig_initial_stock;
                            payload.current_stock = editForm._orig_current_stock;
                          }
                          // Convert dates to ISO if not empty
                          if (payload.start_date) payload.start_date = new Date(payload.start_date).toISOString();
                          if (payload.end_date) payload.end_date = new Date(payload.end_date).toISOString();
                          const url = editTargetType === 'stocker' ? `${API}/stockers/${editTargetId}` : `${API}/ceo/network-marketers/${editTargetId}`;
                          await axios.put(url, payload);
                          toast.success('Detay yo modifye ak siksè!');
                          fetchData();
                          setEditForm(null);
                          setEditTargetId('');
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Erè pandan modifikasyon');
                        } finally {
                          setEditSaving(false);
                        }
                      }}
                      data-testid="edit-save-btn"
                      className="flex-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2">
                      {editSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      Anrejistre Modifikasyon
                    </button>
                    <button
                      onClick={() => { setEditForm(null); setEditTargetId(''); }}
                      data-testid="edit-cancel-btn"
                      className="bg-zinc-700 hover:bg-zinc-600 text-white px-4 rounded-xl">
                      Anile
                    </button>
                  </div>

                  {/* Action utilitaires CEO */}
                  {(editTargetType === 'stocker' || editTargetType === 'nm') && editTargetId && (
                    <div className="mt-3 pt-3 border-t border-zinc-800 space-y-2">
                      {/* New cycle with fresh stock */}
                      <div className="bg-blue-900/20 rounded-xl p-3 border border-blue-700/40">
                        <p className="text-blue-300 text-xs font-bold uppercase mb-2">🔄 Kòmanse Nouvo Sik ak Nouvo Stock</p>
                        <p className="text-zinc-500 text-[10px] mb-2">Ansyen stock ki rete a ap pase nan CARRYOVER (an wouj). Antre kantite nouvo vapes pou nouvo sik la.</p>
                        <div className="flex gap-2">
                          <input type="number" min={1} placeholder="Ex: 17" id={`new-stock-${editTargetId}`}
                            data-testid="new-cycle-stock-input"
                            className="flex-1 bg-zinc-900 border border-blue-700 rounded-lg px-3 py-2 text-white text-sm" />
                          <button onClick={async () => {
                            const newStock = parseInt(document.getElementById(`new-stock-${editTargetId}`).value) || 0;
                            if (newStock <= 0) { toast.error("Antre yon kantite valab"); return; }
                            if (!window.confirm(`Kòmanse nouvo sik ak ${newStock} nouvo vapes? Ansyen stock ki rete ap parèt an wouj kòm carryover.`)) return;
                            try {
                              const url = editTargetType === 'nm'
                                ? `${API}/ceo/cycle-reset/nm/${editTargetId}`
                                : `${API}/ceo/cycle-reset/stocker/${editTargetId}`;
                              const r = await axios.post(url, { new_stock: newStock });
                              toast.success(r.data.message);
                              fetchData();
                              setEditForm(null);
                              setEditTargetId('');
                            } catch (e) { toast.error(e.response?.data?.detail || "Erè"); }
                          }} data-testid="new-cycle-btn"
                            className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold px-4 rounded-lg">
                            ▶ Kòmanse
                          </button>
                        </div>
                      </div>

                      {/* Existing actions */}
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={async () => {
                            if (editTargetType !== 'stocker') { toast.error("Repare Revni sèlman pou Stocker"); return; }
                            if (!window.confirm("Recalkile total_revenue + vape_models[].sold dapre istorik vant aktyèl yo?")) return;
                            try {
                              const r = await axios.post(`${API}/ceo/stockers/${editTargetId}/fix-revenue`);
                              toast.success(`Repare ✔ Nouvo revni: $${r.data.new_total_revenue.toFixed(2)} (${r.data.new_cycle_sold} vapes vann)`);
                              fetchData();
                              setEditForm(null);
                              setEditTargetId('');
                            } catch (e) { toast.error(e.response?.data?.detail || "Erè"); }
                          }}
                          data-testid="fix-revenue-btn"
                          className="bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold py-2 rounded-lg">
                          🔧 Repare Revni
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`🔄 Aktive yon nouvo cycle pou ${editForm.name}?\nDat fen-sik ap chanje → +30 jou.\nStock ki rete a ap tranfere kòm "carryover" (an WOUJ).\nMete pwòp stock fresh nan paramèt si bezwen.`)) return;
                            try {
                              const url = editTargetType === 'nm'
                                ? `${API}/ceo/network-marketers/${editTargetId}/activate-new-cycle`
                                : `${API}/ceo/stockers/${editTargetId}/activate-new-cycle`;
                              const r = await axios.post(url, {});
                              toast.success(`Nouvo sik aktive! Carryover (an wouj): ${r.data.carryover || 0} vape`);
                              fetchData();
                              setEditForm(null);
                              setEditTargetId('');
                            } catch (e) { toast.error(e.response?.data?.detail || "Erè"); }
                          }}
                          data-testid="activate-new-cycle-btn"
                          className="bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold py-2 rounded-lg">
                          🔄 Aktive Nouvo Cycle
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm("⚠️ Sipprime itilizatè sa pou toujou? Aksyon sa pa ka anile.")) return;
                            try {
                              const url = editTargetType === 'nm'
                                ? `${API}/network-marketers/${editTargetId}`
                                : `${API}/ceo/delete-stocker/${editTargetId}`;
                              await axios.delete(url);
                              toast.success("Itilizatè sipprime");
                              fetchData();
                              setEditForm(null);
                              setEditTargetId('');
                            } catch (e) { toast.error("Erè"); }
                          }}
                          data-testid="delete-stocker-btn"
                          className="bg-red-700 hover:bg-red-600 text-white text-xs font-bold py-2 rounded-lg">
                          🗑️ Sipprime
                        </button>
                      </div>
                    </div>
                  )}
                  </div>
                </div>
              )}

              {!editForm && (
                <div className="text-center py-8 text-zinc-600 text-sm">
                  Chwazi yon {editTargetType === 'stocker' ? 'Stocker' : 'Network Marketer'} anwo a pou modifye detay li.
                </div>
              )}
            </div>
            {/* Full history modal for the selected user (persistent monthly breakdown) */}
            {showEditUserHistory && editTargetId && (
              <HistoryModal userId={editTargetId} onClose={() => setShowEditUserHistory(false)} />
            )}
          </div>
        )}

        {/* ========== FOTO PWOFIL TAB — CEO view users with no photo ========== */}
        {activeTab === "photo-profil" && (
          <PhotoProfilePanel />
        )}

        {/* ========== INV MANAGEMENT TAB ========== */}
        {activeTab === "inv-mgmt" && (
          <div className="space-y-4" data-testid="inv-mgmt-tab">
            <div className="glass-panel p-6">
              <h3 className="text-white font-bold mb-2 flex items-center gap-2" style={{fontFamily: 'var(--font-heading)'}}>
                <DollarSign className="w-5 h-5 text-emerald-400" /> Jesyon Investisseurs (INV)
              </h3>
              <p className="text-zinc-500 text-sm mb-4">Kreye kont INV, asiyen stockers ba yo, swiv pèfòmans yo.</p>

              {/* Create new INV form */}
              <div className="bg-zinc-900/60 rounded-xl p-4 mb-4 border border-emerald-700/30">
                <p className="text-emerald-300 text-xs font-bold mb-3">➕ KREYE NOUVO INV</p>
                <div className="grid md:grid-cols-6 gap-2">
                  <input type="text" placeholder="Non INV" value={newInv.name} onChange={(e) => setNewInv({...newInv, name: e.target.value})} data-testid="inv-new-name" className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                  <input type="text" placeholder="Kòd Sekrè" value={newInv.code} onChange={(e) => setNewInv({...newInv, code: e.target.value})} data-testid="inv-new-code" className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                  <select value={newInv.country} onChange={(e) => setNewInv({...newInv, country: e.target.value})} className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm">
                    <option value="haiti">Haiti</option>
                    <option value="dr">DR</option>
                    <option value="usa">USA</option>
                  </select>
                  <input type="number" placeholder="Envestiseman $" value={newInv.investment_amount} onChange={(e) => setNewInv({...newInv, investment_amount: parseFloat(e.target.value) || 0})} data-testid="inv-new-investment" className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                  <input type="number" placeholder="Stock Rezèv" value={newInv.reserve_stock || 0} onChange={(e) => setNewInv({...newInv, reserve_stock: parseInt(e.target.value) || 0})} data-testid="inv-new-reserve" className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                  <button onClick={async () => {
                    if (!newInv.name || !newInv.code) { toast.error("Non ak kòd obligatwa"); return; }
                    try {
                      await axios.post(`${API}/ceo/inv/create`, newInv);
                      toast.success(`INV ${newInv.name} kreye`);
                      setNewInv({ name: "", code: "", country: "haiti", investment_amount: 0, reserve_stock: 0, phone: "" });
                      fetchInvs();
                    } catch (e) { toast.error(e.response?.data?.detail || "Erè"); }
                  }} data-testid="inv-create-btn" className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-4 py-2 rounded-lg flex items-center gap-1 text-sm justify-center">
                    <Plus className="w-4 h-4" /> Kreye
                  </button>
                </div>
              </div>

              {/* List of INVs with their stockers */}
              <div className="space-y-3">
                {invs.map(inv => (
                  <div key={inv.id} className="bg-zinc-900/60 rounded-xl p-4 border border-zinc-800" data-testid={`inv-card-${inv.id}`}>
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <div>
                        <p className="text-white font-bold text-lg">{inv.name}</p>
                        <p className="text-zinc-500 text-xs">Kòd: {inv.code} • {(inv.country || '').toUpperCase()} • Envestiseman: ${(inv.investment_amount || 0).toFixed(2)} • Rezèv: <span className="text-cyan-400 font-bold">{inv.reserve_stock || 0}</span> • Code Secret: <span className={inv.has_secret_code ? "text-emerald-400" : "text-zinc-500"}>{inv.has_secret_code ? "●●●●" : "Pa kreye"}</span></p>
                      </div>
                      <div className="text-right">
                        <p className="text-emerald-400 font-bold">{inv.stocker_count} stockers</p>
                        <p className="text-yellow-400 text-xs">Revni: ${(inv.current_cycle_revenue || 0).toFixed(2)}</p>
                      </div>
                    </div>

                    {/* Reserve Stock + Investment edit row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                      <div>
                        <label className="text-zinc-500 text-[10px]">📦 Stock Rezèv</label>
                        <div className="flex gap-1">
                          <input type="number" min={0} defaultValue={inv.reserve_stock || 0} id={`reserve-${inv.id}`} className="w-full bg-zinc-800 border border-cyan-700/40 rounded px-2 py-1 text-cyan-400 text-sm font-bold" data-testid={`inv-reserve-input-${inv.id}`} />
                          <button onClick={async () => {
                            const val = parseInt(document.getElementById(`reserve-${inv.id}`).value) || 0;
                            try { await axios.put(`${API}/ceo/inv/${inv.id}`, { reserve_stock: val }); toast.success(`Stock rezèv: ${val}`); fetchInvs(); }
                            catch (e) { toast.error("Erè"); }
                          }} data-testid={`inv-reserve-save-${inv.id}`} className="bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold px-2 rounded">✓</button>
                        </div>
                      </div>
                      <div>
                        <label className="text-zinc-500 text-[10px]">💰 Base Investiseman</label>
                        <div className="flex gap-1">
                          <input type="number" min={0} step={0.01} defaultValue={inv.investment_amount || 0} id={`invest-${inv.id}`} className="w-full bg-zinc-800 border border-violet-700/40 rounded px-2 py-1 text-violet-400 text-sm font-bold" data-testid={`inv-invest-input-${inv.id}`} />
                          <button onClick={async () => {
                            const val = parseFloat(document.getElementById(`invest-${inv.id}`).value) || 0;
                            try { await axios.put(`${API}/ceo/inv/${inv.id}`, { investment_amount: val }); toast.success(`Base: $${val}`); fetchInvs(); }
                            catch (e) { toast.error("Erè"); }
                          }} className="bg-violet-600 hover:bg-violet-500 text-white text-xs font-bold px-2 rounded">✓</button>
                        </div>
                      </div>
                      <button onClick={async () => {
                        if (!window.confirm(`Reset kòd sekrè INV ${inv.name}? Li ap dwe kreye yon nouvo nan login.`)) return;
                        try { await axios.post(`${API}/ceo/inv/${inv.id}/reset-secret-code`); toast.success("Kòd sekrè reset"); fetchInvs(); }
                        catch (e) { toast.error("Erè"); }
                      }} data-testid={`inv-reset-secret-${inv.id}`} className="bg-amber-700 hover:bg-amber-600 text-white text-xs font-bold px-2 py-2 rounded self-end">🔐 Reset Code</button>
                      <button onClick={async () => {
                        const newSusp = !inv.suspended;
                        try { await axios.put(`${API}/ceo/inv/${inv.id}`, { suspended: newSusp }); toast.success(newSusp ? "Sispann" : "Aktive"); fetchInvs(); }
                        catch (e) { toast.error("Erè"); }
                      }} className={`text-xs font-bold px-2 py-2 rounded self-end ${inv.suspended ? "bg-emerald-700 hover:bg-emerald-600" : "bg-orange-700 hover:bg-orange-600"} text-white`}>
                        {inv.suspended ? "Aktive" : "Sispann"}
                      </button>
                    </div>
                    {/* List of this INV's stockers */}
                    {inv.stockers && inv.stockers.length > 0 && (
                      <div className="bg-zinc-950/60 rounded-lg p-2 mb-2 space-y-1">
                        {inv.stockers.map(s => (
                          <div key={s.id} className="flex items-center justify-between text-xs px-2 py-1 hover:bg-zinc-800/50 rounded">
                            <span className="text-white">{s.name} <span className="text-zinc-500">({(s.country || '').toUpperCase()})</span></span>
                            <span className="text-zinc-400">Stock: {s.current_stock}/{s.initial_stock} • Rev: ${(s.total_revenue || 0).toFixed(0)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Assign existing stocker */}
                    <div className="flex gap-2 mt-2 flex-wrap">
                      <select id={`assign-stocker-${inv.id}`} className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-xs min-w-[200px]">
                        <option value="">-- Asiyen yon Stocker egzistan ba INV sa a --</option>
                        {stockers.filter(s => !s.inv_id).map(s => (
                          <option key={s.id} value={s.id}>{s.name} ({(s.country || '').toUpperCase()})</option>
                        ))}
                      </select>
                      <button onClick={async () => {
                        const sel = document.getElementById(`assign-stocker-${inv.id}`);
                        const stockerId = sel?.value;
                        if (!stockerId) { toast.error("Chwazi yon stocker"); return; }
                        try {
                          await axios.put(`${API}/stockers/${stockerId}`, { inv_id: inv.id });
                          toast.success("Stocker asiyen ba INV");
                          fetchInvs();
                          fetchData();
                        } catch (e) { toast.error("Erè"); }
                      }} data-testid={`inv-assign-btn-${inv.id}`} className="bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-bold px-3 py-2 rounded-lg">Asiyen</button>
                      <button onClick={async () => {
                        if (!window.confirm(`Efase INV ${inv.name}? Stockers ap delye.`)) return;
                        try { await axios.delete(`${API}/ceo/inv/${inv.id}`); toast.success("INV efase"); fetchInvs(); fetchData(); }
                        catch (e) { toast.error("Erè"); }
                      }} className="bg-red-700 hover:bg-red-600 text-white text-xs font-bold px-3 py-2 rounded-lg">Efase</button>
                    </div>
                  </div>
                ))}
                {invs.length === 0 && <p className="text-zinc-500 text-center py-6 text-sm">Pa gen INV pou kounye a. Kreye youn anwo a.</p>}
              </div>
            </div>
          </div>
        )}

        {/* ========== IMAGE SLIDES TAB ========== */}
        {activeTab === "slides" && (
          <div className="glass-panel p-6" data-testid="slides-tab">
            <h3 className="text-white font-bold mb-2 flex items-center gap-2" style={{fontFamily: 'var(--font-heading)'}}>
              <Instagram className="w-5 h-5 text-pink-400" /> Image Slides — Page d'Accueil
            </h3>
            <p className="text-zinc-500 text-sm mb-6">Gérez les images qui défilent sur la page d'accueil. Maximum 7 images. Sauvegardez après vos modifications.</p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              {sliderImages.map((url, idx) => (
                <div key={idx} className="relative group rounded-2xl overflow-hidden border border-white/10 aspect-video">
                  <img src={url} alt={`Slide ${idx + 1}`} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                    <span className="bg-white/20 text-white text-xs px-3 py-1.5 rounded-full font-bold">Slide {idx + 1}</span>
                    <button onClick={async () => {
                      const updated = sliderImages.filter((_, i) => i !== idx);
                      setSliderImages(updated);
                      await axios.post(`${API}/ceo/slider-images`, { images: updated });
                      toast.success("Image retirée!");
                    }}
                      className="bg-red-500/80 hover:bg-red-600 text-white text-xs px-3 py-1.5 rounded-full font-bold"
                      data-testid={`remove-slide-${idx}`}>
                      Retirer
                    </button>
                  </div>
                  <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded-lg font-bold">{idx + 1}</div>
                </div>
              ))}
              {Array.from({ length: Math.max(0, 7 - sliderImages.length) }).map((_, idx) => (
                <div key={`empty-${idx}`} className="border-2 border-dashed border-white/10 rounded-2xl aspect-video flex items-center justify-center">
                  <span className="text-zinc-600 text-xs">Vide</span>
                </div>
              ))}
            </div>

            {sliderImages.length < 7 && (
              <div className="bg-zinc-900/60 rounded-xl p-4 border border-zinc-800 mb-4">
                <p className="text-zinc-400 text-xs font-bold uppercase mb-3">Ajouter une Image ({sliderImages.length}/7)</p>
                
                {/* Upload from phone */}
                <div className="flex gap-3 mb-3">
                  <label className="flex-1 bg-pink-600 hover:bg-pink-700 text-white px-6 py-3 rounded-xl font-bold text-center cursor-pointer transition-all text-sm"
                    data-testid="upload-slide-btn">
                    <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        toast.info("Upload en cours...");
                        const res = await axios.post(`${API}/ceo/upload-slide`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                        if (res.data.url) {
                          const updated = [...sliderImages, res.data.url];
                          setSliderImages(updated);
                          await axios.post(`${API}/ceo/slider-images`, { images: updated });
                          toast.success("Image uploadée!");
                        }
                      } catch(err) { toast.error("Erreur upload"); }
                      e.target.value = '';
                    }} />
                    Upload Photo
                  </label>
                </div>

                {/* Or paste URL */}
                <p className="text-zinc-600 text-xs mb-2">ou collez un lien:</p>
                <div className="flex gap-3">
                  <input type="text" value={newSlideUrl} onChange={(e) => setNewSlideUrl(e.target.value)}
                    placeholder="https://..."
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl py-3 px-4 text-white text-sm focus:outline-none focus:border-pink-500"
                    data-testid="new-slide-url" />
                  <button onClick={async () => {
                    if (!newSlideUrl.trim()) return;
                    const updated = [...sliderImages, newSlideUrl.trim()];
                    setSliderImages(updated);
                    setNewSlideUrl("");
                    await axios.post(`${API}/ceo/slider-images`, { images: updated });
                    toast.success("Image ajoutée au slider!");
                  }}
                    className="bg-zinc-700 hover:bg-zinc-600 text-white px-6 rounded-xl font-bold"
                    data-testid="add-slide-btn">
                    + Lien
                  </button>
                </div>
              </div>
            )}

            <button onClick={async () => {
              await axios.post(`${API}/ceo/slider-images`, { images: sliderImages });
              toast.success("Slider sauvegardé! Les images apparaîtront sur la page d'accueil.");
            }}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl font-bold transition-all"
              data-testid="save-slides-btn">
              Sauvegarder les Slides
            </button>

            {sliderImages.length > 0 && (
              <div className="mt-6">
                <p className="text-zinc-500 text-xs uppercase tracking-wider mb-3">Aperçu du Slider</p>
                <div className="slide-container aspect-video rounded-2xl overflow-hidden border border-white/10 relative">
                  {sliderImages.map((url, i) => (
                    <img key={i} src={url} alt={`Preview ${i}`}
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === 0 ? 'opacity-100' : 'opacity-0'}`} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {/* Contact Us */}
        <ContactUs />

        {/* ========== OVERDUE REMINDER MODAL ========== */}
        {showOverdueModal && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowOverdueModal(false)} data-testid="overdue-modal">
            <div className="bg-zinc-900 border border-amber-500/40 rounded-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 bg-gradient-to-r from-amber-900/40 to-red-900/40">
                <div>
                  <h3 className="text-white font-black text-lg flex items-center gap-2">⚠️ Rapèl Overdue</h3>
                  <p className="text-amber-300 text-xs">{overdueSummary.count} moun ki dwe kob oswa gen stock ki depase dat.</p>
                </div>
                <button onClick={() => setShowOverdueModal(false)} className="text-zinc-500 hover:text-white text-xl">×</button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {overdueSummary.items.length === 0 && (
                  <p className="text-zinc-500 text-center py-8 text-sm">Pa gen okenn moun overdue kounye a. 🎉</p>
                )}
                {overdueSummary.items.map(i => (
                  <div key={i.id} className={`rounded-xl p-3 border ${i.suspended ? "bg-red-950/40 border-red-500/50" : i.days_overdue >= 5 ? "bg-amber-950/40 border-amber-500/50" : "bg-zinc-800/40 border-zinc-700"}`} data-testid={`overdue-item-${i.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-bold text-sm">{i.name}</span>
                        <span className="text-zinc-500 text-[10px] uppercase">{i.role} • {i.country}</span>
                        {i.suspended && <span className="bg-red-600 text-white text-[10px] px-2 py-0.5 rounded font-bold">SISPANN</span>}
                        <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${i.days_overdue >= 7 ? "bg-red-600 text-white" : i.days_overdue >= 5 ? "bg-amber-600 text-white" : "bg-zinc-700 text-zinc-300"}`}>
                          {i.days_overdue}j an reta
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={async () => {
                            if (!window.confirm(`${i.suspended ? "Retire sispansyon" : "Sispann"} ${i.name}?`)) return;
                            try { await axios.post(`${API}/stockers/${i.id}/toggle-suspend`, { reason: "manual_ceo" }); toast.success("Fèt"); setOverdueSummary({...overdueSummary, items: overdueSummary.items.map(x => x.id === i.id ? {...x, suspended: !x.suspended} : x)}); }
                            catch { toast.error("Erè"); }
                          }}
                          className={`text-white text-[10px] font-bold px-2 py-1 rounded ${i.suspended ? "bg-emerald-600 hover:bg-emerald-500" : "bg-red-600 hover:bg-red-500"}`}
                          data-testid={`overdue-suspend-${i.id}`}>
                          {i.suspended ? "🔓 Retire" : "🔒 Sispann"}
                        </button>
                        {i.phone && (
                          <a href={`https://wa.me/${(i.phone || '').replace(/[^\d]/g, '')}`} target="_blank" rel="noreferrer"
                            className="bg-emerald-700 hover:bg-emerald-600 text-white text-[10px] font-bold px-2 py-1 rounded">
                            💬 WhatsApp
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2 mt-2 text-[11px]">
                      <div className="bg-black/30 rounded p-2 text-center"><p className="text-zinc-500">Stock</p><p className="text-white font-bold">{i.current_stock}</p></div>
                      <div className="bg-black/30 rounded p-2 text-center"><p className="text-zinc-500">Vann</p><p className="text-emerald-400 font-bold">{i.total_sold}</p></div>
                      <div className="bg-black/30 rounded p-2 text-center"><p className="text-zinc-500">Revni</p><p className="text-pink-400 font-bold">${i.total_revenue.toFixed(0)}</p></div>
                      <div className="bg-black/30 rounded p-2 text-center"><p className="text-zinc-500">Dwe</p><p className="text-amber-400 font-bold">${i.balance_owed.toFixed(0)}</p></div>
                    </div>
                    {i.carryover_from_previous_cycle > 0 && (
                      <p className="text-amber-300 text-[10px] mt-1 font-bold">📌 Carryover sik pase: {i.carryover_from_previous_cycle}</p>
                    )}
                    <p className="text-zinc-500 text-[10px] mt-1">Kredi: {i.credit_score}/100 • Sik fen: {new Date(i.end_date).toLocaleDateString('fr-FR')}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {/* ========== CLOSE-ALL PREVIEW MODAL ========== */}
        {showCloseAllPreview && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowCloseAllPreview(null)} data-testid="close-all-preview-modal">
            <div className="bg-zinc-900 border border-red-500/40 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 bg-gradient-to-r from-red-900/50 to-amber-900/40">
                <div>
                  <h3 className="text-white font-black text-lg">🔒 Ferme Tout Sik — Preview</h3>
                  <p className="text-amber-300 text-xs">{showCloseAllPreview.length} sik depase dat, poko fèmen.</p>
                </div>
                <button onClick={() => setShowCloseAllPreview(null)} className="text-zinc-500 hover:text-white text-xl">×</button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {showCloseAllPreview.map(u => (
                  <div key={u.id} className="bg-zinc-800/40 border border-zinc-700 rounded-xl p-3" data-testid={`close-preview-${u.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-bold text-sm">{u.name} <span className="text-zinc-500 text-[10px] uppercase">({u.role === "stockers" ? "Stocker" : "NM"})</span></p>
                        <p className="text-red-300 text-[11px] font-bold">🔒 Fermeture sik — {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-emerald-400 font-bold text-xs">Vann: {u.sold}</p>
                        <p className="text-white text-xs">Rete: {u.remaining}</p>
                        <p className="text-pink-400 text-xs">Revni: ${(u.revenue || 0).toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-4 border-t border-zinc-800 flex gap-2">
                <button onClick={() => setShowCloseAllPreview(null)} className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-2 rounded-lg text-sm font-bold">Anile</button>
                <button
                  onClick={async () => {
                    try {
                      const r = await axios.post(`${API}/ceo/close-all-cycles`, {});
                      toast.success(`✓ ${r.data.closed} sik fèmen.`);
                      setShowCloseAllPreview(null);
                      fetchData();
                    } catch { toast.error("Erè"); }
                  }}
                  data-testid="confirm-close-all-btn"
                  className="flex-1 bg-red-600 hover:bg-red-500 text-white py-2 rounded-lg text-sm font-black">
                  🔒 Ferme Tout ({showCloseAllPreview.length})
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ========== KONTWOL POKO FET MINI-REPORT MODAL ========== */}
        {kontwolReport && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setKontwolReport(null)} data-testid="kontwol-report-modal">
            <div className="bg-zinc-900 border border-red-500/40 rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 bg-gradient-to-r from-red-900/50 to-amber-900/50">
                <div>
                  <h3 className="text-white font-black text-lg">🔴 Kontwòl POKO Fèt</h3>
                  <p className="text-red-300 text-xs">{kontwolReport.user.name} • {kontwolReport.user.country}</p>
                </div>
                <button onClick={() => setKontwolReport(null)} className="text-zinc-500 hover:text-white text-xl">×</button>
              </div>
              {(() => {
                const u = kontwolReport.user;
                const prev = u.previous_cycle || {};
                const cycleSold = prev.total_sold ?? u.total_sold ?? 0;
                const cycleRevenue = prev.total_revenue ?? u.total_revenue ?? 0;
                const rate = parseFloat(u.commission_rate || 12.5);
                const commission = cycleRevenue * (rate / 100);
                const balanceOwed = Math.max(0, cycleRevenue - commission);
                return (
                  <div className="p-5 space-y-3">
                    <div className="grid grid-cols-2 gap-2 text-center">
                      <div className="bg-black/40 rounded-lg p-3">
                        <p className="text-zinc-500 text-[11px] uppercase">Vape vann</p>
                        <p className="text-emerald-400 font-black text-2xl">{cycleSold}</p>
                      </div>
                      <div className="bg-black/40 rounded-lg p-3">
                        <p className="text-zinc-500 text-[11px] uppercase">Vape rete</p>
                        <p className="text-white font-black text-2xl">{prev.current_stock ?? 0}</p>
                      </div>
                      <div className="bg-black/40 rounded-lg p-3">
                        <p className="text-zinc-500 text-[11px] uppercase">Revni total</p>
                        <p className="text-pink-400 font-black text-xl">${cycleRevenue.toFixed(2)}</p>
                      </div>
                      <div className="bg-black/40 rounded-lg p-3">
                        <p className="text-zinc-500 text-[11px] uppercase">Komisyon ({rate}%)</p>
                        <p className="text-yellow-400 font-black text-xl">${commission.toFixed(2)}</p>
                      </div>
                    </div>
                    <div className="bg-red-950/40 border border-red-500/40 rounded-xl p-3 text-center">
                      <p className="text-red-300 text-xs uppercase">Kob pou bay stocker</p>
                      <p className="text-red-400 font-black text-3xl" data-testid="kontwol-report-balance">${balanceOwed.toFixed(2)}</p>
                    </div>
                    {(u.carryover_from_previous_cycle || 0) > 0 && (
                      <p className="text-amber-300 text-xs bg-amber-950/30 border border-amber-700/40 rounded p-2 text-center">📌 Gen {u.carryover_from_previous_cycle} vape sik pase poko vann</p>
                    )}
                    <p className="text-zinc-500 text-[11px] text-center">Dat sik pase: {prev.cycle_ended_at ? new Date(prev.cycle_ended_at).toLocaleDateString('fr-FR') : '—'}</p>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Konfime: mak kontwòl la fèt pou ${u.name}?`)) return;
                        try {
                          const ep = kontwolReport.role === 'nm'
                            ? `${API}/ceo/network-marketers/${u.id}/mark-controlled`
                            : `${API}/ceo/stockers/${u.id}/mark-controlled`;
                          await axios.post(ep);
                          toast.success("✓ Kontwòl fèt — sik pase klase.");
                          setKontwolReport(null);
                          fetchData();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Erè");
                        }
                      }}
                      data-testid="kontwol-report-mark-done"
                      className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-black py-3 rounded-xl">
                      ✓ Effectuer Kontwòl
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* VAPE-YARD CEO FOOTER */}
      <footer className="bg-black border-t border-zinc-900 px-4 lg:px-8 py-5 mt-6" data-testid="ceo-footer">
        <div className="flex flex-wrap items-center justify-between gap-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-amber-400 rounded-lg flex items-center justify-center"><Crown className="w-4 h-4 text-black" /></div>
            <div>
              <p className="text-white font-black text-sm tracking-wider leading-none">VAPE<span className="text-amber-400">-</span>YARD</p>
              <p className="text-zinc-600 text-[9px] uppercase tracking-[0.25em] mt-0.5">Flavors meet the future</p>
            </div>
          </div>
          <p className="text-zinc-500 text-[10px] font-semibold">© {new Date().getFullYear()} VAPE-YARD Enterprise · CEO Command Center</p>
        </div>
      </footer>
    </div>
  );
};

// ==================== TEAM BONUS MANAGER (CEO button + modal) ====================
const TeamBonusManagerButton = () => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [amount, setAmount] = useState("");
  const [label, setLabel] = useState("VAPE-YARD Team Bonus");
  const [list, setList] = useState([]);
  const [tab, setTab] = useState("grant"); // grant | manage
  const [loading, setLoading] = useState(false);

  const loadList = () => axios.get(`${API}/ceo/team-bonus/list`).then(r => setList(r.data.items || [])).catch(() => {});
  useEffect(() => { if (open) loadList(); }, [open]);

  const doSearch = async (q) => {
    setSearch(q);
    if (!q.trim()) { setResults([]); return; }
    try { const r = await axios.get(`${API}/ceo/users-search`, { params: { q } }); setResults(r.data.items || []); }
    catch { setResults([]); }
  };

  const grant = async () => {
    if (!selected || !amount || parseFloat(amount) <= 0) return toast.error("Chwazi yon moun epi mete yon montan");
    setLoading(true);
    try {
      await axios.post(`${API}/ceo/team-bonus/grant`, {
        user_id: selected.id, user_type: selected.user_type,
        amount: parseFloat(amount), label: label.trim() || "VAPE-YARD Team Bonus",
      });
      toast.success(`⭐ Team bonus $${amount} bay ${selected.name}!`);
      setSelected(null); setAmount(""); setSearch(""); setResults([]);
      loadList();
    } catch (e) { toast.error(e.response?.data?.detail || "Erè"); }
    finally { setLoading(false); }
  };

  const updateItem = async (id, patch) => {
    try { await axios.patch(`${API}/ceo/team-bonus/${id}`, patch); toast.success("Mete ajou ✅"); loadList(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erè"); }
  };
  const deleteItem = async (id, name) => {
    if (!window.confirm(`Efase bonus pou ${name}?`)) return;
    try { await axios.delete(`${API}/ceo/team-bonus/${id}`); toast.success("Efase"); loadList(); }
    catch { toast.error("Erè"); }
  };

  const promptEdit = (b) => {
    const newLabel = window.prompt("Non bonus la:", b.label || "VAPE-YARD Team Bonus");
    if (newLabel === null) return;
    const newAmount = window.prompt("Montan ($):", String(b.amount));
    if (newAmount === null) return;
    updateItem(b.id, { label: newLabel, amount: parseFloat(newAmount) });
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="ceo-team-bonus-btn"
        className="flex items-center gap-1.5 bg-gradient-to-r from-amber-500 to-amber-400 hover:brightness-110 text-black font-black px-3 py-2 rounded-xl text-xs"
        title="VAPE-YARD Team Bonus"
      >
        ⭐ <span className="hidden md:inline uppercase tracking-widest">Team Bonus</span>
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/80 z-[90] p-4 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="max-w-2xl mx-auto bg-white rounded-2xl p-5 my-6 relative shadow-2xl" onClick={e => e.stopPropagation()} data-testid="ceo-team-bonus-modal">
            <button onClick={() => setOpen(false)} className="absolute top-3 right-3 bg-zinc-100 hover:bg-red-500 hover:text-white text-zinc-700 rounded-full w-8 h-8 flex items-center justify-center"><X className="w-4 h-4" /></button>
            <h3 className="text-zinc-900 font-black text-xl mb-1">⭐ VAPE-YARD Team Bonus</h3>
            <p className="text-zinc-500 text-xs mb-4">Chèche yon moun, mete yon montan, aktive bonus la. L ap parèt sou widget zetwal nan dashboard yo epi antre sou kob sik lè sik la fèmen.</p>

            <div className="flex gap-2 mb-4 border-b border-zinc-200">
              <button onClick={() => setTab("grant")} className={`px-3 py-2 text-sm font-black ${tab==="grant" ? "border-b-2 border-amber-500 text-zinc-900" : "text-zinc-500"}`} data-testid="tab-grant">Bay Bonus</button>
              <button onClick={() => setTab("manage")} className={`px-3 py-2 text-sm font-black ${tab==="manage" ? "border-b-2 border-amber-500 text-zinc-900" : "text-zinc-500"}`} data-testid="tab-manage">Jere ({list.length})</button>
            </div>

            {tab === "grant" && (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input value={search} onChange={e => doSearch(e.target.value)} placeholder="Chèche pa non oswa kòd..."
                    className="w-full pl-9 pr-3 py-2.5 border border-zinc-300 rounded-xl text-sm text-zinc-900 outline-none focus:border-amber-500"
                    data-testid="team-bonus-search" />
                </div>
                {results.length > 0 && !selected && (
                  <div className="border border-zinc-200 rounded-xl max-h-56 overflow-y-auto">
                    {results.map(u => (
                      <button key={`${u.user_type}-${u.id}`} onClick={() => { setSelected(u); setResults([]); setSearch(""); }} className="w-full flex items-center justify-between px-3 py-2 hover:bg-amber-50 border-b border-zinc-100 last:border-0 text-left" data-testid={`search-pick-${u.id}`}>
                        <div>
                          <p className="text-zinc-900 font-bold text-sm">{u.name}</p>
                          <p className="text-zinc-500 text-xs">{u.user_type === "stocker" ? "Stocker" : "Network Marketer"} • Kòd {u.code} • {u.country?.toUpperCase() || ""}</p>
                        </div>
                        <span className="text-amber-600 text-xs font-bold">Chwazi ›</span>
                      </button>
                    ))}
                  </div>
                )}
                {selected && (
                  <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-3 flex items-center justify-between">
                    <div>
                      <p className="text-zinc-900 font-black">{selected.name}</p>
                      <p className="text-zinc-600 text-xs">{selected.user_type === "stocker" ? "Stocker" : "Network Marketer"} • Kòd {selected.code}</p>
                    </div>
                    <button onClick={() => setSelected(null)} className="text-zinc-500 hover:text-red-500"><X className="w-4 h-4" /></button>
                  </div>
                )}
                <div>
                  <label className="block text-zinc-700 text-xs font-black uppercase tracking-widest mb-1">Non Bonus la</label>
                  <input value={label} onChange={e => setLabel(e.target.value)} placeholder="VAPE-YARD Team Bonus"
                    className="w-full px-3 py-2 border border-zinc-300 rounded-xl text-sm text-zinc-900 outline-none focus:border-amber-500"
                    data-testid="team-bonus-label-input" />
                </div>
                <div>
                  <label className="block text-zinc-700 text-xs font-black uppercase tracking-widest mb-1">Montan ($)</label>
                  <input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="20.00"
                    className="w-full px-3 py-2 border border-zinc-300 rounded-xl text-lg font-black text-zinc-900 outline-none focus:border-amber-500"
                    data-testid="team-bonus-amount-input" />
                </div>
                <button onClick={grant} disabled={loading || !selected || !amount} className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black font-black py-3 rounded-xl text-sm uppercase tracking-widest" data-testid="team-bonus-grant-btn">
                  {loading ? "..." : "⭐ Aktive Bonus"}
                </button>
              </div>
            )}

            {tab === "manage" && (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {list.length === 0 && <p className="text-zinc-500 text-sm text-center py-6">Pa gen team bonus ankò.</p>}
                {list.map(b => (
                  <div key={b.id} className={`border rounded-xl p-3 flex items-center gap-3 ${b.status === "active" ? "border-amber-300 bg-amber-50" : "border-zinc-200 bg-zinc-50 opacity-60"}`} data-testid={`team-bonus-item-${b.id}`}>
                    <div className="w-10 h-10 bg-amber-400 rounded-xl flex items-center justify-center text-xl">⭐</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-zinc-900 font-black text-sm truncate">{b.user_name || "—"} <span className="text-zinc-500 text-xs font-normal">• Kòd {b.user_code}</span></p>
                      <p className="text-zinc-700 text-xs">{b.label} · <span className="font-black">${(b.amount || 0).toFixed(2)}</span></p>
                      {b.status === "cancelled" && <span className="text-red-500 text-[10px] font-black">ANILE</span>}
                      {b.cycle_credited_at && <span className="text-emerald-700 text-[10px] font-black">✓ Antre nan sik</span>}
                    </div>
                    <button onClick={() => promptEdit(b)} className="text-blue-600 hover:text-blue-800 p-1" title="Modifye" data-testid={`team-bonus-edit-${b.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => deleteItem(b.id, b.user_name)} className="text-red-500 hover:text-red-700 p-1" title="Efase" data-testid={`team-bonus-del-${b.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ==================== TEAM BONUS STAR WIDGET ====================
// Blinking green light + star showing CEO-granted VAPE-YARD Team Bonus
const TeamBonusStarWidget = ({ userId }) => {
  const [data, setData] = useState({ total_active: 0, items: [], label: "VAPE-YARD Team Bonus" });
  const [blink, setBlink] = useState(true);
  useEffect(() => {
    if (!userId) return;
    const load = () => axios.get(`${API}/user/${userId}/team-bonus-active`).then(r => setData(r.data)).catch(() => {});
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [userId]);
  useEffect(() => {
    const t = setInterval(() => setBlink(b => !b), 30000);
    return () => clearInterval(t);
  }, []);
  if (!data.total_active || data.total_active <= 0) return null;
  return (
    <div className="relative bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-400 rounded-2xl p-5 shadow-lg overflow-hidden" data-testid="team-bonus-star-widget">
      {/* Blinking green light */}
      <div className={`absolute top-3 right-3 w-3 h-3 rounded-full transition-all duration-500 ${blink ? "bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.9)]" : "bg-emerald-900/30"}`} />
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 bg-amber-400 rounded-xl flex items-center justify-center shadow-md animate-pulse">
          <span className="text-3xl">⭐</span>
        </div>
        <div className="flex-1">
          <p className="text-amber-900 text-[10px] font-black uppercase tracking-widest">{data.label || "VAPE-YARD Team Bonus"}</p>
          <p className="text-amber-950 font-black text-3xl leading-tight">${data.total_active.toFixed(2)}</p>
          <p className="text-amber-800 text-[11px] font-semibold mt-0.5">
            Antre sou kob sik ou nan fen sik la • {data.items.length} bonis aktif
          </p>
        </div>
      </div>
    </div>
  );
};

// ==================== STOCKER DASHBOARD ====================
const NMUnifiedDashboard = () => {
  const navigate = useNavigate();
  const parent = useAuth();
  const [nm, setNm] = useState(() => { try { return JSON.parse(localStorage.getItem("networkMarketerAuth")); } catch (e) { return null; } });
  const [settings, setSettings] = useState(parent?.settings || null);
  useEffect(() => { if (!nm) navigate("/network-marketer/login"); }, [nm]);
  useEffect(() => { if (!settings) axios.get(`${API}/settings`).then((r) => setSettings(r.data)).catch(() => setSettings({})); }, []);
  if (!nm || !settings) return null;
  const value = {
    ...parent, settings, user: { ...nm, is_nm: true },
    logout: () => { localStorage.removeItem("networkMarketerAuth"); setNm(null); },
    refreshUser: (d) => { const u = { ...nm, ...d, is_nm: true }; localStorage.setItem("networkMarketerAuth", JSON.stringify(u)); setNm(u); },
  };
  return <AuthContext.Provider value={value}><Dashboard /></AuthContext.Provider>;
};

const DASH_SECTION_TARGETS = { withdrawal: "dashboard-withdraw-section", renewal: "dashboard-renew-section", history: "transaction-history", commissions: "stat-commission", notifications: "recent-activity" };
const DASH_SECTION_MENUS = ["dashboard", ...Object.keys(DASH_SECTION_TARGETS)];
const LANG_FLAGS = { fr: "🇫🇷", ht: "🇭🇹", en: "🇺🇸", es: "🇪🇸" };

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout, refreshUser, settings } = useAuth();
  const { t, lang } = useLang();
  const siteConfig = useSiteConfig();
  const [alerts, setAlerts] = useState([]);
  const [showWelcome, setShowWelcome] = useState(true);
  const [showGiftBox, setShowGiftBox] = useState(null);
  const [withdrawStep, setWithdrawStep] = useState("idle"); // idle, waiting, approved
  const [withdrawTimer, setWithdrawTimer] = useState(10);
  const [payMethod, setPayMethod] = useState(""); // zelle | cashapp | cash
  const [payAccountId, setPayAccountId] = useState("");
  const [payAccountName, setPayAccountName] = useState("");
  const [renewStep, setRenewStep] = useState("idle"); // idle, not_eligible, accepted, en_route
  const [activeMenu, setActiveMenu] = useState("dashboard");
  const [menuStack, setMenuStack] = useState([]);
  const prevMenuRef = useRef("dashboard");
  const backNavRef = useRef(false);
  const scrollByMenu = useRef({});
  useEffect(() => {
    const prev = prevMenuRef.current;
    if (prev !== activeMenu) {
      scrollByMenu.current[prev] = scrollByMenu.current.__last || 0;
      if (backNavRef.current) {
        backNavRef.current = false;
        const y = scrollByMenu.current[activeMenu] || 0;
        setTimeout(() => window.scrollTo(0, y), 50);
      } else {
        setMenuStack((st) => [...st, prev]);
        window.scrollTo(0, 0);
      }
      prevMenuRef.current = activeMenu;
    }
    if (activeMenu === "nm-team") navigate("/network-marketer/team");
    const target = DASH_SECTION_TARGETS[activeMenu];
    if (target) setTimeout(() => document.querySelector(`[data-testid="${target}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
  }, [activeMenu]);
  useEffect(() => {
    const onScroll = () => { scrollByMenu.current.__last = window.scrollY; };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const goBack = () => {
    if (!menuStack.length) return;
    backNavRef.current = true;
    setActiveMenu(menuStack[menuStack.length - 1]);
    setMenuStack((st) => st.slice(0, -1));
  };
  const [loading, setLoading] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showSalesReport, setShowSalesReport] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile sidebar drawer
  const [headerSearch, setHeaderSearch] = useState("");
  const [showCreditScoreModal, setShowCreditScoreModal] = useState(false); // top-bar credit score modal
  const [showLangModal, setShowLangModal] = useState(false);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [tracking, setTracking] = useState(null); // {status, estimated_date, note, in_renewal}
  useEffect(() => {
    if (!user?.id) return;
    const load = () => axios.get(`${API}/user/${user.id}/tracking`).then(r => setTracking(r.data)).catch(() => setTracking(null));
    load();
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, [user?.id]);

  // 3-second red pulsing control-date reminder popup (auto-closes; user can also × close)
  const [showControlReminder, setShowControlReminder] = useState(false);
  useEffect(() => {
    if (user && (user.is_overdue || (user.days_remaining !== undefined && user.days_remaining <= 0))) {
      setShowControlReminder(true);
      const t = setTimeout(() => setShowControlReminder(false), 3000);
      return () => clearTimeout(t);
    }
  }, [user?.id, user?.is_overdue, user?.days_remaining]);

  // 3-second profile-completion reminder popup (missing photo and/or payment info)
  const [showProfileReminder, setShowProfileReminder] = useState(false);
  const [profileReminderText, setProfileReminderText] = useState("");
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const missing = [];
      // Photo check: user.profile_image OR localStorage
      const hasPhoto = !!(user.profile_image || localStorage.getItem(`stocker_photo_${user.id}`));
      if (!hasPhoto) missing.push("foto pwofil");
      // Payment method + phone check
      try {
        const pp = await axios.get(`${API}/user/${user.id}/payment-profile`);
        const method = pp.data?.payment_profile?.method;
        const accountId = pp.data?.payment_profile?.account_id;
        const phoneHint = pp.data?.payment_profile?.phone;
        if (!method || !accountId) missing.push("mòd peman");
        if (!phoneHint && !user.phone) missing.push("nimewo telefòn");
      } catch (_) {
        missing.push("mòd peman");
      }
      if (cancelled) return;
      if (missing.length) {
        setProfileReminderText(`Konplete ${missing.join(", ")} nan pwofil ou.`);
        setShowProfileReminder(true);
        setTimeout(() => setShowProfileReminder(false), 3000);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);
  
  // Screenshot protection
  useScreenshotProtection();
  
  // Sales data for up to 4 vape models
  const [salesEntries, setSalesEntries] = useState([
    { description: "", quantity: 0, price: 0 },
    { description: "", quantity: 0, price: 0 },
    { description: "", quantity: 0, price: 0 },
    { description: "", quantity: 0, price: 0 }
  ]);

  // VAPE-YARD visual assets (country cover, mini announcements, product images)
  const [countryCover, setCountryCover] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [productItems, setProductItems] = useState([]);
  useEffect(() => {
    if (!user?.id) return;
    const country = (user.country || "").toLowerCase();
    axios.get(`${API}/dashboard-cover-for-country/${country || "general"}`)
      .then(r => setCountryCover(r.data.cover || null)).catch(() => {});
    axios.get(`${API}/announcements`)
      .then(r => setAnnouncements(r.data.items || [])).catch(() => {});
    axios.get(`${API}/product-images`).then(r => setProductItems(r.data.items || [])).catch(() => {});
  }, [user?.id, user?.country]);
  const productImageFor = (modelName, own) => imageForName(modelName, productItems) || (own ? (own.startsWith("http") ? own : `${process.env.REACT_APP_BACKEND_URL}${own}`) : null);

  useEffect(() => {
    if (!user) { navigate('/'); return; }
    fetchAlerts();
    setTimeout(() => setShowWelcome(false), 3000);
    // Load saved profile image
    const savedImage = localStorage.getItem(`profile_${user.id}`);
    if (savedImage) setProfileImage(savedImage);
    // Show role-conversion welcome/demotion message if any (set by CEO conversion endpoint)
    if (user.pending_welcome_message) {
      toast.info(user.pending_welcome_message, { duration: 10000 });
      axios.post(`${API}/user/${user.id}/clear-welcome-message`).catch(() => {});
    }
  }, [user, navigate]);

  const fetchAlerts = async () => {
    try {
      const res = await axios.get(`${API}/stats/alerts/${user.id}`);
      setAlerts(res.data.alerts);
    } catch (e) { console.log("Error fetching alerts"); }
  };

  const handleSubmitSales = async () => {
    const validEntries = salesEntries.filter(e => e.quantity > 0 && e.price > 0);
    if (validEntries.length === 0) {
      toast.error("Entrez au moins une vente");
      return;
    }
    
    setLoading(true);
    try {
      for (const entry of validEntries) {
        await axios.post(`${API}/sales`, {
          stocker_id: user.id,
          vape_model: entry.description || "Vape",
          quantity_sold: entry.quantity,
          price_per_unit: entry.price
        });
      }
      
      // Refresh user data
      const res = await axios.get(`${API}/stockers/${user.id}`);
      refreshUser(res.data);
      
      toast.success("Ventes enregistrées!");
      setSalesEntries([
        { description: "", quantity: 0, price: 0 },
        { description: "", quantity: 0, price: 0 },
        { description: "", quantity: 0, price: 0 },
        { description: "", quantity: 0, price: 0 }
      ]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const handleClaimGift = async (type) => {
    try {
      const res = await axios.post(`${API}/stockers/${user.id}/claim-gift`, { gift_type: type });
      toast.success(res.data.message || "Demande envoyée au CEO!");
      setShowGiftBox(null);
      const updated = await axios.get(`${API}/stockers/${user.id}`);
      refreshUser(updated.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleProfileImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target.result);
        localStorage.setItem(`profile_${user.id}`, e.target.result);
        toast.success("Photo de profil mise à jour!");
      };
      reader.readAsDataURL(file);
    }
  };

  const getProgressColor = () => {
    if (!user) return 'good';
    const pct = user.stock_percentage || 100;
    if (pct > 50) return 'good';
    if (pct > 20) return 'warning';
    return 'critical';
  };

  const handleWithdraw = async () => {
    // Cycle ended check (if end_date passed, withdrawal allowed regardless of %)
    const cycleEnded = user.end_date ? new Date(user.end_date) < new Date() : false;
    // 75% threshold (between 75-80% allowed)
    if (!cycleEnded && sellPercentage < 75) {
      toast.error(`Ventes insuffisantes (${sellPercentage.toFixed(0)}% sou sik aktyèl la). Minimum 75% requis. Si sik la fini, ou ka retire kèlkeswa pousantaj la.`);
      return;
    }
    // FORCE payment method selection before contacting CEO
    try {
      const pp = await axios.get(`${API}/user/${user.id}/payment-profile`);
      const method = pp.data?.payment_profile?.method;
      if (!method) {
        toast.error(t("payment_method_required"));
        setActiveMenu("profile");
        return;
      }
    } catch (_) {
      toast.error(t("payment_method_required"));
      setActiveMenu("profile");
      return;
    }
    setWithdrawStep("waiting");
    setWithdrawTimer(10);
    let tm = 10;
    const interval = setInterval(() => {
      tm -= 1;
      setWithdrawTimer(tm);
      if (tm <= 0) {
        clearInterval(interval);
        // Call API to reset earnings (backend already creates CEO notification with payment_profile)
        axios.post(`${API}/stockers/${user.id}/withdraw`)
          .then(() => {
            setWithdrawStep("approved");
            axios.get(`${API}/stockers/${user.id}`).then(r => refreshUser(r.data));
          })
          .catch((err) => {
            const detail = err.response?.data?.detail || "Erreur lors du retrait. Eseye ankò oswa kontakte CEO.";
            toast.error(detail);
            setWithdrawStep("idle");
          });
      }
    }, 1000);
  };

  const handleRenewStock = () => {
    if (user.awaiting_new_stock || user.renewal_status) {
      toast.info("Votre demande de renouvellement est déjà en cours");
      return;
    }
    if (sellPercentage < 50) {
      setRenewStep("not_eligible");
      return;
    }
    // Call API
    axios.post(`${API}/stockers/${user.id}/renew-stock`)
      .then(() => {
        setRenewStep("accepted");
        // After 24h simulation check
        const renewTime = user.renewal_requested_at;
        if (renewTime) {
          const hoursPassed = (Date.now() - new Date(renewTime).getTime()) / (1000 * 60 * 60);
          if (hoursPassed >= 24) {
            setRenewStep("en_route");
          }
        }
      })
      .catch((e) => {
        if (e.response?.status === 400) {
          setRenewStep("not_eligible");
        } else {
          toast.error("Erreur");
        }
      });
  };

  if (!user) return null;

  const bonus_per_vape = settings?.bonus_per_vape || 0.99;
  const commission_rate = user.commission_rate || 12.5;

  // CYCLE-BASED math — always correct, ignores corrupted total_revenue
  // Weighted per-model math: sum(model.sold × model.price). Falls back to
  // cycle_sold × avg(price weighted by quantity) if per-model sold is missing.
  const cycleSold = getCycleSold(user);
  const vapeModels = user.vape_models || [];
  let cycleRevenue = 0;
  let hasPerModelSold = false;
  for (const m of vapeModels) {
    const soldQty = parseInt(m.sold || 0);
    if (soldQty > 0) hasPerModelSold = true;
    cycleRevenue += soldQty * (parseFloat(m.price) || 0);
  }
  if (!hasPerModelSold) {
    let avgPrice = 0;
    if (vapeModels.length > 0) {
      const totalQty = vapeModels.reduce((s, m) => s + (parseFloat(m.quantity) || 0), 0);
      const totalValue = vapeModels.reduce((s, m) => s + (parseFloat(m.quantity) || 0) * (parseFloat(m.price) || 0), 0);
      avgPrice = totalQty > 0 ? totalValue / totalQty : (parseFloat(vapeModels[0].price) || 40);
    } else {
      avgPrice = 40;
    }
    cycleRevenue = cycleSold * avgPrice;
  }
  const cycleBonus = cycleSold * bonus_per_vape;
  const actualRevenue = cycleRevenue;
  const actualBonus = cycleBonus;
  const commission_earned = actualRevenue * (commission_rate / 100);
  const totalGains = actualRevenue + actualBonus + commission_earned + (parseFloat(user.team_bonus_balance) || 0);
  
  const canClaimGift100 = ((user.lifetime_sold || 0) + (user.total_sold || 0)) >= 100 && !user.gift_claimed_100 && !user.gift_pending_100;
  const canClaimGift150 = ((user.lifetime_sold || 0) + (user.total_sold || 0)) >= 150 && !user.gift_claimed_150 && !user.gift_pending_150;
  const sellPercentage = user.initial_stock > 0 ? (getCycleSold(user) / user.initial_stock) * 100 : 0;
  const remainingVapes = user.current_stock || 0;

  // Check renewal status on mount
  const renewalStatus = user.renewal_status;
  const renewalTime = user.renewal_requested_at;
  let renewalHoursPassed = 0;
  if (renewalTime) {
    renewalHoursPassed = (Date.now() - new Date(renewalTime).getTime()) / (1000 * 60 * 60);
  }

  // Calculate total from current entries
  const currentTotal = salesEntries.reduce((sum, e) => sum + (e.quantity * e.price), 0);
  const currentBonus = salesEntries.reduce((sum, e) => sum + (e.quantity * bonus_per_vape), 0);

  // Menu items (matches reference image sidebar)
  const menuItems = [
    { id: "dashboard", icon: BarChart3, label: t("tab_dashboard") || "Dashboard" },
    { id: "my-stock", icon: Package, label: "My Stock" },
    { id: "sales", icon: FileText, label: "Report Sale" },
    { id: "renewal", icon: Truck, label: "Renewal Request" },
    { id: "withdrawal", icon: Wallet, label: "Withdraw Money" },
    { id: "history", icon: Clock, label: "Order History" },
    { id: "commissions", icon: BarChart3, label: "Commissions" },
    { id: "bonus", icon: DollarSign, label: "Bonus" },
    { id: "cadeaux", icon: Gift, label: t("tab_gifts") || "Gifts" },
    { id: "notifications", icon: Bell, label: "Notifications" },
    { id: "profile", icon: User, label: "My Profile" },
    { id: "support", icon: LifeBuoy, label: "Help & Support" },
    ...(user?.is_nm ? [{ id: "nm-team", icon: Users, label: "Mon Équipe NM" }] : []),
  ];

  return (
    <div className="min-h-screen bg-white relative" data-testid="stocker-dashboard" style={{fontFamily: 'Inter, system-ui, sans-serif'}}>
      {/* 3-second red pulsing control-date reminder */}
      {showControlReminder && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] animate-pulse" data-testid="control-date-reminder">
          <div className="bg-red-600 border-2 border-red-300 rounded-2xl shadow-2xl px-6 py-5 text-white font-black text-center relative min-w-[280px]">
            <button onClick={() => setShowControlReminder(false)} className="absolute top-1 right-2 text-white/70 hover:text-white text-lg" data-testid="close-control-reminder">×</button>
            <div className="text-3xl mb-1">🔴</div>
            <p className="text-base">MOMAN KONTWÒL RIVE</p>
            <p className="text-xs font-normal opacity-90 mt-1">Fè retrait ou / kontakte CEO w</p>
          </div>
        </div>
      )}
      {/* 3-second profile-completion reminder */}
      {showProfileReminder && (
        <div className="fixed top-20 right-6 z-[60] animate-fade-in" data-testid="profile-reminder">
          <div className="bg-amber-600 border-2 border-amber-300 rounded-2xl shadow-2xl px-5 py-4 text-white font-bold relative max-w-xs">
            <button onClick={() => setShowProfileReminder(false)} className="absolute top-1 right-2 text-white/70 hover:text-white text-lg" data-testid="close-profile-reminder">×</button>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl">👤</span>
              <p className="text-sm">Konplete pwofil w</p>
            </div>
            <p className="text-xs font-normal opacity-95">{profileReminderText}</p>
          </div>
        </div>
      )}
      <div className="stocker-club-bg" />
      {/* SUSPENDED USER — LIMITED ACCESS PORTAL */}
      {user?.suspended && (
        <div className="relative z-40 bg-gradient-to-r from-red-700 via-red-800 to-red-700 border-b-4 border-red-500 py-3 px-4 shadow-xl" data-testid="suspended-banner">
          <div className="max-w-3xl mx-auto flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <p className="text-white font-black text-sm">🔴 {t("suspended_title", "KONT OU SISPANN")}</p>
              <p className="text-red-100 text-[11px]">{t("suspended_hint", "Rezoud sik ou pou tounen aktif.")}</p>
            </div>
            <button
              onClick={() => setActiveMenu("sales")}
              data-testid="suspended-resolve-sales-btn"
              className="bg-white text-red-800 text-xs font-black px-3 py-2 rounded-lg hover:bg-red-100">
              ✎ {t("resolve_sales", "Rezoud Vant")}
            </button>
            <button
              onClick={() => setActiveMenu("withdrawal")}
              data-testid="suspended-resolve-payment-btn"
              className="bg-yellow-400 text-red-900 text-xs font-black px-3 py-2 rounded-lg hover:bg-yellow-300">
              💰 {t("resolve_payment", "Rezoud Peman")}
            </button>
          </div>
        </div>
      )}
      <div className="vape-bg" />
      
      {/* Welcome Modal */}
      {showWelcome && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center">
          <div className="glass-panel p-8 text-center max-w-sm mx-4 slide-up">
            <img src={LOGO_URL} alt="Vape Yard" className="w-24 h-24 mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold text-white mb-2">{t("welcome_dashboard")}</h2>
            <p className="text-yellow-400 text-xl font-bold">{user.name}!</p>
            <p className="text-zinc-500 mt-2">Vape Yard Enterprise</p>
          </div>
        </div>
      )}

      {/* Gift Box Modal */}
      {showGiftBox && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center">
          <div className="glass-panel p-8 text-center max-w-sm mx-4">
            <div className="text-6xl mb-4 animate-bounce">🎁</div>
            <h2 className="font-heading text-2xl font-bold text-white mb-2">Cadeau Débloqué!</h2>
            <p className="text-zinc-400 mb-4">{(user.lifetime_sold || 0) + (user.total_sold || 0)} vapes vendus au total!</p>
            <p className="text-yellow-400 text-sm mb-4">Le CEO va choisir votre cadeau. Vous serez notifié!</p>
            <div className="grid grid-cols-2 gap-3 mb-4 text-sm text-zinc-400">
              {showGiftBox === 100 ? (
                <>
                  <div className="bg-zinc-900 p-2 rounded-lg">📱 iPhone 15</div>
                  <div className="bg-zinc-900 p-2 rounded-lg">💵 Cash</div>
                  <div className="bg-zinc-900 p-2 rounded-lg">👟 Tennis Nike</div>
                  <div className="bg-zinc-900 p-2 rounded-lg">🍽️ Dîner Restaurant</div>
                  <div className="bg-zinc-900 p-2 rounded-lg">🎧 AirPods Pro</div>
                </>
              ) : (
                <>
                  <div className="bg-zinc-900 p-2 rounded-lg">💻 MacBook Air</div>
                  <div className="bg-zinc-900 p-2 rounded-lg">💰 $1000 USD</div>
                  <div className="bg-zinc-900 p-2 rounded-lg">✈️ Voyage 3 Jours</div>
                  <div className="bg-zinc-900 p-2 rounded-lg">🎮 PlayStation 5</div>
                  <div className="bg-zinc-900 p-2 rounded-lg">⌚ Apple Watch</div>
                </>
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={() => handleClaimGift(showGiftBox === 100 ? "100" : "150")}
                className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-zinc-900 font-bold py-3 rounded-xl" data-testid="send-gift-request">
                Envoyer la Demande au CEO!
              </button>
              <button onClick={() => setShowGiftBox(null)}
                className="flex-1 bg-zinc-800 text-white font-bold py-3 rounded-xl">Plus tard</button>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex min-h-screen bg-white">
        {/* ============== SIDEBAR (desktop fixed, mobile drawer) ============== */}
        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 bg-black/60 z-40" onClick={() => setSidebarOpen(false)} />
        )}
        <aside className={`${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky top-0 left-0 h-screen w-64 bg-black z-50 lg:z-0 transition-transform duration-300 flex flex-col flex-shrink-0`} data-testid="stocker-sidebar">
          {/* Logo */}
          <div className="px-4 py-5 border-b border-zinc-900 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="VAPE-YARD" className="w-8 h-8" onError={e => { e.target.style.display='none'; }} />
              <div>
                <p className="text-white font-black text-base tracking-wider leading-none">VAPE<span className="text-amber-400">-</span>YARD</p>
                <p className="text-zinc-500 text-[8px] uppercase tracking-[0.25em] mt-0.5">Flavors meet the future</p>
              </div>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-zinc-400 hover:text-white" data-testid="sidebar-close">
              <X className="w-5 h-5" />
            </button>
          </div>
          {/* Menu */}
          <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
            {menuItems.map(item => {
              const active = activeMenu === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => { setActiveMenu(item.id); setSidebarOpen(false); }}
                  data-testid={`sidebar-${item.id}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${active ? "bg-amber-400 text-black shadow-lg" : "text-zinc-400 hover:bg-zinc-900 hover:text-white"}`}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                  {item.id === "notifications" && alerts.length > 0 && (
                    <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{alerts.length}</span>
                  )}
                </button>
              );
            })}
          </nav>
          {/* Logout */}
          <div className="px-3 py-3 border-t border-zinc-900">
            <button onClick={() => { logout(); navigate('/'); }} className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-zinc-500 hover:bg-red-950 hover:text-red-300 font-semibold" data-testid="sidebar-logout">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </aside>

        {/* ============== MAIN WRAPPER ============== */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* ============== TOP BAR ============== */}
          <header className="bg-black border-b border-zinc-900 px-4 py-3 flex items-center gap-3 sticky top-0 z-30" data-testid="stocker-topbar">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-white p-1.5 hover:bg-zinc-900 rounded-lg" data-testid="sidebar-toggle">
              <Menu className="w-5 h-5" />
            </button>
            {menuStack.length > 0 && (
              <button onClick={goBack} title="Retour" className="text-black bg-amber-400 hover:bg-amber-300 p-1.5 rounded-lg active:scale-95 transition-transform" data-testid="topbar-back-btn">
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div className="flex items-center gap-2 lg:hidden">
              <img src={LOGO_URL} alt="VY" className="w-7 h-7" onError={e => { e.target.style.display='none'; }} />
              <p className="text-white font-black text-sm tracking-wider hidden sm:block">VAPE<span className="text-amber-400">-</span>YARD</p>
            </div>
            {/* Search */}
            <div className="flex-1 max-w-xl relative hidden md:block">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text" value={headerSearch} onChange={e => setHeaderSearch(e.target.value)}
                placeholder="Search models, sales, or help..."
                className="w-full bg-zinc-900 border border-zinc-800 rounded-xl pl-10 pr-4 py-2 text-sm text-white placeholder-zinc-500 outline-none focus:border-amber-400"
                data-testid="topbar-search"
              />
            </div>
            <div className="flex-1 md:hidden" />
            {/* Bell + 3 action buttons */}
            <div className="flex items-center gap-2">
              {/* Credit Score */}
              <button
                onClick={() => setShowCreditScoreModal(true)}
                data-testid="topbar-credit-score"
                className="flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-black font-black px-3 py-2 rounded-xl text-[11px] uppercase tracking-widest shadow"
                title="View my Credit Score"
              >
                <Award className="w-3.5 h-3.5" />
                <span className="hidden md:inline">Credit Score</span>
              </button>
              {/* Language with dropdown arrow */}
              <button
                onClick={() => setShowLangModal(true)}
                data-testid="topbar-lang"
                className="flex items-center gap-1 bg-zinc-800 hover:bg-zinc-700 text-white font-black px-2.5 py-2 rounded-xl text-[11px] uppercase tracking-widest border border-zinc-700"
                title="Change language"
              >
                <span className="text-base leading-none" data-testid="topbar-lang-flag">{LANG_FLAGS[lang] || "🇫🇷"}</span><span className="hidden md:inline ml-1">{(lang || "fr").toUpperCase()}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              {/* Tracking (visible only in renewal) */}
              {(tracking?.in_renewal || tracking?.status) && (
                <button
                  onClick={() => setShowTrackingModal(true)}
                  data-testid="topbar-tracking"
                  className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-black px-3 py-2 rounded-xl text-[11px] uppercase tracking-widest shadow relative"
                  title="Tracking renouvèlman"
                >
                  🛵<span className="hidden md:inline">Tracking</span>
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse" />
                </button>
              )}
              <PhotoVerifyButton user={user} />
              <NotificationBell userId={user.id} />
              <button onClick={() => setActiveMenu("profile")} className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full bg-zinc-900 hover:bg-zinc-800 transition" data-testid="topbar-profile-btn">
                {profileImage ? (
                  <img src={profileImage} alt="" className="w-8 h-8 rounded-full object-cover ring-1 ring-amber-400" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-black font-black text-sm">{user.name?.charAt(0).toUpperCase()}</div>
                )}
                <div className="text-left hidden sm:block leading-tight">
                  <p className="text-white font-bold text-xs flex items-center gap-1">
                    {user.name}
                    {user.verified && <CheckCircle2 className="w-3 h-3 text-blue-400 flex-shrink-0" title="Verified by VAPE-YARD" />}
                  </p>
                  <p className="text-zinc-500 text-[10px]">{user.is_nm ? "Network Marketer" : "Stocker"} • {user.code}</p>
                </div>
              </button>
            </div>
          </header>
          {showHistory && <HistoryModal userId={user.id} onClose={() => setShowHistory(false)} />}
          <SupportCenter userId={user.id} userName={user.name} openTrigger={activeMenu === "support"} onClose={() => setActiveMenu("dashboard")} />

          {/* Credit Score Modal (opened from top bar) */}
          {showCreditScoreModal && (
            <div className="fixed inset-0 bg-black/80 z-[80] flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShowCreditScoreModal(false)} data-testid="credit-score-modal">
              <div className="bg-white rounded-2xl p-5 max-w-lg w-full relative my-6 shadow-2xl" onClick={e => e.stopPropagation()}>
                <button onClick={() => setShowCreditScoreModal(false)} className="absolute top-3 right-3 bg-zinc-200 hover:bg-red-500 hover:text-white text-zinc-700 rounded-full w-8 h-8 flex items-center justify-center transition" data-testid="credit-score-modal-close">
                  <X className="w-4 h-4" />
                </button>
                <h3 className="text-zinc-900 font-black text-lg mb-3 flex items-center gap-2">
                  <Award className="w-5 h-5 text-emerald-600" /> My Credit Score
                </h3>
                <div className="mb-3 flex items-center gap-2">
                  <span className="text-zinc-600 text-xs">Change language:</span>
                  <LanguageSwitcher />
                </div>
                <CreditScoreCard userId={user.id} />
              </div>
            </div>
          )}

          {/* Language Modal */}
          {showLangModal && (
            <div className="fixed inset-0 bg-black/80 z-[80] flex items-center justify-center p-4" onClick={() => setShowLangModal(false)} data-testid="lang-modal">
              <div className="bg-white rounded-2xl p-5 max-w-xs w-full relative shadow-2xl" onClick={e => e.stopPropagation()}>
                <button onClick={() => setShowLangModal(false)} className="absolute top-3 right-3 bg-zinc-200 hover:bg-red-500 hover:text-white rounded-full w-8 h-8 flex items-center justify-center"><X className="w-4 h-4" /></button>
                <h3 className="text-zinc-900 font-black text-lg mb-3 flex items-center gap-2">🌐 Chwazi lang</h3>
                <LanguageSwitcher />
              </div>
            </div>
          )}

          {/* Tracking Modal — motorcycle 3-step animation */}
          {showTrackingModal && tracking && (
            <div className="fixed inset-0 bg-black/80 z-[80] flex items-center justify-center p-4" onClick={() => setShowTrackingModal(false)} data-testid="tracking-modal">
              <div className="bg-white rounded-2xl p-5 max-w-md w-full relative shadow-2xl" onClick={e => e.stopPropagation()}>
                <button onClick={() => setShowTrackingModal(false)} className="absolute top-3 right-3 bg-zinc-200 hover:bg-red-500 hover:text-white rounded-full w-8 h-8 flex items-center justify-center"><X className="w-4 h-4" /></button>
                <h3 className="text-zinc-900 font-black text-lg mb-1">🛵 Tracking Stock</h3>
                <p className="text-zinc-500 text-xs mb-5">Kote stock renouvèlman w ye</p>

                {(() => {
                  const status = tracking.status || "preparing";
                  const steps = [
                    {id:"preparing", label:"Preparing Stock", color:"amber"},
                    {id:"on_the_way", label:"On The Way", color:"blue"},
                    {id:"delivered", label:"Delivered", color:"emerald"},
                  ];
                  const activeIdx = steps.findIndex(s => s.id === status);
                  const motoPos = activeIdx === 0 ? "5%" : activeIdx === 1 ? "47%" : "90%";
                  return (
                    <div>
                      {/* Progress bar with motorcycle */}
                      <div className="relative mb-6 pt-10">
                        <div className="absolute top-0 transition-all duration-700" style={{left: motoPos, transform: 'translateX(-50%)'}}>
                          <span className="text-3xl">🛵</span>
                        </div>
                        <div className="h-1.5 bg-zinc-200 rounded-full overflow-hidden mx-5">
                          <div className={`h-full rounded-full transition-all duration-700 ${activeIdx === 0 ? "bg-amber-400 w-[10%]" : activeIdx === 1 ? "bg-blue-400 w-1/2" : "bg-emerald-400 w-full"}`} />
                        </div>
                        <div className="flex justify-between -mt-2 px-1">
                          {steps.map((s, i) => (
                            <div key={s.id} className="flex flex-col items-center" data-testid={`track-step-${s.id}`}>
                              <div className={`w-4 h-4 rounded-full border-2 ${
                                i < activeIdx ? `bg-${s.color}-500 border-${s.color}-500` :
                                i === activeIdx ? `bg-${s.color}-400 border-${s.color}-600 ring-4 ring-${s.color}-200 animate-pulse` :
                                "bg-white border-zinc-300"
                              }`} style={{
                                backgroundColor: i<=activeIdx ? (s.color==="amber"?"#f59e0b":s.color==="blue"?"#3b82f6":"#10b981") : "white",
                                borderColor: i<=activeIdx ? (s.color==="amber"?"#f59e0b":s.color==="blue"?"#3b82f6":"#10b981") : "#d4d4d8",
                              }} />
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Status details */}
                      <div className="space-y-3">
                        {steps.map((s, i) => (
                          <div key={s.id} className={`p-3 rounded-xl border-2 ${i === activeIdx ? "bg-amber-50 border-amber-400 shadow-md" : i < activeIdx ? "bg-emerald-50 border-emerald-300" : "bg-zinc-50 border-zinc-200 opacity-60"}`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-black text-sm" style={{color: i<=activeIdx ? (s.color==="amber"?"#92400e":s.color==="blue"?"#1e40af":"#065f46") : "#71717a"}}>
                                  {i < activeIdx && "✓ "}{s.label}
                                </p>
                                {s.id === "preparing" && tracking.estimated_date && (
                                  <p className="text-amber-700 text-[11px] mt-0.5 font-semibold">Estime: {tracking.estimated_date}</p>
                                )}
                                {s.id === status && tracking.note && (
                                  <p className="text-zinc-600 text-[11px] mt-0.5 italic">{tracking.note}</p>
                                )}
                              </div>
                              {i === activeIdx && <span className="text-xs font-black uppercase tracking-widest" style={{color: s.color==="amber"?"#92400e":s.color==="blue"?"#1e40af":"#065f46"}}>Aktyèl</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* ============== CONTENT AREA ============== */}
          <main className="flex-1 bg-gradient-to-b from-zinc-50 to-white px-4 lg:px-6 py-5 overflow-x-hidden" data-testid="stocker-main-content">

        {/* OVERDUE / RETARD BANNER (5-day grace) */}
        {user.is_overdue && (
          <div className="w-full mb-4 bg-gradient-to-r from-red-900/60 to-orange-900/60 border-2 border-red-500 rounded-xl p-3 flex items-center gap-3 animate-pulse" data-testid="overdue-banner">
            <AlertTriangle className="w-7 h-7 text-red-400 flex-shrink-0" />
            <div className="text-left flex-1">
              <p className="font-bold text-red-200 text-sm">⚠️ RETARD — Sik ou ekspire</p>
              <p className="text-red-100/80 text-xs">
                Ou ekspire depi {user.days_overdue} jou.
                {user.in_grace
                  ? ` Ou gen ${user.grace_remaining} jou gras rete pou fè retrait avan tout aksè restren.`
                  : ` Peryòd gras la (5j) depase — kontakte CEO pou renouvle imedyatman.`}
              </p>
            </div>
          </div>
        )}

        {/* CREDIT SCORE now in top bar modal — removed from here */}

        {/* Gift Notifications */}
        {canClaimGift100 && (
          <button onClick={() => setShowGiftBox(100)} className="w-full mb-4 bg-gradient-to-r from-yellow-500 to-orange-500 p-3 rounded-xl flex items-center gap-3 text-zinc-900">
            <Gift className="w-8 h-8 animate-bounce" />
            <div className="text-left">
              <p className="font-bold text-sm">🎁 {t("gift_unlocked")}</p>
              <p className="text-xs">{t("gift_claim_100")}</p>
            </div>
          </button>
        )}
        
        {canClaimGift150 && (
          <button onClick={() => setShowGiftBox(150)} className="w-full mb-4 bg-gradient-to-r from-purple-500 to-pink-500 p-3 rounded-xl flex items-center gap-3 text-white">
            <Gift className="w-8 h-8 animate-bounce" />
            <div className="text-left">
              <p className="font-bold text-sm">🎁 {t("gift_vip")}</p>
              <p className="text-xs">{t("gift_claim_150")}</p>
            </div>
          </button>
        )}

        {/* Alerts */}
        {alerts.length > 0 && activeMenu === "dashboard" && (
          <div className="space-y-2 mb-4">
            {alerts.map((alert, idx) => (
              <div key={idx} className={`alert-banner ${alert.severity}`}>
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm">{alert.message}</span>
              </div>
            ))}
          </div>
        )}

        {/* DASHBOARD VIEW */}
        {DASH_SECTION_MENUS.includes(activeMenu) && (
          <>
            {/* ============ WELCOME COVER (hero banner, matches reference) ============ */}
            <div className="relative rounded-2xl overflow-hidden mb-5 shadow-xl" style={{minHeight: 220}} data-testid="country-welcome-cover" data-no-contrast-guard>
              {coverSlides(countryCover).length ? (
                <CoverMedia cover={countryCover} />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-black to-zinc-900">
                  <div className="absolute right-6 top-6 opacity-20 text-white text-6xl font-black italic" style={{fontFamily:'var(--font-heading)'}}>Flavors</div>
                  <div className="absolute right-6 bottom-6 opacity-20 text-amber-400 text-5xl font-black italic">Future</div>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
              <div className="relative p-6 md:p-8 min-h-[220px] flex flex-col justify-center max-w-xl">
                <p className="text-white/90 text-sm md:text-base font-light leading-tight">Welcome Back,</p>
                <h1 className="text-white font-black text-3xl md:text-5xl leading-tight tracking-tight mt-0.5" style={{fontFamily:'var(--font-heading)'}}>
                  {user.name}
                </h1>
                <p className="text-zinc-300 text-xs md:text-sm mt-1.5">{user.is_nm ? "Network Marketer" : "Stocker"} Code: <span className="font-mono font-bold text-amber-300">{user.code}</span></p>
                <p className="text-zinc-400 text-xs md:text-sm italic mt-1">{countryCover?.subtitle || "Keep pushing, great things ahead!"}</p>
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <button onClick={() => setActiveMenu("my-stock")} className="bg-amber-400 hover:bg-amber-300 text-black text-xs md:text-sm font-black px-5 py-2 rounded-full shadow-lg transition-transform active:scale-95" data-testid="cover-view-stock-btn">
                    View My Stock →
                  </button>
                  {countryCover?.title && (
                    <span className="bg-white/10 backdrop-blur border border-white/20 text-white font-black text-[10px] px-3 py-1.5 rounded-full uppercase tracking-widest">
                      {countryCover.title}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <UnsoldStockAlert user={user} />
            {/* ============ STATS CARDS (4) — matches reference ============ */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5" data-testid="stats-cards-row">
              {/* Total Sold — yellow */}
              <div className="bg-gradient-to-br from-amber-50 to-amber-200 border border-amber-300 rounded-2xl p-4 shadow-sm hover:shadow-md transition" data-testid="stat-total-sold">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-amber-700" /></div>
                  <BarChart3 className="w-4 h-4 text-amber-600/60" />
                </div>
                <p className="text-amber-900 text-[11px] font-bold uppercase tracking-wider">Total Sold</p>
                <p className="text-amber-900 font-black text-3xl mt-0.5">{getCycleSold(user)}</p>
                <p className="text-amber-700 text-[10px] font-semibold mt-0.5">This Cycle</p>
              </div>
              {/* Commission — black */}
              <div className="bg-gradient-to-br from-zinc-900 to-black border border-zinc-800 rounded-2xl p-4 shadow-sm hover:shadow-md transition relative" data-testid="stat-commission">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center"><Wallet className="w-5 h-5 text-amber-400" /></div>
                  <span className="text-zinc-500 text-[10px]">›</span>
                </div>
                <p className="text-zinc-400 text-[11px] font-bold uppercase tracking-wider">Commission</p>
                <p className="text-white font-black text-3xl mt-0.5">${commission_earned.toFixed(2)}</p>
                <p className="text-amber-400 text-[10px] font-semibold mt-0.5">{commission_rate}%</p>
              </div>
              {/* Bonuses — mint/teal */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-100 border border-teal-200 rounded-2xl p-4 shadow-sm hover:shadow-md transition" data-testid="stat-bonuses">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center"><Gift className="w-5 h-5 text-teal-700" /></div>
                </div>
                <p className="text-teal-900 text-[11px] font-bold uppercase tracking-wider">Bonus</p>
                <p className="text-teal-900 font-black text-3xl mt-0.5">${actualBonus.toFixed(2)}</p>
                <p className="text-teal-700 text-[10px] font-semibold mt-0.5">This Cycle</p>
              </div>
              <TeamBonusStatCard userId={user.id} testid="stat-team-bonus" />
              {/* Balance — gray */}
              <div className="bg-gradient-to-br from-zinc-100 to-zinc-200 border border-zinc-300 rounded-2xl p-4 shadow-sm hover:shadow-md transition" data-testid="stat-balance">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-10 h-10 rounded-xl bg-zinc-900 flex items-center justify-center"><Package className="w-5 h-5 text-amber-400" /></div>
                </div>
                <p className="text-zinc-700 text-[11px] font-bold uppercase tracking-wider">Balance</p>
                <p className="text-zinc-900 font-black text-3xl mt-0.5">${totalGains.toFixed(2)}</p>
                <p className="text-zinc-500 text-[10px] font-semibold mt-0.5">Pending Payment</p>
              </div>
            </div>

            {/* ============ QUICK ACTION BUTTONS ============ */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-5" data-testid="quick-actions-row">
              {[
                {id: "sales", icon: FileText, title: "Report Sale", sub: "Add your new sale"},
                {id: "renewal", icon: Truck, title: "Renewal Request", sub: "Request new stock"},
                {id: "withdrawal", icon: Wallet, title: "Withdraw Money", sub: "Request your earnings"},
                {id: "history", icon: Clock, title: "Order History", sub: "View past requests"},
                {id: "commissions", icon: BarChart3, title: "Commissions", sub: "View your earnings"},
              ].map(qa => (
                <button
                  key={qa.id}
                  onClick={() => { if (qa.id === "history") setShowHistory(true); else setActiveMenu(qa.id); }}
                  data-testid={`quick-action-${qa.id}`}
                  className="group bg-white border border-zinc-200 hover:border-amber-400 hover:bg-amber-50 rounded-2xl p-3.5 text-left shadow-sm hover:shadow-md transition-all"
                >
                  <div className="w-9 h-9 bg-zinc-100 group-hover:bg-amber-200 rounded-xl flex items-center justify-center mb-2 transition-colors">
                    <qa.icon className="w-4 h-4 text-zinc-700 group-hover:text-amber-800" />
                  </div>
                  <p className="text-zinc-900 font-black text-xs">{qa.title}</p>
                  <p className="text-zinc-500 text-[10px] mt-0.5">{qa.sub}</p>
                </button>
              ))}
            </div>

            {/* ============ NEW ARRIVALS mini-banner ============ */}
            <div className="relative rounded-2xl overflow-hidden mb-5 bg-gradient-to-r from-black via-zinc-900 to-black border border-zinc-800" data-testid="new-arrivals-banner">
              <div className="relative p-5 md:p-6 flex items-center justify-between gap-4">
                <div className="flex-1">
                  <span className="inline-block bg-amber-400 text-black font-black text-[10px] px-2.5 py-0.5 rounded uppercase tracking-widest mb-2">New</span>
                  <h3 className="text-white font-black text-xl md:text-2xl leading-tight" style={{fontFamily:'var(--font-heading)'}}>
                    <span className="text-amber-400">VAPE-YARD</span><br/>NEW ARRIVALS
                  </h3>
                  <p className="text-zinc-400 text-xs mt-1">TOP FLAVORS. TOP QUALITY.</p>
                  <button onClick={() => siteConfig.new_arrival_link ? window.open(siteConfig.new_arrival_link, "_blank", "noopener") : setActiveMenu("my-stock")} className="mt-3 bg-white/10 hover:bg-amber-400 hover:text-black border border-white/20 text-white text-[11px] font-black px-4 py-1.5 rounded-full uppercase tracking-widest transition" data-testid="new-arrivals-cta">
                    {siteConfig.new_arrival_label || "EXPLORE MORE"} →
                  </button>
                </div>
                <div className="hidden md:flex items-center gap-6 pr-4">
                  <div className="text-center">
                    <div className="w-10 h-10 bg-amber-400/20 rounded-full flex items-center justify-center mb-1"><Zap className="w-5 h-5 text-amber-400" /></div>
                    <p className="text-white text-[10px] font-bold leading-tight">LONG<br/>LASTING</p>
                  </div>
                  <div className="text-center">
                    <div className="w-10 h-10 bg-amber-400/20 rounded-full flex items-center justify-center mb-1"><Sparkles className="w-5 h-5 text-amber-400" /></div>
                    <p className="text-white text-[10px] font-bold leading-tight">PREMIUM<br/>QUALITY</p>
                  </div>
                  <div className="text-center">
                    <div className="w-10 h-10 bg-amber-400/20 rounded-full flex items-center justify-center mb-1"><Award className="w-5 h-5 text-amber-400" /></div>
                    <p className="text-white text-[10px] font-bold leading-tight">TRENDING<br/>FLAVORS</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Previous cycle outstanding banner (visible until CEO clicks Kontwòl Efektye) */}
            {user.previous_cycle_pending && user.previous_cycle && (user.previous_cycle.initial_stock > 0 || user.previous_cycle.remaining > 0) && (
              <div className="bg-amber-950/30 border-2 border-amber-500/50 rounded-xl p-3 mb-3 animate-pulse-slow" data-testid="prev-cycle-banner">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">⏳</span>
                    <div>
                      <p className="text-amber-300 font-black text-sm">Sik anvan an poko fèmen</p>
                      <p className="text-zinc-400 text-[10px]">Tann CEO peze Kontwòl Efektye</p>
                    </div>
                  </div>
                  <div className="flex gap-2 text-xs">
                    <div className="bg-zinc-950/60 px-2 py-1 rounded-lg">
                      <span className="text-zinc-500 text-[10px]">Te gen</span>{" "}
                      <span className="text-white font-bold">{user.previous_cycle.initial_stock}</span>
                    </div>
                    <div className="bg-zinc-950/60 px-2 py-1 rounded-lg">
                      <span className="text-zinc-500 text-[10px]">Vann</span>{" "}
                      <span className="text-emerald-400 font-bold">{user.previous_cycle.sold}</span>
                    </div>
                    <div className="bg-red-950/40 border border-red-500/40 px-2 py-1 rounded-lg">
                      <span className="text-red-400 text-[10px]">Rete</span>{" "}
                      <span className="text-red-300 font-black">{user.previous_cycle.remaining}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Main Stats */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="glass-panel p-4 text-center">
                <DollarSign className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-emerald-400">${getCycleBonus(user).toFixed(2)}</p>
                <p className="text-zinc-500 text-xs">{t("cycle_bonus")}</p>
              </div>
              <div className="glass-panel p-4 text-center">
                <TrendingUp className="w-5 h-5 text-violet-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-white">${(getCycleSold(user) * (user.vape_models?.[0]?.price || 0)).toFixed(0)}</p>
                <p className="text-zinc-500 text-xs">{t("revenue")}</p>
              </div>
              <div className="glass-panel p-4 text-center">
                <Package className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-white">{getCycleSold(user)}</p>
                <p className="text-zinc-500 text-xs">{t("vapes_sold")}</p>
              </div>
              <div className="glass-panel p-4 text-center">
                <Award className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-yellow-400">{commission_rate}%</p>
                <p className="text-zinc-500 text-xs">{t("commission")}</p>
              </div>
            </div>

            {/* Commission Earned */}
            <div className="glass-panel p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-zinc-400 text-sm">{t("commission_earned")} ({commission_rate}%)</span>
                <span className="text-emerald-400 font-bold">${commission_earned.toFixed(2)}</span>
              </div>
              <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full" 
                     style={{ width: `${Math.min(100, (getCycleSold(user) / Math.max(1, user.initial_stock || 1)) * 100)}%` }} />
              </div>
              <p className="text-zinc-600 text-xs mt-1">{getCycleSold(user)} / {user.initial_stock || 0} {t("vapes_sold_lower")}</p>
            </div>

            {/* Stock Progress */}
            <div className="glass-panel p-4 mb-4">
              <div className="flex justify-between mb-2">
                <span className="text-zinc-400 text-sm flex items-center gap-2"><Package className="w-4 h-4" /> {t("stock")}</span>
                <span className="text-white font-bold">{user.current_stock || 0} / {user.initial_stock || 0}</span>
              </div>
              <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                <div className={`stock-bar h-full rounded-full ${getProgressColor()}`} style={{ width: `${user.stock_percentage || 0}%` }} />
              </div>
              <div className="flex justify-between mt-2">
                <span className={`text-sm font-bold ${getProgressColor() === 'good' ? 'text-emerald-400' : getProgressColor() === 'warning' ? 'text-amber-400' : 'text-red-400'}`}>
                  {user.stock_percentage?.toFixed(1) || 0}% {t("remaining_pct_suffix")}
                </span>
                <span className="text-zinc-500 text-sm">{user.days_remaining || 0} {t("days")}</span>
              </div>
              {(user.carryover_from_previous_cycle || 0) > 0 && (
                <div className="mt-3 p-3 rounded-xl border-2 border-red-500 animate-pulse" style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)' }} data-testid="stocker-carryover-alert">
                  <p className="text-sm font-black text-red-400">🔴 {user.carryover_from_previous_cycle} vape sik ki sot pase a — POKO VANN</p>
                  <p className="text-xs mt-1 text-red-300">Vann yo an premye pou konplete sik anvan an.</p>
                  {(user.vape_models || []).filter(m => Math.max(0, (m.carryover_qty || 0) - (m.sold || 0)) > 0).map((m, i) => {
                    const leftUnsold = Math.max(0, (m.carryover_qty || 0) - (m.sold || 0));
                    return (
                      <div key={i} className="mt-2 flex items-center justify-between bg-red-950/40 rounded-lg px-2 py-1" data-testid={`stocker-carryover-model-${i}`}>
                        <span className="text-red-200 text-xs font-bold">{m.name}</span>
                        <span className="text-red-300 text-xs">Rete: <b>{leftUnsold}</b> @ ${(m.price || 0).toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ========== MY STOCK VISUAL — foto pwodwi + bar pwogresyon pou chak modèl (reference-style light theme) ========== */}
            {(user.vape_models || []).length > 0 && (
              <div className="bg-white border border-zinc-200 rounded-2xl p-4 md:p-5 shadow-sm mb-5" data-testid="my-stock-visual">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-amber-400 rounded-xl flex items-center justify-center shadow"><Package className="w-5 h-5 text-black" /></div>
                    <div>
                      <h3 className="text-zinc-900 font-black text-base">My Stock</h3>
                      <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-black">
                        {user.current_stock || 0} LEFT OUT OF {user.initial_stock || 0}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="bg-amber-400 text-black text-[10px] font-black px-3 py-1.5 rounded-full uppercase tracking-widest shadow">Current Stock</span>
                    <button className="hidden md:inline-flex items-center gap-1 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-[11px] font-bold px-3 py-1.5 rounded-full">All Models ›</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {(user.vape_models || []).map((m, i) => {
                    const imgUrl = productImageFor(m.name, m.image_url);
                    const sold = parseInt(m.sold || 0);
                    const qty = parseInt(m.quantity || 0);
                    const left = Math.max(0, qty - sold);
                    const pct = qty > 0 ? Math.min(100, Math.round((left / qty) * 100)) : 0;
                    return (
                      <div key={i} className="bg-zinc-50 border border-zinc-200 rounded-xl p-3 hover:border-amber-400 hover:shadow-md transition" data-testid={`my-stock-model-${i}`}>
                        <div className="aspect-square bg-white rounded-lg overflow-hidden flex items-center justify-center mb-2 border border-zinc-100">
                          {imgUrl ? (
                            <img src={imgUrl} alt={m.name} className="w-full h-full object-contain" onError={(e) => { e.target.style.display='none'; }} />
                          ) : (
                            <div className="text-5xl opacity-20">💨</div>
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-zinc-900 font-black text-xs truncate" title={m.name}>{m.name}</p>
                          <span className="text-zinc-400 text-[10px]">›</span>
                        </div>
                        <div className="h-1.5 bg-zinc-200 rounded-full overflow-hidden my-2">
                          <div className={`h-full rounded-full ${pct > 50 ? "bg-amber-400" : pct > 20 ? "bg-amber-500" : "bg-red-500"}`} style={{width: `${pct}%`}} />
                        </div>
                        <div className="flex justify-between text-[11px]">
                          <span className={`font-black ${left === 0 ? "text-red-600" : left <= 2 ? "text-amber-700" : "text-emerald-700"}`}>Left: {left}</span>
                          <span className="text-zinc-500">Sold: {sold}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ========== 3-COLUMN: Cycle Progress + Next Control + Stock Waiting Comp ========== */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5" data-testid="three-col-widgets">
              {/* Cycle Progress Donut */}
              <div className="bg-white border border-zinc-200 rounded-2xl p-4 shadow-sm" data-testid="cycle-progress-donut">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 bg-emerald-100 rounded-lg flex items-center justify-center"><TrendingUp className="w-4 h-4 text-emerald-600" /></div>
                  <h4 className="text-zinc-900 font-black text-sm">Cycle Progress</h4>
                </div>
                <div className="flex items-center gap-4">
                  {(() => {
                    const sold = getCycleSold(user);
                    const total = user.initial_stock || 0;
                    const pct = total > 0 ? Math.min(100, Math.round((sold / total) * 100)) : 0;
                    const r = 36; const C = 2 * Math.PI * r; const dash = (pct/100) * C;
                    return (
                      <svg width="96" height="96" viewBox="0 0 96 96">
                        <circle cx="48" cy="48" r={r} stroke="#e4e4e7" strokeWidth="10" fill="none" />
                        <circle cx="48" cy="48" r={r} stroke="#10b981" strokeWidth="10" fill="none" strokeDasharray={`${dash} ${C}`} strokeDashoffset="0" strokeLinecap="round" transform="rotate(-90 48 48)" />
                        <text x="48" y="54" textAnchor="middle" fontSize="18" fontWeight="900" fill="#10b981">{pct}%</text>
                      </svg>
                    );
                  })()}
                  <div className="flex-1">
                    <p className="text-zinc-900 font-black text-xl">{getCycleSold(user)} / {user.initial_stock || 0}</p>
                    <p className="text-zinc-500 text-xs">Sold</p>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center gap-1.5 text-[11px]"><span className="w-2 h-2 rounded-full bg-emerald-500" /><span className="text-zinc-700 font-bold">Sold: {getCycleSold(user)}</span></div>
                      <div className="flex items-center gap-1.5 text-[11px]"><span className="w-2 h-2 rounded-full bg-zinc-400" /><span className="text-zinc-600">Remaining: {user.current_stock || 0}</span></div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Next Control Date */}
              <div className="bg-white border border-zinc-200 rounded-2xl p-4 shadow-sm" data-testid="next-control-date">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 bg-amber-100 rounded-lg flex items-center justify-center"><Calendar className="w-4 h-4 text-amber-700" /></div>
                  <h4 className="text-zinc-900 font-black text-sm">Next Control Date</h4>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 bg-amber-50 border-2 border-amber-300 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-7 h-7 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-zinc-900 font-black text-base leading-tight">
                      {(() => {
                        try { return new Date(user.end_date).toLocaleDateString("en-US", {month:"short", day:"numeric", year:"numeric"}); } catch { return "—"; }
                      })()}
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3 text-zinc-400" />
                      <p className="text-zinc-600 text-xs font-bold">{user.days_remaining || 0} Days Remaining</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Stock Waiting Compensation */}
              <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 shadow-sm" data-testid="stock-waiting-comp">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-amber-200 rounded-lg flex items-center justify-center"><Clock className="w-4 h-4 text-amber-800" /></div>
                    <h4 className="text-amber-900 font-black text-sm">Stock Waiting Compensation</h4>
                  </div>
                  <span className="text-amber-400">›</span>
                </div>
                <span className="inline-block bg-amber-300 text-amber-950 font-black text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest mb-2">Pending</span>
                {(() => {
                  const waitingDays = user.awaiting_new_stock_days || user.waiting_days || (user.renewal_status === "in_progress" ? 1 : 0);
                  const dailyComp = 1.00;
                  const accumulated = waitingDays * dailyComp;
                  return (
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between"><span className="text-amber-900">Waiting Time:</span><span className="font-black text-amber-950">{waitingDays} Day{waitingDays !== 1 ? "s" : ""}</span></div>
                      <div className="flex justify-between"><span className="text-amber-900">Daily Compensation:</span><span className="font-black text-amber-950">${dailyComp.toFixed(2)}</span></div>
                      <div className="flex justify-between"><span className="text-amber-900">Accumulated Amount:</span><span className="font-black text-amber-950">${accumulated.toFixed(2)}</span></div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Performance Chart */}
            <div className="glass-panel p-4">
              <h3 className="text-white font-bold mb-3 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-violet-400" /> {t("monthly_performance")}</h3>
              <div className="grid grid-cols-4 gap-2 text-center">
                {['S1', 'S2', 'S3', 'S4'].map((week, idx) => {
                  const height = Math.random() * 60 + 20;
                  return (
                    <div key={week} className="flex flex-col items-center">
                      <div className="w-full h-20 bg-zinc-800 rounded-lg flex items-end justify-center p-1">
                        <div className="w-full bg-gradient-to-t from-violet-600 to-violet-400 rounded" style={{ height: `${height}%` }} />
                      </div>
                      <span className="text-zinc-500 text-xs mt-1">{week}</span>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-3 text-xs text-zinc-500">
                <span>{t("this_month_sales")}: {getCycleSold(user)} {t("sales_word")}</span>
                <span className="text-emerald-400">+{commission_rate}% {t("commission").toLowerCase()}</span>
              </div>
            </div>

            {/* Renewal status message */}
            {(user.renewal_status === "in_progress" || user.awaiting_new_stock) && (
              <div className="glass-panel p-4 mt-4 border border-blue-500/40 bg-blue-500/5" data-testid="renewal-in-progress">
                <div className="flex items-center gap-3">
                  <div className="animate-pulse"><Package className="w-6 h-6 text-blue-400" /></div>
                  <div>
                    <p className="text-blue-400 font-bold">Renouvellement Stock en cours</p>
                    <p className="text-zinc-500 text-sm">{t("ceo_will_add_stock")}</p>
                    <p className="text-emerald-400 text-xs mt-1 italic animate-pulse">⏳ {t("stock_in_progress")}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Lifetime Ventes */}
            {(user.lifetime_sold || 0) > 0 && (
              <div className="glass-panel p-4 mt-4" data-testid="lifetime-stats">
                <h3 className="text-white font-bold mb-2 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-violet-400" /> {t("total_sales_since_start")}
                </h3>
                <div className="flex items-center justify-between bg-zinc-800/50 p-3 rounded-xl">
                  <span className="text-zinc-400">{t("total_vapes_sold")}</span>
                  <span className="text-emerald-400 font-black text-2xl">{user.lifetime_sold || 0}</span>
                </div>
              </div>
            )}

            {/* Remaining vapes in RED */}
            {remainingVapes > 0 && (
              <div className="glass-panel p-4 mt-4 border border-red-500/40 bg-red-500/5">
                <div className="flex items-center gap-3">
                  <Package className="w-6 h-6 text-red-400" />
                  <div>
                    <p className="text-red-400 font-bold">{remainingVapes} vape(s) restant(s)</p>
                    <p className="text-red-400/70 text-sm">Vous n'avez pas encore terminé votre stock</p>
                  </div>
                </div>
              </div>
            )}

            {/* ===== TRANSACTION HISTORY ===== */}
            {user.transactions && user.transactions.length > 0 && (
              <div className="glass-panel p-4 mt-4" data-testid="transaction-history">
                <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-violet-400" /> Historique Transactions
                </h3>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {user.transactions.slice().reverse().map((tx) => (
                    <div key={tx.id} className={`flex items-center justify-between p-3 rounded-lg ${tx.status === 'paid' ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-amber-500/10 border border-amber-500/20'}`}>
                      <div>
                        <p className="text-white text-sm font-bold">${tx.amount?.toFixed(2)}</p>
                        <p className="text-zinc-500 text-xs">{new Date(tx.date).toLocaleDateString('fr-FR')} {new Date(tx.date).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}</p>
                      </div>
                      <span className={`text-xs px-3 py-1 rounded-lg font-bold ${tx.status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                        {tx.status === 'paid' ? 'Payé' : 'En attente'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ===== RETIRER L'ARGENT - VISIBLE ON MAIN DASHBOARD ===== */}
            <div className="glass-panel p-5 mt-4 border border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-emerald-600/10" data-testid="dashboard-withdraw-section">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-emerald-500/20 p-3 rounded-xl">
                  <DollarSign className="w-7 h-7 text-emerald-400" />
                </div>
                <div>
                  <h4 className="text-white font-bold text-lg">{t("withdraw_money")}</h4>
                  <p className="text-zinc-500 text-sm">{t("your_gains")}: ${totalGains.toFixed(2)}</p>
                </div>
              </div>
              
              {withdrawStep === "idle" && (
                <>
                  <div className="bg-zinc-900/60 rounded-xl p-4 mb-4">
                    <div className="flex justify-between py-2 border-b border-zinc-800">
                      <span className="text-zinc-400 text-sm">{t("vapes_sold_current_cycle")}</span>
                      <span className="text-white font-bold">{getCycleSold(user)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-zinc-800">
                      <span className="text-zinc-400 text-sm">{t("sales_revenue")}</span>
                      <span className="text-white font-bold">${actualRevenue.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-zinc-800">
                      <span className="text-zinc-400 text-sm">{t("bonus_per_vape_label")} (${bonus_per_vape}/{t("per_vape")})</span>
                      <span className="text-emerald-400 font-bold">${actualBonus.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-zinc-800">
                      <span className="text-zinc-400 text-sm">{t("commission_calc")} ({commission_rate}%)</span>
                      <span className="text-yellow-400 font-bold">${commission_earned.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-3 mt-1">
                      <span className="text-white font-bold">{t("total_amount")}</span>
                      <span className="text-emerald-400 font-black text-2xl">${totalGains.toFixed(2)}</span>
                    </div>
                  </div>
                  <button onClick={handleWithdraw}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-3 transition-all active:scale-95"
                    data-testid="dashboard-withdraw-btn">
                    <DollarSign className="w-6 h-6" /> {t("withdraw_money")}
                  </button>
                </>
              )}
              {withdrawStep === "waiting" && (
                <div className="text-center py-8">
                  <div className="w-24 h-24 mx-auto mb-4 rounded-full border-4 border-emerald-500 flex items-center justify-center animate-pulse">
                    <span className="text-4xl font-black text-emerald-400">{withdrawTimer}</span>
                  </div>
                  <p className="text-white font-bold text-lg mb-2">Veuillez patienter...</p>
                  <p className="text-zinc-400 text-sm">Traitement en cours</p>
                  <div className="mt-4 h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full transition-all duration-1000" style={{ width: `${((10 - withdrawTimer) / 10) * 100}%` }} />
                  </div>
                </div>
              )}
              {withdrawStep === "approved" && (
                <div className="py-4" data-testid="withdraw-payment-method-step">
                  <div className="text-center mb-4">
                    <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <CheckCircle className="w-8 h-8 text-emerald-400" />
                    </div>
                    <p className="text-emerald-400 font-bold text-lg mb-1">Demande Approuvée!</p>
                    <p className="text-zinc-400 text-sm">Chwazi kijan w vle resevwa peman: ${totalGains.toFixed(2)}</p>
                  </div>

                  {!payMethod && (
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      <button onClick={() => setPayMethod("zelle")} data-testid="pay-method-zelle" className="flex flex-col items-center gap-2 bg-purple-600/20 hover:bg-purple-600/40 border-2 border-purple-500/40 hover:border-purple-400 rounded-xl p-4 transition-all">
                        <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center text-white font-black text-xl">Z</div>
                        <span className="text-white font-bold text-sm">Zelle</span>
                      </button>
                      <button onClick={() => setPayMethod("cashapp")} data-testid="pay-method-cashapp" className="flex flex-col items-center gap-2 bg-emerald-600/20 hover:bg-emerald-600/40 border-2 border-emerald-500/40 hover:border-emerald-400 rounded-xl p-4 transition-all">
                        <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center text-white font-black text-xl">$</div>
                        <span className="text-white font-bold text-sm">Cash App</span>
                      </button>
                      <button onClick={() => setPayMethod("cash")} data-testid="pay-method-cash" className="flex flex-col items-center gap-2 bg-amber-600/20 hover:bg-amber-600/40 border-2 border-amber-500/40 hover:border-amber-400 rounded-xl p-4 transition-all">
                        <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center text-white font-black text-2xl">💵</div>
                        <span className="text-white font-bold text-sm">Cash</span>
                      </button>
                    </div>
                  )}

                  {payMethod && payMethod !== "cash" && (
                    <div className="bg-zinc-900/60 rounded-xl p-4 mb-4 border border-zinc-700" data-testid={`pay-details-${payMethod}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-white ${payMethod === "zelle" ? "bg-purple-600" : "bg-emerald-500"}`}>{payMethod === "zelle" ? "Z" : "$"}</div>
                        <p className="text-white font-bold">{payMethod === "zelle" ? "Zelle" : "Cash App"}</p>
                        <button onClick={() => { setPayMethod(""); setPayAccountId(""); setPayAccountName(""); }} className="ml-auto text-zinc-500 hover:text-white text-xs">Chanje</button>
                      </div>
                      <label className="text-zinc-400 text-xs">{payMethod === "zelle" ? "Nimewo Zelle (telefòn oswa imel)" : "Cash App Tag"}</label>
                      <input type="text" value={payAccountId} onChange={e => setPayAccountId(e.target.value)} placeholder={payMethod === "zelle" ? "e.g. +1 555 123 4567" : "$YourTag"} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white mb-2" data-testid={`pay-input-${payMethod}-id`} />
                      <label className="text-zinc-400 text-xs">Non sou kont lan</label>
                      <input type="text" value={payAccountName} onChange={e => setPayAccountName(e.target.value)} placeholder="Non konplè ki sou kont lan" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white" data-testid={`pay-input-${payMethod}-name`} />
                    </div>
                  )}

                  {payMethod === "cash" && (
                    <div className="bg-amber-950/40 border border-amber-500/40 rounded-xl p-4 mb-4 text-center" data-testid="pay-details-cash">
                      <p className="text-amber-300 font-bold text-lg mb-1">💵 Peman an Cash</p>
                      <p className="text-zinc-400 text-xs">Kontakte CEO pou konfime kote ak lè ou vle chwazi kob ou an</p>
                      <button onClick={() => setPayMethod("")} className="text-zinc-500 hover:text-white text-xs mt-2 underline">Chanje metòd</button>
                    </div>
                  )}

                  {payMethod && (payMethod === "cash" || (payAccountId && payAccountName)) && (
                    <a
                      href={`https://wa.me/17866309220?text=${encodeURIComponent(
                        `🔔 NOTIFICATION — VAPE YARD\n👤 ${user.is_nm ? "Network Marketer" : "Stocker"} : ${user.name}\n🔑 Code : ${user.code}\n🌎 Pays : ${user.country?.toUpperCase()}\n\n📋 DEMANDE DE RETRAIT\n💰 Montant à recevoir : $${totalGains.toFixed(2)}\n\n📦 STOCK\n• Vapes vendues : ${cycleSold}\n• Vapes restantes : ${remainingVapes}\n\n💵 GAINS\n💼 Commission (${commission_rate}%) : $${commission_earned.toFixed(2)}\n🎁 Bonus : $${actualBonus.toFixed(2)}${(parseFloat(user.team_bonus_balance) || 0) > 0 ? `\n⭐ Team Bonus : $${(parseFloat(user.team_bonus_balance) || 0).toFixed(2)}` : ""}\n\n📱 Méthode de paiement : ${payMethod === "zelle" ? "🟣 ZELLE" : payMethod === "cashapp" ? "💚 CASH APP" : "💵 CASH"}${payMethod !== "cash" ? `\n👤 Nom sur compte : ${payAccountName}\n🔢 ${payMethod === "zelle" ? "Numéro Zelle" : "Cash Tag"} : ${payAccountId}` : ""}\n\n✅ Statut : Approuvée — en attente de paiement`
                      )}`}
                      target="_blank" rel="noopener noreferrer"
                      onClick={() => {
                        // Persist payment method on backend withdrawal record
                        try {
                          axios.post(`${API}/stockers/${user.id}/withdraw-details`, {
                            payment_method: payMethod,
                            account_id: payAccountId || null,
                            account_name: payAccountName || null,
                          }).catch(() => {});
                        } catch(_) {}
                      }}
                      className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-3"
                      data-testid="withdraw-whatsapp-final-btn"
                    >
                      <MessageCircle className="w-6 h-6" /> Voye sou WhatsApp CEO
                    </a>
                  )}

                  <p className="text-zinc-500 text-xs mt-3 text-center">+1 786 630 9220</p>
                  <button onClick={() => { setWithdrawStep("idle"); setPayMethod(""); setPayAccountId(""); setPayAccountName(""); }} className="w-full mt-2 text-zinc-500 hover:text-zinc-300 text-sm underline">Fèmen</button>
                </div>
              )}
            </div>

            {/* ===== RENOUVELER STOCK - VISIBLE ON MAIN DASHBOARD ===== */}
            <div className="glass-panel p-5 mt-4 border border-blue-500/30 bg-gradient-to-br from-blue-500/5 to-blue-600/10" data-testid="dashboard-renew-section">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-blue-500/20 p-3 rounded-xl">
                  <Package className="w-7 h-7 text-blue-400" />
                </div>
                <div>
                  <h4 className="text-white font-bold text-lg">{t("renew_stock")}</h4>
                  <p className="text-blue-400 text-sm">{user.awaiting_new_stock || user.renewal_status ? t("request_pending") : sellPercentage >= 50 ? t("eligible") : `${sellPercentage.toFixed(0)}% ${t("not_eligible_min")}`}</p>
                </div>
              </div>

              {renewStep === "idle" && !renewalStatus && !user.awaiting_new_stock && (
                <>
                  <div className="bg-zinc-900/60 rounded-xl p-4 mb-4">
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <p className="text-zinc-500 text-xs">{t("stock")}</p>
                        <p className="text-white font-bold text-lg">{user.initial_stock || 0}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500 text-xs">{t("sold")}</p>
                        <p className="text-emerald-400 font-bold text-lg">{getCycleSold(user)}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500 text-xs">{t("remaining")}</p>
                        <p className={`font-bold text-lg ${remainingVapes > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{remainingVapes}</p>
                      </div>
                    </div>
                    <div className="mt-3 h-3 bg-zinc-800 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${sellPercentage >= 50 ? 'bg-emerald-400' : 'bg-amber-400'}`} style={{ width: `${Math.min(100, sellPercentage)}%` }} />
                    </div>
                  </div>
                  <button onClick={handleRenewStock}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-3"
                    data-testid="dashboard-renew-btn">
                    <Package className="w-6 h-6" /> {t("request_renewal")}
                  </button>
                </>
              )}
              {renewStep === "not_eligible" && (
                <div className="text-center py-6">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                    <AlertTriangle className="w-10 h-10 text-red-400" />
                  </div>
                  <p className="text-red-400 font-bold text-lg mb-2">Non Éligible</p>
                  <p className="text-zinc-400 text-sm mb-4">Ventes à {sellPercentage.toFixed(0)}% — minimum 50% requis. Contactez votre Manager.</p>
                  <a href={`https://wa.me/13239275787?text=${encodeURIComponent(`Bonjour Manager! Je suis ${user.name} (Code: ${user.code}). Mes ventes sont à ${sellPercentage.toFixed(0)}%. Je voudrais discuter de mon renouvellement.`)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-3">
                    <MessageCircle className="w-6 h-6" /> Contacter Manager via WhatsApp
                  </a>
                  <p className="text-zinc-500 text-xs mt-2">+1 323 927 5787</p>
                  <button onClick={() => setRenewStep("idle")} className="mt-3 text-zinc-500 hover:text-zinc-300 text-sm underline">Retour</button>
                </div>
              )}
              {user.awaiting_new_stock && renewStep === "idle" && (
                <div className="text-center py-6">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-blue-500/20 flex items-center justify-center animate-pulse">
                    <Package className="w-10 h-10 text-blue-400" />
                  </div>
                  <p className="text-blue-400 font-bold text-xl mb-2">Renouvellement en cours</p>
                  <p className="text-zinc-400 text-sm">{t("ceo_will_add_stock")}</p>
                  <p className="text-emerald-400 text-xs mt-2 italic animate-pulse">⏳ {t("stock_in_progress")}</p>
                </div>
              )}
              {(renewStep === "accepted" || (renewalStatus === "pending" && renewalHoursPassed < 24 && !user.awaiting_new_stock)) && (
                <div className="text-center py-6">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-blue-500/20 flex items-center justify-center animate-pulse">
                    <CheckCircle className="w-10 h-10 text-blue-400" />
                  </div>
                  <p className="text-blue-400 font-bold text-xl mb-2">Demande Acceptée!</p>
                  <p className="text-zinc-400 text-sm">Votre stock est en process. Manager va kontakte w nan mwens ke 24h.</p>
                </div>
              )}
              {(renewStep === "en_route" || (renewalStatus === "pending" && renewalHoursPassed >= 24)) && (
                <div className="text-center py-6">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <Truck className="w-10 h-10 text-emerald-400" />
                  </div>
                  <p className="text-emerald-400 font-bold text-xl mb-2">Votre stock est en route!</p>
                  <p className="text-zinc-400 text-sm">Le manager de votre zone va vous contacter bientôt.</p>
                </div>
              )}
            </div>

            {/* ========== MINI COVER / LATEST ANNOUNCEMENTS + RECENT ACTIVITY (2-col on desktop) ========== */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              {/* LEFT: Latest Announcements */}
              {announcements.length > 0 && (
                <div data-testid="latest-announcements">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-amber-400 rounded-xl flex items-center justify-center shadow-sm">
                        <Megaphone className="w-4 h-4 text-black" />
                      </div>
                      <h3 className="text-zinc-900 font-black text-sm">Latest Announcements</h3>
                    </div>
                    <button className="text-amber-600 hover:text-amber-700 text-[11px] font-bold">View All ›</button>
                  </div>
                  <AnnouncementSlider items={announcements} />
                </div>
              )}

              {/* RIGHT: Recent Activity */}
              <div className="bg-white border border-zinc-200 rounded-2xl p-4 shadow-sm" data-testid="recent-activity">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <Clock className="w-4 h-4 text-emerald-600" />
                    </div>
                    <h3 className="text-zinc-900 font-black text-sm">Recent Activity</h3>
                  </div>
                  <button onClick={() => setShowHistory(true)} className="text-emerald-600 hover:text-emerald-700 text-[11px] font-bold" data-testid="recent-activity-view-all">View All ›</button>
                </div>
                <div className="space-y-2">
                  {(() => {
                    const activities = [];
                    // Latest sales (up to 3)
                    (user.sales_history || []).slice(-3).reverse().forEach((s, i) => {
                      const qty = parseInt(s.quantity_sold || s.quantity || 0);
                      const rev = parseFloat(s.revenue || s.total || 0);
                      const when = s.recorded_at || s.created_at || s.date;
                      activities.push({
                        icon: TrendingUp, iconBg: "bg-emerald-100", iconColor: "text-emerald-700",
                        title: "Sale Recorded",
                        subtitle: `${qty} unit${qty !== 1 ? "s" : ""}${rev > 0 ? ` • $${rev.toFixed(2)}` : ""}`,
                        when,
                      });
                    });
                    // Renewal state
                    if (user.renewal_status === "pending" || user.renewal_status === "in_progress" || user.awaiting_new_stock) {
                      activities.push({
                        icon: Truck, iconBg: "bg-amber-100", iconColor: "text-amber-700",
                        title: "Renewal Request Submitted",
                        subtitle: "Your request has been received",
                        when: user.renewal_requested_at || user.updated_at,
                      });
                    }
                    // Commission update
                    if ((user.total_revenue || 0) > 0) {
                      activities.push({
                        icon: Wallet, iconBg: "bg-blue-100", iconColor: "text-blue-700",
                        title: "Commission Update",
                        subtitle: `Cycle commission: $${commission_earned.toFixed(2)}`,
                        when: user.updated_at,
                      });
                    }
                    if (activities.length === 0) {
                      return <p className="text-zinc-500 text-sm text-center py-6">Pa gen aktivite ankò.</p>;
                    }
                    const fmtAgo = (iso) => {
                      try {
                        const d = new Date(iso); const diffMs = Date.now() - d.getTime();
                        const mins = Math.floor(diffMs / 60000); const hrs = Math.floor(mins / 60); const days = Math.floor(hrs / 24);
                        if (days > 0) return `${days}d ago`;
                        if (hrs > 0) return `${hrs}h ago`;
                        if (mins > 0) return `${mins}m ago`;
                        return "just now";
                      } catch { return ""; }
                    };
                    return activities.slice(0, 5).map((a, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-50 transition-colors" data-testid={`activity-item-${i}`}>
                        <div className={`w-9 h-9 rounded-xl ${a.iconBg} flex items-center justify-center flex-shrink-0`}>
                          <a.icon className={`w-4 h-4 ${a.iconColor}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-zinc-900 font-bold text-xs truncate">{a.title}</p>
                          <p className="text-zinc-500 text-[11px] truncate">{a.subtitle}</p>
                        </div>
                        <p className="text-zinc-400 text-[10px] flex-shrink-0 font-semibold">{fmtAgo(a.when)}</p>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>

            {/* HIDDEN: duplicate legacy announcements block removed (new 2-col grid above covers both) */}
            {false && announcements.length > 0 && (
              <div className="mt-4" data-testid="latest-announcements-legacy">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-amber-500 rounded-xl flex items-center justify-center">
                      <span className="text-sm">📣</span>
                    </div>
                    <h3 className="text-white font-black text-sm">Latest Announcements</h3>
                  </div>
                </div>
                <div className="space-y-2">
                  {announcements.slice(0, 3).map((a) => (
                    <div key={a.id} className="relative rounded-xl overflow-hidden border border-amber-500/20 bg-gradient-to-br from-zinc-900 to-black group hover:border-amber-500/50 transition-colors" data-testid={`announcement-${a.id}`}>
                      {a.image_url && (
                        <img src={`${BACKEND_URL}${a.image_url}`} alt={a.title} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-85 transition-opacity" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
                      <div className="relative p-4 min-h-[90px] flex flex-col justify-center">
                        <h4 className="text-amber-400 font-black text-base tracking-wide" style={{fontFamily:'var(--font-heading)'}}>{a.title}</h4>
                        {a.body && <p className="text-zinc-100 text-xs mt-0.5 line-clamp-2">{a.body}</p>}
                        {a.cta_label && (
                          <button className="mt-2 w-fit bg-amber-400 hover:bg-amber-300 text-black font-black text-[10px] px-3 py-1 rounded-full uppercase tracking-widest shadow-lg active:scale-95 transition" data-testid={`announcement-cta-${a.id}`}>
                            {a.cta_label} →
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* MY STOCK VIEW — foto, pri, klike pou Report Sale */}
        {activeMenu === "my-stock" && (
          <div className="space-y-4" data-testid="my-stock-view">
            <div className="bg-white border border-zinc-200 rounded-2xl p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 bg-amber-400 rounded-xl flex items-center justify-center"><Package className="w-5 h-5 text-black" /></div>
                  <div>
                    <h3 className="text-zinc-900 font-black text-base">My Stock</h3>
                    <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-black">
                      {user.current_stock || 0} LEFT OUT OF {user.initial_stock || 0}
                    </p>
                  </div>
                </div>
                <button onClick={() => setActiveMenu("sales")} className="bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-black px-3 py-1.5 rounded-full uppercase tracking-widest shadow" data-testid="go-to-sales-btn">Report Sale →</button>
              </div>
              <p className="text-zinc-500 text-xs mb-4">Klike sou nenpòt vape pou w rapòte lavant li dirèkteman.</p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {(user.vape_models || []).map((m, i) => {
                  const imgUrl = productImageFor(m.name, m.image_url);
                  const sold = parseInt(m.sold || 0);
                  const qty = parseInt(m.quantity || 0);
                  const left = Math.max(0, qty - sold);
                  const pct = qty > 0 ? Math.min(100, Math.round((left / qty) * 100)) : 0;
                  const price = parseFloat(m.price || 0);
                  return (
                    <button
                      key={i}
                      onClick={() => {
                        // Pre-fill the first empty sales entry with this model
                        setSalesEntries(prev => {
                          const next = [...prev];
                          const emptyIdx = next.findIndex(e => !e.description);
                          if (emptyIdx >= 0) {
                            next[emptyIdx] = { description: m.name, quantity: 1, price: price };
                          }
                          return next;
                        });
                        setActiveMenu("sales");
                        toast.success(`${m.name} chaje nan Report Sale`);
                      }}
                      data-testid={`my-stock-click-${i}`}
                      className="bg-zinc-50 border border-zinc-200 rounded-xl p-3 hover:border-amber-400 hover:shadow-md transition text-left group"
                    >
                      <div className="aspect-square bg-white rounded-lg overflow-hidden flex items-center justify-center mb-2 border border-zinc-100 relative">
                        {imgUrl ? (
                          <img src={imgUrl} alt={m.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform" onError={(e) => { e.target.style.display='none'; }} />
                        ) : (
                          <div className="text-5xl opacity-20">💨</div>
                        )}
                        <span className="absolute top-1 right-1 bg-emerald-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full shadow">${price.toFixed(2)}</span>
                      </div>
                      <p className="text-zinc-900 font-black text-sm truncate" title={m.name}>{m.name}</p>
                      <div className="h-1.5 bg-zinc-200 rounded-full overflow-hidden my-1.5">
                        <div className={`h-full rounded-full ${pct > 50 ? "bg-amber-400" : pct > 20 ? "bg-amber-500" : "bg-red-500"}`} style={{width: `${pct}%`}} />
                      </div>
                      <div className="flex justify-between text-[11px]">
                        <span className={`font-black ${left === 0 ? "text-red-600" : left <= 2 ? "text-amber-700" : "text-emerald-700"}`}>Left: {left}</span>
                        <span className="text-zinc-500">Sold: {sold}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* SALES VIEW */}
        {activeMenu === "sales" && (
          <div className="glass-panel p-4">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-violet-400" /> {t("record_sales_header")}</h3>
            
            <div className="space-y-4">
              {salesEntries.map((entry, idx) => (
                <div key={idx} className="bg-zinc-900/50 p-3 rounded-xl">
                  <p className="text-zinc-400 text-xs mb-2 uppercase">{t("vape_description_num")} {idx + 1}</p>
                  <div className="mb-2">
                  <ProductAutocomplete value={entry.description} models={user.vape_models || []} placeholder={t("vape_description")}
                    testid={`sales-desc-input-${idx}`}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-white text-sm focus:border-amber-500 outline-none"
                    onChange={(v) => {
                      const updated = [...salesEntries];
                      updated[idx].description = v;
                      const match = (user.vape_models || []).find(m => m.name && v && m.name.toLowerCase().trim() === v.toLowerCase().trim());
                      if (match && !updated[idx].price) updated[idx].price = parseFloat(match.price) || 0;
                      setSalesEntries(updated);
                    }}
                    onPick={(p) => {
                      const updated = [...salesEntries];
                      updated[idx].description = p.name;
                      if (!updated[idx].price && p.price) updated[idx].price = parseFloat(p.price) || 0;
                      if (!updated[idx].quantity) updated[idx].quantity = 1;
                      setSalesEntries(updated);
                    }} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-zinc-500 text-xs">{t("quantity")}</label>
                      <input type="number" value={entry.quantity || ''} 
                        onChange={(e) => {
                          const updated = [...salesEntries];
                          updated[idx].quantity = parseInt(e.target.value) || 0;
                          setSalesEntries(updated);
                        }}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-white text-center" />
                    </div>
                    <div>
                      <label className="text-zinc-500 text-xs">{t("price_sold")}</label>
                      <input type="number" value={entry.price || ''} 
                        onChange={(e) => {
                          const updated = [...salesEntries];
                          updated[idx].price = parseFloat(e.target.value) || 0;
                          setSalesEntries(updated);
                        }}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-white text-center" />
                    </div>
                  </div>
                  {entry.quantity > 0 && entry.price > 0 && (
                    <div className="flex justify-between mt-2 text-sm">
                      <span className="text-zinc-400">{t("subtotal")}: ${(entry.quantity * entry.price).toFixed(2)}</span>
                      <span className="text-emerald-400">+${(entry.quantity * bonus_per_vape).toFixed(2)} {t("bonus").toLowerCase()}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {currentTotal > 0 && (
              <div className="bg-violet-600/20 border border-violet-500/30 p-3 rounded-xl mt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-300">{t("total_sales_label")}:</span>
                  <span className="text-white font-bold">${currentTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-zinc-300">{t("bonus_to_receive")}:</span>
                  <span className="text-emerald-400 font-bold">+${currentBonus.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-zinc-300">{t("commission")} ({commission_rate}%):</span>
                  <span className="text-yellow-400 font-bold">+${(currentTotal * commission_rate / 100).toFixed(2)}</span>
                </div>
              </div>
            )}
            
            <button onClick={handleSubmitSales} disabled={loading || currentTotal === 0}
              className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="submit-sale-button">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Zap className="w-5 h-5" /> {t("record_sales_btn")}</>}
            </button>

            {/* Sales Report card — click to open the 0-sale reason picker */}
            <div className="mt-4">
              <button
                onClick={() => setShowSalesReport(v => !v)}
                data-testid="open-sales-report-btn"
                className="w-full bg-zinc-900/60 hover:bg-zinc-900/80 border border-zinc-700 rounded-xl p-3 text-left flex items-center justify-between">
                <div>
                  <p className="text-white font-bold text-sm">📋 {t("sales_report", "Sales Report")}</p>
                  <p className="text-zinc-500 text-[11px]">{t("sales_report_hint", "Antre 0 kòm vant + rezon.")}</p>
                </div>
                <span className="text-zinc-500 text-lg">{showSalesReport ? "−" : "+"}</span>
              </button>
              {showSalesReport && (
                <div className="mt-2 bg-zinc-950/60 border border-zinc-700 rounded-xl p-4" data-testid="zero-sale-panel">
                  <p className="text-zinc-400 text-xs mb-3">{t("zero_sale_reason", "Chwazi rezon nan — ekip la ap konnen:")}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { code: "no_customer", label: t("reason_no_customer", "a) Pat gen kliyan") },
                      { code: "price_high", label: t("reason_price_high", "b) Pri pa mache") },
                      { code: "not_available", label: t("reason_not_available", "c) Mwen pat disponib") },
                      { code: "other", label: t("reason_other", "d) Lòt rezon") },
                    ].map(r => (
                      <button
                        key={r.code}
                        onClick={async () => {
                          let detail = "";
                          if (r.code === "other") {
                            detail = window.prompt(t("reason_other_prompt", "Ekri rezon w:")) || "";
                            if (!detail.trim()) return;
                          }
                          try {
                            await axios.post(`${API}/stockers/${user.id}/report-zero-sale`, { reason: r.code, detail });
                            toast.success(t("report_sent", "Rezon voye"));
                            setShowSalesReport(false);
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Erè");
                          }
                        }}
                        className="bg-zinc-800 hover:bg-red-900/40 border border-zinc-700 text-white text-xs font-bold py-2 rounded-lg"
                        data-testid={`zero-sale-${r.code}`}>
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* BONUS VIEW */}
        {activeMenu === "bonus" && (
          <div className="space-y-4">
            <div className="glass-panel p-6 text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                <DollarSign className="w-10 h-10 text-white" />
              </div>
              <p className="text-zinc-400 text-sm mb-1">Total Bonus Gagné</p>
              <p className="text-4xl font-black text-emerald-400">${getCycleBonus(user).toFixed(2)}</p>
              <p className="text-zinc-500 text-sm mt-2">+${bonus_per_vape.toFixed(2)} par vape vendu</p>
            </div>

            {/* ⭐ VAPE-YARD TEAM BONUS — blinking star widget */}
            <TeamBonusStarWidget userId={user.id} />
            
            <div className="glass-panel p-4">
              <h4 className="text-white font-bold mb-3">Détails</h4>
              <div className="space-y-2">
                <div className="flex justify-between py-2 border-b border-zinc-800">
                  <span className="text-zinc-400">Vapes Vendus</span>
                  <span className="text-white font-bold">{getCycleSold(user)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-zinc-800">
                  <span className="text-zinc-400">Bonus par Vape</span>
                  <span className="text-emerald-400 font-bold">${bonus_per_vape.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-zinc-800">
                  <span className="text-zinc-400">Commission ({commission_rate}%)</span>
                  <span className="text-yellow-400 font-bold">${commission_earned.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-zinc-400 font-bold">Total Gains</span>
                  <span className="text-white font-bold text-lg">${totalGains.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Remaining vapes in RED */}
            {remainingVapes > 0 && (
              <div className="glass-panel p-4 border border-red-500/40 bg-red-500/5" data-testid="remaining-vapes-warning">
                <div className="flex items-center gap-3">
                  <Package className="w-6 h-6 text-red-400" />
                  <div>
                    <p className="text-red-400 font-bold">{remainingVapes} vape(s) restant(s)</p>
                    <p className="text-red-400/70 text-sm">Vous n'avez pas encore terminé votre stock</p>
                  </div>
                </div>
              </div>
            )}

            {/* ===== RETIRER L'ARGENT SECTION ===== */}
            <div className="glass-panel p-5 border border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-emerald-600/10" data-testid="withdraw-section">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-emerald-500/20 p-3 rounded-xl">
                  <DollarSign className="w-7 h-7 text-emerald-400" />
                </div>
                <div>
                  <h4 className="text-white font-bold text-lg">Retirer l'Argent</h4>
                  <p className="text-zinc-500 text-sm">Contactez le CEO pour recevoir vos gains</p>
                </div>
              </div>
              
              {withdrawStep === "idle" && (
                <>
                  <div className="bg-zinc-900/60 rounded-xl p-4 mb-4">
                    <div className="flex justify-between py-2 border-b border-zinc-800">
                      <span className="text-zinc-400 text-sm">Revenus Ventes</span>
                      <span className="text-white font-bold">${(user.total_revenue || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-zinc-800">
                      <span className="text-zinc-400 text-sm">Bonus Total</span>
                      <span className="text-emerald-400 font-bold">${(user.total_bonus || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-zinc-800">
                      <span className="text-zinc-400 text-sm">Commission ({commission_rate}%)</span>
                      <span className="text-yellow-400 font-bold">${commission_earned.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-3 mt-1">
                      <span className="text-white font-bold">Montant Total</span>
                      <span className="text-emerald-400 font-black text-2xl">${totalGains.toFixed(2)}</span>
                    </div>
                  </div>
                  
                  <button onClick={handleWithdraw}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-3 transition-all active:scale-95"
                    data-testid="withdraw-btn">
                    <DollarSign className="w-6 h-6" />
                    Retirer l'Argent
                  </button>
                </>
              )}

              {withdrawStep === "waiting" && (
                <div className="text-center py-8">
                  <div className="w-24 h-24 mx-auto mb-4 rounded-full border-4 border-emerald-500 flex items-center justify-center animate-pulse">
                    <span className="text-4xl font-black text-emerald-400">{withdrawTimer}</span>
                  </div>
                  <p className="text-white font-bold text-lg mb-2">Veuillez patienter...</p>
                  <p className="text-zinc-400 text-sm">Traitement en cours</p>
                  <div className="mt-4 h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full transition-all duration-1000" 
                      style={{ width: `${((10 - withdrawTimer) / 10) * 100}%` }} />
                  </div>
                </div>
              )}

              {withdrawStep === "approved" && (
                <div className="text-center py-6">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <CheckCircle className="w-10 h-10 text-emerald-400" />
                  </div>
                  <p className="text-emerald-400 font-bold text-xl mb-2">Demande Approuvée!</p>
                  <p className="text-zinc-400 text-sm mb-4">Contactez votre CEO pour recevoir vos gains</p>
                  
                  <a href={`https://wa.me/17866309220?text=${encodeURIComponent(`Salut l'équipe Vape Yard! Je suis ${user.name} (Code: ${user.code}, Pays: ${user.country?.toUpperCase()}). Ma demande de retrait a été approuvée. Montant: $${totalGains.toFixed(2)}\n📅 Date de la demande: ${new Date().toLocaleString('fr-FR')}`)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-3 transition-all active:scale-95"
                    data-testid="withdraw-whatsapp-btn">
                    <MessageCircle className="w-6 h-6" />
                    Contacter CEO via WhatsApp
                  </a>
                  <p className="text-zinc-500 text-xs mt-2">+1 786 630 9220</p>
                  
                  <button onClick={() => setWithdrawStep("idle")}
                    className="mt-4 text-zinc-500 hover:text-zinc-300 text-sm underline">
                    Fermer
                  </button>
                </div>
              )}
            </div>

            {/* ===== RENOUVELER STOCK SECTION ===== */}
            <div className="glass-panel p-5 border border-blue-500/30 bg-gradient-to-br from-blue-500/5 to-blue-600/10" data-testid="renew-stock-section">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-blue-500/20 p-3 rounded-xl">
                  <Package className="w-7 h-7 text-blue-400" />
                </div>
                <div>
                  <h4 className="text-white font-bold text-lg">Renouveler Stock</h4>
                  <p className="text-blue-400 text-sm">Ventes: {sellPercentage.toFixed(0)}% {sellPercentage >= 50 ? '- Éligible' : '- Non éligible'}</p>
                </div>
              </div>

              {renewStep === "idle" && !renewalStatus && !user.awaiting_new_stock && (
                <>
                  <div className="bg-zinc-900/60 rounded-xl p-4 mb-4">
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <p className="text-zinc-500 text-xs">Stock Initial</p>
                        <p className="text-white font-bold text-lg">{user.initial_stock || 0}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500 text-xs">Vendus</p>
                        <p className="text-emerald-400 font-bold text-lg">{getCycleSold(user)}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500 text-xs">Restant</p>
                        <p className={`font-bold text-lg ${remainingVapes > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{remainingVapes}</p>
                      </div>
                    </div>
                    <div className="mt-3 h-3 bg-zinc-800 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${sellPercentage >= 50 ? 'bg-emerald-400' : 'bg-amber-400'}`}
                        style={{ width: `${Math.min(100, sellPercentage)}%` }} />
                    </div>
                    <p className="text-center text-xs mt-1 text-zinc-500">{sellPercentage.toFixed(0)}% vendu (minimum 50% requis)</p>
                  </div>

                  <button onClick={handleRenewStock}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-3 transition-all active:scale-95"
                    data-testid="renew-stock-btn">
                    <Package className="w-6 h-6" />
                    Demander Renouvellement
                  </button>
                </>
              )}

              {user.awaiting_new_stock && renewStep === "idle" && (
                <div className="text-center py-6">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-blue-500/20 flex items-center justify-center animate-pulse">
                    <Package className="w-10 h-10 text-blue-400" />
                  </div>
                  <p className="text-blue-400 font-bold text-xl mb-2">Renouvellement en cours</p>
                  <p className="text-zinc-400 text-sm">{t("ceo_will_add_stock")}</p>
                  <p className="text-emerald-400 text-xs mt-2 italic animate-pulse">⏳ {t("stock_in_progress")}</p>
                </div>
              )}

              {renewStep === "not_eligible" && (
                <div className="text-center py-6">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                    <AlertTriangle className="w-10 h-10 text-red-400" />
                  </div>
                  <p className="text-red-400 font-bold text-lg mb-2">Non Éligible</p>
                  <p className="text-zinc-400 text-sm mb-1">Vos ventes sont à {sellPercentage.toFixed(0)}% — minimum 50% requis.</p>
                  <p className="text-zinc-400 text-sm mb-4">Contactez votre Manager pour plus d'informations.</p>
                  
                  <a href={`https://wa.me/13239275787?text=${encodeURIComponent(`Bonjour Manager Vape Yard! Je suis ${user.name} (Code: ${user.code}, Pays: ${user.country?.toUpperCase()}). J'ai vendu ${sellPercentage.toFixed(0)}% de mon stock et je voudrais discuter de mon renouvellement.`)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-3 transition-all active:scale-95"
                    data-testid="contact-manager-btn">
                    <MessageCircle className="w-6 h-6" />
                    Contacter Manager via WhatsApp
                  </a>
                  <p className="text-zinc-500 text-xs mt-2">+1 323 927 5787 (République Dominicaine)</p>
                  
                  <button onClick={() => setRenewStep("idle")}
                    className="mt-4 text-zinc-500 hover:text-zinc-300 text-sm underline">
                    Retour
                  </button>
                </div>
              )}

              {(renewStep === "accepted" || (renewalStatus === "pending" && renewalHoursPassed < 24 && !user.awaiting_new_stock)) && (
                <div className="text-center py-6">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-blue-500/20 flex items-center justify-center animate-pulse">
                    <CheckCircle className="w-10 h-10 text-blue-400" />
                  </div>
                  <p className="text-blue-400 font-bold text-xl mb-2">Demande Acceptée!</p>
                  <p className="text-zinc-400 text-sm mb-2">Votre stock est en process pour arriver.</p>
                  <p className="text-zinc-500 text-sm">Le manager de votre zone vous contactera dans moins de 24 heures.</p>
                </div>
              )}

              {(renewStep === "en_route" || (renewalStatus === "pending" && renewalHoursPassed >= 24 && !user.awaiting_new_stock)) && (
                <div className="text-center py-6">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <Truck className="w-10 h-10 text-emerald-400" />
                  </div>
                  <p className="text-emerald-400 font-bold text-xl mb-2">Votre stock est en route!</p>
                  <p className="text-zinc-400 text-sm mb-2">Le manager de votre zone va vous contacter bientôt.</p>
                  
                  <a href={`https://wa.me/13239275787?text=${encodeURIComponent(`Bonjour! Je suis ${user.name} (Code: ${user.code}). Mon stock de renouvellement est en route. Merci de me contacter pour la livraison.`)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-3 mt-4">
                    <MessageCircle className="w-6 h-6" />
                    Contacter Manager
                  </a>
                  <p className="text-zinc-500 text-xs mt-2">+1 323 927 5787</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* CADEAUX VIEW */}
        {activeMenu === "cadeaux" && (
          <div className="space-y-4">
            <div className="glass-panel p-5 text-center" data-testid="gifts-summary">
              <Gift className="w-12 h-12 text-yellow-400 mx-auto mb-2" />
              <p className="text-white font-bold text-lg">Cadeaux à Débloquer</p>
              <p className="text-zinc-400 text-xs mb-3">Vendez des vapes pour gagner des cadeaux!</p>
              {(() => {
                const totalSold = (user.lifetime_sold || 0) + (user.total_sold || 0);
                const milestones = [100, 500, 1000, 5000, 10000];
                const nextGoal = milestones.find(m => totalSold < m) || milestones[milestones.length-1];
                const prevGoal = milestones.slice().reverse().find(m => totalSold >= m) || 0;
                const toNext = Math.max(0, nextGoal - totalSold);
                const pct = nextGoal > 0 ? Math.min(100, Math.round((totalSold / nextGoal) * 100)) : 0;
                return (
                  <div className="space-y-2">
                    <p className="text-emerald-400 text-3xl font-black">{totalSold}</p>
                    <p className="text-zinc-300 text-xs font-semibold">vapes vann (total jiska kounye a)</p>
                    <div className="bg-zinc-800 rounded-full h-3 overflow-hidden mt-3">
                      <div className="h-full bg-gradient-to-r from-yellow-500 to-amber-400 transition-all" style={{width: `${pct}%`}} />
                    </div>
                    <div className="flex items-center justify-between text-xs mt-1">
                      <span className="text-zinc-500">{prevGoal}</span>
                      <span className="text-amber-400 font-black">{pct}% → {nextGoal}</span>
                    </div>
                    <p className="text-yellow-300 text-sm font-bold mt-1">
                      Ou manke <span className="text-white font-black text-xl">{toNext}</span> vape pou w rive nan pwochen kado w la ({nextGoal}) 🎁
                    </p>
                  </div>
                );
              })()}
            </div>
            
            {/* 100 Vapes Gift */}
            <div className={`glass-panel p-4 ${user.gift_claimed_100 ? 'border-2 border-emerald-500 bg-emerald-500/5' : user.gift_pending_100 ? 'border-2 border-yellow-500 bg-yellow-500/5' : ((user.lifetime_sold || 0) + (user.total_sold || 0)) >= 100 ? 'border-2 border-yellow-500' : ''}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-white font-bold">100 Vapes Vendus</span>
                {user.gift_claimed_100 ? (
                  <span className="bg-emerald-500/20 text-emerald-400 text-sm px-3 py-1 rounded-lg font-bold">Cadeau: {user.gift_won_100}</span>
                ) : user.gift_pending_100 ? (
                  <span className="bg-yellow-500/20 text-yellow-400 text-sm px-3 py-1 rounded-lg font-bold animate-pulse">En attente du CEO...</span>
                ) : ((user.lifetime_sold || 0) + (user.total_sold || 0)) >= 100 ? (
                  <button onClick={() => setShowGiftBox(100)} className="bg-yellow-500 text-zinc-900 px-3 py-1 rounded-lg text-sm font-bold" data-testid="claim-gift-100">Débloquer!</button>
                ) : (
                  <span className="text-zinc-500 text-sm">{(user.lifetime_sold || 0) + (user.total_sold || 0)}/100</span>
                )}
              </div>
              <div className="grid grid-cols-5 gap-2">
                {[{e:"📱",n:"iPhone"},{e:"💵",n:"Cash"},{e:"👟",n:"Tennis"},{e:"🍽️",n:"Dîner"},{e:"🎧",n:"AirPods"}].map((g,i) => (
                  <div key={i} className={`p-2 rounded-lg text-center ${user.gift_claimed_100 && user.gift_won_100?.includes(g.n) ? 'bg-emerald-500/20 border border-emerald-500/50' : user.gift_claimed_100 || user.gift_pending_100 ? 'bg-emerald-900/20' : 'bg-zinc-800'}`}>
                    <span className="text-2xl">{g.e}</span><p className="text-xs text-zinc-400">{g.n}</p>
                  </div>
                ))}
              </div>
              <div className="h-2 bg-zinc-800 rounded-full mt-3 overflow-hidden">
                <div className={`h-full rounded-full ${user.gift_claimed_100 ? 'bg-emerald-500' : 'bg-yellow-500'}`} style={{ width: `${Math.min(100, (((user.lifetime_sold || 0) + (user.total_sold || 0)) / 100) * 100)}%` }} />
              </div>
            </div>
            
            {/* 150 Vapes Gift */}
            <div className={`glass-panel p-4 ${user.gift_claimed_150 ? 'border-2 border-emerald-500 bg-emerald-500/5' : user.gift_pending_150 ? 'border-2 border-purple-500 bg-purple-500/5' : ((user.lifetime_sold || 0) + (user.total_sold || 0)) >= 150 ? 'border-2 border-purple-500' : ''}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-white font-bold">150 Vapes Vendus</span>
                {user.gift_claimed_150 ? (
                  <span className="bg-emerald-500/20 text-emerald-400 text-sm px-3 py-1 rounded-lg font-bold">Cadeau: {user.gift_won_150}</span>
                ) : user.gift_pending_150 ? (
                  <span className="bg-purple-500/20 text-purple-400 text-sm px-3 py-1 rounded-lg font-bold animate-pulse">En attente du CEO...</span>
                ) : ((user.lifetime_sold || 0) + (user.total_sold || 0)) >= 150 ? (
                  <button onClick={() => setShowGiftBox(150)} className="bg-purple-500 text-white px-3 py-1 rounded-lg text-sm font-bold" data-testid="claim-gift-150">Débloquer!</button>
                ) : (
                  <span className="text-zinc-500 text-sm">{(user.lifetime_sold || 0) + (user.total_sold || 0)}/150</span>
                )}
              </div>
              <div className="grid grid-cols-5 gap-2">
                {[{e:"💻",n:"MacBook"},{e:"💰",n:"$1000"},{e:"✈️",n:"Voyage"},{e:"🎮",n:"PS5"},{e:"⌚",n:"Watch"}].map((g,i) => (
                  <div key={i} className={`p-2 rounded-lg text-center ${user.gift_claimed_150 && user.gift_won_150?.includes(g.n) ? 'bg-emerald-500/20 border border-emerald-500/50' : user.gift_claimed_150 || user.gift_pending_150 ? 'bg-emerald-900/20' : 'bg-zinc-800'}`}>
                    <span className="text-2xl">{g.e}</span><p className="text-xs text-zinc-400">{g.n}</p>
                  </div>
                ))}
              </div>
              <div className="h-2 bg-zinc-800 rounded-full mt-3 overflow-hidden">
                <div className={`h-full rounded-full ${user.gift_claimed_150 ? 'bg-emerald-500' : 'bg-purple-500'}`} style={{ width: `${Math.min(100, (((user.lifetime_sold || 0) + (user.total_sold || 0)) / 150) * 100)}%` }} />
              </div>
            </div>
          </div>
        )}

        {/* PROFILE VIEW */}
        {activeMenu === "profile" && (
          <div className="space-y-4">
            <div className="glass-panel p-6 text-center">
              <div className="relative inline-block mb-4">
                {profileImage ? (
                  <img src={profileImage} alt="Profile" className="w-24 h-24 rounded-full object-cover border-4 border-violet-500" />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-violet-600 flex items-center justify-center text-white font-bold text-3xl">
                    {user.name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <label className="absolute bottom-0 right-0 w-8 h-8 bg-violet-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-violet-700">
                  <input type="file" accept="image/*" onChange={handleProfileImage} className="hidden" />
                  <Plus className="w-4 h-4 text-white" />
                </label>
              </div>
              <h3 className="text-white font-bold text-xl">{user.name}</h3>
              <p className="text-zinc-400">{user.is_nm ? "Network Marketer" : "Stocker"} • {user.country?.toUpperCase()}</p>
              <p className="text-zinc-500 text-sm mt-1">Code: {user.code}</p>
            </div>
            
            {/* Payment Profile — Zelle / CashApp / Cash */}
            <PaymentProfileCard userId={user.id} />
            
            <div className="glass-panel p-4">
              <h4 className="text-white font-bold mb-3">{t("informations")}</h4>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-zinc-800">
                  <span className="text-zinc-400">{t("pays")}</span>
                  <span className="text-white">{user.country === 'usa' ? '🇺🇸 États-Unis' : user.country === 'dr' ? '🇩🇴 Rép. Dominicaine' : '🇭🇹 Haïti'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-zinc-800">
                  <span className="text-zinc-400">{t("member_since")}</span>
                  <span className="text-white">{new Date(user.start_date).toLocaleDateString('fr-FR')}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-zinc-800">
                  <span className="text-zinc-400">{t("end_period")}</span>
                  <span className="text-white">{new Date(user.end_date).toLocaleDateString('fr-FR')}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-zinc-400">{t("days_remaining")}</span>
                  <span className={`font-bold ${user.days_remaining <= 3 ? 'text-red-400' : 'text-white'}`}>{user.days_remaining}</span>
                </div>
              </div>
            </div>
            
            <div className="glass-panel p-4">
              <h4 className="text-white font-bold mb-3">{t("statistics")}</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-zinc-800/50 p-3 rounded-lg text-center">
                  <p className="text-emerald-400 font-bold text-lg">${user.total_bonus?.toFixed(2) || '0.00'}</p>
                  <p className="text-zinc-500 text-xs">{t("bonus_total")}</p>
                </div>
                <div className="bg-zinc-800/50 p-3 rounded-lg text-center">
                  <p className="text-white font-bold text-lg">{getCycleSold(user)}</p>
                  <p className="text-zinc-500 text-xs">{t("vapes_sold")}</p>
                </div>
                <div className="bg-zinc-800/50 p-3 rounded-lg text-center">
                  <p className="text-white font-bold text-lg">${user.total_revenue?.toFixed(0) || 0}</p>
                  <p className="text-zinc-500 text-xs">{t("total_revenue")}</p>
                </div>
                <div className="bg-zinc-800/50 p-3 rounded-lg text-center">
                  <p className="text-yellow-400 font-bold text-lg">${commission_earned.toFixed(2)}</p>
                  <p className="text-zinc-500 text-xs">{t("commission")}</p>
                </div>
              </div>
            </div>

            {/* PRIVACY POLICY */}
            <div className="glass-panel p-4" data-testid="privacy-policy-card">
              <h4 className="text-white font-bold mb-3 flex items-center gap-2">
                🔒 Politik Konfidansyalite
              </h4>
              <div className="bg-zinc-950/60 rounded-lg p-4 text-xs text-zinc-300 leading-relaxed space-y-2 max-h-[300px] overflow-y-auto">
                <p className="font-bold text-emerald-400">Vape Yard — Angajman Konfidansyalite ak Sekirite</p>
                <p>1. <strong>Done pèsonèl ou</strong>: Non, kòd login ak istorik vant ou stoke sou platfòm nou ak proteksyon SSL. Se sèl CEO ki gen aksè konplè.</p>
                <p>2. <strong>Sekirite kont</strong>: Ou responsab pou kenbe kòd login ou (nimewo + kòd secret) an sekirè. Pa pataje l ak pèsonn.</p>
                <p>3. <strong>Kaptè ekran</strong>: Pou pwoteje enfòmasyon sansib CEO a, tantativ kaptè ekran bloke sou platfòm sa a.</p>
                <p>4. <strong>Reta ak pèfòmans</strong>: Chak reta redwi <strong>Credit Score</strong> ou (-10 pwen chak fwa, plis -1 pwen chak jou apre 3 jou). 3 reta konsekitif → risk banisman otomatik.</p>
                <p>5. <strong>Kontrè</strong>: CEO gen dwa suspann oswa retire yon stocker si Credit Score tonbe anba 15 pwen oswa si pèfòmans mansyèl rete anba 50% pandan 3 mwa konsekitif.</p>
                <p>6. <strong>Peyman</strong>: Kòb komisyon ap distribye sèlman apre CEO fin verifye vant yo ak konfime peyman kliyan yo.</p>
                <p>7. <strong>Dispi</strong>: Nenpòt dezakò ap rezoud dirèkteman ak CEO. Nenpòt fasi enfòmasyon ka mennen sispansyon imedyat.</p>
                <p>8. <strong>Aksè done</strong>: Ou ka mande CEO pou <strong>korije</strong> nenpòt done ki pa kòrèk sou kont ou nenpòt kilè.</p>
                <p className="text-zinc-500 text-[10px] mt-2">© 2026 Vape Yard. Tout dwa rezève. Lè w ap itilize platfòm sa a, ou aksepte tout kondisyon sa yo.</p>
              </div>
            </div>
            
            {/* Contact Us */}
            <ContactUs />
          </div>
        )}
          </main>
          {/* ============== FOOTER ============== */}
          <footer className="bg-black px-4 lg:px-8 py-5 border-t border-zinc-900" data-testid="stocker-footer">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <img src={LOGO_URL} alt="VAPE-YARD" className="w-7 h-7" onError={e => { e.target.style.display='none'; }} />
                <div>
                  <p className="text-white font-black text-sm tracking-wider leading-none">VAPE<span className="text-amber-400">-</span>YARD</p>
                  <p className="text-zinc-600 text-[9px] uppercase tracking-[0.25em] mt-0.5">Flavors meet the future</p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-zinc-400 text-xs flex-wrap">
                <button onClick={() => setActiveMenu("support")} className="hover:text-amber-400">Help & Support</button>
                <a href="#" className="hover:text-amber-400">Terms of Service</a>
                <a href="#" className="hover:text-amber-400">Privacy Policy</a>
              </div>
              <div className="flex items-center gap-3 text-zinc-500">
                <a href="#" className="hover:text-amber-400 text-sm"><Instagram className="w-4 h-4" /></a>
                <a href="#" className="hover:text-amber-400 text-sm"><Mail className="w-4 h-4" /></a>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-amber-400" />
                <div className="leading-tight">
                  <p className="text-white text-xs font-bold">VAPE-YARD Team</p>
                  <p className="text-zinc-500 text-[10px]">Stay connected</p>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
};

// ==================== APP ====================

// ==================== HOOKAH DASHBOARD ====================
// =================== HOOKAH IMAGE LIBRARY ===================
// REAL hookah photos generated via Gemini Nano Banana, hosted on our backend (reliable)
const HOOKAH_PHOTOS = [
  `${BACKEND_URL}/api/static/hookahs/1.png`,
  `${BACKEND_URL}/api/static/hookahs/2.png`,
  `${BACKEND_URL}/api/static/hookahs/3.png`,
  `${BACKEND_URL}/api/static/hookahs/4.png`,
  `${BACKEND_URL}/api/static/hookahs/5.png`,
  `${BACKEND_URL}/api/static/hookahs/6.png`,
];

const makeHookahSVG = (n, accent) => {
  const colors = {
    violet: { glow: '#a78bfa', body: '#7c3aed', glass: '#5b21b6', smoke: '#c4b5fd' },
    emerald: { glow: '#34d399', body: '#059669', glass: '#047857', smoke: '#a7f3d0' },
    amber: { glow: '#fbbf24', body: '#d97706', glass: '#b45309', smoke: '#fcd34d' },
    pink: { glow: '#f472b6', body: '#db2777', glass: '#9d174d', smoke: '#fbcfe8' },
    blue: { glow: '#60a5fa', body: '#2563eb', glass: '#1e40af', smoke: '#bfdbfe' },
    cyan: { glow: '#22d3ee', body: '#0891b2', glass: '#155e75', smoke: '#a5f3fc' },
  };
  const c = colors[accent] || colors.violet;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
    <defs>
      <radialGradient id="bg${n}" cx="50%" cy="40%" r="60%">
        <stop offset="0%" stop-color="${c.glow}" stop-opacity="0.35"/>
        <stop offset="100%" stop-color="#09090b" stop-opacity="1"/>
      </radialGradient>
      <linearGradient id="glass${n}" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="${c.glow}" stop-opacity="0.9"/>
        <stop offset="100%" stop-color="${c.glass}" stop-opacity="1"/>
      </linearGradient>
    </defs>
    <rect width="200" height="200" fill="url(#bg${n})"/>
    <ellipse cx="130" cy="28" rx="14" ry="6" fill="${c.smoke}" opacity="0.45"/>
    <ellipse cx="150" cy="18" rx="10" ry="4" fill="${c.smoke}" opacity="0.30"/>
    <ellipse cx="115" cy="38" rx="8" ry="3" fill="${c.smoke}" opacity="0.50"/>
    <ellipse cx="100" cy="52" rx="18" ry="8" fill="${c.body}"/>
    <rect x="86" y="44" width="28" height="10" rx="2" fill="${c.body}"/>
    <ellipse cx="100" cy="44" rx="14" ry="3" fill="${c.glow}" opacity="0.6"/>
    <rect x="95" y="58" width="10" height="62" fill="${c.body}"/>
    <path d="M 70 120 Q 65 130, 65 145 Q 65 175, 100 178 Q 135 175, 135 145 Q 135 130, 130 120 Z" fill="url(#glass${n})" stroke="${c.glow}" stroke-width="1.5" opacity="0.9"/>
    <path d="M 130 135 Q 165 130, 170 110 Q 175 95, 160 92" fill="none" stroke="${c.body}" stroke-width="5" stroke-linecap="round"/>
    <circle cx="160" cy="92" r="4" fill="${c.glow}"/>
  </svg>`;
  return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
};

const HOOKAH_ACCENTS = ['violet', 'emerald', 'amber', 'pink', 'blue', 'cyan'];

// Photo URL by hookah object or table number (primary). If hookah has a custom image stored, use it.
const hookahImageFor = (hOrNumber) => {
  if (hOrNumber && typeof hOrNumber === 'object') {
    if (hOrNumber.image) {
      // Backend stores like "/api/static/hookahs/custom_xxx.png"
      return hOrNumber.image.startsWith('http') ? hOrNumber.image : `${BACKEND_URL}${hOrNumber.image}`;
    }
    const num = parseInt(hOrNumber.number || 1);
    return HOOKAH_PHOTOS[(num - 1) % HOOKAH_PHOTOS.length];
  }
  const num = parseInt(hOrNumber || 1);
  return HOOKAH_PHOTOS[(num - 1) % HOOKAH_PHOTOS.length];
};

// SVG fallback when photo fails
const hookahFallbackFor = (n) => {
  const num = parseInt(n || 1);
  return makeHookahSVG(String(num).padStart(2, '0'), HOOKAH_ACCENTS[(num - 1) % HOOKAH_ACCENTS.length]);
};

// Hero banner — use one of our generated hookah photos as background
const HOOKAH_HERO = `${BACKEND_URL}/api/static/hookahs/1.png`;
const HOOKAH_HERO_FALLBACK = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 200">
  <defs><linearGradient id="hb" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#1e1b4b"/><stop offset="50%" stop-color="#581c87"/><stop offset="100%" stop-color="#831843"/></linearGradient></defs>
  <rect width="800" height="200" fill="url(#hb)"/>
  <circle cx="100" cy="40" r="30" fill="#fbbf24" opacity="0.15"/>
  <circle cx="700" cy="50" r="35" fill="#34d399" opacity="0.15"/>
</svg>`);

const HOOKAH_FALLBACK = hookahFallbackFor(1);

const HookahDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [hookahs, setHookahs] = useState([]);
  const [dailySession, setDailySession] = useState(null);
  const [summary, setSummary] = useState({ base_revenue: 0, refill_revenue: 0, total_revenue: 0, stocker_earnings: 0, active_count: 0, completed_count: 0, tables: [] });
  const [showOpenSession, setShowOpenSession] = useState(false);
  const [showAddTable, setShowAddTable] = useState(null); // hookah being configured
  const [showRefill, setShowRefill] = useState(null); // hookah being refilled
  const [now, setNow] = useState(new Date());

  // Open session form
  const [openForm, setOpenForm] = useState({
    responsable_name: "",
    servers_text: "",
    address: "",
    event: "",
    price_per_hookah: 0,
    hookahs_in_service: 0,
  });

  // Table form (per table session)
  const [tableForm, setTableForm] = useState({
    server_name: "",
    client_name: "",
    duration_hours: 0,
    duration_minutes: 60,
    price: 0,
  });

  // Refill form
  const [refillForm, setRefillForm] = useState({ minutes: 30, price: 0 });

  useEffect(() => {
    if (!user) { navigate('/'); return; }
    loadAll();
    const timer = setInterval(() => { setNow(new Date()); loadAll(); }, 10000);
    return () => clearInterval(timer);
  }, [navigate, user]);

  const loadAll = async () => {
    try {
      const [hres, dres] = await Promise.all([
        axios.get(`${API}/hookah/${user?.country || 'haiti'}`),
        axios.get(`${API}/hookah/daily-session/active/${user?.id}`)
      ]);
      setHookahs(hres.data);
      const ds = dres.data && dres.data.id ? dres.data : null;
      setDailySession(ds);
      if (ds) {
        const sres = await axios.get(`${API}/hookah/daily-session/${ds.id}/summary`);
        setSummary(sres.data);
        // Default tableForm price to daily session price
        setTableForm(t => ({ ...t, price: ds.price_per_hookah || 0 }));
      }
    } catch(e) {}
  };

  const openDailySession = async () => {
    if (!openForm.responsable_name.trim()) { toast.error("Antre non responsab la"); return; }
    if (openForm.hookahs_in_service <= 0) { toast.error("Antre kantite hookah ki nan sèvis"); return; }
    if (openForm.price_per_hookah <= 0) { toast.error("Antre pri pou yon hookah"); return; }
    const servers = openForm.servers_text.split(',').map(s => s.trim()).filter(Boolean);
    try {
      await axios.post(`${API}/hookah/daily-session/open`, {
        stocker_id: user.id,
        stocker_name: user.name,
        country: user.country,
        responsable_name: openForm.responsable_name,
        servers,
        address: openForm.address,
        event: openForm.event,
        price_per_hookah: parseFloat(openForm.price_per_hookah) || 0,
        hookahs_in_service: parseInt(openForm.hookahs_in_service) || 0,
      });
      toast.success("Sesyon ouvri! Tab yo disponib kounye a.");
      setShowOpenSession(false);
      setOpenForm({ responsable_name: "", servers_text: "", address: "", event: "", price_per_hookah: 0, hookahs_in_service: 0 });
      loadAll();
    } catch(e) {
      toast.error(e.response?.data?.detail || "Erè");
    }
  };

  const closeDailySession = async () => {
    if (!window.confirm("Fèmen sesyon jou a? Tout tab ki ouvri ap fèmen tou. Rapò yo ap rete aktif.")) return;
    try {
      await axios.post(`${API}/hookah/daily-session/${dailySession.id}/close`);
      toast.success("Sesyon fèmen ✔");
      loadAll();
    } catch(e) { toast.error("Erè"); }
  };

  const startTable = async (hookahId) => {
    if (!tableForm.server_name.trim()) { toast.error("Non sèvè a obligatwa (kiyès ki sèvi kliyan an)"); return; }
    if (!tableForm.client_name.trim()) { toast.error("Non kliyan an obligatwa"); return; }
    const totalMin = (parseInt(tableForm.duration_hours) || 0) * 60 + (parseInt(tableForm.duration_minutes) || 0);
    if (totalMin <= 0) { toast.error("Antre yon dire valab"); return; }
    try {
      await axios.post(`${API}/hookah/${hookahId}/start`, {
        ...tableForm,
        stocker_id: user.id,
        stocker_name: user.name,
        daily_session_id: dailySession?.id,
        price: parseFloat(tableForm.price) || dailySession?.price_per_hookah || 0,
      });
      toast.success("Tab limen! 🔥");
      setShowAddTable(null);
      setTableForm({ server_name: "", client_name: "", duration_hours: 0, duration_minutes: 60, price: dailySession?.price_per_hookah || 0 });
      loadAll();
    } catch(e) { toast.error(e.response?.data?.detail || "Erè"); }
  };

  const refillTable = async (hookahId) => {
    if (refillForm.price <= 0) { toast.error("Antre pri refil"); return; }
    if (refillForm.minutes <= 0) { toast.error("Antre minit"); return; }
    try {
      await axios.post(`${API}/hookah/${hookahId}/renew`, {
        extra_minutes: parseInt(refillForm.minutes),
        refill_price: parseFloat(refillForm.price),
      });
      toast.success("Charbon ajoute ✔");
      setShowRefill(null);
      setRefillForm({ minutes: 30, price: 0 });
      loadAll();
    } catch(e) { toast.error("Erè"); }
  };

  const endTable = async (hookahId) => {
    if (!window.confirm("Fini sesyon tab sa?")) return;
    await axios.post(`${API}/hookah/${hookahId}/end`);
    toast.success("Tab fini");
    loadAll();
  };

  const getTimeRemaining = (session) => {
    if (!session) return "";
    const end = new Date(session.end_time);
    const diff = end - now;
    if (diff <= 0) return "TAN FINI";
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return h > 0 ? `${h}h ${m}m` : `${m}m ${s}s`;
  };

  // Compute light color: green pulsing if >5min remaining; yellow if <5min; red pulsing if expired
  const getLight = (session) => {
    if (!session) return { color: "bg-emerald-400", pulse: true, label: "DISPONIB" };
    const diff = new Date(session.end_time) - now;
    if (diff <= 0) return { color: "bg-red-500", pulse: true, label: "TAN FINI" };
    if (diff < 5 * 60 * 1000) return { color: "bg-yellow-400", pulse: true, label: "BYANTO" };
    return { color: "bg-emerald-400", pulse: true, label: "AP SÈVI" };
  };

  if (!user) return null;

  const sessionDate = dailySession ? new Date(dailySession.opened_at) : null;
  const sessionDuration = sessionDate ? (() => {
    const diff = now - sessionDate;
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return `${h}h ${m}m`;
  })() : "";

  return (
    <div className="min-h-screen relative overflow-hidden" style={{background: 'var(--vy-green-900)'}} data-testid="hookah-dashboard">
      {/* Watermark */}
      <div className="absolute bottom-0 left-0 right-0 text-center vy-watermark overflow-hidden">VAPE YARD</div>
      {user && <SupportCenter userId={user.id} userName={user.name} />}

      <div className="relative content-wrapper max-w-7xl mx-auto px-4 md:px-8 py-6">
        {/* HERO — Starbucks-style with vertical side text */}
        <div className="relative rounded-3xl overflow-hidden mb-6" style={{background: 'var(--vy-green-800)', minHeight: 280}}>
          {/* Vertical side text */}
          <div className="absolute right-0 top-0 bottom-0 w-24 flex items-center justify-center" style={{background: 'linear-gradient(135deg, var(--vy-green-700), var(--vy-green-600))'}}>
            <span className="vy-side-text" style={{fontSize: '3rem'}}>HOOKAH</span>
          </div>
          {/* Hero photo with overlay */}
          <div className="absolute inset-0 right-24">
            <img src={HOOKAH_HERO} alt="" onError={(e) => { e.target.src = HOOKAH_HERO_FALLBACK; }} className="w-full h-full object-cover opacity-40" />
            <div className="absolute inset-0" style={{background: 'linear-gradient(90deg, var(--vy-green-900) 0%, rgba(13,79,60,0.5) 100%)'}} />
          </div>
          {/* Content */}
          <div className="relative p-8 md:p-12 pr-32">
            <p className="text-xs font-bold tracking-[0.3em] uppercase mb-3" style={{color: 'var(--vy-gold-400)'}}>HOOKAH EXPERIENCE</p>
            <h1 className="text-5xl md:text-7xl font-black text-white tracking-tighter uppercase" style={{fontFamily:'var(--font-heading)'}}>Vape Yard</h1>
            <p className="text-white/90 text-lg mt-3" data-testid="hookah-welcome-message">
              Bienvenue à votre session <span className="font-black" style={{color: 'var(--vy-gold-400)'}}>{user.name}</span>
            </p>
            {dailySession && (
              <div className="flex flex-wrap gap-2 mt-4">
                <span className="vy-pill vy-pill-white text-xs">📅 {sessionDate?.toLocaleDateString('fr-FR')} • {sessionDate?.toLocaleTimeString('fr-FR', {hour:'2-digit', minute:'2-digit'})}</span>
                <span className="vy-pill vy-pill-green text-xs">⏱ {sessionDuration}</span>
                <span className="vy-pill text-xs" style={{background: 'var(--vy-gold-400)', color: 'var(--vy-green-900)'}}>{dailySession.hookahs_in_service} tab</span>
              </div>
            )}
            <div className="mt-6 flex flex-wrap gap-3">
              {!dailySession && (
                <button onClick={() => setShowOpenSession(true)} data-testid="hookah-open-daily-session-btn"
                  className="vy-pill text-base font-bold flex items-center gap-2 px-6 py-3" style={{background: 'var(--vy-gold-400)', color: 'var(--vy-green-900)'}}>
                  <Plus className="w-5 h-5" /> Nouvelle Session
                </button>
              )}
              {dailySession && (
                <button onClick={closeDailySession} data-testid="hookah-close-daily-session-btn"
                  className="vy-pill text-base font-bold flex items-center gap-2 px-6 py-3" style={{background: 'var(--vy-danger)', color: 'white'}}>
                  <LogOut className="w-5 h-5" /> Fermer la Session
                </button>
              )}
              <button onClick={() => { logout(); navigate('/'); }} className="vy-pill px-4 py-3" style={{background: 'rgba(255,255,255,0.1)', color: 'white'}}>
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* REVENUE SUMMARY BAR — Starbucks-style white cards */}
        {dailySession && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="hookah-summary">
            <div className="vy-cream-card p-4">
              <p className="text-[10px] font-black uppercase tracking-wider" style={{color: 'var(--vy-green-700)'}}>Pri pa Hookah</p>
              <p className="text-2xl font-black mt-1" style={{color: 'var(--vy-green-900)'}}>${(dailySession.price_per_hookah||0).toFixed(2)}</p>
            </div>
            <div className="vy-cream-card p-4">
              <p className="text-[10px] font-black uppercase tracking-wider" style={{color: 'var(--vy-green-700)'}}>Total Hookah</p>
              <p className="text-2xl font-black mt-1" style={{color: 'var(--vy-green-900)'}} data-testid="hookah-base-revenue">${summary.base_revenue.toFixed(2)}</p>
              <p className="text-[10px] mt-0.5" style={{color: 'var(--vy-green-600)'}}>{summary.completed_count + summary.active_count} tab</p>
            </div>
            <div className="vy-cream-card p-4">
              <p className="text-[10px] font-black uppercase tracking-wider" style={{color: 'var(--vy-green-700)'}}>Total Refil</p>
              <p className="text-2xl font-black mt-1" style={{color: 'var(--vy-green-900)'}} data-testid="hookah-refill-revenue">${summary.refill_revenue.toFixed(2)}</p>
            </div>
            <div className="vy-cream-card p-4">
              <p className="text-[10px] font-black uppercase tracking-wider" style={{color: 'var(--vy-gold-500)'}}>Total Global</p>
              <p className="text-2xl font-black mt-1" style={{color: 'var(--vy-gold-500)'}} data-testid="hookah-total-revenue">${summary.total_revenue.toFixed(2)}</p>
            </div>
            <div className="rounded-2xl p-4 col-span-2 md:col-span-1" style={{background: 'var(--vy-gold-400)', color: 'var(--vy-green-900)', boxShadow: '0 20px 40px -15px rgba(212,175,55,0.5)'}}>
              <p className="text-[10px] font-black uppercase tracking-wider">Ou Genyen</p>
              <p className="text-3xl font-black mt-1" data-testid="hookah-stocker-earnings">${summary.stocker_earnings.toFixed(2)}</p>
              <p className="text-[10px] font-bold opacity-80">25% / 50%</p>
            </div>
          </div>
        )}

        {/* DAILY SESSION INFO BANNER */}
        {dailySession && (
          <div className="vy-cream-card p-4 mb-6 flex flex-wrap gap-4 text-sm" data-testid="hookah-daily-info">
            <div><span className="text-[10px] font-black uppercase" style={{color: 'var(--vy-green-700)'}}>Responsab:</span> <span className="font-black" style={{color: 'var(--vy-green-900)'}}>{dailySession.responsable_name}</span></div>
            {dailySession.servers && dailySession.servers.length > 0 && (
              <div><span className="text-[10px] font-black uppercase" style={{color: 'var(--vy-green-700)'}}>Sèvè:</span> <span className="font-black" style={{color: 'var(--vy-gold-500)'}}>{dailySession.servers.join(", ")}</span></div>
            )}
            {dailySession.address && (
              <div><span className="text-[10px] font-black uppercase" style={{color: 'var(--vy-green-700)'}}>Adrès:</span> <span style={{color: 'var(--vy-green-800)'}}>{dailySession.address}</span></div>
            )}
            {dailySession.event && (
              <div><span className="text-[10px] font-black uppercase" style={{color: 'var(--vy-green-700)'}}>Evènman:</span> <span style={{color: 'var(--vy-gold-500)', fontWeight: 700}}>{dailySession.event}</span></div>
            )}
          </div>
        )}

        {/* LOCKED TABLES PREVIEW (before session) */}
        {!dailySession && (
          <div className="mb-6" data-testid="hookah-no-session">
            <div className="vy-cream-card p-6 mb-5 text-center">
              <Zap className="w-12 h-12 mx-auto mb-3" style={{color: 'var(--vy-green-700)'}} />
              <p className="text-2xl font-black uppercase tracking-tight" style={{color: 'var(--vy-green-900)', fontFamily:'var(--font-heading)'}}>Aperçu de vos Tables</p>
              <p className="text-sm mt-2" style={{color: 'var(--vy-green-700)'}}>Cliquez sur <span className="font-black" style={{color: 'var(--vy-gold-500)'}}>Nouvelle Session</span> pour activer les tables.</p>
            </div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-3xl font-black text-white tracking-tight uppercase" style={{fontFamily:'var(--font-heading)'}}>Mes Tables <span className="text-white/40 text-lg">(verrouillées)</span></h2>
              <div className="vy-ribbon text-xs">LOCKED</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {hookahs.map(h => (
                <div key={h.id} data-testid={`hookah-preview-${h.number}`}
                  className="vy-card-hover relative rounded-2xl overflow-hidden opacity-80" style={{background: 'var(--vy-cream-100)'}}>
                  <div className="relative aspect-square" style={{background: 'var(--vy-cream-200)'}}>
                    <img src={hookahImageFor(h)} alt={`Table ${h.number}`} onError={(e) => { e.target.src = hookahFallbackFor(h.number); }} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/30" />
                    <div className="absolute top-3 left-3 px-3 py-1 rounded-full" style={{background: 'var(--vy-green-800)'}}>
                      <p className="text-white font-black text-xs uppercase tracking-wider">Tab {h.number}</p>
                    </div>
                    <div className="absolute top-3 right-3 text-white text-2xl">🔒</div>
                  </div>
                  <div className="p-3 text-center">
                    <p className="text-[10px] font-black uppercase" style={{color: 'var(--vy-green-700)'}}>Verrouillée</p>
                    <p className="text-xs" style={{color: 'var(--vy-green-600)'}}>Ouvrir une session</p>
                  </div>
                </div>
              ))}
            </div>
            {hookahs.length === 0 && (
              <div className="vy-cream-card p-6 text-center">
                <p className="text-sm" style={{color: 'var(--vy-green-700)'}}>Aucune table configurée — Le CEO doit configurer les hookahs.</p>
              </div>
            )}
          </div>
        )}

        {dailySession && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-3xl font-black text-white tracking-tight uppercase" style={{fontFamily:'var(--font-heading)'}}>Mes Tables</h2>
              <div className="vy-ribbon text-xs">LIVE NOW</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {hookahs.slice(0, dailySession.hookahs_in_service).map(h => {
                const inSession = h.status === "in_session" && h.current_session;
                const expired = inSession && new Date() > new Date(h.current_session.end_time);
                const light = getLight(inSession ? h.current_session : null);
                const isAvailable = h.status === "available";
                const tableMatchesDaily = inSession && h.current_session?.daily_session_id === dailySession.id;
                return (
                  <div key={h.id} data-testid={`hookah-table-${h.number}`}
                    className="vy-card-hover relative rounded-2xl overflow-hidden"
                    style={{
                      background: 'var(--vy-cream-100)',
                      boxShadow: expired ? '0 20px 40px -10px rgba(225,29,72,0.4)' : inSession ? '0 20px 40px -10px rgba(212,175,55,0.3)' : '0 20px 40px -15px rgba(0,0,0,0.3)'
                    }}>
                    {/* Photo */}
                    <div className="relative aspect-square" style={{background: 'var(--vy-cream-200)'}}>
                      <img src={hookahImageFor(h)} alt={`Table ${h.number}`} onError={(e) => { e.target.src = hookahFallbackFor(h.number); }} className={`w-full h-full object-cover ${isAvailable ? '' : 'brightness-90'}`} />
                      {/* Live status light */}
                      <div className={`absolute top-3 right-3 w-4 h-4 rounded-full ${light.color} ${light.pulse ? 'animate-pulse' : ''}`} style={{boxShadow: '0 0 16px currentColor'}} />
                      {/* Table number badge in green ribbon */}
                      <div className="absolute top-3 left-3 px-3 py-1 rounded-full" style={{background: 'var(--vy-green-800)'}}>
                        <p className="text-white font-black text-xs uppercase tracking-wider">Tab {h.number}</p>
                      </div>
                    </div>

                    {/* Card body — Starbucks style */}
                    <div className="p-4">
                      {inSession && tableMatchesDaily ? (
                        <>
                          <p className="text-[10px] font-black uppercase tracking-wider mb-1" style={{color: expired ? 'var(--vy-danger)' : 'var(--vy-green-700)'}}>{light.label}</p>
                          <p className="font-black text-base truncate uppercase" style={{color: 'var(--vy-green-900)'}}>{h.current_session.client_name || '—'}</p>
                          <p className="text-xs mt-1" style={{color: 'var(--vy-green-700)'}}>Sèvè: {h.current_session.server_name || h.current_session.stocker_name}</p>
                          <p className={`font-mono font-black text-sm mt-1 ${expired ? '' : ''}`} style={{color: expired ? 'var(--vy-danger)' : 'var(--vy-green-600)'}}>⏱ {getTimeRemaining(h.current_session)}</p>
                          <div className="mt-2 pt-2 border-t" style={{borderColor: 'var(--vy-cream-200)'}}>
                            <p className="text-xs font-bold" style={{color: 'var(--vy-gold-500)'}}>${(h.current_session.price||0).toFixed(2)} + ${(h.current_session.total_refill_amount||0).toFixed(2)}</p>
                          </div>
                          <div className="flex gap-1 mt-3">
                            <button onClick={() => { setShowRefill(h); setRefillForm({minutes: 30, price: 0}); }} className="vy-pill flex-1 text-[10px] font-black uppercase py-1.5" style={{background: 'var(--vy-green-700)', color: 'white'}} data-testid={`hookah-refill-${h.number}`}>Refil</button>
                            <button onClick={() => endTable(h.id)} className="vy-pill flex-1 text-[10px] font-black uppercase py-1.5" style={{background: 'var(--vy-danger)', color: 'white'}} data-testid={`hookah-finish-${h.number}`}>{expired ? 'Fini' : 'Stop'}</button>
                          </div>
                        </>
                      ) : isAvailable ? (
                        <>
                          <p className="text-[10px] font-black uppercase tracking-wider" style={{color: 'var(--vy-green-700)'}}>Disponib</p>
                          <p className="font-black text-base uppercase" style={{color: 'var(--vy-green-900)'}}>Table {h.number}</p>
                          <p className="text-xs mt-1" style={{color: 'var(--vy-green-700)'}}>Klike pou ajoute kliyan</p>
                          <button onClick={() => { setShowAddTable(h); setTableForm({server_name: '', client_name: '', duration_hours: 0, duration_minutes: 60, price: dailySession.price_per_hookah || 0}); }}
                            data-testid={`hookah-add-${h.number}`}
                            className="vy-pill w-full mt-3 text-xs font-black uppercase py-2" style={{background: 'var(--vy-green-800)', color: 'white'}}>
                            + Ajouter
                          </button>
                        </>
                      ) : (
                        <p className="text-xs text-center py-2" style={{color: 'var(--vy-green-700)'}}>{h.status}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* HISTORY — completed table sessions in this daily session */}
        {dailySession && summary.tables && summary.tables.filter(t => t.status === "completed").length > 0 && (
          <div className="glass-panel p-4 mb-5">
            <h3 className="text-white font-bold mb-3" style={{fontFamily:'var(--font-heading)'}}>📜 Istorik Tab Fini (Sesyon Sa)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-zinc-500 text-[10px] uppercase">
                  <tr><th className="text-left p-2">Tab</th><th className="text-left">Kliyan</th><th className="text-left">Sèvè</th><th className="text-right">Pri</th><th className="text-right">Refil</th><th className="text-right">Total</th></tr>
                </thead>
                <tbody>
                  {summary.tables.filter(t => t.status === "completed").map((t, i) => (
                    <tr key={i} className="border-t border-zinc-800 text-zinc-300">
                      <td className="p-2">#{t.hookah_number}</td>
                      <td>{t.client_name}</td>
                      <td className="text-emerald-300">{t.server_name || t.stocker_name}</td>
                      <td className="text-right text-yellow-400">${(t.price||0).toFixed(2)}</td>
                      <td className="text-right text-blue-400">${(t.total_refill_amount||0).toFixed(2)}</td>
                      <td className="text-right text-emerald-400 font-bold">${((t.price||0)+(t.total_refill_amount||0)).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* OPEN DAILY SESSION MODAL */}
        {showOpenSession && (
          <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowOpenSession(false)}>
            <div className="bg-zinc-900 rounded-3xl p-6 max-w-lg w-full border-2 border-emerald-500 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-bold text-white text-xl mb-1" style={{fontFamily:'var(--font-heading)'}}>Nouvelle Session</h3>
              <p className="text-zinc-400 text-xs mb-4">Konfigire sesyon jou ou anvan tab yo parèt.</p>

              <div className="space-y-3">
                <div>
                  <label className="text-zinc-400 text-[10px] uppercase font-bold">Responsab jou a</label>
                  <input type="text" placeholder="Non responsab" value={openForm.responsable_name}
                    onChange={(e) => setOpenForm({...openForm, responsable_name: e.target.value})}
                    data-testid="hookah-responsable-input"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl py-2.5 px-4 text-white" />
                </div>
                <div>
                  <label className="text-zinc-400 text-[10px] uppercase font-bold">Sèvè/Service (separe ak vigil)</label>
                  <input type="text" placeholder="Ex: Pierre, Marie, John" value={openForm.servers_text}
                    onChange={(e) => setOpenForm({...openForm, servers_text: e.target.value})}
                    data-testid="hookah-servers-input"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl py-2.5 px-4 text-white" />
                </div>
                <div>
                  <label className="text-zinc-400 text-[10px] uppercase font-bold">Adrès</label>
                  <input type="text" placeholder="Kote hookah yo ap sèvi" value={openForm.address}
                    onChange={(e) => setOpenForm({...openForm, address: e.target.value})}
                    data-testid="hookah-address-input"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl py-2.5 px-4 text-white" />
                </div>
                <div>
                  <label className="text-zinc-400 text-[10px] uppercase font-bold">Evènman</label>
                  <input type="text" placeholder="Non evènman" value={openForm.event}
                    onChange={(e) => setOpenForm({...openForm, event: e.target.value})}
                    data-testid="hookah-event-input"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl py-2.5 px-4 text-white" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-zinc-400 text-[10px] uppercase font-bold">Pri pa Hookah ($)</label>
                    <input type="number" min={0} step={0.01} placeholder="0.00" value={openForm.price_per_hookah}
                      onChange={(e) => setOpenForm({...openForm, price_per_hookah: e.target.value})}
                      data-testid="hookah-price-input"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl py-2.5 px-4 text-white text-xl font-bold" />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-[10px] uppercase font-bold">Kantite Hookah</label>
                    <input type="number" min={1} max={hookahs.length || 20} placeholder={`max ${hookahs.length}`} value={openForm.hookahs_in_service}
                      onChange={(e) => setOpenForm({...openForm, hookahs_in_service: e.target.value})}
                      data-testid="hookah-count-input"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl py-2.5 px-4 text-white text-xl font-bold" />
                  </div>
                </div>
                {hookahs.length > 0 && openForm.hookahs_in_service > 0 && (
                  <div className="bg-zinc-950/50 rounded-xl p-3 border border-zinc-800">
                    <p className="text-zinc-500 text-[10px] uppercase mb-2 font-bold">📸 Aperçu — Tab ki pral disponib</p>
                    <div className="grid grid-cols-5 gap-2">
                      {hookahs.slice(0, parseInt(openForm.hookahs_in_service) || 0).map(h => (
                        <div key={h.id} className="relative aspect-square rounded-lg overflow-hidden border-2 border-emerald-700/50 hover:scale-105 transition-transform">
                          <img src={hookahImageFor(h.number)} alt={`Table ${h.number}`} onError={(e) => { e.target.src = hookahFallbackFor(h.number); }} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                          <span className="absolute bottom-1 left-1 right-1 text-center text-white text-[10px] font-black drop-shadow">Tab {h.number}</span>
                          <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <button onClick={openDailySession} data-testid="hookah-open-session-confirm"
                className="w-full mt-4 bg-gradient-to-r from-emerald-600 to-violet-600 hover:from-emerald-500 hover:to-violet-500 text-white font-black py-4 rounded-2xl text-lg">
                ▶ OPEN SESSION
              </button>
            </div>
          </div>
        )}

        {/* ADD TABLE MODAL */}
        {showAddTable && (
          <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAddTable(null)}>
            <div className="bg-zinc-900 rounded-3xl p-6 max-w-md w-full border-2 border-violet-500 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-white text-xl" style={{fontFamily:'var(--font-heading)'}}>Tab {showAddTable.number}</h3>
                <button onClick={() => setShowAddTable(null)} className="text-zinc-500 hover:text-white text-2xl">✕</button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-zinc-400 text-[10px] uppercase font-bold">Sèvè (kiyès ki sèvi)</label>
                  <select value={tableForm.server_name} onChange={(e) => setTableForm({...tableForm, server_name: e.target.value})}
                    data-testid="hookah-table-server"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl py-2.5 px-4 text-white">
                    <option value="">— Chwazi —</option>
                    <option value={dailySession?.responsable_name}>{dailySession?.responsable_name} (responsab)</option>
                    {(dailySession?.servers || []).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-[10px] uppercase font-bold">Non Kliyan</label>
                  <input type="text" placeholder="Non kliyan an" value={tableForm.client_name}
                    onChange={(e) => setTableForm({...tableForm, client_name: e.target.value})}
                    data-testid="hookah-table-client"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl py-2.5 px-4 text-white" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-zinc-400 text-[10px] uppercase font-bold">Èdtan</label>
                    <input type="number" min={0} value={tableForm.duration_hours}
                      onChange={(e) => setTableForm({...tableForm, duration_hours: e.target.value})}
                      data-testid="hookah-table-hours"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl py-2.5 px-3 text-white text-center text-lg font-bold" />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-[10px] uppercase font-bold">Minit</label>
                    <input type="number" min={0} max={59} value={tableForm.duration_minutes}
                      onChange={(e) => setTableForm({...tableForm, duration_minutes: e.target.value})}
                      data-testid="hookah-table-mins"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl py-2.5 px-3 text-white text-center text-lg font-bold" />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-[10px] uppercase font-bold">Pri ($)</label>
                    <input type="number" min={0} step={0.01} value={tableForm.price}
                      onChange={(e) => setTableForm({...tableForm, price: e.target.value})}
                      data-testid="hookah-table-price"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl py-2.5 px-3 text-emerald-400 text-center text-lg font-bold" />
                  </div>
                </div>
              </div>
              <button onClick={() => startTable(showAddTable.id)} data-testid="hookah-allumer-btn"
                className="w-full mt-4 bg-gradient-to-r from-emerald-600 to-violet-600 hover:from-emerald-500 hover:to-violet-500 text-white font-black py-4 rounded-2xl text-lg">
                ▶ ALLUMER LA TABLE
              </button>
            </div>
          </div>
        )}

        {/* REFILL MODAL */}
        {showRefill && (
          <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowRefill(null)}>
            <div className="bg-zinc-900 rounded-3xl p-6 max-w-md w-full border-2 border-blue-500" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-bold text-white text-xl mb-3" style={{fontFamily:'var(--font-heading)'}}>Refil — Tab {showRefill.number}</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-zinc-400 text-[10px] uppercase font-bold">Minit pou Ajoute</label>
                  <input type="number" min={1} value={refillForm.minutes}
                    onChange={(e) => setRefillForm({...refillForm, minutes: e.target.value})}
                    data-testid="hookah-refill-minutes"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl py-3 px-4 text-white text-2xl font-bold text-center" />
                  <div className="flex gap-1 mt-2">
                    {[15, 30, 45, 60].map(m => (
                      <button key={m} onClick={() => setRefillForm({...refillForm, minutes: m})}
                        className="text-xs bg-zinc-800 hover:bg-blue-700 text-zinc-300 hover:text-white px-3 py-1 rounded-full">
                        {m}min
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-zinc-400 text-[10px] uppercase font-bold">Pri Refil ($)</label>
                  <input type="number" min={0} step={0.01} value={refillForm.price}
                    onChange={(e) => setRefillForm({...refillForm, price: e.target.value})}
                    data-testid="hookah-refill-price"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl py-3 px-4 text-emerald-400 text-2xl font-bold text-center" />
                </div>
              </div>
              <button onClick={() => refillTable(showRefill.id)} data-testid="hookah-refill-confirm"
                className="w-full mt-4 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 text-white font-black py-4 rounded-2xl text-lg">
                ▶ AJOUTE CHARBON
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// =================== INV LOGIN (FRANSE) ===================
const InvLogin = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [secretCode, setSecretCode] = useState("");
  const [newSecretCode, setNewSecretCode] = useState("");
  const [confirmNewSecret, setConfirmNewSecret] = useState("");
  const [step, setStep] = useState(1); // 1: name+code, 2: enter secret, 3: create new secret
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (step === 1) {
      if (!name || !code) { toast.error("Entrez votre nom et code"); return; }
      setLoading(true);
      try {
        const r = await axios.post(`${API}/auth/inv/login`, { name, code });
        if (r.data.need_secret_creation) {
          setStep(3);
          toast.info("Première connexion — créez votre code secret");
        } else if (r.data.need_secret) {
          setStep(2);
        } else {
          localStorage.setItem('vapeyard_inv', JSON.stringify(r.data.inv));
          toast.success(`Bienvenue ${r.data.inv.name}!`);
          navigate('/inv/dashboard');
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Erreur de connexion");
      } finally { setLoading(false); }
      return;
    }

    if (step === 2) {
      if (secretCode.length !== 4) { toast.error("Code secret = 4 chiffres"); return; }
      setLoading(true);
      try {
        const r = await axios.post(`${API}/auth/inv/login`, { name, code, secret_code: secretCode });
        localStorage.setItem('vapeyard_inv', JSON.stringify(r.data.inv));
        toast.success(`Bienvenue ${r.data.inv.name}!`);
        navigate('/inv/dashboard');
      } catch (e) {
        toast.error(e.response?.data?.detail || "Code secret incorrect");
        setSecretCode("");
      } finally { setLoading(false); }
      return;
    }

    if (step === 3) {
      if (newSecretCode.length !== 4 || !/^\d{4}$/.test(newSecretCode)) { toast.error("Le code doit avoir 4 chiffres"); return; }
      if (newSecretCode !== confirmNewSecret) { toast.error("Les codes ne correspondent pas"); return; }
      setLoading(true);
      try {
        const r = await axios.post(`${API}/auth/inv/login`, { name, code, new_secret_code: newSecretCode });
        localStorage.setItem('vapeyard_inv', JSON.stringify(r.data.inv));
        toast.success("Code secret créé! Bienvenue.");
        navigate('/inv/dashboard');
      } catch (e) {
        toast.error(e.response?.data?.detail || "Erreur");
      } finally { setLoading(false); }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-zinc-950 to-black flex items-center justify-center p-4">
      <div className="glass-panel p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-600/20 flex items-center justify-center mb-3">
            <DollarSign className="w-8 h-8 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-1" style={{fontFamily:'var(--font-heading)'}}>Espace INV</h2>
          <p className="text-zinc-400 text-sm">
            {step === 1 && "Identification investisseur"}
            {step === 2 && "Authentification — code secret"}
            {step === 3 && "Sécurisez votre compte"}
          </p>
        </div>

        {step === 1 && (
          <>
            <input type="text" placeholder="Nom" value={name} onChange={(e) => setName(e.target.value)} data-testid="inv-name-input" className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-white mb-3" />
            <input type="password" placeholder="Code INV" value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleLogin()} data-testid="inv-code-input" className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-white mb-4" />
          </>
        )}

        {step === 2 && (
          <>
            <p className="text-zinc-400 text-xs mb-2">Bonjour <strong className="text-white">{name}</strong>, entrez votre code secret personnel à 4 chiffres.</p>
            <input type="password" inputMode="numeric" maxLength={4} placeholder="••••" value={secretCode} onChange={(e) => setSecretCode(e.target.value.replace(/\D/g,'').slice(0,4))} onKeyDown={(e) => e.key === 'Enter' && handleLogin()} data-testid="inv-secret-input" className="w-full bg-zinc-900 border-2 border-emerald-700 rounded-xl px-4 py-4 text-white text-center text-3xl tracking-widest mb-4" />
            <button onClick={() => { setStep(1); setSecretCode(""); }} className="text-zinc-500 text-xs mb-2 hover:text-white">← Retour</button>
          </>
        )}

        {step === 3 && (
          <>
            <p className="text-amber-400 text-xs mb-3">🔒 Première connexion détectée. Créez votre code secret à 4 chiffres. Seul vous le connaîtrez.</p>
            <input type="password" inputMode="numeric" maxLength={4} placeholder="Nouveau code (4 chiffres)" value={newSecretCode} onChange={(e) => setNewSecretCode(e.target.value.replace(/\D/g,'').slice(0,4))} data-testid="inv-new-secret-input" className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-white text-center text-2xl tracking-widest mb-3" />
            <input type="password" inputMode="numeric" maxLength={4} placeholder="Confirmer le code" value={confirmNewSecret} onChange={(e) => setConfirmNewSecret(e.target.value.replace(/\D/g,'').slice(0,4))} onKeyDown={(e) => e.key === 'Enter' && handleLogin()} data-testid="inv-confirm-secret-input" className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-white text-center text-2xl tracking-widest mb-4" />
            <button onClick={() => { setStep(1); setNewSecretCode(""); setConfirmNewSecret(""); }} className="text-zinc-500 text-xs mb-2 hover:text-white">← Retour</button>
          </>
        )}

        <button onClick={handleLogin} disabled={loading} data-testid="inv-login-btn" className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold py-3 rounded-xl">
          {loading ? "..." : step === 1 ? "Continuer" : step === 2 ? "Se Connecter" : "Créer & Connecter"}
        </button>
        <button onClick={() => navigate('/')} className="w-full text-zinc-500 text-sm mt-3 hover:text-white">Retour à l'accueil</button>
      </div>
    </div>
  );
};

// =================== INV DASHBOARD (FRANSE KONPLÈ) ===================
const InvDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [inv, setInv] = useState(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const [showAddInvest, setShowAddInvest] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showReinvest, setShowReinvest] = useState(false);
  const [showSimulator, setShowSimulator] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(null); // holds selected plan or null
  const [showChangeSecret, setShowChangeSecret] = useState(false);
  const [oldSecret, setOldSecret] = useState("");
  const [newSecret, setNewSecret] = useState("");
  const [confirmSecret, setConfirmSecret] = useState("");
  const [addAmount, setAddAmount] = useState("");
  const [reinvestAmount, setReinvestAmount] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  // Live tracking modal
  const [showLiveTracking, setShowLiveTracking] = useState(false);
  const [liveData, setLiveData] = useState(null);
  // History modal
  const [showInvHistory, setShowInvHistory] = useState(false);
  // Simulator inputs
  const [simNbStockers, setSimNbStockers] = useState(3);
  const [simVapesPerStocker, setSimVapesPerStocker] = useState(12);
  const CEO_WHATSAPP = "17866309220"; // +1 786 630 9220
  const VAPE_COST = 25.64;
  const VAPE_PRICE = 40;
  const FREE_PER_DOZEN = 1;
  const STOCKER_COMMISSION = 0.125; // 12.5% average

  // Helper: calculate margins for a given investment amount
  const calcFromInvestment = (investAmount) => {
    const totalVapesBought = Math.floor(investAmount / VAPE_COST);
    const freeVapes = Math.floor(totalVapesBought / 12) * FREE_PER_DOZEN;
    const totalSellable = totalVapesBought + freeVapes;
    const grossRevenue = totalSellable * VAPE_PRICE;
    const stockerCommission = grossRevenue * STOCKER_COMMISSION;
    const platformFee = investAmount * 0.15;
    const netProfit = grossRevenue - investAmount - stockerCommission - platformFee;
    return { totalVapesBought, freeVapes, totalSellable, grossRevenue, stockerCommission, platformFee, netProfit };
  };

  // Simulator computation
  const simStats = (() => {
    const totalVapes = simNbStockers * simVapesPerStocker;
    const freeVapes = simNbStockers * Math.floor(simVapesPerStocker / 12) * FREE_PER_DOZEN;
    const totalSellable = totalVapes + freeVapes;
    const cost = totalVapes * VAPE_COST; // free ones don't cost
    const grossRevenue = totalSellable * VAPE_PRICE;
    const stockerCommission = grossRevenue * STOCKER_COMMISSION;
    const platformFee = cost * 0.15;
    const netProfit = grossRevenue - cost - stockerCommission - platformFee;
    return { totalVapes, freeVapes, totalSellable, cost, grossRevenue, stockerCommission, platformFee, netProfit };
  })();

  // Predefined plans
  const INVESTMENT_PLANS = [
    { name: "Starter", amount: 3000, color: "emerald" },
    { name: "Croissance", amount: 5000, color: "blue" },
    { name: "Pro", amount: 10000, color: "violet" },
    { name: "Empire", amount: 20000, color: "amber" },
  ].map(p => ({ ...p, ...calcFromInvestment(p.amount) }));

  useEffect(() => {
    const stored = localStorage.getItem('vapeyard_inv');
    if (!stored) { navigate('/inv/login'); return; }
    const i = JSON.parse(stored);
    setInv(i);
    setPhotoUrl(i.photo_url || "");
    fetchData(i.id);
    const interval = setInterval(() => fetchData(i.id), 15000);
    return () => clearInterval(interval);
  }, [navigate]);

  const fetchData = async (invId) => {
    try {
      const r = await axios.get(`${API}/inv/${invId}/dashboard`);
      setData(r.data);
      if (r.data.inv) setInv(r.data.inv);
    } catch (e) { /* ignore */ }
  };

  const fetchLiveTracking = async () => {
    if (!inv) return;
    try {
      const r = await axios.get(`${API}/inv/${inv.id}/live-tracking`);
      setLiveData(r.data);
    } catch (e) { /* ignore */ }
  };

  // Poll live tracking every 10s while modal is open
  useEffect(() => {
    if (!showLiveTracking) return;
    fetchLiveTracking();
    const t = setInterval(fetchLiveTracking, 10000);
    return () => clearInterval(t);
  }, [showLiveTracking, inv]);

  const handleWithdraw = async () => {
    if (!inv) return;
    const total = (data?.totals?.revenue_cycle || 0) + (data?.totals?.bonus_cycle || 0);
    if (total <= 0) { toast.error("Pas de bénéfices à retirer pour le moment"); return; }
    if (!window.confirm(`Confirmer le retrait de $${total.toFixed(2)}? Le CEO va recevoir la demande.`)) return;
    setWithdrawing(true);
    try {
      const r = await axios.post(`${API}/inv/${inv.id}/withdraw`);
      toast.success(`Demande de retrait $${r.data.amount.toFixed(2)} envoyée au CEO`);
      fetchData(inv.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setWithdrawing(false); }
  };

  const handleAddInvestment = () => {
    const amt = parseFloat(addAmount);
    if (!amt || amt <= 0) { toast.error("Entrez un montant valide"); return; }
    // Send via WhatsApp + create a request notification for CEO
    axios.post(`${API}/inv/${inv.id}/add-investment-request`, { amount: amt })
      .then(() => {
        toast.success("Demande envoyée au CEO");
        // Also open WhatsApp
        const text = encodeURIComponent(`Bonjour CEO, je suis ${inv.name}. Je souhaite ajouter $${amt} à mon investissement Vape Yard.`);
        window.open(`https://wa.me/${CEO_WHATSAPP}?text=${text}`, '_blank');
        setShowAddInvest(false);
        setAddAmount("");
      })
      .catch(() => toast.error("Erreur"));
  };

  const handleSelectPlan = (plan) => {
    // 1. Create CEO notification with full details
    axios.post(`${API}/inv/${inv.id}/request-plan`, {
      amount: plan.amount,
      projected_profit: plan.netProfit,
      plan_name: plan.name,
    }).then(() => {
      // 2. Redirect to WhatsApp with pre-filled message
      const text = encodeURIComponent(
        `Bonjour CEO, je suis ${inv.name} (INV Vape Yard).\n\n` +
        `Je souhaite souscrire au PLAN ${plan.name.toUpperCase()} : $${plan.amount}.\n` +
        `• Vapes à acheter: ${plan.totalVapesBought} + ${plan.freeVapes} gratuits = ${plan.totalSellable}\n` +
        `• Profit net projeté: $${plan.netProfit.toFixed(2)}\n\n` +
        `Merci de confirmer pour activer mon plan.`
      );
      window.open(`https://wa.me/${CEO_WHATSAPP}?text=${text}`, '_blank');
      toast.success(`Demande envoyée au CEO pour le plan ${plan.name}`);
      setShowPlanModal(null);
    }).catch(() => toast.error("Erreur lors de l'envoi"));
  };

  const handleChangeSecret = async () => {
    if (newSecret.length !== 4 || !/^\d{4}$/.test(newSecret)) { toast.error("4 chiffres requis"); return; }
    if (newSecret !== confirmSecret) { toast.error("Les codes ne correspondent pas"); return; }
    try {
      await axios.put(`${API}/inv/${inv.id}/change-secret-code`, {
        old_secret_code: oldSecret,
        new_secret_code: newSecret,
      });
      toast.success("Code secret modifié");
      setShowChangeSecret(false);
      setOldSecret(""); setNewSecret(""); setConfirmSecret("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleReinvest = () => {
    const amt = parseFloat(reinvestAmount);
    if (!amt || amt <= 0) { toast.error("Entrez un montant valide"); return; }
    const available = (data?.totals?.revenue_cycle || 0) + (data?.totals?.bonus_cycle || 0);
    if (amt > available) { toast.error(`Maximum disponible: $${available.toFixed(2)}`); return; }
    axios.post(`${API}/inv/${inv.id}/reinvest`, { amount: amt })
      .then(() => {
        toast.success(`$${amt} envoyé sur votre base d'investissement`);
        setShowReinvest(false);
        setReinvestAmount("");
        fetchData(inv.id);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Erreur"));
  };

  const handleSavePhoto = () => {
    axios.put(`${API}/inv/${inv.id}/profile`, { photo_url: photoUrl })
      .then(() => { toast.success("Photo mise à jour"); setShowProfile(false); fetchData(inv.id); })
      .catch(() => toast.error("Erreur"));
  };

  if (!data || !inv) return <div className="min-h-screen bg-black text-zinc-400 flex items-center justify-center">Chargement...</div>;

  const { stockers, totals, chart_data, notifications } = data;
  const availableToWithdraw = (totals.revenue_cycle || 0) + (totals.bonus_cycle || 0);
  const investmentBase = inv.investment_amount || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-zinc-950 to-black p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            {/* Profile photo */}
            <button onClick={() => setShowProfile(true)} className="w-14 h-14 rounded-full bg-emerald-700/30 border-2 border-emerald-500 overflow-hidden flex items-center justify-center" data-testid="inv-profile-btn">
              {inv.photo_url ? <img src={inv.photo_url} alt="" className="w-full h-full object-cover" /> : <User className="w-7 h-7 text-emerald-400" />}
            </button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-white" style={{fontFamily:'var(--font-heading)'}} data-testid="inv-welcome">
                Bienvenue sur votre compte INV
              </h1>
              <p className="text-emerald-400 text-sm">{inv.name} • {(inv.country || '').toUpperCase()}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <NotificationBell userId={inv.id} />
            <LanguageSwitcher />
            <button onClick={() => setShowPolicy(true)} data-testid="inv-policy-btn" className="bg-zinc-800 hover:bg-zinc-700 text-zinc-300 px-3 py-2 rounded-lg text-xs">📜 Politique</button>
            <button onClick={() => setShowInvHistory(true)} data-testid="inv-history-btn" className="bg-emerald-700 hover:bg-emerald-600 text-white px-3 py-2 rounded-lg text-xs">📜 Istori</button>
            <button onClick={() => setShowChangeSecret(true)} data-testid="inv-change-secret-btn" className="bg-zinc-800 hover:bg-zinc-700 text-zinc-300 px-3 py-2 rounded-lg text-xs">🔐 Code Secret</button>
            <button onClick={() => { localStorage.removeItem('vapeyard_inv'); navigate('/'); }} className="bg-zinc-800 hover:bg-zinc-700 text-white px-3 py-2 rounded-lg text-xs" data-testid="inv-logout">Déconnexion</button>
          </div>
        </div>
        <SupportCenter userId={inv.id} userName={inv.name} />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <div className="glass-panel p-4">
            <p className="text-zinc-500 text-xs">Stockers</p>
            <p className="text-white text-2xl font-bold" data-testid="inv-stocker-count">{totals.stocker_count}</p>
          </div>
          <div className="glass-panel p-4">
            <p className="text-zinc-500 text-xs">Vendus (Cycle)</p>
            <p className="text-emerald-400 text-2xl font-bold" data-testid="inv-vapes-sold">{totals.vapes_sold_cycle}</p>
          </div>
          <div className="glass-panel p-4">
            <p className="text-zinc-500 text-xs">Revenus (Cycle)</p>
            <p className="text-yellow-400 text-2xl font-bold" data-testid="inv-revenue">${totals.revenue_cycle.toFixed(2)}</p>
          </div>
          <div className="glass-panel p-4 border-violet-700/50">
            <p className="text-zinc-500 text-xs">Base Investissement</p>
            <p className="text-violet-400 text-2xl font-bold" data-testid="inv-investment">${investmentBase.toFixed(2)}</p>
          </div>
          <div className="glass-panel p-4 border-cyan-700/50 col-span-2 md:col-span-1">
            <p className="text-zinc-500 text-xs flex items-center gap-1">📦 Stock en Réserve</p>
            <p className="text-cyan-400 text-2xl font-bold" data-testid="inv-reserve-stock">{totals.reserve_stock || 0}</p>
            <p className="text-zinc-600 text-[10px]">vapes gardés par le CEO</p>
          </div>
        </div>

        {/* Bénéfices + Actions */}
        <div className="grid md:grid-cols-2 gap-3 mb-6">
          {/* Bénéfices à retirer */}
          <div className="glass-panel p-5 bg-emerald-950/30 border-emerald-700/50">
            <p className="text-emerald-300 text-sm font-bold">💰 Bénéfices Disponibles</p>
            <p className="text-white text-3xl font-black my-2">${availableToWithdraw.toFixed(2)}</p>
            <p className="text-zinc-500 text-xs mb-3">Revenus ${totals.revenue_cycle.toFixed(2)} + Bonus ${totals.bonus_cycle.toFixed(2)}</p>
            <div className="flex gap-2 flex-wrap">
              <button onClick={handleWithdraw} disabled={withdrawing || availableToWithdraw === 0} data-testid="inv-withdraw-btn" className="flex-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold px-4 py-2 rounded-lg text-sm">
                {withdrawing ? "..." : "💵 Retirer"}
              </button>
              <button onClick={() => setShowReinvest(true)} disabled={availableToWithdraw === 0} data-testid="inv-reinvest-btn" className="flex-1 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-bold px-4 py-2 rounded-lg text-sm">
                📈 Réinvestir
              </button>
            </div>
          </div>
          {/* Base Investissement */}
          <div className="glass-panel p-5 bg-violet-950/30 border-violet-700/50">
            <p className="text-violet-300 text-sm font-bold">🏦 Base d'Investissement</p>
            <p className="text-white text-3xl font-black my-2">${investmentBase.toFixed(2)}</p>
            <p className="text-zinc-500 text-xs mb-3">Montant total investi dans Vape Yard</p>
            <button onClick={() => setShowAddInvest(true)} data-testid="inv-add-invest-btn" className="w-full bg-emerald-700 hover:bg-emerald-600 text-white font-bold px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-1">
              <Plus className="w-4 h-4" /> Ajouter à mon Investissement
            </button>
          </div>
        </div>

        {/* PROFIT SIMULATOR + INVESTMENT PLANS */}
        <div className="grid lg:grid-cols-2 gap-3 mb-6">
          {/* Simulator */}
          <div className="glass-panel p-5 bg-gradient-to-br from-zinc-900 to-emerald-950/30 border-emerald-700/50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-bold flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-emerald-400" />
                Simulateur de Profit
              </h3>
              <button onClick={() => setShowSimulator(s => !s)} className="text-emerald-400 text-xs hover:underline" data-testid="inv-simulator-toggle">
                {showSimulator ? "Masquer" : "Détails"}
              </button>
            </div>
            <p className="text-zinc-500 text-xs mb-3">Coût: <strong className="text-amber-400">${VAPE_COST}</strong>/vape • Vente: <strong className="text-emerald-400">${VAPE_PRICE}</strong>/vape • <strong className="text-violet-400">+1 vape GRATUIT pour chaque 12</strong></p>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-zinc-400 text-xs">Nombre de Stockers</label>
                <input type="number" min={1} max={50} value={simNbStockers} onChange={(e) => setSimNbStockers(Math.max(1, parseInt(e.target.value) || 1))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white" data-testid="sim-nb-stockers" />
              </div>
              <div>
                <label className="text-zinc-400 text-xs">Vapes par Stocker</label>
                <input type="number" min={1} max={100} value={simVapesPerStocker} onChange={(e) => setSimVapesPerStocker(Math.max(1, parseInt(e.target.value) || 1))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white" data-testid="sim-vapes-per-stocker" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-center text-xs">
              <div className="bg-blue-500/10 p-2 rounded"><p className="text-blue-400">Vapes à acheter</p><p className="text-blue-400 font-bold text-lg" data-testid="sim-vapes-total">{simStats.totalVapes}</p></div>
              <div className="bg-violet-500/10 p-2 rounded"><p className="text-violet-400">Gratuits (+1/12)</p><p className="text-violet-400 font-bold text-lg" data-testid="sim-vapes-free">+{simStats.freeVapes}</p></div>
              <div className="bg-amber-500/10 p-2 rounded"><p className="text-amber-400">Investissement</p><p className="text-amber-400 font-bold text-lg" data-testid="sim-cost">${simStats.cost.toFixed(2)}</p></div>
              <div className="bg-yellow-500/10 p-2 rounded"><p className="text-yellow-400">Chiffre d'Affaires</p><p className="text-yellow-400 font-bold text-lg" data-testid="sim-revenue">${simStats.grossRevenue.toFixed(2)}</p></div>
            </div>

            {showSimulator && (
              <div className="mt-3 p-3 bg-zinc-950/50 rounded-lg border border-zinc-800 text-xs space-y-1">
                <div className="flex justify-between"><span className="text-zinc-500">Total vendable</span><span className="text-white">{simStats.totalSellable} vapes</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Commission Stockers (12.5%)</span><span className="text-zinc-300">-${simStats.stockerCommission.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Frais Plateforme (15%)</span><span className="text-zinc-300">-${simStats.platformFee.toFixed(2)}</span></div>
                <div className="flex justify-between border-t border-zinc-800 pt-1 mt-1"><span className="text-emerald-400 font-bold">Profit Net</span><span className="text-emerald-400 font-bold text-lg" data-testid="sim-net-profit">${simStats.netProfit.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">ROI</span><span className="text-emerald-400">{simStats.cost > 0 ? ((simStats.netProfit/simStats.cost)*100).toFixed(1) : 0}%</span></div>
              </div>
            )}
          </div>

          {/* Predefined Plans */}
          <div className="glass-panel p-5 bg-gradient-to-br from-zinc-900 to-violet-950/30 border-violet-700/50">
            <h3 className="text-white font-bold flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-violet-400" />
              Plans d'Investissement
            </h3>
            <p className="text-zinc-500 text-xs mb-3">Choisissez un plan — un message WhatsApp sera envoyé au CEO pour activation.</p>
            <div className="grid grid-cols-2 gap-2">
              {INVESTMENT_PLANS.map(plan => (
                <button
                  key={plan.name}
                  onClick={() => setShowPlanModal(plan)}
                  data-testid={`inv-plan-${plan.name.toLowerCase()}`}
                  className={`text-left p-3 rounded-xl border-2 transition-all hover:scale-[1.02] bg-zinc-900/80 border-${plan.color}-700/60 hover:border-${plan.color}-500`}
                  style={{ borderColor: plan.color === 'emerald' ? '#047857' : plan.color === 'blue' ? '#1d4ed8' : plan.color === 'violet' ? '#6d28d9' : '#b45309' }}
                >
                  <p className={`text-${plan.color}-400 text-xs font-bold uppercase`} style={{ color: plan.color === 'emerald' ? '#34d399' : plan.color === 'blue' ? '#60a5fa' : plan.color === 'violet' ? '#a78bfa' : '#fbbf24' }}>{plan.name}</p>
                  <p className="text-white text-xl font-black my-1">${plan.amount}</p>
                  <p className="text-zinc-400 text-[10px]">{plan.totalVapesBought} vapes + {plan.freeVapes} gratuits</p>
                  <p className="text-emerald-400 text-xs font-bold mt-1">Profit ~${plan.netProfit.toFixed(0)}</p>
                </button>
              ))}
            </div>
            <p className="text-[10px] text-zinc-600 mt-3 text-center">Estimations basées sur la vente complète du stock alloué.</p>
          </div>
        </div>

        {/* Live Chart */}
        <div className="glass-panel p-6 mb-6">
          <h3 className="text-white font-bold mb-3 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-emerald-400" />
            Performance des Stockers — Temps Réel
          </h3>
          {chart_data && chart_data.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chart_data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="name" stroke="#a1a1aa" />
                <YAxis stroke="#a1a1aa" />
                <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 8 }} />
                <Legend />
                <Bar dataKey="sold" name="Vendus" fill="#10b981" />
                <Bar dataKey="revenue" name="Revenus $" fill="#facc15" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-zinc-500 text-center py-8">Pas encore de données. Le graphique se mettra à jour automatiquement lors des ventes.</p>
          )}
        </div>

        {/* My Stockers List */}
        <div className="glass-panel p-6 mb-6">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="text-white font-bold">Mes Stockers ({stockers.length})</h3>
            <button onClick={() => setShowLiveTracking(true)} data-testid="inv-live-tracking-btn"
              className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold px-4 py-2 rounded-lg flex items-center gap-2">
              <span className="w-2 h-2 bg-red-400 rounded-full animate-pulse" />
              🛰️ Suivi en Direct
            </button>
          </div>
          <div className="space-y-2">
            {stockers.map(s => {
              const cycleSold = Math.max(0, (s.initial_stock || 0) - (s.current_stock || 0));
              const pct = s.initial_stock > 0 ? (cycleSold / s.initial_stock) * 100 : 0;
              const cycleRev = s.total_revenue || 0;
              const commRate = s.commission_rate || 12.5;
              const comm = cycleRev * commRate / 100;
              return (
                <div key={s.id} className="bg-zinc-900/60 rounded-xl p-4 border border-zinc-800" data-testid={`inv-stocker-${s.id}`}>
                  <div className="flex items-center justify-between mb-2 flex-wrap">
                    <div>
                      <p className="text-white font-bold">{s.name}</p>
                      <p className="text-zinc-500 text-xs">{(s.country || '').toUpperCase()} • {s.city || '—'}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-bold ${pct >= 75 ? 'bg-emerald-900/50 text-emerald-400' : pct >= 40 ? 'bg-amber-900/50 text-amber-400' : 'bg-zinc-800 text-zinc-400'}`}>{pct.toFixed(0)}%</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center text-xs">
                    <div className="bg-blue-500/10 p-2 rounded"><p className="text-blue-400">Stock</p><p className="text-blue-400 font-bold">{s.initial_stock || 0}</p></div>
                    <div className="bg-emerald-500/10 p-2 rounded"><p className="text-emerald-400">Vendus</p><p className="text-emerald-400 font-bold">{cycleSold}</p></div>
                    <div className="bg-amber-500/10 p-2 rounded"><p className="text-amber-400">Restant</p><p className="text-amber-400 font-bold">{s.current_stock || 0}</p></div>
                    <div className="bg-yellow-500/10 p-2 rounded"><p className="text-yellow-400">Commission {commRate}%</p><p className="text-yellow-400 font-bold">${comm.toFixed(2)}</p></div>
                    <div className="bg-violet-500/10 p-2 rounded"><p className="text-violet-400">Bonus</p><p className="text-violet-400 font-bold">${(s.total_bonus || 0).toFixed(2)}</p></div>
                  </div>
                  <div className="flex justify-between text-[10px] text-zinc-500 mt-2">
                    <span>Début: {(s.start_date || '').substring(0,10)}</span>
                    <span>Fin du cycle: {(s.end_date || '').substring(0,10)}</span>
                  </div>
                </div>
              );
            })}
            {stockers.length === 0 && <p className="text-zinc-500 text-center py-6">Aucun stocker associé pour le moment.</p>}
          </div>
        </div>

        {/* Notifications */}
        <div className="glass-panel p-6">
          <h3 className="text-white font-bold mb-3">Notifications</h3>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {notifications.slice(0, 30).map(n => (
              <div key={n.id} className="bg-zinc-900/60 rounded-lg p-3 border border-zinc-800">
                <p className="text-white text-sm">{n.message}</p>
                <p className="text-zinc-600 text-[10px] mt-1">{(n.created_at || '').substring(0, 16)}</p>
              </div>
            ))}
            {notifications.length === 0 && <p className="text-zinc-500 text-center py-4 text-sm">Pas de notifications</p>}
          </div>
        </div>
      </div>

      {/* INV History modal */}
      {showInvHistory && <HistoryModal userId={inv?.id} onClose={() => setShowInvHistory(false)} />}

      {/* LIVE TRACKING MODAL — pulse-based real-time view */}
      {showLiveTracking && (
        <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm overflow-y-auto p-4" data-testid="inv-live-tracking-modal">
          <div className="max-w-5xl mx-auto bg-gradient-to-br from-zinc-900 via-emerald-950/30 to-zinc-900 border-2 border-emerald-500/40 rounded-3xl p-6 my-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="w-3 h-3 bg-red-400 rounded-full animate-pulse" />
                <h3 className="text-white font-black text-2xl" style={{fontFamily:'var(--font-heading)'}}>🛰️ Suivi en Direct — Vos Stockers</h3>
              </div>
              <button onClick={() => setShowLiveTracking(false)} data-testid="inv-live-close-btn"
                className="bg-zinc-800 hover:bg-red-600 text-white p-2 rounded-full"><X className="w-5 h-5" /></button>
            </div>

            {liveData ? (
              <>
                {/* Live totals */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
                  <div className="bg-blue-500/10 border border-blue-500/40 rounded-xl p-3 text-center">
                    <p className="text-blue-300 text-[10px] uppercase">Stockers Aktif</p>
                    <p className="text-white font-bold text-2xl">{liveData.totals.active_count}</p>
                  </div>
                  <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-3 text-center">
                    <p className="text-emerald-300 text-[10px] uppercase">Total Vann (Cycle)</p>
                    <p className="text-emerald-400 font-bold text-2xl">{liveData.totals.total_sold}</p>
                  </div>
                  <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-3 text-center">
                    <p className="text-amber-300 text-[10px] uppercase">Stock Rete</p>
                    <p className="text-amber-400 font-bold text-2xl">{liveData.totals.total_current}</p>
                  </div>
                  <div className="bg-yellow-500/10 border border-yellow-500/40 rounded-xl p-3 text-center">
                    <p className="text-yellow-300 text-[10px] uppercase">Revni Cycle</p>
                    <p className="text-yellow-400 font-bold text-2xl">${(liveData.totals.total_revenue || 0).toFixed(0)}</p>
                  </div>
                  <div className="bg-violet-500/10 border border-violet-500/40 rounded-xl p-3 text-center">
                    <p className="text-violet-300 text-[10px] uppercase">Stock Inisyal</p>
                    <p className="text-violet-400 font-bold text-2xl">{liveData.totals.total_initial}</p>
                  </div>
                </div>

                {/* Live Stocker cards with pulsing status */}
                <div className="grid md:grid-cols-2 gap-3 mb-5">
                  {liveData.cards.map(c => (
                    <div key={c.id} className="bg-zinc-900/80 border border-zinc-700 rounded-xl p-4 relative overflow-hidden" data-testid={`live-card-${c.id}`}>
                      {/* Status pulse */}
                      <div className="absolute top-3 right-3 flex items-center gap-1">
                        <span className={`w-2.5 h-2.5 rounded-full ${c.active ? 'bg-emerald-400 animate-pulse' : 'bg-zinc-600'}`} />
                        <span className={`text-[10px] font-bold ${c.active ? 'text-emerald-400' : 'text-zinc-500'}`}>{c.active ? 'AKTIF' : 'PA AKTIF'}</span>
                      </div>
                      <p className="text-white font-bold">{c.name}</p>
                      <p className="text-zinc-500 text-xs">📍 {c.city || (c.country || '').toUpperCase()}</p>
                      <div className="grid grid-cols-4 gap-1 text-center text-[10px] mt-3">
                        <div><p className="text-zinc-500">Init</p><p className="text-white font-bold">{c.initial_stock}</p></div>
                        <div><p className="text-zinc-500">Vann</p><p className="text-emerald-400 font-bold">{c.sold}</p></div>
                        <div><p className="text-zinc-500">Rete</p><p className="text-amber-400 font-bold">{c.current_stock}</p></div>
                        <div><p className="text-zinc-500">Revni</p><p className="text-yellow-400 font-bold">${c.revenue.toFixed(0)}</p></div>
                      </div>
                      <div className="h-2 bg-zinc-800 rounded-full overflow-hidden mt-2">
                        <div className="h-full transition-all" style={{ width: `${c.progress_pct}%`, background: c.progress_pct >= 75 ? '#10b981' : c.progress_pct >= 40 ? '#f59e0b' : '#ef4444' }} />
                      </div>
                      <p className="text-zinc-500 text-[10px] mt-1">{c.progress_pct}% sold</p>
                      {c.last_sale && (
                        <p className="text-zinc-400 text-[10px] mt-2">Dènye: {c.last_sale.quantity_sold}× {c.last_sale.vape_model} • {new Date(c.last_sale.recorded_at).toLocaleString('fr-FR')}</p>
                      )}
                    </div>
                  ))}
                  {liveData.cards.length === 0 && <p className="text-zinc-500 text-center py-8 col-span-2">Pa gen stockers asiyen pou ou poko.</p>}
                </div>

                {/* Activity stream */}
                <div className="bg-zinc-950/80 rounded-xl border border-zinc-800 p-4">
                  <p className="text-white font-bold mb-2 text-sm">📡 Aktivite Live (15 dènye)</p>
                  <div className="space-y-1 max-h-[260px] overflow-y-auto">
                    {liveData.stream.map((s, i) => (
                      <div key={i} className="flex items-center justify-between bg-zinc-900/60 p-2 rounded text-xs">
                        <div className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                          <span className="text-white font-bold">{s.stocker_name}</span>
                          <span className="text-zinc-500">📍 {s.city || '—'}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-zinc-300">{s.model}</span>
                          <span className="text-emerald-400 font-bold">+{s.qty}</span>
                          <span className="text-zinc-500">{new Date(s.recorded_at).toLocaleString('fr-FR')}</span>
                        </div>
                      </div>
                    ))}
                    {liveData.stream.length === 0 && <p className="text-zinc-600 text-center py-4">Pa gen aktivite poko.</p>}
                  </div>
                </div>

                <p className="text-zinc-500 text-[10px] text-center mt-3">↻ Otomatik mete ajou chak 10 segond</p>
              </>
            ) : (
              <div className="text-center py-12 text-zinc-500"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>
            )}
          </div>
        </div>
      )}

      {/* PLAN CONFIRMATION MODAL */}
      {showPlanModal && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-2xl p-6 max-w-md w-full border-2 border-violet-500">
            <h4 className="text-white font-bold text-xl mb-1">Plan {showPlanModal.name}</h4>
            <p className="text-violet-400 text-3xl font-black mb-4">${showPlanModal.amount}</p>
            <div className="bg-zinc-950/60 rounded-lg p-3 mb-4 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-zinc-500">Vapes à acheter</span><span className="text-white font-bold">{showPlanModal.totalVapesBought}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Vapes gratuites (+1/12)</span><span className="text-violet-400 font-bold">+{showPlanModal.freeVapes}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Total vendable</span><span className="text-emerald-400 font-bold">{showPlanModal.totalSellable}</span></div>
              <div className="flex justify-between border-t border-zinc-800 pt-2 mt-2"><span className="text-zinc-500">Chiffre d'affaires</span><span className="text-yellow-400">${showPlanModal.grossRevenue.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">- Commission stockers</span><span className="text-zinc-300">${showPlanModal.stockerCommission.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">- Frais plateforme</span><span className="text-zinc-300">${showPlanModal.platformFee.toFixed(2)}</span></div>
              <div className="flex justify-between border-t border-zinc-800 pt-2 mt-2"><span className="text-emerald-400 font-bold">Profit net estimé</span><span className="text-emerald-400 font-bold text-lg">${showPlanModal.netProfit.toFixed(2)}</span></div>
            </div>
            <p className="text-zinc-400 text-xs mb-4">En confirmant, un message sera envoyé sur WhatsApp au CEO (+1 786 630 9220) avec votre demande, et la notification sera enregistrée dans son tableau de bord.</p>
            <div className="flex gap-2">
              <button onClick={() => setShowPlanModal(null)} className="flex-1 bg-zinc-700 text-white px-4 py-2 rounded-lg text-sm">Annuler</button>
              <button onClick={() => handleSelectPlan(showPlanModal)} data-testid="inv-plan-confirm-btn" className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-4 py-2 rounded-lg text-sm">📱 Envoyer via WhatsApp</button>
            </div>
          </div>
        </div>
      )}

      {/* CHANGE SECRET CODE MODAL */}
      {showChangeSecret && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-2xl p-6 max-w-md w-full border-2 border-amber-500">
            <h4 className="text-white font-bold text-lg mb-1">🔐 Changer mon Code Secret</h4>
            <p className="text-zinc-400 text-xs mb-4">Seul vous connaissez ce code. Ne le partagez avec personne, pas même le CEO.</p>
            <input type="password" inputMode="numeric" maxLength={4} placeholder="Ancien code (4 chiffres)" value={oldSecret} onChange={(e) => setOldSecret(e.target.value.replace(/\D/g,'').slice(0,4))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white text-center text-2xl tracking-widest mb-3" data-testid="inv-old-secret" />
            <input type="password" inputMode="numeric" maxLength={4} placeholder="Nouveau code" value={newSecret} onChange={(e) => setNewSecret(e.target.value.replace(/\D/g,'').slice(0,4))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white text-center text-2xl tracking-widest mb-3" data-testid="inv-new-secret" />
            <input type="password" inputMode="numeric" maxLength={4} placeholder="Confirmer le nouveau code" value={confirmSecret} onChange={(e) => setConfirmSecret(e.target.value.replace(/\D/g,'').slice(0,4))} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white text-center text-2xl tracking-widest mb-4" data-testid="inv-confirm-secret" />
            <div className="flex gap-2">
              <button onClick={() => setShowChangeSecret(false)} className="flex-1 bg-zinc-700 text-white px-4 py-2 rounded-lg text-sm">Annuler</button>
              <button onClick={handleChangeSecret} data-testid="inv-save-secret-btn" className="flex-1 bg-amber-600 hover:bg-amber-500 text-white font-bold px-4 py-2 rounded-lg text-sm">Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {/* ADD INVESTMENT MODAL */}
      {showAddInvest && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-2xl p-6 max-w-md w-full border-2 border-emerald-500">
            <h4 className="text-white font-bold text-lg mb-2">Ajouter à mon Investissement</h4>
            <p className="text-zinc-400 text-xs mb-4">Entrez le montant que vous voulez ajouter. Une demande sera envoyée au CEO via WhatsApp et dans son tableau de bord.</p>
            <input type="number" placeholder="Montant ($)" value={addAmount} onChange={(e) => setAddAmount(e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white mb-3" data-testid="inv-add-amount-input" />
            <div className="flex gap-2">
              <button onClick={() => setShowAddInvest(false)} className="flex-1 bg-zinc-700 text-white px-4 py-2 rounded-lg text-sm">Annuler</button>
              <button onClick={handleAddInvestment} data-testid="inv-add-send-btn" className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-4 py-2 rounded-lg text-sm">Envoyer la Demande</button>
            </div>
          </div>
        </div>
      )}

      {/* REINVEST MODAL */}
      {showReinvest && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-2xl p-6 max-w-md w-full border-2 border-violet-500">
            <h4 className="text-white font-bold text-lg mb-2">Réinvestir mes Bénéfices</h4>
            <p className="text-zinc-400 text-xs mb-2">Vos bénéfices seront ajoutés à votre base d'investissement.</p>
            <p className="text-emerald-400 text-sm mb-3">Disponible: ${availableToWithdraw.toFixed(2)}</p>
            <input type="number" placeholder="Montant à réinvestir" value={reinvestAmount} onChange={(e) => setReinvestAmount(e.target.value)} max={availableToWithdraw} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white mb-3" data-testid="inv-reinvest-amount-input" />
            <div className="flex gap-2">
              <button onClick={() => setShowReinvest(false)} className="flex-1 bg-zinc-700 text-white px-4 py-2 rounded-lg text-sm">Annuler</button>
              <button onClick={handleReinvest} data-testid="inv-reinvest-confirm-btn" className="flex-1 bg-violet-600 hover:bg-violet-500 text-white font-bold px-4 py-2 rounded-lg text-sm">Confirmer</button>
            </div>
          </div>
        </div>
      )}

      {/* PROFILE MODAL */}
      {showProfile && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-2xl p-6 max-w-md w-full border-2 border-emerald-500">
            <h4 className="text-white font-bold text-lg mb-3">Mon Profil</h4>
            <div className="w-24 h-24 rounded-full mx-auto bg-zinc-800 overflow-hidden mb-3 flex items-center justify-center">
              {photoUrl ? <img src={photoUrl} alt="" className="w-full h-full object-cover" /> : <User className="w-12 h-12 text-zinc-500" />}
            </div>
            <p className="text-white text-center font-bold">{inv.name}</p>
            <p className="text-zinc-400 text-center text-xs mb-4">{inv.country?.toUpperCase()} • Code: {inv.code}</p>
            <label className="text-zinc-400 text-xs block mb-1">URL de la photo de profil</label>
            <input type="url" placeholder="https://..." value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm mb-3" data-testid="inv-photo-input" />
            <div className="flex gap-2">
              <button onClick={() => setShowProfile(false)} className="flex-1 bg-zinc-700 text-white px-4 py-2 rounded-lg text-sm">Fermer</button>
              <button onClick={handleSavePhoto} data-testid="inv-photo-save-btn" className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-4 py-2 rounded-lg text-sm">Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {/* POLICY MODAL */}
      {showPolicy && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-zinc-900 rounded-2xl p-6 max-w-2xl w-full border-2 border-amber-500 my-8">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-bold text-lg">📜 Politique de Confidentialité — Investisseurs Vape Yard</h4>
              <button onClick={() => setShowPolicy(false)} className="text-zinc-500 hover:text-white">✕</button>
            </div>
            <div className="space-y-3 text-zinc-300 text-sm">
              <p><strong className="text-emerald-400">1. Droit de retrait d'investissement</strong></p>
              <p>Tout investisseur a le droit de demander l'arrêt total de son investissement après une période minimale de <strong>6 mois</strong> depuis le dépôt initial. La demande doit être adressée formellement au CEO de Vape Yard.</p>

              <p><strong className="text-emerald-400">2. Délai de traitement</strong></p>
              <p>Une fois la demande d'arrêt acceptée, le traitement et le remboursement seront effectués sur une période de <strong>2 mois</strong>. Ce délai permet d'éviter toute perturbation dans la marge opérationnelle de l'entreprise.</p>

              <p><strong className="text-amber-400">3. Pénalité en cas de retrait anticipé</strong></p>
              <p>Si un investisseur souhaite arrêter son investissement <strong>avant la fin du cycle convenu</strong> ou pendant la période d'investissement active, une pénalité de <strong>15% sera retenue sur la base de l'investissement initial</strong>. Cette retenue couvre les frais opérationnels et les engagements pris par Vape Yard pendant cette période.</p>

              <p><strong className="text-yellow-400">4. Marge opérationnelle Vape Yard (15%)</strong></p>
              <p>La plateforme Vape Yard prélève en permanence une marge de <strong>15% sur la base d'investissement</strong>. Cette marge couvre:</p>
              <ul className="list-disc list-inside ml-2 text-zinc-400">
                <li>Les maillots, accessoires et uniformes des stockers</li>
                <li>Les services de stickers, sachets et matériel de présentation</li>
                <li>La logistique et les livreurs sur le terrain</li>
                <li>La bonne gestion administrative et organisationnelle des stockers</li>
                <li>Les cadeaux et récompenses aux stockers performants</li>
                <li>L'entretien général des opérations Vape Yard</li>
              </ul>

              <p><strong className="text-violet-400">5. Sécurité des investissements</strong></p>
              <p>Toutes les informations relatives à votre investissement sont confidentielles. Seul le CEO et vous-même y avez accès. La plateforme garantit la transparence totale sur vos bénéfices et la performance de vos stockers en temps réel.</p>

              <p><strong className="text-emerald-400">6. Bénéfices et réinvestissement</strong></p>
              <p>Vous avez le droit de retirer vos bénéfices à tout moment ou de les réinjecter sur votre base d'investissement pour augmenter vos parts. Toute action est suivie en temps réel sur ce tableau de bord.</p>

              <p className="text-xs text-zinc-500 pt-3 border-t border-zinc-800 mt-4">En continuant à utiliser cette plateforme, vous acceptez ces termes. Pour toute question, contactez directement le CEO de Vape Yard.</p>
            </div>
            <button onClick={() => setShowPolicy(false)} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-4 py-3 rounded-lg mt-4">J'ai lu et compris</button>
          </div>
        </div>
      )}
    </div>
  );
};


function App() {
  const [user, setUser] = useState(null);
  const [settings, setSettings] = useState(null);

  // SCREENSHOT PROTECTION: for all users EXCEPT CEO, apply anti-screenshot behaviors
  useEffect(() => {
    const applyProtection = () => {
      const path = window.location.pathname;
      const isCeo = path.includes('/ceo/');
      if (!isCeo) {
        document.body.classList.add('no-screenshot');
        document.oncontextmenu = () => false;
      } else {
        document.body.classList.remove('no-screenshot');
        document.oncontextmenu = null;
      }
    };
    applyProtection();
    // React on route change
    window.addEventListener('popstate', applyProtection);
    const interval = setInterval(applyProtection, 800);
    // Block screenshot key combos
    const keyBlock = (e) => {
      const path = window.location.pathname;
      if (path.includes('/ceo/')) return; // CEO bypass
      if (e.key === 'PrintScreen' ||
          (e.ctrlKey && (e.key === 'p' || e.key === 'P')) ||
          (e.metaKey && e.shiftKey && (e.key === '3' || e.key === '4' || e.key === '5')) ||
          (e.ctrlKey && e.shiftKey && (e.key === 'S' || e.key === 's'))) {
        e.preventDefault();
        try { toast.error("🔒 Kaptè ekran bloke sou platfòm sa"); } catch {}
        return false;
      }
    };
    window.addEventListener('keydown', keyBlock, true);
    return () => {
      window.removeEventListener('popstate', applyProtection);
      window.removeEventListener('keydown', keyBlock, true);
      clearInterval(interval);
      document.body.classList.remove('no-screenshot');
    };
  }, []);

  const login = (userData, settingsData) => {
    setUser(userData);
    setSettings(settingsData);
    localStorage.setItem('vapeyard_user', JSON.stringify(userData));
    localStorage.setItem('vapeyard_settings', JSON.stringify(settingsData));
  };

  const logout = () => {
    setUser(null);
    setSettings(null);
    localStorage.removeItem('vapeyard_user');
    localStorage.removeItem('vapeyard_settings');
  };

  const refreshUser = (updatedData) => {
    setUser(updatedData);
    localStorage.setItem('vapeyard_user', JSON.stringify(updatedData));
  };

  useEffect(() => {
    const savedUser = localStorage.getItem('vapeyard_user');
    const savedSettings = localStorage.getItem('vapeyard_settings');
    if (savedUser) {
      try { setUser(JSON.parse(savedUser)); } catch (e) { localStorage.removeItem('vapeyard_user'); }
    }
    if (savedSettings) {
      try { setSettings(JSON.parse(savedSettings)); } catch (e) { localStorage.removeItem('vapeyard_settings'); }
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, settings, login, logout, refreshUser }}>
      <LangProvider>
      <div className="App">
        <Toaster position="top-center" richColors theme="dark" toastOptions={{ style: { background: '#18181b', border: '1px solid #27272a' } }} />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<WelcomePage />} />
            <Route path="/login/:country" element={<LoginPage />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/ceo/login" element={<CEOLogin />} />
            <Route path="/ceo/dashboard" element={<CEODashboard />} />
            <Route path="/manager/login" element={<ManagerLogin />} />
            <Route path="/manager/dashboard" element={<ManagerDashboardV2 />} />
            <Route path="/network-marketer/login" element={<NetworkMarketerLogin />} />
            <Route path="/network-marketer/dashboard" element={<NMUnifiedDashboard />} />
            <Route path="/network-marketer/team" element={<NetworkMarketerDashboard />} />
            <Route path="/inv/login" element={<InvLogin />} />
            <Route path="/inv/dashboard" element={<InvDashboard />} />
            <Route path="/hookah/dashboard" element={<HookahDashboard />} />
          </Routes>
        </BrowserRouter>
      </div>
      </LangProvider>
    </AuthContext.Provider>
  );
}

export default App;
